import * as React from 'react';
import React__default, { useMemo, useRef, useState, useEffect, useLayoutEffect, Fragment, useCallback } from 'react';
import cn from 'classnames';
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';
import { FixedSizeList } from 'react-window';
import { Combobox, Disclosure, Dialog } from '@headlessui/react';
import { usePopper } from 'react-popper';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
  __assign = Object.assign || function __assign(t) {
      for (var s, i = 1, n = arguments.length; i < n; i++) {
          s = arguments[i];
          for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
      return t;
  };
  return __assign.apply(this, arguments);
};

function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
      t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function")
      for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
          if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
              t[p[i]] = s[p[i]];
      }
  return t;
}

var styles$o = {"skeleton":"Skeleton-module_skeleton__AFJSJ","row":"Skeleton-module_row__v0z4E","circle":"Skeleton-module_circle__UM7M6","shimmer":"Skeleton-module_shimmer__-h-gz","placeholderShimmer":"Skeleton-module_placeholderShimmer__9ITSP"};

/**
 * @experiemental Component show Loading State
 */
var Skeleton = function (_a) {
    var _b = _a.count, count = _b === void 0 ? 1 : _b, lastLineLength = _a.lastLineLength, _c = _a.screenReaderText, screenReaderText = _c === void 0 ? 'Loading' : _c, circle = _a.circle, size = _a.size, className = _a.className, style = _a.style;
    var getStyles = function (isLast) {
        var styles = {};
        if (lastLineLength && isLast) {
            var width = '';
            switch (lastLineLength) {
                case 'short':
                    width = '35%';
                    break;
                case 'medium':
                    width = '70%';
                    break;
                default:
                    width = "".concat(lastLineLength, "%");
            }
            styles.width = width;
        }
        if (size) {
            styles.width = "".concat(size, "px");
        }
        if (style) {
            styles = __assign(__assign({}, styles), style);
        }
        return styles;
    };
    return (React__default.createElement("div", { className: styles$o.skeleton }, new Array(count).fill(null).map(function (_, i) {
        var _a;
        return (React__default.createElement("div", { className: cn(styles$o.row, styles$o.shimmer, (_a = {}, _a[styles$o.circle] = circle, _a), className), style: getStyles(i === count - 1) }, screenReaderText));
    })));
};

var styles$n = {"table":"Table-module_table__s5zPi","tr":"Table-module_tr__g76kU","pinFirstCol":"Table-module_pinFirstCol__-o5QD","td":"Table-module_td__45JS9","th":"Table-module_th__Fbfg0","scrolledX":"Table-module_scrolledX__-zraI","scrollX":"Table-module_scrollX__opu9-","sortButton":"Table-module_sortButton__h1BqY","thead":"Table-module_thead__wemXg","tbody":"Table-module_tbody__xDWuz","verticalScrollOverlay":"Table-module_verticalScrollOverlay__nFlVP","scrolledY":"Table-module_scrolledY__TC4i0","lastRow":"Table-module_lastRow__XMuCt","tfoot":"Table-module_tfoot__NvNdJ","small":"Table-module_small__NL4Oj","loading":"Table-module_loading__slZKh","noData":"Table-module_noData__C3OER"};

var useScrollBarWidth = function () {
    var systemScrollbarWidth = useMemo(function () {
        // Create the measurement node
        var scrollDiv = document.createElement('div');
        scrollDiv.style.width = '100px';
        scrollDiv.style.height = '100px';
        scrollDiv.style.overflow = 'scroll';
        scrollDiv.style.position = 'absolute';
        scrollDiv.style.top = '-9999px';
        document.body.appendChild(scrollDiv);
        // Get the scrollbar width
        var scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
        // Delete the DIV
        document.body.removeChild(scrollDiv);
        return scrollbarWidth;
    }, []);
    return systemScrollbarWidth;
};

var styles$m = {"iwIcon":"Icon-module_iwIcon__MM5At"};

var _path$bb;
function _extends$bc() { _extends$bc = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$bc.apply(this, arguments); }
var SvgAdjust = function SvgAdjust(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$bc({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$bb || (_path$bb = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 5a2 2 0 1 0 0 4 2 2 0 0 0 0-4ZM3 7a4 4 0 0 1 7.876-.992c.04-.005.082-.008.124-.008h9a1 1 0 1 1 0 2h-9a1.02 1.02 0 0 1-.124-.008A4.002 4.002 0 0 1 3 7Zm14 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-3.874 1A4.002 4.002 0 0 1 21 17a4 4 0 0 1-7.874 1H5a1 1 0 1 1 0-2h8.126Z",
    clipRule: "evenodd"
  })));
};

var _path$ba;
function _extends$bb() { _extends$bb = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$bb.apply(this, arguments); }
var SvgAirplay = function SvgAirplay(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$bb({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$ba || (_path$ba = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h1a1 1 0 1 1 0 2H4a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3h16a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3h-1a1 1 0 1 1 0-2h1a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H4Zm8 10a1 1 0 0 1 .768.36l5 6A1 1 0 0 1 17 22H7a1 1 0 0 1-.768-1.64l5-6A1 1 0 0 1 12 14Zm-2.865 6h5.73L12 16.562 9.135 20Z",
    clipRule: "evenodd"
  })));
};

var _path$b9;
function _extends$ba() { _extends$ba = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$ba.apply(this, arguments); }
var SvgAlarmCheck = function SvgAlarmCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$ba({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$b9 || (_path$b9 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.707 2.293a1 1 0 0 1 0 1.414l-3 3a1 1 0 0 1-1.414-1.414l3-3a1 1 0 0 1 1.414 0Zm12.586 0a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1-1.414 1.414l-3-3a1 1 0 0 1 0-1.414ZM12 6a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm7.023 12.63A9 9 0 1 0 3 13a8.97 8.97 0 0 0 1.993 5.65l-1.688 1.63a1 1 0 0 0 1.39 1.44l1.72-1.663A8.962 8.962 0 0 0 12 22c2.12 0 4.068-.733 5.606-1.959l1.692 1.67a1 1 0 0 0 1.405-1.423l-1.68-1.659Zm-3.316-8.337a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 1 1 1.414-1.414L11 13.586l3.293-3.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$b8;
function _extends$b9() { _extends$b9 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b9.apply(this, arguments); }
var SvgAlarmClock = function SvgAlarmClock(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b9({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$b8 || (_path$b8 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.707 2.293a1 1 0 0 1 0 1.414l-3 3a1 1 0 0 1-1.414-1.414l3-3a1 1 0 0 1 1.414 0Zm12.586 0a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1-1.414 1.414l-3-3a1 1 0 0 1 0-1.414ZM12 6a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm7.023 12.63A9 9 0 1 0 3 13a8.97 8.97 0 0 0 1.993 5.65l-1.688 1.63a1 1 0 0 0 1.39 1.44l1.72-1.663A8.962 8.962 0 0 0 12 22c2.12 0 4.068-.733 5.606-1.959l1.692 1.67a1 1 0 0 0 1.405-1.423l-1.68-1.659ZM12 8a1 1 0 0 1 1 1v3.586l1.707 1.707a1 1 0 0 1-1.414 1.414l-2-2A1 1 0 0 1 11 13V9a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$b7;
function _extends$b8() { _extends$b8 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b8.apply(this, arguments); }
var SvgAlarmClockOff = function SvgAlarmClockOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b8({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$b7 || (_path$b7 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 1.414 0l20 20a1 1 0 0 1-1.414 1.414l-3.138-3.138a8.997 8.997 0 0 1-11.85.491l-1.587 1.636a1 1 0 1 1-1.436-1.392l1.614-1.664A9 9 0 0 1 5.43 6.845L4 5.415 2.707 6.706a1 1 0 0 1-1.414-1.414L2.586 4 1.293 2.707a1 1 0 0 1 0-1.414ZM6.846 8.26a7 7 0 0 0 .069 9.654.962.962 0 0 1 .11.11 7 7 0 0 0 9.715.13L6.846 8.26Zm11.447-5.967a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1-1.414 1.414l-3-3a1 1 0 0 1 0-1.414Zm-7.699 1.82a9 9 0 0 1 10.294 10.293 1 1 0 0 1-1.976-.312 6.998 6.998 0 0 0-8.006-8.006 1 1 0 0 1-.312-1.976Z",
    clipRule: "evenodd"
  })));
};

var _path$b6;
function _extends$b7() { _extends$b7 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b7.apply(this, arguments); }
var SvgAlertCircle = function SvgAlertCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b7({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$b6 || (_path$b6 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-5a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0V8a1 1 0 0 1 1-1Zm-1 9a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$b5;
function _extends$b6() { _extends$b6 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b6.apply(this, arguments); }
var SvgAlertOctagon = function SvgAlertOctagon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b6({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$b5 || (_path$b5 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7.153 1.293A1 1 0 0 1 7.86 1h8.28a1 1 0 0 1 .707.293l5.86 5.86A1 1 0 0 1 23 7.86v8.28a1 1 0 0 1-.293.707l-5.86 5.86a1 1 0 0 1-.707.293H7.86a1 1 0 0 1-.707-.293l-5.86-5.86A1 1 0 0 1 1 16.14V7.86a1 1 0 0 1 .293-.707l5.86-5.86ZM8.274 3 3 8.274v7.452L8.274 21h7.452L21 15.726V8.274L15.726 3H8.274ZM12 7a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0V8a1 1 0 0 1 1-1Zm-1 9a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$b4;
function _extends$b5() { _extends$b5 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b5.apply(this, arguments); }
var SvgAlertTriangle = function SvgAlertTriangle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b5({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$b4 || (_path$b4 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.48 2.394a3 3 0 0 1 4.119 1.111v.002L22.597 17.5v.002A3 3 0 0 1 20.002 22H4.004a3 3 0 0 1-2.62-4.5L9.38 3.507v-.002a3 3 0 0 1 1.1-1.111Zm1.51 1.592a1 1 0 0 0-.87.507l-.002.003L3.116 18.5A1 1 0 0 0 3.99 20h16.008a1 1 0 0 0 .865-1.5l-.002-.004-8-14-.002-.003a1 1 0 0 0-.87-.507ZM12 8a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0V9a1 1 0 0 1 1-1Zm-1 9a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$b3;
function _extends$b4() { _extends$b4 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b4.apply(this, arguments); }
var SvgAlignCenter = function SvgAlignCenter(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b4({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$b3 || (_path$b3 = /*#__PURE__*/React.createElement("path", {
    d: "M5 7c-.666 0-1-.333-1-1s.333-1 1-1h13.995c.669 0 1.003.333 1.005 1 .002.667-.332 1-1 1H5Zm4 4c-.666 0-.999-.333-1-1-.001-.667.331-1 .997-1H15c.667 0 1 .332 1 1 0 .668-.333 1-1 1H9Zm-4 4c-.666 0-1-.332-1-1 0-.668.332-1 .998-1H19c.667 0 1 .333 1 1s-.333 1-1 1H5Zm4 4c-.668 0-1.001-.333-1-1 .001-.667.336-1 1.003-1H15c.667.006 1 .341 1 1.005 0 .663-.333.995-1 .995H9Z"
  })));
};

var _path$b2;
function _extends$b3() { _extends$b3 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b3.apply(this, arguments); }
var SvgAlignJustify = function SvgAlignJustify(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b3({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$b2 || (_path$b2 = /*#__PURE__*/React.createElement("path", {
    d: "M5 7c-.666 0-1-.333-1-1s.333-1 1-1h13.995c.669 0 1.003.333 1.005 1 .002.667-.332 1-1 1H5Zm0 4c-.666 0-.999-.333-1-1-.001-.667.331-1 .997-1H19c.667 0 1 .332 1 1 0 .668-.333 1-1 1H5Zm0 4c-.666 0-1-.332-1-1 0-.668.332-1 .998-1H19c.667 0 1 .333 1 1s-.333 1-1 1H5Zm0 4c-.668 0-1.001-.333-1-1 .001-.667.336-1 1.003-1H19c.667.006 1 .341 1 1.005 0 .663-.333.995-1 .995H5Z"
  })));
};

var _path$b1;
function _extends$b2() { _extends$b2 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b2.apply(this, arguments); }
var SvgAlignLeft = function SvgAlignLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b2({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$b1 || (_path$b1 = /*#__PURE__*/React.createElement("path", {
    d: "M5 7c-.666 0-1-.333-1-1s.333-1 1-1h13.995c.669 0 1.003.333 1.005 1 .002.667-.332 1-1 1H5Zm8 4c-.666 0-.999-.333-1-1-.001-.667.331-1 .997-1H19c.667 0 1 .332 1 1 0 .668-.333 1-1 1h-6Zm-8 4c-.666 0-1-.332-1-1 0-.668.332-1 .998-1H19c.667 0 1 .333 1 1s-.333 1-1 1H5Zm8 4c-.668 0-1.001-.333-1-1 .001-.667.336-1 1.003-1H19c.667.006 1 .341 1 1.005 0 .663-.333.995-1 .995h-6Z"
  })));
};

var _path$b0;
function _extends$b1() { _extends$b1 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b1.apply(this, arguments); }
var SvgAlignRight = function SvgAlignRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b1({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$b0 || (_path$b0 = /*#__PURE__*/React.createElement("path", {
    d: "M5 7c-.666 0-1-.333-1-1s.333-1 1-1h13.995c.669 0 1.003.333 1.005 1 .002.667-.332 1-1 1H5Zm0 4c-.666 0-.999-.333-1-1-.001-.667.331-1 .997-1H11c.667 0 1 .332 1 1 0 .668-.333 1-1 1H5Zm0 4c-.666 0-1-.332-1-1 0-.668.332-1 .998-1H19c.667 0 1 .333 1 1s-.333 1-1 1H5Zm0 4c-.668 0-1.001-.333-1-1 .001-.667.336-1 1.003-1H11c.667.006 1 .341 1 1.005 0 .663-.333.995-1 .995H5Z"
  })));
};

var _path$a$;
function _extends$b0() { _extends$b0 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b0.apply(this, arguments); }
var SvgAngleDoubleDown = function SvgAngleDoubleDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b0({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$a$ || (_path$a$ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.293 5.293a1 1 0 0 1 1.414 0L12 9.586l4.293-4.293a1 1 0 1 1 1.414 1.414l-5 5a1 1 0 0 1-1.414 0l-5-5a1 1 0 0 1 0-1.414Zm0 7a1 1 0 0 1 1.414 0L12 16.586l4.293-4.293a1 1 0 0 1 1.414 1.414l-5 5a1 1 0 0 1-1.414 0l-5-5a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$a_;
function _extends$a$() { _extends$a$ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a$.apply(this, arguments); }
var SvgAngleDoubleLeft = function SvgAngleDoubleLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a$({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$a_ || (_path$a_ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.707 6.293a1 1 0 0 1 0 1.414L7.414 12l4.293 4.293a1 1 0 0 1-1.414 1.414l-5-5a1 1 0 0 1 0-1.414l5-5a1 1 0 0 1 1.414 0Zm7 0a1 1 0 0 1 0 1.414L14.414 12l4.293 4.293a1 1 0 0 1-1.414 1.414l-5-5a1 1 0 0 1 0-1.414l5-5a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$aZ;
function _extends$a_() { _extends$a_ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a_.apply(this, arguments); }
var SvgAngleDoubleRight = function SvgAngleDoubleRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a_({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aZ || (_path$aZ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.293 6.293a1 1 0 0 1 1.414 0l5 5a1 1 0 0 1 0 1.414l-5 5a1 1 0 0 1-1.414-1.414L9.586 12 5.293 7.707a1 1 0 0 1 0-1.414Zm7 0a1 1 0 0 1 1.414 0l5 5a1 1 0 0 1 0 1.414l-5 5a1 1 0 0 1-1.414-1.414L16.586 12l-4.293-4.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$aY;
function _extends$aZ() { _extends$aZ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aZ.apply(this, arguments); }
var SvgAngleDoubleUp = function SvgAngleDoubleUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aZ({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aY || (_path$aY = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 5.293a1 1 0 0 1 1.414 0l5 5a1 1 0 0 1-1.414 1.414L12 7.414l-4.293 4.293a1 1 0 0 1-1.414-1.414l5-5Zm-5 12 5-5a1 1 0 0 1 1.414 0l5 5a1 1 0 0 1-1.414 1.414L12 14.414l-4.293 4.293a1 1 0 0 1-1.414-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$aX;
function _extends$aY() { _extends$aY = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aY.apply(this, arguments); }
var SvgAngleDown = function SvgAngleDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aY({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aX || (_path$aX = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.293 8.293a1 1 0 0 1 1.414 0L12 13.586l5.293-5.293a1 1 0 1 1 1.414 1.414l-6 6a1 1 0 0 1-1.414 0l-6-6a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$aW;
function _extends$aX() { _extends$aX = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aX.apply(this, arguments); }
var SvgAngleLeft = function SvgAngleLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aX({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aW || (_path$aW = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.707 5.293a1 1 0 0 1 0 1.414L10.414 12l5.293 5.293a1 1 0 0 1-1.414 1.414l-6-6a1 1 0 0 1 0-1.414l6-6a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$aV;
function _extends$aW() { _extends$aW = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aW.apply(this, arguments); }
var SvgAngleRight = function SvgAngleRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aW({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aV || (_path$aV = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.293 5.293a1 1 0 0 1 1.414 0l6 6a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1-1.414-1.414L13.586 12 8.293 6.707a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$aU;
function _extends$aV() { _extends$aV = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aV.apply(this, arguments); }
var SvgAngleUp = function SvgAngleUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aV({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aU || (_path$aU = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 8.293a1 1 0 0 1 1.414 0l6 6a1 1 0 0 1-1.414 1.414L12 10.414l-5.293 5.293a1 1 0 0 1-1.414-1.414l6-6Z",
    clipRule: "evenodd"
  })));
};

var _path$aT;
function _extends$aU() { _extends$aU = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aU.apply(this, arguments); }
var SvgAngry = function SvgAngry(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aU({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aT || (_path$aT = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm5.572-4.371a1 1 0 0 1 1.3-.557l2.5 1a1 1 0 1 1-.743 1.856l-2.5-1a1 1 0 0 1-.557-1.3Zm10.857 0a1 1 0 0 1-.558 1.3l-2.5 1a1 1 0 0 1-.742-1.857l2.5-1a1 1 0 0 1 1.3.557ZM8.473 14.19c.798-.58 2-1.191 3.526-1.191 1.525 0 2.728.61 3.526 1.191a6.944 6.944 0 0 1 1.17 1.08c.03.035.053.064.07.086l.022.027.007.01.003.003.001.002S16.8 15.4 16 16l.8-.6a1 1 0 0 1-1.597 1.204l-.005-.006a3.709 3.709 0 0 0-.194-.219 4.953 4.953 0 0 0-.655-.57C13.772 15.389 12.975 15 12 15c-.975 0-1.772.39-2.35.809a4.952 4.952 0 0 0-.815.748l-.034.041-.004.006.001-.002H8.8m0 0-.002.002A1 1 0 0 1 7.2 15.4l.8.6-.8-.6.002-.003.003-.004.007-.009.021-.027.07-.086a6.942 6.942 0 0 1 1.171-1.08",
    clipRule: "evenodd"
  })));
};

var _path$aS;
function _extends$aT() { _extends$aT = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aT.apply(this, arguments); }
var SvgAnnoyed = function SvgAnnoyed(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aT({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aS || (_path$aS = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm6-3a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm6 0a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2h-2a1 1 0 0 1-1-1Zm-6 6a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$aR;
function _extends$aS() { _extends$aS = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aS.apply(this, arguments); }
var SvgArchiveFilled = function SvgArchiveFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aS({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aR || (_path$aR = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 7.996h18v11.987c0 1.307-.667 1.973-2 1.998-1.333.025-6 .025-14 0-1.333 0-2-.666-2-1.998V7.996Zm19-1.998H2v-2C2 2.666 2.68 2 4.038 2H20c1.333 0 2 .666 2 1.998v2ZM9 9.993c-.667 0-1 .333-1 .999 0 .666.333.999 1 .999h6c.667 0 1-.333 1-1 0-.665-.333-.998-1-.998H9Z",
    clipRule: "evenodd"
  })));
};

var _path$aQ;
function _extends$aR() { _extends$aR = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aR.apply(this, arguments); }
var SvgArchiveOutline = function SvgArchiveOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aR({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aQ || (_path$aQ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 7.994c-.667 0-1-.333-1-.999V3.998C2 2.666 2.68 2 4.038 2H20c1.333 0 2 .666 2 1.998v2.997c0 .666-.33 1-.993 1H21v11.988c0 1.307-.667 1.973-2 1.998-1.333.025-6 .025-14 0-1.333 0-2-.666-2-1.998V7.994Zm1-3.996v1.998h16V3.998H4Zm1 3.996v11.99h14V7.993H5v.001Zm4 1.999h6c.667 0 1 .333 1 .999 0 .666-.333.999-1 .999H9c-.667 0-1-.333-1-1 0-.665.333-.998 1-.998Z",
    clipRule: "evenodd"
  })));
};

var _path$aP;
function _extends$aQ() { _extends$aQ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aQ.apply(this, arguments); }
var SvgArrowBigDown = function SvgArrowBigDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aQ({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aP || (_path$aP = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 6a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v5h3a1 1 0 0 1 .707 1.707l-7 7a1 1 0 0 1-1.414 0l-7-7A1 1 0 0 1 5 11h3V6Zm2 1v5a1 1 0 0 1-1 1H7.414L12 17.586 16.586 13H15a1 1 0 0 1-1-1V7h-4Z",
    clipRule: "evenodd"
  })));
};

var _path$aO;
function _extends$aP() { _extends$aP = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aP.apply(this, arguments); }
var SvgArrowBigDownDash = function SvgArrowBigDownDash(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aP({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aO || (_path$aO = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 5a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1Zm0 4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2h3a1 1 0 0 1 .707 1.707l-7 7a1 1 0 0 1-1.414 0l-7-7A1 1 0 0 1 5 11h3V9Zm2 1v2a1 1 0 0 1-1 1H7.414L12 17.586 16.586 13H15a1 1 0 0 1-1-1v-2h-4Z",
    clipRule: "evenodd"
  })));
};

var _path$aN;
function _extends$aO() { _extends$aO = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aO.apply(this, arguments); }
var SvgArrowBigLeft = function SvgArrowBigLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aO({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aN || (_path$aN = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.383 4.076A1 1 0 0 1 13 5v3h5a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-5v3a1 1 0 0 1-1.707.707l-7-7a1 1 0 0 1 0-1.414l7-7a1 1 0 0 1 1.09-.217ZM6.414 12 11 16.586V15a1 1 0 0 1 1-1h5v-4h-5a1 1 0 0 1-1-1V7.414L6.414 12Z",
    clipRule: "evenodd"
  })));
};

var _path$aM;
function _extends$aN() { _extends$aN = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aN.apply(this, arguments); }
var SvgArrowBigLeftDash = function SvgArrowBigLeftDash(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aN({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aM || (_path$aM = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.383 4.076A1 1 0 0 1 13 5v3h2a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-2v3a1 1 0 0 1-1.707.707l-7-7a1 1 0 0 1 0-1.414l7-7a1 1 0 0 1 1.09-.217ZM6.414 12 11 16.586V15a1 1 0 0 1 1-1h2v-4h-2a1 1 0 0 1-1-1V7.414L6.414 12ZM19 8a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0V9a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$aL;
function _extends$aM() { _extends$aM = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aM.apply(this, arguments); }
var SvgArrowBigRight = function SvgArrowBigRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aM({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aL || (_path$aL = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.617 4.076a1 1 0 0 1 1.09.217l7 7a1 1 0 0 1 0 1.414l-7 7A1 1 0 0 1 11 19v-3H6a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h5V5a1 1 0 0 1 .617-.924ZM13 7.414V9a1 1 0 0 1-1 1H7v4h5a1 1 0 0 1 1 1v1.586L17.586 12 13 7.414Z",
    clipRule: "evenodd"
  })));
};

var _path$aK;
function _extends$aL() { _extends$aL = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aL.apply(this, arguments); }
var SvgArrowBigRightDash = function SvgArrowBigRightDash(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aL({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aK || (_path$aK = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.617 4.076a1 1 0 0 1 1.09.217l7 7a1 1 0 0 1 0 1.414l-7 7A1 1 0 0 1 11 19v-3H9a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h2V5a1 1 0 0 1 .617-.924ZM13 7.414V9a1 1 0 0 1-1 1h-2v4h2a1 1 0 0 1 1 1v1.586L17.586 12 13 7.414ZM5 8a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0V9a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$aJ;
function _extends$aK() { _extends$aK = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aK.apply(this, arguments); }
var SvgArrowBigUp = function SvgArrowBigUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aK({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aJ || (_path$aJ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 4.293a1 1 0 0 1 1.414 0l7 7A1 1 0 0 1 19 13h-3v5a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-5H5a1 1 0 0 1-.707-1.707l7-7ZM7.414 11H9a1 1 0 0 1 1 1v5h4v-5a1 1 0 0 1 1-1h1.586L12 6.414 7.414 11Z",
    clipRule: "evenodd"
  })));
};

var _path$aI;
function _extends$aJ() { _extends$aJ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aJ.apply(this, arguments); }
var SvgArrowBigUpDash = function SvgArrowBigUpDash(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aJ({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aI || (_path$aI = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 4.293a1 1 0 0 1 1.414 0l7 7A1 1 0 0 1 19 13h-3v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-2H5a1 1 0 0 1-.707-1.707l7-7ZM7.414 11H9a1 1 0 0 1 1 1v2h4v-2a1 1 0 0 1 1-1h1.586L12 6.414 7.414 11ZM8 19a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$aH;
function _extends$aI() { _extends$aI = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aI.apply(this, arguments); }
var SvgArrowDown = function SvgArrowDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aI({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aH || (_path$aH = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a1 1 0 0 1 1 1v11.586l5.293-5.293a1 1 0 0 1 1.414 1.414l-7 7a1 1 0 0 1-1.414 0l-7-7a1 1 0 1 1 1.414-1.414L11 16.586V5a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$aG;
function _extends$aH() { _extends$aH = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aH.apply(this, arguments); }
var SvgArrowDownAxAsc = function SvgArrowDownAxAsc(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aH({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aG || (_path$aG = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 3a1 1 0 0 1 1 1v13.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L6 17.586V4a1 1 0 0 1 1-1Zm10.5 2A1.5 1.5 0 0 0 16 6.5V7h3v-.5A1.5 1.5 0 0 0 17.5 5ZM21 6.5a3.5 3.5 0 1 0-7 0V10a1 1 0 1 0 2 0V9h3v1a1 1 0 1 0 2 0V6.5ZM14 14a1 1 0 0 1 1-1h5a1 1 0 0 1 .768 1.64L17.135 19H20a1 1 0 1 1 0 2h-5a1 1 0 0 1-.768-1.64L17.865 15H15a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$aF;
function _extends$aG() { _extends$aG = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aG.apply(this, arguments); }
var SvgArrowDownCircle = function SvgArrowDownCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aG({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aF || (_path$aF = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-5a1 1 0 0 1 1 1v5.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L11 13.586V8a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$aE;
function _extends$aF() { _extends$aF = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aF.apply(this, arguments); }
var SvgArrowDownFromLine = function SvgArrowDownFromLine(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aF({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aE || (_path$aE = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 3a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1Zm8 3a1 1 0 0 1 1 1v11.586l4.293-4.293a1 1 0 0 1 1.414 1.414l-6 6a1 1 0 0 1-1.414 0l-6-6a1 1 0 1 1 1.414-1.414L11 18.586V7a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$aD;
function _extends$aE() { _extends$aE = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aE.apply(this, arguments); }
var SvgArrowDownLeft = function SvgArrowDownLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aE({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aD || (_path$aD = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 6a1 1 0 0 1 1 1v7.586l8.293-8.293a1 1 0 1 1 1.414 1.414L9.414 16H17a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$aC;
function _extends$aD() { _extends$aD = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aD.apply(this, arguments); }
var SvgArrowDownLeftFromCircle = function SvgArrowDownLeftFromCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aD({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aC || (_path$aC = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13.756 3.173A9 9 0 0 0 3 12a1 1 0 1 1-2 0 11 11 0 1 1 11 11 1 1 0 1 1 0-2 9 9 0 0 0 1.756-17.827Zm-1.049 8.12a1 1 0 0 1 0 1.414L4.414 21H8a1 1 0 1 1 0 2H2a1 1 0 0 1-1-1v-6a1 1 0 1 1 2 0v3.586l8.293-8.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$aB;
function _extends$aC() { _extends$aC = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aC.apply(this, arguments); }
var SvgArrowDownLeftSquare = function SvgArrowDownLeftSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aC({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aB || (_path$aB = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm6 2a1 1 0 0 1 1 1v5.586l6.293-6.293a1 1 0 1 1 1.414 1.414L10.414 15H16a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$aA;
function _extends$aB() { _extends$aB = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aB.apply(this, arguments); }
var SvgArrowDownNarrowWide = function SvgArrowDownNarrowWide(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aB({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aA || (_path$aA = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 3a1 1 0 0 1 1 1v13.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L6 17.586V4a1 1 0 0 1 1-1Zm3 1a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1Zm0 4a1 1 0 0 1 1-1h7a1 1 0 1 1 0 2h-7a1 1 0 0 1-1-1Zm0 4a1 1 0 0 1 1-1h10a1 1 0 1 1 0 2H11a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$az;
function _extends$aA() { _extends$aA = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aA.apply(this, arguments); }
var SvgArrowDownNumAsc = function SvgArrowDownNumAsc(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aA({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$az || (_path$az = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 3a1 1 0 0 1 1 1v13.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L6 17.586V4a1 1 0 0 1 1-1Zm10 2a1 1 0 0 0-1 1v2a1 1 0 1 0 2 0V6a1 1 0 0 0-1-1Zm-3 1a3 3 0 1 1 6 0v2a3 3 0 1 1-6 0V6Zm0 8a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5h1a1 1 0 1 1 0 2h-4a1 1 0 1 1 0-2h1v-4h-1a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$ay;
function _extends$az() { _extends$az = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$az.apply(this, arguments); }
var SvgArrowDownRight = function SvgArrowDownRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$az({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$ay || (_path$ay = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.293 6.293a1 1 0 0 1 1.414 0L16 14.586V7a1 1 0 1 1 2 0v10a1 1 0 0 1-1 1H7a1 1 0 1 1 0-2h7.586L6.293 7.707a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$ax;
function _extends$ay() { _extends$ay = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$ay.apply(this, arguments); }
var SvgArrowDownRightFromCircle = function SvgArrowDownRightFromCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$ay({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$ax || (_path$ax = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.444 3.685A9 9 0 1 0 12 21a1 1 0 1 1 0 2 11 11 0 1 1 11-11 1 1 0 1 1-2 0 9 9 0 0 0-5.556-8.315Zm-4.151 7.608a1 1 0 0 1 1.414 0L21 19.586V16a1 1 0 1 1 2 0v6a1 1 0 0 1-1 1h-6a1 1 0 1 1 0-2h3.586l-8.293-8.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$aw;
function _extends$ax() { _extends$ax = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$ax.apply(this, arguments); }
var SvgArrowDownRightSquare = function SvgArrowDownRightSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$ax({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aw || (_path$aw = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm5.293 2.293a1 1 0 0 1 1.414 0L15 13.586V8a1 1 0 1 1 2 0v8a1 1 0 0 1-1 1H8a1 1 0 1 1 0-2h5.586L7.293 8.707a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$av;
function _extends$aw() { _extends$aw = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aw.apply(this, arguments); }
var SvgArrowDownSquare = function SvgArrowDownSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aw({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$av || (_path$av = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm10 2a1 1 0 0 1 1 1v5.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L11 13.586V8a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$au;
function _extends$av() { _extends$av = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$av.apply(this, arguments); }
var SvgArrowDownToDot = function SvgArrowDownToDot(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$av({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$au || (_path$au = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 1a1 1 0 0 1 1 1v11.586l5.293-5.293a1 1 0 1 1 1.414 1.414l-7 7a1 1 0 0 1-1.414 0l-7-7a1 1 0 0 1 1.414-1.414L11 13.586V2a1 1 0 0 1 1-1Zm-2 20a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z",
    clipRule: "evenodd"
  })));
};

var _path$at;
function _extends$au() { _extends$au = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$au.apply(this, arguments); }
var SvgArrowDownUp = function SvgArrowDownUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$au({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$at || (_path$at = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 3a1 1 0 0 1 1 1v13.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L6 17.586V4a1 1 0 0 1 1-1Zm9.293.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L18 6.414V20a1 1 0 1 1-2 0V6.414l-2.293 2.293a1 1 0 1 1-1.414-1.414l4-4Z",
    clipRule: "evenodd"
  })));
};

var _path$as;
function _extends$at() { _extends$at = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$at.apply(this, arguments); }
var SvgArrowDownWideNarrow = function SvgArrowDownWideNarrow(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$at({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$as || (_path$as = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 3a1 1 0 0 1 1 1v13.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L6 17.586V4a1 1 0 0 1 1-1Zm3 1a1 1 0 0 1 1-1h10a1 1 0 1 1 0 2H11a1 1 0 0 1-1-1Zm0 4a1 1 0 0 1 1-1h7a1 1 0 1 1 0 2h-7a1 1 0 0 1-1-1Zm0 4a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$ar;
function _extends$as() { _extends$as = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$as.apply(this, arguments); }
var SvgArrowLeft = function SvgArrowLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$as({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$ar || (_path$ar = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.707 4.293a1 1 0 0 1 0 1.414L7.414 11H19a1 1 0 1 1 0 2H7.414l5.293 5.293a1 1 0 0 1-1.414 1.414l-7-7a1 1 0 0 1 0-1.414l7-7a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$aq;
function _extends$ar() { _extends$ar = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$ar.apply(this, arguments); }
var SvgArrowLeftCircle = function SvgArrowLeftCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$ar({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aq || (_path$aq = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11.707-4.707a1 1 0 0 1 0 1.414L10.414 11H16a1 1 0 1 1 0 2h-5.586l2.293 2.293a1 1 0 0 1-1.414 1.414l-4-4a1 1 0 0 1 0-1.414l4-4a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$ap;
function _extends$aq() { _extends$aq = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aq.apply(this, arguments); }
var SvgArrowLeftFromLine = function SvgArrowLeftFromLine(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aq({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$ap || (_path$ap = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M21 4a1 1 0 0 1 1 1v14a1 1 0 1 1-2 0V5a1 1 0 0 1 1-1ZM9.707 5.293a1 1 0 0 1 0 1.414L5.414 11H17a1 1 0 1 1 0 2H5.414l4.293 4.293a1 1 0 0 1-1.414 1.414l-6-6a1 1 0 0 1 0-1.414l6-6a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$ao;
function _extends$ap() { _extends$ap = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$ap.apply(this, arguments); }
var SvgArrowLeftSquare = function SvgArrowLeftSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$ap({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$ao || (_path$ao = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm10.707 2.293a1 1 0 0 1 0 1.414L10.414 11H16a1 1 0 1 1 0 2h-5.586l2.293 2.293a1 1 0 0 1-1.414 1.414l-4-4a1 1 0 0 1 0-1.414l4-4a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$an;
function _extends$ao() { _extends$ao = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$ao.apply(this, arguments); }
var SvgArrowLeftToLine = function SvgArrowLeftToLine(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$ao({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$an || (_path$an = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 4a1 1 0 0 1 1 1v14a1 1 0 1 1-2 0V5a1 1 0 0 1 1-1Zm10.707 1.293a1 1 0 0 1 0 1.414L9.414 11H21a1 1 0 1 1 0 2H9.414l4.293 4.293a1 1 0 0 1-1.414 1.414l-6-6a1 1 0 0 1 0-1.414l6-6a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$am;
function _extends$an() { _extends$an = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$an.apply(this, arguments); }
var SvgArrowRight = function SvgArrowRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$an({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$am || (_path$am = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 4.293a1 1 0 0 1 1.414 0l7 7a1 1 0 0 1 0 1.414l-7 7a1 1 0 0 1-1.414-1.414L16.586 13H5a1 1 0 1 1 0-2h11.586l-5.293-5.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$al;
function _extends$am() { _extends$am = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$am.apply(this, arguments); }
var SvgArrowRightCircle = function SvgArrowRightCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$am({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$al || (_path$al = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm10.293-3.293a1 1 0 0 1 1.414-1.414l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414-1.414L13.586 13H8a1 1 0 1 1 0-2h5.586l-2.293-2.293Z",
    clipRule: "evenodd"
  })));
};

var _path$ak;
function _extends$al() { _extends$al = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$al.apply(this, arguments); }
var SvgArrowRightFromLine = function SvgArrowRightFromLine(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$al({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$ak || (_path$ak = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 4a1 1 0 0 1 1 1v14a1 1 0 1 1-2 0V5a1 1 0 0 1 1-1Zm11.293 1.293a1 1 0 0 1 1.414 0l6 6a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1-1.414-1.414L18.586 13H7a1 1 0 1 1 0-2h11.586l-4.293-4.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$aj;
function _extends$ak() { _extends$ak = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$ak.apply(this, arguments); }
var SvgArrowRightLeft = function SvgArrowRightLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$ak({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aj || (_path$aj = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.293 2.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414-1.414L17.586 8H4a1 1 0 0 1 0-2h13.586l-2.293-2.293a1 1 0 0 1 0-1.414Zm-6.586 10a1 1 0 0 1 0 1.414L6.414 16H20a1 1 0 1 1 0 2H6.414l2.293 2.293a1 1 0 1 1-1.414 1.414l-4-4a1 1 0 0 1 0-1.414l4-4a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$ai;
function _extends$aj() { _extends$aj = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aj.apply(this, arguments); }
var SvgArrowRightSquare = function SvgArrowRightSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aj({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$ai || (_path$ai = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm9.293 2.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414-1.414L13.586 13H8a1 1 0 1 1 0-2h5.586l-2.293-2.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$ah;
function _extends$ai() { _extends$ai = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$ai.apply(this, arguments); }
var SvgArrowRightToLine = function SvgArrowRightToLine(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$ai({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$ah || (_path$ah = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M21 4a1 1 0 0 1 1 1v14a1 1 0 1 1-2 0V5a1 1 0 0 1 1-1ZM10.293 5.293a1 1 0 0 1 1.414 0l6 6a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1-1.414-1.414L14.586 13H3a1 1 0 1 1 0-2h11.586l-4.293-4.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$ag;
function _extends$ah() { _extends$ah = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$ah.apply(this, arguments); }
var SvgArrowUp = function SvgArrowUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$ah({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$ag || (_path$ag = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 4.293a1 1 0 0 1 1.414 0l7 7a1 1 0 0 1-1.414 1.414L13 7.414V19a1 1 0 1 1-2 0V7.414l-5.293 5.293a1 1 0 0 1-1.414-1.414l7-7Z",
    clipRule: "evenodd"
  })));
};

var _path$af;
function _extends$ag() { _extends$ag = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$ag.apply(this, arguments); }
var SvgArrowUpAzDesc = function SvgArrowUpAzDesc(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$ag({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$af || (_path$af = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.293 3.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L8 6.414V20a1 1 0 1 1-2 0V6.414L3.707 8.707a1 1 0 0 1-1.414-1.414l4-4ZM17.5 5A1.5 1.5 0 0 0 16 6.5V7h3v-.5A1.5 1.5 0 0 0 17.5 5ZM21 6.5a3.5 3.5 0 1 0-7 0V10a1 1 0 1 0 2 0V9h3v1a1 1 0 1 0 2 0V6.5ZM14 14a1 1 0 0 1 1-1h5a1 1 0 0 1 .768 1.64L17.135 19H20a1 1 0 1 1 0 2h-5a1 1 0 0 1-.768-1.64L17.865 15H15a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$ae;
function _extends$af() { _extends$af = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$af.apply(this, arguments); }
var SvgArrowUpCircle = function SvgArrowUpCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$af({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$ae || (_path$ae = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm6.293-.707 4-4a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L13 10.414V16a1 1 0 1 1-2 0v-5.586l-2.293 2.293a1 1 0 0 1-1.414-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$ad;
function _extends$ae() { _extends$ae = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$ae.apply(this, arguments); }
var SvgArrowUpDown = function SvgArrowUpDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$ae({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$ad || (_path$ad = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.293 3.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L8 6.414V20a1 1 0 1 1-2 0V6.414L3.707 8.707a1 1 0 0 1-1.414-1.414l4-4ZM17 3a1 1 0 0 1 1 1v13.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 1.414-1.414L16 17.586V4a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$ac;
function _extends$ad() { _extends$ad = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$ad.apply(this, arguments); }
var SvgArrowUpFromDot = function SvgArrowUpFromDot(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$ad({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$ac || (_path$ac = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 1.293a1 1 0 0 1 1.414 0l7 7a1 1 0 0 1-1.414 1.414L13 4.414V16a1 1 0 1 1-2 0V4.414L5.707 9.707a1 1 0 0 1-1.414-1.414l7-7ZM10 21a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z",
    clipRule: "evenodd"
  })));
};

var _path$ab;
function _extends$ac() { _extends$ac = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$ac.apply(this, arguments); }
var SvgArrowUpLeft = function SvgArrowUpLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$ac({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$ab || (_path$ab = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 7a1 1 0 0 1 1-1h10a1 1 0 1 1 0 2H9.414l8.293 8.293a1 1 0 0 1-1.414 1.414L8 9.414V17a1 1 0 1 1-2 0V7Z",
    clipRule: "evenodd"
  })));
};

var _path$aa;
function _extends$ab() { _extends$ab = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$ab.apply(this, arguments); }
var SvgArrowUpLeftFromCircle = function SvgArrowUpLeftFromCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$ab({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$aa || (_path$aa = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1 2a1 1 0 0 1 1-1h6a1 1 0 0 1 0 2H4.414l8.293 8.293a1 1 0 0 1-1.414 1.414L3 4.414V8a1 1 0 0 1-2 0V2Zm16 2.517A9 9 0 0 0 12 3a1 1 0 1 1 0-2A11 11 0 1 1 1 12a1 1 0 1 1 2 0 9 9 0 1 0 14-7.483Z",
    clipRule: "evenodd"
  })));
};

var _path$a9;
function _extends$aa() { _extends$aa = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$aa.apply(this, arguments); }
var SvgArrowUpLeftSquare = function SvgArrowUpLeftSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$aa({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$a9 || (_path$a9 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm5 3a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2h-5.586l6.293 6.293a1 1 0 0 1-1.414 1.414L9 10.414V16a1 1 0 1 1-2 0V8Z",
    clipRule: "evenodd"
  })));
};

var _path$a8;
function _extends$a9() { _extends$a9 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a9.apply(this, arguments); }
var SvgArrowUpNarrowWide = function SvgArrowUpNarrowWide(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a9({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$a8 || (_path$a8 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.293 3.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L8 6.414V20a1 1 0 1 1-2 0V6.414L3.707 8.707a1 1 0 0 1-1.414-1.414l4-4ZM15 13h-4a1 1 0 1 1 0-2h4a1 1 0 1 1 0 2Zm3 4h-7a1 1 0 1 1 0-2h7a1 1 0 1 1 0 2Zm-7 4a1 1 0 1 1 0-2h10a1 1 0 1 1 0 2H11Z",
    clipRule: "evenodd"
  })));
};

var _path$a7;
function _extends$a8() { _extends$a8 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a8.apply(this, arguments); }
var SvgArrowUpNumDesc = function SvgArrowUpNumDesc(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a8({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$a7 || (_path$a7 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.293 3.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L8 6.414V20a1 1 0 1 1-2 0V6.414L3.707 8.707a1 1 0 0 1-1.414-1.414l4-4ZM17 5a1 1 0 0 0-1 1v2a1 1 0 1 0 2 0V6a1 1 0 0 0-1-1Zm-3 1a3 3 0 1 1 6 0v2a3 3 0 1 1-6 0V6Zm0 8a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5h1a1 1 0 1 1 0 2h-4a1 1 0 1 1 0-2h1v-4h-1a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$a6;
function _extends$a7() { _extends$a7 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a7.apply(this, arguments); }
var SvgArrowUpRight = function SvgArrowUpRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a7({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$a6 || (_path$a6 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 8a1 1 0 0 1 0-2h10a1 1 0 0 1 1 1v10a1 1 0 1 1-2 0V9.414l-8.293 8.293a1 1 0 0 1-1.414-1.414L14.586 8H7Z",
    clipRule: "evenodd"
  })));
};

var _path$a5;
function _extends$a6() { _extends$a6 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a6.apply(this, arguments); }
var SvgArrowUpRightFromCircle = function SvgArrowUpRightFromCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a6({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$a5 || (_path$a5 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 9 9 1 1 0 1 1 2 0A11 11 0 1 1 12 1a1 1 0 1 1 0 2Zm4 0a1 1 0 1 1 0-2h6a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0V4.414l-8.293 8.293a1 1 0 0 1-1.414-1.414L19.586 3H16Z",
    clipRule: "evenodd"
  })));
};

var _path$a4;
function _extends$a5() { _extends$a5 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a5.apply(this, arguments); }
var SvgArrowUpRightSquare = function SvgArrowUpRightSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a5({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$a4 || (_path$a4 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm6 4a1 1 0 0 1 0-2h8a1 1 0 0 1 1 1v8a1 1 0 1 1-2 0v-5.586l-6.293 6.293a1 1 0 0 1-1.414-1.414L13.586 9H8Z",
    clipRule: "evenodd"
  })));
};

var _path$a3;
function _extends$a4() { _extends$a4 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a4.apply(this, arguments); }
var SvgArrowUpSquare = function SvgArrowUpSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a4({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$a3 || (_path$a3 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm9.293 2.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L13 10.414V16a1 1 0 1 1-2 0v-5.586l-2.293 2.293a1 1 0 0 1-1.414-1.414l4-4Z",
    clipRule: "evenodd"
  })));
};

var _path$a2;
function _extends$a3() { _extends$a3 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a3.apply(this, arguments); }
var SvgArrowUpToLine = function SvgArrowUpToLine(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a3({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$a2 || (_path$a2 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 3a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1Zm7.293 3.293a1 1 0 0 1 1.414 0l6 6a1 1 0 0 1-1.414 1.414L13 9.414V21a1 1 0 1 1-2 0V9.414l-4.293 4.293a1 1 0 0 1-1.414-1.414l6-6Z",
    clipRule: "evenodd"
  })));
};

var _path$a1;
function _extends$a2() { _extends$a2 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a2.apply(this, arguments); }
var SvgArrowUpWideNarrow = function SvgArrowUpWideNarrow(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a2({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$a1 || (_path$a1 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.293 3.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L8 6.414V20a1 1 0 1 1-2 0V6.414L3.707 8.707a1 1 0 0 1-1.414-1.414l4-4ZM21 13H11a1 1 0 1 1 0-2h10a1 1 0 1 1 0 2Zm-3 4h-7a1 1 0 1 1 0-2h7a1 1 0 1 1 0 2Zm-7 4a1 1 0 1 1 0-2h4a1 1 0 1 1 0 2h-4Z",
    clipRule: "evenodd"
  })));
};

var _path$a0;
function _extends$a1() { _extends$a1 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a1.apply(this, arguments); }
var SvgArrowsUpFromLine = function SvgArrowsUpFromLine(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a1({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$a0 || (_path$a0 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.293 2.293a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1-1.414 1.414L8 5.414V17a1 1 0 1 1-2 0V5.414L4.707 6.707a1 1 0 0 1-1.414-1.414l3-3Zm10 0a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1-1.414 1.414L18 5.414V17a1 1 0 1 1-2 0V5.414l-1.293 1.293a1 1 0 1 1-1.414-1.414l3-3ZM3 21a1 1 0 0 1 1-1h16a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$9$;
function _extends$a0() { _extends$a0 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a0.apply(this, arguments); }
var SvgBadgeAlert = function SvgBadgeAlert(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a0({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9$ || (_path$9$ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3.005a3 3 0 0 0-2.527 1.384 1 1 0 0 1-1.062.437 3 3 0 0 0-3.585 3.577 1 1 0 0 1-.441 1.062 3 3 0 0 0 0 5.07 1 1 0 0 1 .441 1.062 3 3 0 0 0 3.585 3.577 1 1 0 0 1 1.063.44 3 3 0 0 0 5.062 0 1 1 0 0 1 1.06-.44 3 3 0 0 0 3.578-3.585 1 1 0 0 1 .438-1.062 3 3 0 0 0 0-5.055 1 1 0 0 1-.438-1.061 3 3 0 0 0-3.585-3.585 1 1 0 0 1-1.061-.437A3 3 0 0 0 12 3.005ZM9.6 1.619a5 5 0 0 1 6.215 1.152 5 5 0 0 1 5.415 5.415 5 5 0 0 1 0 7.628 4.998 4.998 0 0 1-5.402 5.413 4.998 4.998 0 0 1-7.642.001 5 5 0 0 1-5.412-5.4 5 5 0 0 1 0-7.656 5 5 0 0 1 5.413-5.4A5 5 0 0 1 9.6 1.619ZM12 7a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0V8a1 1 0 0 1 1-1Zm-1 9a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$9_;
function _extends$9$() { _extends$9$ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9$.apply(this, arguments); }
var SvgBadgeCheck = function SvgBadgeCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9$({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9_ || (_path$9_ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3.005a3 3 0 0 0-2.527 1.384 1 1 0 0 1-1.062.437 3 3 0 0 0-3.585 3.577 1 1 0 0 1-.441 1.062 3 3 0 0 0 0 5.07 1 1 0 0 1 .441 1.062 3 3 0 0 0 3.585 3.577 1 1 0 0 1 1.063.44 3 3 0 0 0 5.062 0 1 1 0 0 1 1.06-.44 3 3 0 0 0 3.578-3.585 1 1 0 0 1 .438-1.062 3 3 0 0 0 0-5.055 1 1 0 0 1-.438-1.061 3 3 0 0 0-3.585-3.585 1 1 0 0 1-1.061-.437A3 3 0 0 0 12 3.005ZM9.6 1.619a5 5 0 0 1 6.215 1.152 5 5 0 0 1 5.415 5.415 5 5 0 0 1 0 7.628 4.998 4.998 0 0 1-5.402 5.413 4.998 4.998 0 0 1-7.642.001 5 5 0 0 1-5.412-5.4 5 5 0 0 1 0-7.656 5 5 0 0 1 5.413-5.4A5 5 0 0 1 9.6 1.619Zm6.108 7.674a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 1 1 1.414-1.414L11 12.586l3.293-3.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$9Z;
function _extends$9_() { _extends$9_ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9_.apply(this, arguments); }
var SvgBalanceFilled = function SvgBalanceFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9_({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9Z || (_path$9Z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H4Zm6.213 8.465c.229-.484.434-.919.434-1.641a2.824 2.824 0 0 0-5.647 0c0 1.127.369 3.576.84 5.647h3.837c-.247-2.35.174-3.24.536-4.006Zm-.336 5.417h-3.68c.07.253.143.49.215.706.156.47.706 1.412 1.764 1.412 1.06 0 1.807-1.059 1.765-1.765l-.064-.353ZM13.4 8.824c0 .722.206 1.157.434 1.641.363.767.783 1.656.537 4.006h3.836c.471-2.071.84-4.52.84-5.647a2.824 2.824 0 0 0-5.647 0Zm4.45 7.058h-3.68c-.02.115-.041.233-.064.353-.042.706.706 1.765 1.765 1.765 1.058 0 1.608-.942 1.764-1.412.072-.216.144-.453.216-.706Z",
    clipRule: "evenodd"
  })));
};

var _path$9Y, _path2$A;
function _extends$9Z() { _extends$9Z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9Z.apply(this, arguments); }
var SvgBalanceOutline = function SvgBalanceOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9Z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9Y || (_path$9Y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H4Zm16 2H4v16h16V4Z",
    clipRule: "evenodd"
  })), _path2$A || (_path2$A = /*#__PURE__*/React.createElement("path", {
    d: "M10.213 10.465c.229-.484.434-.919.434-1.641a2.824 2.824 0 0 0-5.647 0c0 1.127.369 3.576.84 5.647h3.837c-.247-2.35.174-3.24.536-4.006Zm-.336 5.417h-3.68c.07.253.143.49.215.706.156.47.706 1.412 1.764 1.412 1.06 0 1.807-1.059 1.765-1.765l-.064-.353Zm3.957-5.417c-.228-.484-.434-.919-.434-1.641a2.824 2.824 0 0 1 5.647 0c0 1.127-.369 3.576-.84 5.647H14.37c.246-2.35-.175-3.24-.537-4.006Zm.336 5.417h3.68c-.07.253-.143.49-.215.706-.156.47-.706 1.412-1.764 1.412-1.06 0-1.807-1.059-1.765-1.765.023-.12.044-.238.064-.353Z"
  })));
};

var _path$9X;
function _extends$9Y() { _extends$9Y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9Y.apply(this, arguments); }
var SvgBanFilled = function SvgBanFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9Y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9X || (_path$9X = /*#__PURE__*/React.createElement("path", {
    d: "m4.358 5.557 14.085 14.085A9.956 9.956 0 0 1 12 22C6.486 22 2 17.514 2 12c0-2.453.887-4.702 2.358-6.443Zm1.436-1.393A9.952 9.952 0 0 1 12 2c5.514 0 10 4.486 10 10 0 2.343-.81 4.5-2.164 6.206L5.794 4.164Z"
  })));
};

var _path$9W;
function _extends$9X() { _extends$9X = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9X.apply(this, arguments); }
var SvgBanOutline = function SvgBanOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9X({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9W || (_path$9W = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.968 6.382a9 9 0 0 0 12.65 12.65L4.968 6.382Zm-.954-1.947A10.962 10.962 0 0 0 1 12c0 6.075 4.925 11 11 11a10.96 10.96 0 0 0 7.565-3.015.997.997 0 0 0 .42-.42A10.962 10.962 0 0 0 23 12c0-6.075-4.925-11-11-11-2.93 0-5.594 1.146-7.565 3.014a1 1 0 0 0-.42.421Zm2.368.533 12.65 12.65a9 9 0 0 0-12.65-12.65Z",
    clipRule: "evenodd"
  })));
};

var _path$9V;
function _extends$9W() { _extends$9W = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9W.apply(this, arguments); }
var SvgBandaidFilled = function SvgBandaidFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9W({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9V || (_path$9V = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "m11.41 3.646 8.939 8.89c1.767 1.767 2.475 5.302 0 7.777-2.475 2.475-6.046 1.804-7.779 0l-8.938-8.889c-1.766-1.764-2.472-5.3.002-7.776 2.475-2.475 6.01-1.768 7.776-.002ZM4.993 5.117C3.58 6.532 3.708 8.667 5.2 10.16c.025.025 4.937-4.958 4.937-4.958C8.655 3.72 6.535 3.576 5.12 4.99c-1.414 1.414 1.287-1.288-.127.126v.001Zm8.691 13.528c1.376 1.376 3.718 1.786 5.132.372 1.415-1.414-1.255 1.256.159-.158s1.034-3.786-.342-5.162l-4.949 4.948Zm-1.406-8.493a.999.999 0 0 0-.708-1.707 1 1 0 1 0 .708 1.707Zm-2.122 2.122a1 1 0 1 0-1.389-1.438 1 1 0 0 0 1.389 1.438Zm4.95.707a1 1 0 1 0-1.389-1.438 1 1 0 0 0 1.389 1.438Zm-2.121 2.121a1 1 0 1 0-1.415-1.414 1 1 0 0 0 1.415 1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$9U;
function _extends$9V() { _extends$9V = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9V.apply(this, arguments); }
var SvgBandaidOutline = function SvgBandaidOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9V({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9U || (_path$9U = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "m11.405 3.642 8.939 8.889c1.767 1.768 2.475 5.303 0 7.778-2.475 2.475-6.046 1.803-7.778 0L3.627 11.42c-1.766-1.766-2.473-5.3.003-7.776 2.475-2.475 6.01-1.768 7.776-.002h-.001ZM4.988 5.113c-1.414 1.414-1.285 3.55.207 5.041.025.025 4.937-4.957 4.937-4.957C8.65 3.715 6.53 3.572 5.115 4.986l-.127.127Zm8.691 13.528c1.376 1.376 3.718 1.786 5.133.372 1.414-1.414-1.256 1.256.158-.159 1.414-1.414 1.034-3.785-.342-5.161l-4.949 4.948Zm-7.063-7.079 5.657 5.657 4.95-4.95-5.657-5.656-4.95 4.95v-.001Zm5.657-1.414a.999.999 0 0 1-1.632-1.09 1.002 1.002 0 0 1 1.632-.325 1 1 0 0 1 0 1.415ZM10.15 12.27a1 1 0 1 1-1.438-1.388 1 1 0 0 1 1.438 1.388Zm4.95.708a1 1 0 1 1-1.414-1.415 1 1 0 0 1 1.414 1.415Zm-2.121 2.12a1 1 0 1 1-1.414-1.413 1 1 0 0 1 1.414 1.414v-.001Z",
    clipRule: "evenodd"
  })));
};

var _path$9T;
function _extends$9U() { _extends$9U = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9U.apply(this, arguments); }
var SvgBarChart = function SvgBarChart(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9U({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9T || (_path$9T = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a1 1 0 0 1 1 1v16a1 1 0 1 1-2 0V4a1 1 0 0 1 1-1Zm6 6a1 1 0 0 1 1 1v10a1 1 0 1 1-2 0V10a1 1 0 0 1 1-1ZM6 13a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$9S;
function _extends$9T() { _extends$9T = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9T.apply(this, arguments); }
var SvgBarChartAxis = function SvgBarChartAxis(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9T({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9S || (_path$9S = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 2a1 1 0 0 1 1 1v17h17a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Zm10 2a1 1 0 0 1 1 1v12a1 1 0 1 1-2 0V5a1 1 0 0 1 1-1Zm5 4a1 1 0 0 1 1 1v8a1 1 0 1 1-2 0V9a1 1 0 0 1 1-1ZM8 13a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0v-3a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$9R;
function _extends$9S() { _extends$9S = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9S.apply(this, arguments); }
var SvgBarChartAxisUptrend = function SvgBarChartAxisUptrend(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9S({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9R || (_path$9R = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 2a1 1 0 0 1 1 1v17h17a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Zm15 2a1 1 0 0 1 1 1v12a1 1 0 1 1-2 0V5a1 1 0 0 1 1-1Zm-5 4a1 1 0 0 1 1 1v8a1 1 0 1 1-2 0V9a1 1 0 0 1 1-1Zm-5 5a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0v-3a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$9Q;
function _extends$9R() { _extends$9R = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9R.apply(this, arguments); }
var SvgBarChartBig = function SvgBarChartBig(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9R({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9Q || (_path$9Q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 2a1 1 0 0 1 1 1v17h17a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Zm11 4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2V6Zm4 0h-2v10h2V6ZM6 11a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-5Zm4 0H8v5h2v-5Z",
    clipRule: "evenodd"
  })));
};

var _path$9P;
function _extends$9Q() { _extends$9Q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9Q.apply(this, arguments); }
var SvgBarChartHorizontal = function SvgBarChartHorizontal(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9Q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9P || (_path$9P = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 2a1 1 0 0 1 1 1v17h17a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Zm3 4a1 1 0 0 1 1-1h3a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1Zm0 5a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1Zm0 5a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$9O;
function _extends$9P() { _extends$9P = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9P.apply(this, arguments); }
var SvgBarChartHorizontalBig = function SvgBarChartHorizontalBig(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9P({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9O || (_path$9O = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 2a1 1 0 0 1 1 1v17h17a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Zm3 4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6Zm12 0H8v2h10V6ZM6 14a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-2Zm7 0H8v2h5v-2Z",
    clipRule: "evenodd"
  })));
};

var _path$9N;
function _extends$9O() { _extends$9O = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9O.apply(this, arguments); }
var SvgBarChartUptrend = function SvgBarChartUptrend(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9O({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9N || (_path$9N = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18 3a1 1 0 0 1 1 1v16a1 1 0 1 1-2 0V4a1 1 0 0 1 1-1Zm-6 6a1 1 0 0 1 1 1v10a1 1 0 1 1-2 0V10a1 1 0 0 1 1-1Zm-6 6a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0v-4a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$9M;
function _extends$9N() { _extends$9N = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9N.apply(this, arguments); }
var SvgBasketFilled = function SvgBasketFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9N({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9M || (_path$9M = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.868 4.496a1 1 0 1 0-1.736-.992L4.42 10H2a1 1 0 0 0 0 2h.693l1.428 6.606A3 3 0 0 0 7.11 21h9.79c1.358 0 2.67-1.014 2.975-2.38l.001-.003L21.396 12H22a1 1 0 1 0 0-2h-2.42l-3.712-6.496a1 1 0 1 0-1.736.992L17.277 10H6.723l3.145-5.504Zm.527 8.187c-.049-.422-.444-.725-.883-.678-.44.046-.756.426-.707.847l.301 1.838H6.8c-.442 0-.8.344-.8.768 0 .423.358.767.8.767h2.484l.32 2.092c.05.421.445.725.884.678.44-.047.756-.426.707-.847l-.301-1.923h2.598l-.208 1.923c-.049.421.268.8.707.847.439.047.834-.257.883-.678l.228-2.092H17.2c.442 0 .8-.344.8-.767 0-.424-.358-.768-.8-.768h-1.92l.213-1.838c.048-.421-.268-.8-.707-.847-.44-.047-.835.256-.884.678l-.232 2.007h-2.954l-.32-2.007Z",
    clipRule: "evenodd"
  })));
};

var _path$9L;
function _extends$9M() { _extends$9M = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9M.apply(this, arguments); }
var SvgBasketOutline = function SvgBasketOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9M({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9L || (_path$9L = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.496 3.132a1 1 0 0 1 .372 1.364L6.723 10h10.554l-3.145-5.504a1 1 0 1 1 1.736-.992L19.58 10H22a1 1 0 1 1 0 2h-.604l-1.52 6.617v.004C19.57 19.986 18.257 21 16.9 21H7.109a3 3 0 0 1-2.988-2.394L2.693 12H2a1 1 0 1 1 0-2h2.42l3.712-6.496a1 1 0 0 1 1.364-.372ZM4.74 12l.54 2.5h3.104L8.105 12H4.739Zm5.378 0 .278 2.5h3.21l.278-2.5h-3.766Zm5.778 0-.278 2.5h3.153l.574-2.5h-3.45Zm2.415 4.5h-2.915l-.278 2.5H16.9c.44 0 .928-.384 1.024-.817l.001-.007.385-1.676ZM13.105 19l.278-2.5h-2.766l.278 2.5h2.21Zm-4.222 0-.278-2.5H5.712l.368 1.7a1 1 0 0 0 1 .8h1.803Z",
    clipRule: "evenodd"
  })));
};

var _path$9K;
function _extends$9L() { _extends$9L = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9L.apply(this, arguments); }
var SvgBatteryFull = function SvgBatteryFull(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9L({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9K || (_path$9K = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.004 4h2.01c0-1.333.666-2 2-2 1.333 0 1.997.667 1.991 2H16c1.333 0 2 .667 2 2v14c0 1.333-.667 2-2 2H8c-1.333 0-2-.667-2-2V6.004C6 4.667 6.668 3.999 8.004 4Z",
    clipRule: "evenodd"
  })));
};

var _path$9J;
function _extends$9K() { _extends$9K = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9K.apply(this, arguments); }
var SvgBatteryLow = function SvgBatteryLow(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9K({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9J || (_path$9J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.004 4h2.01c0-1.333.666-2 2-2 1.333 0 1.997.667 1.991 2H16c1.333 0 2 .667 2 2v14c0 1.333-.667 2-2 2H8c-1.333 0-2-.667-2-2V6.004C6 4.667 6.668 3.999 8.004 4ZM8 6v12h8V6H8Z",
    clipRule: "evenodd"
  })));
};

var _path$9I;
function _extends$9J() { _extends$9J = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9J.apply(this, arguments); }
var SvgBatteryMedium = function SvgBatteryMedium(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9J({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9I || (_path$9I = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.004 4h2.01c0-1.333.666-2 2-2 1.333 0 1.997.667 1.991 2H16c1.333 0 2 .667 2 2v14c0 1.333-.667 2-2 2H8c-1.333 0-2-.667-2-2V6.004C6 4.667 6.668 3.999 8.004 4ZM8 6v8h8V6H8Z",
    clipRule: "evenodd"
  })));
};

var _path$9H;
function _extends$9I() { _extends$9I = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9I.apply(this, arguments); }
var SvgBeach = function SvgBeach(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9I({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9H || (_path$9H = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.502 14.285c.607-.496 1.157-1.412 1.65-2.75.513 4.337.788 6.825.824 7.464h6.026c.666 0 1 .334 1 1 0 .668-.334 1-1 1H2.998c-.664 0-.997-.332-.998-.996-.001-.664.333-.998 1.002-1.001h9.99c-.067-1.295-.49-4.717-.49-4.717Zm-3.2-7.77C8.056 6.812 7.244 7.58 6.869 8.82c-.564 1.859.35 3.445.35 3.445-.656.245-1.452.296-2.389.15-.627-1.804-.354-3.53.82-5.178 1.173-1.65 2.821-2.442 4.945-2.38 1.303-2.29 3.263-3.202 5.879-2.74 3.925.69 4.505 4.296 4.505 4.296-.336.383-.782.714-1.339.992-.501-1.71-1.53-2.885-3.085-3.526-1.554-.64-3.126-.44-4.714.605 1.92-.138 3.49.218 4.714 1.07 1.757 1.26 2.436 3.079 2.038 5.458a5.012 5.012 0 0 1-3.254-.222c-1.061-.437-1.854-1.142-2.377-2.115.145.56.167 1.266.065 2.115-.278 2.14-2.432 3.427-2.432 3.427s-1.975-.941-2.392-3.427c-.278-1.657.089-3.082 1.1-4.275h-.001Z",
    clipRule: "evenodd"
  })));
};

var _path$9G;
function _extends$9H() { _extends$9H = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9H.apply(this, arguments); }
var SvgBellDot = function SvgBellDot(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9H({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9G || (_path$9G = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3C9.252 3 7 5.252 7 8c0 3.647-.783 6.065-1.622 7.604-.076.14-.153.271-.229.396h13.732c-.12-.193-.242-.402-.363-.63a1 1 0 0 1 1.764-.94 7.39 7.39 0 0 0 .965 1.418 3.968 3.968 0 0 0 .354.352l.007.006A1 1 0 0 1 21 18H3a1 1 0 0 1-.563-1.826l.052-.042c.053-.044.141-.123.254-.242.224-.237.547-.636.88-1.244C4.282 13.435 5 11.353 5 8c0-3.852 3.148-7 7-7 .805 0 1.511.116 2.216.351a1 1 0 0 1-.632 1.898A4.824 4.824 0 0 0 12 3ZM2.444 16.169ZM18 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-4 2a4 4 0 1 1 8 0 4 4 0 0 1-8 0ZM9.818 20.124a1 1 0 0 1 1.358.394.94.94 0 0 0 1.648 0 1 1 0 0 1 1.752.964 2.94 2.94 0 0 1-5.152 0 1 1 0 0 1 .394-1.358Z",
    clipRule: "evenodd"
  })));
};

var _path$9F;
function _extends$9G() { _extends$9G = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9G.apply(this, arguments); }
var SvgBellFilled = function SvgBellFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9G({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9F || (_path$9F = /*#__PURE__*/React.createElement("path", {
    d: "m21.563 16.17.003.002A1 1 0 0 1 21 17.996H3a1 1 0 0 1-.566-1.824l.003-.002.052-.042a2.99 2.99 0 0 0 .254-.241c.224-.237.547-.636.88-1.244C4.282 13.432 5 11.35 5 7.998A6.997 6.997 0 0 1 12 1a7 7 0 0 1 7 6.998c0 3.352.717 5.434 1.378 6.645.332.608.655 1.007.88 1.244a3.02 3.02 0 0 0 .305.283Zm-10.387 4.344a1 1 0 0 0-1.752.963 2.939 2.939 0 0 0 5.152 0 1 1 0 1 0-1.753-.963.939.939 0 0 1-.723.481h-.2a.941.941 0 0 1-.724-.482Z"
  })));
};

var _path$9E;
function _extends$9F() { _extends$9F = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9F.apply(this, arguments); }
var SvgBellMinus = function SvgBellMinus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9F({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9E || (_path$9E = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3C9.252 3 7 5.252 7 8c0 3.647-.783 6.065-1.622 7.604-.076.14-.153.271-.229.396h13.705c-.535-.88-1.079-2.113-1.433-3.794a1 1 0 1 1 1.957-.412c.373 1.769.968 2.891 1.438 3.552.235.332.442.552.578.681.069.065.12.108.148.13l.024.019A1 1 0 0 1 21 18H3a1 1 0 0 1-.563-1.826 3.037 3.037 0 0 0 .306-.283c.224-.238.547-.637.88-1.245C4.282 13.435 5 11.353 5 8c0-3.852 3.148-7 7-7 2.103 0 3.964.935 5.237 2.324a1 1 0 1 1-1.474 1.352A5.11 5.11 0 0 0 12 3ZM2.444 16.169ZM14 8a1 1 0 0 1 1-1h6a1 1 0 0 1 0 2h-6a1 1 0 0 1-1-1ZM9.818 20.124a1 1 0 0 1 1.358.394.94.94 0 0 0 1.647 0 1 1 0 1 1 1.753.964 2.939 2.939 0 0 1-5.153 0 1 1 0 0 1 .395-1.358Z",
    clipRule: "evenodd"
  })));
};

var _path$9D;
function _extends$9E() { _extends$9E = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9E.apply(this, arguments); }
var SvgBellOff = function SvgBellOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9E({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9D || (_path$9D = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 1.414 0l4.284 4.284.032.031L17.704 16.29a.159.159 0 0 1 .006.006l4.997 4.997a1 1 0 0 1-1.414 1.414L16.586 18H3a1 1 0 0 1-.563-1.826l.052-.042c.053-.044.141-.123.254-.242.224-.237.547-.636.88-1.244.66-1.21 1.376-3.291 1.377-6.64a5.67 5.67 0 0 1 .167-1.425L1.293 2.707a1 1 0 0 1 0-1.414ZM6.997 8.41c-.057 3.419-.812 5.713-1.62 7.193-.075.14-.152.271-.228.396h9.437l-7.59-7.59Zm-4.553 7.758ZM14.368 3.607a5 5 0 0 0-5.118.228 1 1 0 1 1-1.1-1.67A7 7 0 0 1 19 7.998V8c.002 1.606.194 3.205.572 4.765a1 1 0 1 1-1.944.472A22.3 22.3 0 0 1 17 8h1l-1 .002V8a5 5 0 0 0-2.632-4.394Zm-4.55 16.517a1 1 0 0 1 1.358.394.94.94 0 0 0 1.647 0 1 1 0 0 1 1.753.964 2.94 2.94 0 0 1-5.152 0 1 1 0 0 1 .394-1.358Z",
    clipRule: "evenodd"
  })));
};

var _path$9C;
function _extends$9D() { _extends$9D = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9D.apply(this, arguments); }
var SvgBellOutline = function SvgBellOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9D({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9C || (_path$9C = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a5 5 0 0 0-5 4.998c0 3.647-.783 6.064-1.622 7.602-.076.14-.153.272-.229.396h13.702a9.422 9.422 0 0 1-.229-.396C17.782 14.062 17 11.645 17 7.998A4.998 4.998 0 0 0 12 3Zm9.566 13.172-.003-.002a3.02 3.02 0 0 1-.306-.283 5.938 5.938 0 0 1-.88-1.244C19.718 13.432 19 11.35 19 7.998A6.997 6.997 0 0 0 12 1a7 7 0 0 0-7 6.998c0 3.352-.717 5.434-1.378 6.645a5.936 5.936 0 0 1-.88 1.244 3.015 3.015 0 0 1-.308.285A1 1 0 0 0 3 17.996h18a1 1 0 0 0 .566-1.824Zm-19.122-.007Zm7.374 3.954a1 1 0 0 1 1.358.395.939.939 0 0 0 1.647 0 1 1 0 0 1 1.753.963 2.94 2.94 0 0 1-5.152 0 1 1 0 0 1 .394-1.358Z",
    clipRule: "evenodd"
  })));
};

var _path$9B;
function _extends$9C() { _extends$9C = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9C.apply(this, arguments); }
var SvgBellPlus = function SvgBellPlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9C({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9B || (_path$9B = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3C9.252 3 7 5.252 7 8c0 3.647-.783 6.065-1.622 7.604-.076.14-.153.271-.229.396h13.676a9.391 9.391 0 0 1-.42-.753 1 1 0 0 1 1.79-.894 6.21 6.21 0 0 0 .988 1.466 3.482 3.482 0 0 0 .375.35l.007.006A1 1 0 0 1 21 18H3a1 1 0 0 1-.563-1.826 3.037 3.037 0 0 0 .306-.283c.224-.238.547-.637.88-1.245C4.282 13.435 5 11.353 5 8c0-3.852 3.148-7 7-7 1.149 0 2.22.234 3.286.826a1 1 0 0 1-.972 1.748C13.58 3.166 12.851 3 12 3ZM2.444 16.169ZM18 4a1 1 0 0 1 1 1v2h2a1 1 0 0 1 0 2h-2v2a1 1 0 0 1-2 0V9h-2a1 1 0 1 1 0-2h2V5a1 1 0 0 1 1-1ZM9.818 20.124a1 1 0 0 1 1.358.394.94.94 0 0 0 1.647 0 1 1 0 1 1 1.753.964 2.939 2.939 0 0 1-5.153 0 1 1 0 0 1 .395-1.358Z",
    clipRule: "evenodd"
  })));
};

var _path$9A;
function _extends$9B() { _extends$9B = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9B.apply(this, arguments); }
var SvgBellRing = function SvgBellRing(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9B({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9A || (_path$9A = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.577 1.183a1 1 0 0 1 .24 1.394C3.711 4.143 3 5.946 3 8a1 1 0 0 1-2 0c0-2.546.889-4.743 2.183-6.577a1 1 0 0 1 1.394-.24Zm14.846 0a1 1 0 0 1 1.394.24C22.111 3.257 23 5.454 23 8a1 1 0 1 1-2 0c0-2.054-.711-3.857-1.817-5.423a1 1 0 0 1 .24-1.394ZM12 3a5 5 0 0 0-5 5c0 3.647-.783 6.065-1.622 7.604-.076.14-.153.271-.229.396h13.702a9.421 9.421 0 0 1-.229-.396C17.782 14.065 17 11.647 17 8a5 5 0 0 0-5-5Zm9.566 13.176-.002-.002a3.042 3.042 0 0 1-.306-.283 5.94 5.94 0 0 1-.88-1.245C19.718 13.435 19 11.353 19 8A7 7 0 1 0 5 8c0 3.353-.717 5.435-1.378 6.646a5.939 5.939 0 0 1-.88 1.244 3.036 3.036 0 0 1-.308.286A1 1 0 0 0 3 18h18a1 1 0 0 0 .566-1.824Zm-19.122-.007Zm7.374 3.955a1 1 0 0 1 1.358.394.94.94 0 0 0 1.647 0 1 1 0 0 1 1.753.964 2.94 2.94 0 0 1-5.152 0 1 1 0 0 1 .394-1.358Z",
    clipRule: "evenodd"
  })));
};

var _path$9z;
function _extends$9A() { _extends$9A = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9A.apply(this, arguments); }
var SvgBloodPressureFilled = function SvgBloodPressureFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9A({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9z || (_path$9z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7.008 13.918A6.002 6.002 0 0 1 8 2a6 6 0 0 1 1 11.917c.01 1.64.083 2.634.22 2.983.6 1.8 2.28 3.01 4.28 3.01 2.5 0 4.5-2 4.5-4.5V14c-1.16-.41-2-3.71-2-5a3 3 0 0 1 6 0c0 1.29-.84 4.58-2 5v1.5a6.499 6.499 0 0 1-6.5 6.5c-3.08 0-5.74-2.08-6.36-5.09-.074-.371-.118-1.368-.132-2.992ZM8.2 5.666l-1 1.732c-.333.577-.211 1.033.366 1.366.577.333 1.033.211 1.366-.366l1-1.732c.333-.577.211-1.033-.366-1.366-.577-.333-1.033-.211-1.366.366Z",
    clipRule: "evenodd"
  })));
};

var _path$9y;
function _extends$9z() { _extends$9z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9z.apply(this, arguments); }
var SvgBloodPressureOutline = function SvgBloodPressureOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9y || (_path$9y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7.008 13.918A6.002 6.002 0 0 1 8 2a6 6 0 0 1 1 11.917c.01 1.64.083 2.634.22 2.983.6 1.8 2.28 3.01 4.28 3.01 2.5 0 4.5-2 4.5-4.5V14c-1.16-.41-2-3.71-2-5a3 3 0 0 1 6 0c0 1.29-.84 4.58-2 5v1.5a6.499 6.499 0 0 1-6.5 6.5c-3.08 0-5.74-2.08-6.36-5.09-.074-.371-.118-1.368-.132-2.992ZM19 12c.552 0 1-2.448 1-3a1 1 0 0 0-2 0c0 .552.448 3 1 3ZM8 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm.2 1.666c.333-.577.789-.7 1.366-.366.577.333.7.789.366 1.366l-1 1.732c-.333.577-.789.7-1.366.366-.577-.333-.7-.789-.366-1.366l1-1.732Z",
    clipRule: "evenodd"
  })));
};

var _path$9x;
function _extends$9y() { _extends$9y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9y.apply(this, arguments); }
var SvgBluetooth = function SvgBluetooth(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9x || (_path$9x = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.617 1.076a1 1 0 0 1 1.09.217l5 5a1 1 0 0 1 0 1.414L13.414 12l4.293 4.293a1 1 0 0 1 0 1.414l-5 5A1 1 0 0 1 11 22v-7.586l-3.293 3.293a1 1 0 0 1-1.414-1.414L10.586 12 6.293 7.707a1 1 0 0 1 1.414-1.414L11 9.586V2a1 1 0 0 1 .617-.924ZM13 14.414 15.586 17 13 19.586v-5.172Zm0-4.828V4.414L15.586 7 13 9.586Z",
    clipRule: "evenodd"
  })));
};

var _path$9w;
function _extends$9x() { _extends$9x = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9x.apply(this, arguments); }
var SvgBluetoothOff = function SvgBluetoothOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9x({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9w || (_path$9w = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.617 1.076a1 1 0 0 1 1.09.217l5 5a1 1 0 0 1 0 1.414l-2.5 2.5a1 1 0 0 1-1.414-1.414L15.586 7 13 4.414V6.5a1 1 0 1 1-2 0V2a1 1 0 0 1 .617-.924Zm-10.324.217a1 1 0 0 1 1.414 0l9.995 9.994a.54.54 0 0 1 .01.011l9.995 9.995a1 1 0 0 1-1.414 1.414L17 18.414l-4.293 4.293A1 1 0 0 1 11 22v-7.586l-3.293 3.293a1 1 0 0 1-1.414-1.414L10.586 12 1.293 2.707a1 1 0 0 1 0-1.414ZM13 14.414v5.172L15.586 17 13 14.414Z",
    clipRule: "evenodd"
  })));
};

var _path$9v;
function _extends$9w() { _extends$9w = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9w.apply(this, arguments); }
var SvgBluetoothSearching = function SvgBluetoothSearching(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9w({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9v || (_path$9v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.617 1.076a1 1 0 0 1 1.09.217l5 5a1 1 0 0 1 0 1.414L13.414 12l4.293 4.293a1 1 0 0 1 0 1.414l-5 5A1 1 0 0 1 11 22v-7.586l-3.293 3.293a1 1 0 0 1-1.414-1.414L10.586 12 6.293 7.707a1 1 0 0 1 1.414-1.414L11 9.586V2a1 1 0 0 1 .617-.924ZM13 14.414 15.586 17 13 19.586v-5.172Zm0-4.828V4.414L15.586 7 13 9.586Zm7.123-1.123a1 1 0 0 1 1.414 0 5 5 0 0 1 0 7.075 1 1 0 0 1-1.414-1.415 2.999 2.999 0 0 0 0-4.246 1 1 0 0 1 0-1.414ZM17 12a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H18a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$9u;
function _extends$9v() { _extends$9v = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9v.apply(this, arguments); }
var SvgBold = function SvgBold(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9v({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9u || (_path$9u = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 17.002V7.011C7 5.671 7.668 5 9.005 5h2.747c.892 0 1.652.09 2.278.269.627.18 1.138.432 1.534.76.396.326.684.723.864 1.19.18.468.27.99.27 1.567 0 .314-.04.616-.124.908a2.76 2.76 0 0 1-.39.821 3.026 3.026 0 0 1-.682.697c-.278.208-.612.39-1.002.543.851.224 1.48.588 1.888 1.09.408.503.612 1.149.612 1.937a4.27 4.27 0 0 1-.32 1.662c-.212.513-.524.96-.934 1.34a4.33 4.33 0 0 1-1.52.894c-.604.215-1.292.322-2.066.322H9.015C7.672 19 7 18.334 7 17.002Zm2.996-3.94v3.488h2.11c.396 0 .72-.055.971-.164.251-.108.448-.25.59-.422.142-.173.239-.369.292-.587.053-.217.08-.438.08-.663 0-.256-.031-.486-.093-.691a1.154 1.154 0 0 0-.32-.52c-.15-.14-.35-.25-.598-.326-.248-.077-.561-.115-.94-.115H9.996Zm0-2.172h1.56c.332 0 .63-.025.896-.077.266-.05.492-.14.678-.269a1.21 1.21 0 0 0 .426-.528c.097-.224.146-.506.146-.846 0-.333-.038-.61-.115-.83a1.191 1.191 0 0 0-.355-.534 1.435 1.435 0 0 0-.607-.288 3.856 3.856 0 0 0-.873-.087H9.996v3.46-.001Z",
    clipRule: "evenodd"
  })));
};

var _path$9t;
function _extends$9u() { _extends$9u = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9u.apply(this, arguments); }
var SvgBook = function SvgBook(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9u({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9t || (_path$9t = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.5 3A1.5 1.5 0 0 0 5 4.5v11.838A3.5 3.5 0 0 1 6.5 16H19V3H6.5ZM21 2a1 1 0 0 0-1-1H6.5A3.5 3.5 0 0 0 3 4.5v15A3.5 3.5 0 0 0 6.5 23H20a1 1 0 0 0 1-1V2Zm-2 16H6.5a1.5 1.5 0 1 0 0 3H19v-3Z",
    clipRule: "evenodd"
  })));
};

var _path$9s;
function _extends$9t() { _extends$9t = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9t.apply(this, arguments); }
var SvgBookCopy = function SvgBookCopy(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9t({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9s || (_path$9s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 3a1 1 0 0 0-1 1v9.172A3 3 0 0 1 4 13h1a1 1 0 1 1 0 2H4a1 1 0 0 0 0 2h1a1 1 0 1 1 0 2H4a3 3 0 0 1-3-3V4a3 3 0 0 1 3-3h11a1 1 0 1 1 0 2H4Zm4.879 2.879A3 3 0 0 1 11 5h11a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H11a3 3 0 0 1-3-3V8a3 3 0 0 1 .879-2.121ZM10 17.172V8a1 1 0 0 1 1-1h10v10H11a3 3 0 0 0-1 .172ZM21 19H11a1 1 0 0 0 0 2h10v-2Z",
    clipRule: "evenodd"
  })));
};

var _path$9r;
function _extends$9s() { _extends$9s = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9s.apply(this, arguments); }
var SvgBookDown = function SvgBookDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9s({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9r || (_path$9r = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.5 3A1.5 1.5 0 0 0 5 4.5v11.838A3.5 3.5 0 0 1 6.5 16H19V3H6.5ZM21 2a1 1 0 0 0-1-1H6.5A3.5 3.5 0 0 0 3 4.5v15A3.5 3.5 0 0 0 6.5 23H20a1 1 0 0 0 1-1V2Zm-2 16H6.5a1.5 1.5 0 1 0 0 3H19v-3ZM12 6a1 1 0 0 1 1 1v3.586l1.293-1.293a1 1 0 1 1 1.414 1.414l-3 3a1 1 0 0 1-1.414 0l-3-3a1 1 0 0 1 1.414-1.414L11 10.586V7a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$9q;
function _extends$9r() { _extends$9r = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9r.apply(this, arguments); }
var SvgBookKey = function SvgBookKey(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9r({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9q || (_path$9q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.5 3A1.5 1.5 0 0 0 5 4.5v11.838A3.5 3.5 0 0 1 6.5 16H19V8a1 1 0 1 1 2 0v14a1 1 0 0 1-1 1H6.5A3.5 3.5 0 0 1 3 19.5v-15A3.5 3.5 0 0 1 6.5 1H14a1 1 0 1 1 0 2H6.5ZM19 18H6.5a1.5 1.5 0 1 0 0 3H19v-3Zm.293-16.707a1 1 0 1 1 1.414 1.414L20.414 3l.293.293a1 1 0 0 1-1.414 1.414L19 4.414l-2.292 2.293a3 3 0 1 1-1.414-1.414l3.999-4ZM14 7a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z",
    clipRule: "evenodd"
  })));
};

var _path$9p;
function _extends$9q() { _extends$9q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9q.apply(this, arguments); }
var SvgBookLock = function SvgBookLock(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9p || (_path$9p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.5 3A1.5 1.5 0 0 0 5 4.5v11.838A3.5 3.5 0 0 1 6.5 16H19v-1a1 1 0 1 1 2 0v7a1 1 0 0 1-1 1H6.5A3.5 3.5 0 0 1 3 19.5v-15A3.5 3.5 0 0 1 6.5 1H10a1 1 0 1 1 0 2H6.5ZM19 18H6.5a1.5 1.5 0 1 0 0 3H19v-3ZM13.879 1.879A3 3 0 0 1 19 4v1a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2V4a3 3 0 0 1 .879-2.121ZM13 7v3h6V7h-6Zm4-2h-2V4a1 1 0 0 1 2 0v1Z",
    clipRule: "evenodd"
  })));
};

var _path$9o;
function _extends$9p() { _extends$9p = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9p.apply(this, arguments); }
var SvgBookMarked = function SvgBookMarked(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9p({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9o || (_path$9o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.5 3A1.5 1.5 0 0 0 5 4.5v11.838A3.5 3.5 0 0 1 6.5 16H19V3h-2v7a1 1 0 0 1-1.707.707L13 8.414l-2.293 2.293A1 1 0 0 1 9 10V3H6.5Zm0-2A3.5 3.5 0 0 0 3 4.5v15A3.5 3.5 0 0 0 6.5 23H20a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H6.5ZM11 3v4.586l1.293-1.293a1 1 0 0 1 1.414 0L15 7.586V3h-4Zm8 15H6.5a1.5 1.5 0 1 0 0 3H19v-3Z",
    clipRule: "evenodd"
  })));
};

var _path$9n;
function _extends$9o() { _extends$9o = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9o.apply(this, arguments); }
var SvgBookMinus = function SvgBookMinus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9o({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9n || (_path$9n = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.5 3A1.5 1.5 0 0 0 5 4.5v11.838A3.5 3.5 0 0 1 6.5 16H19V3H6.5ZM21 2a1 1 0 0 0-1-1H6.5A3.5 3.5 0 0 0 3 4.5v15A3.5 3.5 0 0 0 6.5 23H20a1 1 0 0 0 1-1V2Zm-2 16H6.5a1.5 1.5 0 1 0 0 3H19v-3ZM8 10a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$9m;
function _extends$9n() { _extends$9n = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9n.apply(this, arguments); }
var SvgBookOpen = function SvgBookOpen(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9n({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9m || (_path$9m = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1 3a1 1 0 0 1 1-1h6a5 5 0 0 1 4 2 5 5 0 0 1 4-2h6a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1h-7a2 2 0 0 0-2 2 1 1 0 1 1-2 0 2 2 0 0 0-2-2H2a1 1 0 0 1-1-1V3Zm10 14.536A4 4 0 0 0 9 17H3V4h5a3 3 0 0 1 3 3v10.536Zm2 0A4 4 0 0 1 15 17h6V4h-5a3 3 0 0 0-3 3v10.536Z",
    clipRule: "evenodd"
  })));
};

var _path$9l;
function _extends$9m() { _extends$9m = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9m.apply(this, arguments); }
var SvgBookPlus = function SvgBookPlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9m({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9l || (_path$9l = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.5 3A1.5 1.5 0 0 0 5 4.5v11.838A3.5 3.5 0 0 1 6.5 16H19V3H6.5ZM21 2a1 1 0 0 0-1-1H6.5A3.5 3.5 0 0 0 3 4.5v15A3.5 3.5 0 0 0 6.5 23H20a1 1 0 0 0 1-1V2Zm-2 16H6.5a1.5 1.5 0 1 0 0 3H19v-3ZM12 6a1 1 0 0 1 1 1v2h2a1 1 0 1 1 0 2h-2v2a1 1 0 1 1-2 0v-2H9a1 1 0 1 1 0-2h2V7a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$9k;
function _extends$9l() { _extends$9l = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9l.apply(this, arguments); }
var SvgBookTemplate = function SvgBookTemplate(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9l({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9k || (_path$9k = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.5 3A1.5 1.5 0 0 0 5 4.5V5a1 1 0 0 1-2 0v-.5A3.5 3.5 0 0 1 6.5 1H8a1 1 0 0 1 0 2H6.5ZM11 2a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2h-2a1 1 0 0 1-1-1Zm6 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0V3h-1a1 1 0 0 1-1-1Zm3 5a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0V8a1 1 0 0 1 1-1ZM4 8a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0V9a1 1 0 0 1 1-1Zm0 6a1 1 0 0 1 1 1v1.338A3.5 3.5 0 0 1 6.5 16H8a1 1 0 1 1 0 2H6.5a1.5 1.5 0 1 0 0 3H8a1 1 0 1 1 0 2H6.5A3.5 3.5 0 0 1 3 19.5V15a1 1 0 0 1 1-1Zm16 0a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-2a1 1 0 1 1 0-2h1v-1a1 1 0 0 1 1-1Zm-9 3a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2h-2a1 1 0 0 1-1-1Zm0 5a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2h-2a1 1 0 0 1-1-1Zm6 0a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2h-2a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$9j;
function _extends$9k() { _extends$9k = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9k.apply(this, arguments); }
var SvgBookUp = function SvgBookUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9k({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9j || (_path$9j = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.5 3A1.5 1.5 0 0 0 5 4.5v11.838A3.5 3.5 0 0 1 6.5 16H19V3H6.5ZM21 2a1 1 0 0 0-1-1H6.5A3.5 3.5 0 0 0 3 4.5v15A3.5 3.5 0 0 0 6.5 23H20a1 1 0 0 0 1-1V2Zm-2 16H6.5a1.5 1.5 0 1 0 0 3H19v-3ZM11.293 6.293a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1-1.414 1.414L13 9.414V13a1 1 0 1 1-2 0V9.414l-1.293 1.293a1 1 0 0 1-1.414-1.414l3-3Z",
    clipRule: "evenodd"
  })));
};

var _path$9i;
function _extends$9j() { _extends$9j = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9j.apply(this, arguments); }
var SvgBookUpDouble = function SvgBookUpDouble(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9j({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9i || (_path$9i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.5 3A1.5 1.5 0 0 0 5 4.5v11.838A3.5 3.5 0 0 1 6.5 16H19V3h-1a1 1 0 1 1 0-2h2a1 1 0 0 1 1 1v20a1 1 0 0 1-1 1H6.5A3.5 3.5 0 0 1 3 19.5v-15A3.5 3.5 0 0 1 6.5 1a1 1 0 0 1 0 2ZM19 18H6.5a1.5 1.5 0 1 0 0 3H19v-3ZM11.293 1.293a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1-1.414 1.414L12 3.414 9.707 5.707a1 1 0 0 1-1.414-1.414l3-3ZM11 9.414l-1.293 1.293a1 1 0 0 1-1.414-1.414l3-3a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1-1.414 1.414L13 9.414V13a1 1 0 1 1-2 0V9.414Z",
    clipRule: "evenodd"
  })));
};

var _path$9h;
function _extends$9i() { _extends$9i = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9i.apply(this, arguments); }
var SvgBookX = function SvgBookX(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9i({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9h || (_path$9h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.5 3A1.5 1.5 0 0 0 5 4.5v11.838A3.5 3.5 0 0 1 6.5 16H19V3H6.5ZM21 2a1 1 0 0 0-1-1H6.5A3.5 3.5 0 0 0 3 4.5v15A3.5 3.5 0 0 0 6.5 23H20a1 1 0 0 0 1-1V2Zm-2 16H6.5a1.5 1.5 0 1 0 0 3H19v-3ZM8.793 6.293a1 1 0 0 1 1.414 0L12 8.086l1.793-1.793a1 1 0 1 1 1.414 1.414L13.414 9.5l1.793 1.793a1 1 0 0 1-1.414 1.414L12 10.914l-1.793 1.793a1 1 0 0 1-1.414-1.414L10.586 9.5 8.793 7.707a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$9g;
function _extends$9h() { _extends$9h = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9h.apply(this, arguments); }
var SvgBookmarkFilled = function SvgBookmarkFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9h({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9g || (_path$9g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 2h12c1.333 0 2 .667 2 2v16.532c0 .588-.222 1.01-.665 1.265-.443.256-.888.274-1.335.056l-6-2-6 2c-.48.216-.932.198-1.36-.056-.426-.253-.64-.675-.64-1.265V4c0-1.333.667-2 2-2Z",
    clipRule: "evenodd"
  })));
};

var _path$9f;
function _extends$9g() { _extends$9g = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9g.apply(this, arguments); }
var SvgBookmarkMinus = function SvgBookmarkMinus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9g({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9f || (_path$9f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 4a1 1 0 0 0-1 1v14.277l5.504-3.145a1 1 0 0 1 .992 0L18 19.277V5a1 1 0 0 0-1-1H7ZM4.879 2.879A3 3 0 0 1 7 2h10a3 3 0 0 1 3 3v16a1 1 0 0 1-1.496.868L12 18.152l-6.504 3.716A1 1 0 0 1 4 21V5a3 3 0 0 1 .879-2.121ZM8 10a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$9e;
function _extends$9f() { _extends$9f = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9f.apply(this, arguments); }
var SvgBookmarkOutline = function SvgBookmarkOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9f({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9e || (_path$9e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 2h12c1.333 0 2 .667 2 2v16.532c0 .588-.222 1.01-.665 1.265-.443.256-.888.274-1.335.056l-6-2-6 2c-.48.216-.932.198-1.36-.056-.426-.253-.64-.675-.64-1.265V4c0-1.333.667-2 2-2Zm0 2v15.846l6-1.992 6 1.992V4H6Z",
    clipRule: "evenodd"
  })));
};

var _path$9d;
function _extends$9e() { _extends$9e = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9e.apply(this, arguments); }
var SvgBookmarkPlus = function SvgBookmarkPlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9e({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9d || (_path$9d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 4a1 1 0 0 0-1 1v14.277l5.504-3.145a1 1 0 0 1 .992 0L18 19.277V5a1 1 0 0 0-1-1H7ZM4.879 2.879A3 3 0 0 1 7 2h10a3 3 0 0 1 3 3v16a1 1 0 0 1-1.496.868L12 18.152l-6.504 3.716A1 1 0 0 1 4 21V5a3 3 0 0 1 .879-2.121ZM12 6a1 1 0 0 1 1 1v2h2a1 1 0 1 1 0 2h-2v2a1 1 0 1 1-2 0v-2H9a1 1 0 1 1 0-2h2V7a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$9c;
function _extends$9d() { _extends$9d = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9d.apply(this, arguments); }
var SvgBoxFilled = function SvgBoxFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9d({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9c || (_path$9c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13 8h9v11.983c0 1.307-.667 1.973-2 1.998-1.333.025-6.667.025-16 0-1.333 0-2-.666-2-1.998V8h9v1c0 .668.334 1 1 1 .666 0 1-.332 1-1V8Zm-2-2H2.385l1.49-3.001C4.14 2.333 4.514 2 5 2h14c.486 0 .855.333 1.107.999L21.617 6H13V2h-2v4ZM6 16c-.667 0-1 .333-1 1 0 .665.333.998 1 .998h6c.667 0 1-.333 1-.999 0-.666-.333-.999-1-.999H6Zm0-4c-.667 0-1 .333-1 1 0 .665.333.998 1 .998h3c.667 0 1-.333 1-.999 0-.666-.333-.999-1-.999H6Z",
    clipRule: "evenodd"
  })));
};

var _path$9b;
function _extends$9c() { _extends$9c = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9c.apply(this, arguments); }
var SvgBoxOutline = function SvgBoxOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9c({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9b || (_path$9b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11 7.994H4v11.99h16V7.993h-7V8c0 .667-.334 1-1 1-.666 0-1-.333-1-1v-.007.001ZM11 4H5.5l-1 2H11V4Zm2 0v2h6.5l-1-2H13ZM2 6.995c0-.205.09-.458.267-.759L3.874 3c.266-.667.64-1 1.126-1h14c.486 0 .855.333 1.107.999l1.552 3.083c.227.396.341.7.341.912v12.99c0 1.306-.667 1.972-2 1.997-1.333.025-6.667.025-16 0-1.333 0-2-.666-2-1.998V6.994v.001ZM7 16h6c.667 0 1 .333 1 1 0 .665-.333.998-1 .998H7c-.667 0-1-.333-1-.999 0-.666.333-.999 1-.999Zm0-4h3c.667 0 1 .333 1 1 0 .665-.333.998-1 .998H7c-.667 0-1-.333-1-.999 0-.666.333-.999 1-.999Z",
    clipRule: "evenodd"
  })));
};

var _path$9a;
function _extends$9b() { _extends$9b = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9b.apply(this, arguments); }
var SvgBraille = function SvgBraille(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9b({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9a || (_path$9a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15 16a2 2 0 1 1 0 4 2 2 0 0 1 0-4ZM4 17a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm5 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm11 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2ZM4 10a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm16 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm-5 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm-6 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2ZM4 4a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm5 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm6 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm5 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z",
    clipRule: "evenodd"
  })));
};

var _path$99;
function _extends$9a() { _extends$9a = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9a.apply(this, arguments); }
var SvgBricksFilled = function SvgBricksFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9a({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$99 || (_path$99 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 3c1.333.018 2 .684 2 2v1c0 1.333-.667 2-2 2V3Zm0 7h4.143C7.38 10 8 10.667 8 12v1c0 1.333-.619 2-1.857 2H2v-5Zm5.875-7h3.25C12.375 3 13 3.667 13 5v1c0 1.333-.625 2-1.875 2h-3.25C6.625 8 6 7.333 6 6V5c0-1.333.625-2 1.875-2Zm4 7h3.25c1.25 0 1.875.667 1.875 2v1c0 1.333-.625 2-1.875 2h-3.25C10.625 15 10 14.333 10 13v-1c0-1.333.625-2 1.875-2Zm-4 7h3.25c1.25 0 1.875.667 1.875 2v1c0 1.333-.625 2-1.875 2h-3.25C6.625 22 6 21.333 6 20v-1c0-1.333.625-2 1.875-2Zm9 0h3.25c1.25 0 1.875.667 1.875 2v1c0 1.333-.625 2-1.875 2h-3.25C15.625 22 15 21.333 15 20v-1c0-1.333.625-2 1.875-2ZM2 17c1.333.018 2 .684 2 2v1c0 1.333-.667 2-2 2v-5Z",
    clipRule: "evenodd"
  })));
};

var _path$98;
function _extends$99() { _extends$99 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$99.apply(this, arguments); }
var SvgBricksOutline = function SvgBricksOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$99({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$98 || (_path$98 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7.004 2H11c1.333 0 2 .667 2 2v2c0 1.333-.667 2-2 2H7c-1.333 0-2-.667-2-2V4c0-1.333.668-2 2.004-2ZM7 4v2h4V4H7Zm4.004 5H15c1.333 0 2 .667 2 2v2c0 1.333-.667 2-2 2h-4c-1.333 0-2-.667-2-2v-2c0-1.333.668-2 2.004-2ZM11 11v2h4v-2h-4Zm-3.996 5H11c1.333 0 2 .667 2 2v2c0 1.333-.667 2-2 2H7c-1.333 0-2-.667-2-2v-2c0-1.333.668-2 2.004-2ZM7 18v2h4v-2H7Zm9.004-2H20c1.333 0 2 .667 2 2v2c0 1.333-.667 2-2 2h-4c-1.333 0-2-.667-2-2v-2c0-1.333.668-2 2.004-2ZM16 18v2h4v-2h-4ZM2 2c1.333.07 2 .737 2 2v2c0 1.333-.667 2-2 2V2Zm0 14c1.333.07 2 .737 2 2v2c0 1.333-.667 2-2 2v-6Zm.001-3H6v-2H2.003l.001-2H6c1.333 0 2 .667 2 2v2c0 1.333-.667 2-2 2H2l.001-2Z",
    clipRule: "evenodd"
  })));
};

var _path$97;
function _extends$98() { _extends$98 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$98.apply(this, arguments); }
var SvgBridge = function SvgBridge(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$98({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$97 || (_path$97 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7.76 7.084c1.213 0 2.144.48 2.795 1.442.65.961.976 2.306.976 4.035 0 1.058-.163 2-.488 2.826-.325.826-.791 1.471-1.397 1.936-.606.464-1.324.696-2.153.696-.549 0-1.05-.116-1.503-.348a3.424 3.424 0 0 1-1.157-.968l-.154.987H2V3.33L5.024 3v5.458A3.254 3.254 0 0 1 6.2 7.452a3.37 3.37 0 0 1 1.56-.368Zm-1.148 8.71c1.187 0 1.78-1.078 1.78-3.233 0-1.226-.14-2.07-.42-2.535-.281-.465-.69-.697-1.226-.697-.676 0-1.25.4-1.722 1.2v4.277c.191.31.424.552.698.726.275.175.571.261.89.261ZM12.431 21v-2.4H22V21h-9.57Z",
    clipRule: "evenodd"
  })));
};

var _path$96;
function _extends$97() { _extends$97 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$97.apply(this, arguments); }
var SvgBriefcase = function SvgBriefcase(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$97({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$96 || (_path$96 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14 2h-4a3 3 0 0 0-3 3v1H5a3 3 0 0 0-3 3v2.477a3 3 0 0 0 2 2.829V19a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-4.694a3 3 0 0 0 2-2.829V9a3 3 0 0 0-3-3h-2V5a3 3 0 0 0-3-3ZM4.79 12.455a.998.998 0 0 1-.79-.978V9a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2.477a1 1 0 0 1-.79.978L16.666 13l-2.167.464a2 2 0 0 0-2-1.964h-1a2 2 0 0 0-2 1.964L7.333 13l-2.543-.545ZM9.5 15.51 6 14.76V19a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-4.24l-3.5.75a2 2 0 0 1-2 1.99h-1a2 2 0 0 1-2-1.99ZM15 6V5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v1h6Zm-2.5 7.5v2h-1v-2h1Z",
    clipRule: "evenodd"
  })));
};

var _path$95;
function _extends$96() { _extends$96 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$96.apply(this, arguments); }
var SvgCalculator = function SvgCalculator(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$96({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$95 || (_path$95 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 3a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H6ZM3 4a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v16a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4Zm4 2a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm0 4a1 1 0 0 1 1-1h.01a1 1 0 0 1 0 2H8a1 1 0 0 1-1-1Zm4 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Zm4 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H16a1 1 0 0 1-1-1Zm-8 4a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm4 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Zm5-1a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0v-4a1 1 0 0 1 1-1Zm-9 5a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm4 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$94;
function _extends$95() { _extends$95 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$95.apply(this, arguments); }
var SvgCalendar = function SvgCalendar(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$95({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$94 || (_path$94 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 1a1 1 0 0 1 1 1v1h6V2a1 1 0 1 1 2 0v1h2a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h2V2a1 1 0 0 1 1-1ZM7 5H5a1 1 0 0 0-1 1v3h16V6a1 1 0 0 0-1-1h-2v1a1 1 0 1 1-2 0V5H9v1a1 1 0 0 1-2 0V5Zm13 6H4v9a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-9Z",
    clipRule: "evenodd"
  })));
};

var _path$93;
function _extends$94() { _extends$94 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$94.apply(this, arguments); }
var SvgCalendarCheck = function SvgCalendarCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$94({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$93 || (_path$93 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 1a1 1 0 0 1 1 1v1h6V2a1 1 0 1 1 2 0v1h2a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h2V2a1 1 0 0 1 1-1ZM7 5H5a1 1 0 0 0-1 1v3h16V6a1 1 0 0 0-1-1h-2v1a1 1 0 1 1-2 0V5H9v1a1 1 0 0 1-2 0V5Zm13 6H4v9a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-9Zm-4.293 2.293a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 1 1 1.414-1.414L11 16.586l3.293-3.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$92;
function _extends$93() { _extends$93 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$93.apply(this, arguments); }
var SvgCalendarCheckInside = function SvgCalendarCheckInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$93({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$92 || (_path$92 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 1a1 1 0 0 1 1 1v1h6V2a1 1 0 1 1 2 0v1h2a3 3 0 0 1 3 3v8a1 1 0 1 1-2 0v-3H4v9a1 1 0 0 0 1 1h8a1 1 0 1 1 0 2H5a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h2V2a1 1 0 0 1 1-1ZM7 5H5a1 1 0 0 0-1 1v3h16V6a1 1 0 0 0-1-1h-2v1a1 1 0 1 1-2 0V5H9v1a1 1 0 0 1-2 0V5Zm15.707 12.293a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L18 20.586l3.293-3.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$91;
function _extends$92() { _extends$92 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$92.apply(this, arguments); }
var SvgCalendarClock = function SvgCalendarClock(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$92({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$91 || (_path$91 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 1a1 1 0 0 1 1 1v1h6V2a1 1 0 1 1 2 0v1h2a3 3 0 0 1 3 3v1.5a1 1 0 1 1-2 0V6a1 1 0 0 0-1-1h-2v1a1 1 0 1 1-2 0V5H9v1a1 1 0 0 1-2 0V5H5a1 1 0 0 0-1 1v3h4a1 1 0 0 1 0 2H4v9a1 1 0 0 0 1 1h3.5a1 1 0 1 1 0 2H5a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h2V2a1 1 0 0 1 1-1Zm8 10a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm-4.95.05a7 7 0 1 1 9.9 9.9 7 7 0 0 1-9.9-9.9ZM16 13a1 1 0 0 1 1 1v1.782l1.14.95a1 1 0 1 1-1.28 1.536l-1.5-1.25a1 1 0 0 1-.36-.768V14a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$90;
function _extends$91() { _extends$91 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$91.apply(this, arguments); }
var SvgCalendarFilled = function SvgCalendarFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$91({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$90 || (_path$90 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9 2a1 1 0 0 0-2 0v2H5a2 2 0 0 0-2 2v3h18V6a2 2 0 0 0-2-2h-2V2a1 1 0 1 0-2 0v2H9V2ZM3 20v-9h18v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Zm4-6a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm4 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Zm4 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H16a1 1 0 0 1-1-1Zm-8 4a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm4 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Zm4 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H16a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$8$;
function _extends$90() { _extends$90 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$90.apply(this, arguments); }
var SvgCalendarHeart = function SvgCalendarHeart(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$90({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8$ || (_path$8$ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 1a1 1 0 0 1 1 1v1h6V2a1 1 0 1 1 2 0v1h2a3 3 0 0 1 3 3v4a1 1 0 0 1-1 1H4v9c0 .548.452 1 1 1h7a1 1 0 1 1 0 2H5c-1.652 0-3-1.348-3-3V6a3 3 0 0 1 3-3h2V2a1 1 0 0 1 1-1ZM7 5H5a1 1 0 0 0-1 1v3h16V6a1 1 0 0 0-1-1h-2v1a1 1 0 1 1-2 0V5H9v1a1 1 0 0 1-2 0V5Zm11.262 8.254a3.43 3.43 0 0 1 3.733.737.966.966 0 0 1 .09.102c1.288 1.373 1.24 3.533-.275 5.061l-.008.008-3.6 3.55a1 1 0 0 1-1.405-.001L13.2 19.154c-1.544-1.557-1.58-3.781-.195-5.153a3.43 3.43 0 0 1 4.49-.313c.234-.176.491-.323.767-.434Zm1.59 1.765a1.43 1.43 0 0 0-.827.084l-.013.005a1.379 1.379 0 0 0-.465.31l-.34.34a1 1 0 0 1-1.404.01l-.35-.34-.011-.012a1.43 1.43 0 0 0-1.56-.312l-.01.005c-.16.063-.309.162-.455.308l-.003.004c-.514.508-.649 1.46.203 2.322l2.884 2.852 2.893-2.853c.846-.856.71-1.809.193-2.321a.983.983 0 0 1-.066-.072 1.43 1.43 0 0 0-.67-.33Z",
    clipRule: "evenodd"
  })));
};

var _path$8_;
function _extends$8$() { _extends$8$ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8$.apply(this, arguments); }
var SvgCalendarMinus = function SvgCalendarMinus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8$({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8_ || (_path$8_ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 1a1 1 0 0 1 1 1v1h6V2a1 1 0 1 1 2 0v1h2a3 3 0 0 1 3 3v7a1 1 0 1 1-2 0v-2H4v9a1 1 0 0 0 1 1h8a1 1 0 1 1 0 2H5a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h2V2a1 1 0 0 1 1-1ZM7 5H5a1 1 0 0 0-1 1v3h16V6a1 1 0 0 0-1-1h-2v1a1 1 0 1 1-2 0V5H9v1a1 1 0 0 1-2 0V5Zm8 14a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2h-6a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$8Z;
function _extends$8_() { _extends$8_ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8_.apply(this, arguments); }
var SvgCalendarOff = function SvgCalendarOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8_({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8Z || (_path$8Z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 1.414 0l2.164 2.163.033.034L21.51 20.096l.033.033 1.163 1.164a1 1 0 0 1-1.414 1.414l-.39-.39a3.004 3.004 0 0 1-1.9.683H5a3 3 0 0 1-3-3V5.998a3 3 0 0 1 .684-1.9l-1.391-1.39a1 1 0 0 1 0-1.415Zm2.825 4.24A1 1 0 0 0 4 6v3h3.586L4.118 5.532ZM9.586 11H4v9a1 1 0 0 0 1 1h13.998a.999.999 0 0 0 .47-.118L9.586 11ZM15 3V2a1 1 0 1 1 2 0v1h2a3 3 0 0 1 3 3v9.5a1 1 0 1 1-2 0V11h-4.5a1 1 0 1 1 0-2H20V6a1 1 0 0 0-1-1h-2v1a1 1 0 1 1-2 0V5H9.5a1 1 0 0 1 0-2H15Z",
    clipRule: "evenodd"
  })));
};

var _path$8Y;
function _extends$8Z() { _extends$8Z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8Z.apply(this, arguments); }
var SvgCalendarOutline = function SvgCalendarOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8Z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8Y || (_path$8Y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 1a1 1 0 0 1 1 1v1h6V2a1 1 0 1 1 2 0v1h2a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h2V2a1 1 0 0 1 1-1ZM7 5H5a1 1 0 0 0-1 1v3h16V6a1 1 0 0 0-1-1h-2v1a1 1 0 1 1-2 0V5H9v1a1 1 0 0 1-2 0V5Zm13 6H4v9a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-9ZM7 14a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm4 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Zm4 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H16a1 1 0 0 1-1-1Zm-8 4a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm4 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Zm4 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H16a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$8X;
function _extends$8Y() { _extends$8Y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8Y.apply(this, arguments); }
var SvgCalendarPlus = function SvgCalendarPlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8Y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8X || (_path$8X = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 1a1 1 0 0 1 1 1v1h6V2a1 1 0 1 1 2 0v1h2a3 3 0 0 1 3 3v7a1 1 0 1 1-2 0v-2H4v9a1 1 0 0 0 1 1h8a1 1 0 1 1 0 2H5a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h2V2a1 1 0 0 1 1-1ZM7 5H5a1 1 0 0 0-1 1v3h16V6a1 1 0 0 0-1-1h-2v1a1 1 0 1 1-2 0V5H9v1a1 1 0 0 1-2 0V5Zm12 10a1 1 0 0 1 1 1v2h2a1 1 0 1 1 0 2h-2v2a1 1 0 1 1-2 0v-2h-2a1 1 0 1 1 0-2h2v-2a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$8W;
function _extends$8X() { _extends$8X = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8X.apply(this, arguments); }
var SvgCalendarRange = function SvgCalendarRange(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8X({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8W || (_path$8W = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 1a1 1 0 0 1 1 1v1h6V2a1 1 0 1 1 2 0v1h2a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h2V2a1 1 0 0 1 1-1ZM7 5H5a1 1 0 0 0-1 1v3h16V6a1 1 0 0 0-1-1h-2v1a1 1 0 1 1-2 0V5H9v1a1 1 0 0 1-2 0V5Zm13 6H4v9a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-9ZM6 14a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1Zm4 0a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2h-6a1 1 0 0 1-1-1Zm-4 4a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1Zm10 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H17a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$8V;
function _extends$8W() { _extends$8W = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8W.apply(this, arguments); }
var SvgCalendarSearch = function SvgCalendarSearch(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8W({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8V || (_path$8V = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 1a1 1 0 0 1 1 1v1h6V2a1 1 0 1 1 2 0v1h2a3 3 0 0 1 3 3v6a1 1 0 1 1-2 0v-1H4v9c0 .548.452 1 1 1h7.5a1 1 0 1 1 0 2H5c-1.652 0-3-1.348-3-3V6a3 3 0 0 1 3-3h2V2a1 1 0 0 1 1-1ZM7 5H5a1 1 0 0 0-1 1v3h16V6a1 1 0 0 0-1-1h-2v1a1 1 0 1 1-2 0V5H9v1a1 1 0 0 1-2 0V5Zm8.172 10.172a4 4 0 0 1 6.274 4.86l1.261 1.26a1 1 0 0 1-1.414 1.415l-1.261-1.261a3.999 3.999 0 0 1-4.86-6.274ZM18 16a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z",
    clipRule: "evenodd"
  })));
};

var _path$8U;
function _extends$8V() { _extends$8V = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8V.apply(this, arguments); }
var SvgCalendarX = function SvgCalendarX(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8V({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8U || (_path$8U = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 1a1 1 0 0 1 1 1v1h6V2a1 1 0 1 1 2 0v1h2a3 3 0 0 1 3 3v7a1 1 0 1 1-2 0v-2H4v9a1 1 0 0 0 1 1h8a1 1 0 1 1 0 2H5a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h2V2a1 1 0 0 1 1-1ZM7 5H5a1 1 0 0 0-1 1v3h16V6a1 1 0 0 0-1-1h-2v1a1 1 0 1 1-2 0V5H9v1a1 1 0 0 1-2 0V5Zm9.293 12.707a1 1 0 0 1 1.414-1.414l1.793 1.793 1.793-1.793a1 1 0 0 1 1.414 1.414L20.914 19.5l1.793 1.793a1 1 0 0 1-1.414 1.414L19.5 20.914l-1.793 1.793a1 1 0 0 1-1.414-1.414l1.793-1.793-1.793-1.793Z",
    clipRule: "evenodd"
  })));
};

var _path$8T;
function _extends$8U() { _extends$8U = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8U.apply(this, arguments); }
var SvgCalendarXInside = function SvgCalendarXInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8U({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8T || (_path$8T = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 1a1 1 0 0 1 1 1v1h6V2a1 1 0 1 1 2 0v1h2a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h2V2a1 1 0 0 1 1-1ZM7 5H5a1 1 0 0 0-1 1v3h16V6a1 1 0 0 0-1-1h-2v1a1 1 0 1 1-2 0V5H9v1a1 1 0 0 1-2 0V5Zm13 6H4v9a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-9ZM9.293 13.293a1 1 0 0 1 1.414 0L12 14.586l1.293-1.293a1 1 0 0 1 1.414 1.414L13.414 16l1.293 1.293a1 1 0 0 1-1.414 1.414L12 17.414l-1.293 1.293a1 1 0 0 1-1.414-1.414L10.586 16l-1.293-1.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$8S, _path2$z;
function _extends$8T() { _extends$8T = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8T.apply(this, arguments); }
var SvgCameraFilled = function SvgCameraFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8T({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8S || (_path$8S = /*#__PURE__*/React.createElement("path", {
    d: "M10 13a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z"
  })), _path2$z || (_path2$z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.732 3.36A1 1 0 0 1 9.5 3h5a1 1 0 0 1 .768.36l2.2 2.64H20a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3H4a3 3 0 0 1-3-3V9a3 3 0 0 1 3-3h2.532l2.2-2.64ZM12 9a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z",
    clipRule: "evenodd"
  })));
};

var _path$8R;
function _extends$8S() { _extends$8S = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8S.apply(this, arguments); }
var SvgCameraOff = function SvgCameraOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8S({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8R || (_path$8R = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 1.414 0l20 20a1 1 0 0 1-1.414 1.414L19.586 21H4a3 3 0 0 1-3-3V9a3 3 0 0 1 3-3h.586L1.293 2.707a1 1 0 0 1 0-1.414ZM6.586 8H4a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h13.586l-2.968-2.968a3.999 3.999 0 0 1-6.676-3.028 4 4 0 0 1 1.027-2.621L6.586 8Zm3.8 3.801a2 2 0 0 0 2.814 2.813l-2.813-2.813ZM8.5 4a1 1 0 0 1 1-1h5a1 1 0 0 1 .768.36l2.2 2.64H20a3 3 0 0 1 3 3v7.5a1 1 0 1 1-2 0V9a1 1 0 0 0-1-1h-3a1 1 0 0 1-.768-.36L14.032 5H9.5a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$8Q;
function _extends$8R() { _extends$8R = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8R.apply(this, arguments); }
var SvgCameraOutline = function SvgCameraOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8R({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8Q || (_path$8Q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.732 3.36A1 1 0 0 1 9.5 3h5a1 1 0 0 1 .768.36l2.2 2.64H20a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3H4a3 3 0 0 1-3-3V9a3 3 0 0 1 3-3h2.532l2.2-2.64ZM9.968 5l-2.2 2.64A1 1 0 0 1 7 8H4a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1h-3a1 1 0 0 1-.768-.36L14.032 5H9.968ZM12 11a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-4 2a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z",
    clipRule: "evenodd"
  })));
};

var _path$8P;
function _extends$8Q() { _extends$8Q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8Q.apply(this, arguments); }
var SvgCaretDown = function SvgCaretDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8Q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8P || (_path$8P = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13.276 15.045a1 1 0 0 1-1.552 0l-4.4-5.414A1 1 0 0 1 8.102 8H16.9a1 1 0 0 1 .776 1.63l-4.399 5.415Z",
    clipRule: "evenodd"
  })));
};

var _path$8O;
function _extends$8P() { _extends$8P = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8P.apply(this, arguments); }
var SvgCaretLeft = function SvgCaretLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8P({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8O || (_path$8O = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.455 12.776a1 1 0 0 1 0-1.552l5.414-4.4a1 1 0 0 1 1.631.777v8.798a1 1 0 0 1-1.63.776l-5.415-4.399Z",
    clipRule: "evenodd"
  })));
};

var _path$8N;
function _extends$8O() { _extends$8O = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8O.apply(this, arguments); }
var SvgCaretRight = function SvgCaretRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8O({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8N || (_path$8N = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.545 11.224a1 1 0 0 1 0 1.552l-5.414 4.4a1 1 0 0 1-1.631-.777V7.601a1 1 0 0 1 1.63-.776l5.415 4.399Z",
    clipRule: "evenodd"
  })));
};

var _path$8M;
function _extends$8N() { _extends$8N = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8N.apply(this, arguments); }
var SvgCaretUp = function SvgCaretUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8N({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8M || (_path$8M = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.724 8.955a1 1 0 0 1 1.552 0l4.4 5.414A1 1 0 0 1 16.898 16H8.101a1 1 0 0 1-.776-1.63l4.399-5.415Z",
    clipRule: "evenodd"
  })));
};

var _path$8L;
function _extends$8M() { _extends$8M = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8M.apply(this, arguments); }
var SvgChatFilled = function SvgChatFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8M({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8L || (_path$8L = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 4.001c0-1.332.667-1.999 2-2h16c1.318.001 1.984.668 2 2v14l-.024 4L16 18H4c-1.333 0-2-.667-2-2V4v.001Zm6 7a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm4 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm4 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z",
    clipRule: "evenodd"
  })));
};

var _path$8K;
function _extends$8L() { _extends$8L = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8L.apply(this, arguments); }
var SvgChatOutline = function SvgChatOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8L({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8K || (_path$8K = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 4.001c0-1.332.667-1.999 2-2h16c1.318.001 1.984.668 2 2v14l-.024 4L16 18H4c-1.333 0-2-.667-2-2V4v.001ZM4 4v12h13l3 2V4H4Zm4 7.001a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm4 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm4 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z",
    clipRule: "evenodd"
  })));
};

var _path$8J;
function _extends$8K() { _extends$8K = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8K.apply(this, arguments); }
var SvgCheckCheck = function SvgCheckCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8K({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8J || (_path$8J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18.707 5.293a1 1 0 0 1 0 1.414l-11 11a1 1 0 0 1-1.414 0l-5-5a1 1 0 1 1 1.414-1.414L7 15.586 17.293 5.293a1 1 0 0 1 1.414 0Zm4 4a1 1 0 0 1 0 1.414l-7.5 7.5a1 1 0 0 1-1.414 0l-1.5-1.5a1 1 0 0 1 1.414-1.414l.793.793 6.793-6.793a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$8I;
function _extends$8J() { _extends$8J = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8J.apply(this, arguments); }
var SvgCheckCircle = function SvgCheckCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8J({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8I || (_path$8I = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.663 3.773A9 9 0 1 0 21 12v-.919a1 1 0 1 1 2 0V12A11.002 11.002 0 0 1 8.188 22.313a11 11 0 1 1 8.289-20.366 1 1 0 1 1-.814 1.826Zm7.044-.48a1 1 0 0 1 0 1.414l-10 10.01a1 1 0 0 1-1.414 0l-3-3a1 1 0 1 1 1.414-1.414L12 12.595l9.293-9.302a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$8H;
function _extends$8I() { _extends$8I = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8I.apply(this, arguments); }
var SvgCheckCircleFilled = function SvgCheckCircleFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8I({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8H || (_path$8H = /*#__PURE__*/React.createElement("path", {
    d: "M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10c0 5.522-4.477 10-10 10S2 17.522 2 12Zm13.294-3.707-5.293 5.293-2.294-2.294a1 1 0 1 0-1.414 1.414l3.001 3.001a1 1 0 0 0 1.414 0l6-6a1 1 0 1 0-1.414-1.414Z"
  })));
};

var _path$8G;
function _extends$8H() { _extends$8H = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8H.apply(this, arguments); }
var SvgCheckCircleOutline = function SvgCheckCircleOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8H({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8G || (_path$8G = /*#__PURE__*/React.createElement("path", {
    d: "M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10c0 5.522-4.477 10-10 10S2 17.522 2 12Zm2 0a8 8 0 1 0 16 0 8 8 0 0 0-16 0Zm11.294-3.707a1 1 0 0 1 1.414 1.414l-6 6a1 1 0 0 1-1.414 0l-3.001-3.001a1 1 0 0 1 1.414-1.414l2.294 2.294 5.293-5.293Z"
  })));
};

var _path$8F;
function _extends$8G() { _extends$8G = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8G.apply(this, arguments); }
var SvgCheckDefault = function SvgCheckDefault(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8G({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8F || (_path$8F = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18.294 7.293a1 1 0 1 1 1.414 1.414l-9 9a1 1 0 0 1-1.414 0l-5.001-5.001a1 1 0 0 1 1.414-1.414l4.294 4.294 8.293-8.293Z",
    clipRule: "evenodd"
  })));
};

var _path$8E;
function _extends$8F() { _extends$8F = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8F.apply(this, arguments); }
var SvgCheckSquare = function SvgCheckSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8F({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8E || (_path$8E = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7a1 1 0 1 1 2 0v7a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3h11a1 1 0 1 1 0 2H5Zm16.293-.707a1 1 0 1 1 1.414 1.414l-10 10a1 1 0 0 1-1.414 0l-3-3a1 1 0 1 1 1.414-1.414L12 12.586l9.293-9.293Z",
    clipRule: "evenodd"
  })));
};

var _path$8D;
function _extends$8E() { _extends$8E = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8E.apply(this, arguments); }
var SvgChevronDownCircle = function SvgChevronDownCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8E({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8D || (_path$8D = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm6.293-2.707a1 1 0 0 1 1.414 0L12 12.586l3.293-3.293a1 1 0 1 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$8C;
function _extends$8D() { _extends$8D = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8D.apply(this, arguments); }
var SvgChevronDownSquare = function SvgChevronDownSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8D({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8C || (_path$8C = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm5.293 4.293a1 1 0 0 1 1.414 0L12 12.586l3.293-3.293a1 1 0 1 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$8B;
function _extends$8C() { _extends$8C = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8C.apply(this, arguments); }
var SvgChevronFirst = function SvgChevronFirst(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8C({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8B || (_path$8B = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 5a1 1 0 0 1 1 1v12a1 1 0 1 1-2 0V6a1 1 0 0 1 1-1Zm9.293.293a1 1 0 1 1 1.414 1.414L12.414 12l5.293 5.293a1 1 0 0 1-1.414 1.414l-6-6a1 1 0 0 1 0-1.414l6-6Z",
    clipRule: "evenodd"
  })));
};

var _path$8A;
function _extends$8B() { _extends$8B = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8B.apply(this, arguments); }
var SvgChevronLast = function SvgChevronLast(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8B({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8A || (_path$8A = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.293 5.293a1 1 0 0 1 1.414 0l6 6a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1-1.414-1.414L11.586 12 6.293 6.707a1 1 0 0 1 0-1.414ZM17 19a1 1 0 0 1-1-1V6a1 1 0 1 1 2 0v12a1 1 0 0 1-1 1Z",
    clipRule: "evenodd"
  })));
};

var _path$8z;
function _extends$8A() { _extends$8A = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8A.apply(this, arguments); }
var SvgChevronLeftCircle = function SvgChevronLeftCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8A({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8z || (_path$8z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm13.707-4.707a1 1 0 0 1 0 1.414L11.414 12l3.293 3.293a1 1 0 0 1-1.414 1.414l-4-4a1 1 0 0 1 0-1.414l4-4a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$8y;
function _extends$8z() { _extends$8z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8z.apply(this, arguments); }
var SvgChevronLeftSquare = function SvgChevronLeftSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8y || (_path$8y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm12.707 2.293a1 1 0 0 1 0 1.414L11.414 12l3.293 3.293a1 1 0 0 1-1.414 1.414l-4-4a1 1 0 0 1 0-1.414l4-4a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$8x;
function _extends$8y() { _extends$8y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8y.apply(this, arguments); }
var SvgChevronRightCircle = function SvgChevronRightCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8x || (_path$8x = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm8.293-4.707a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414-1.414L12.586 12 9.293 8.707a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$8w;
function _extends$8x() { _extends$8x = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8x.apply(this, arguments); }
var SvgChevronRightSquare = function SvgChevronRightSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8x({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8w || (_path$8w = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm7.293 2.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414-1.414L12.586 12 9.293 8.707a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$8v;
function _extends$8w() { _extends$8w = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8w.apply(this, arguments); }
var SvgChevronUpCircle = function SvgChevronUpCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8w({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8v || (_path$8v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-.586-3.293 3.293a1 1 0 0 1-1.414-1.414l4-4a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L12 11.414Z",
    clipRule: "evenodd"
  })));
};

var _path$8u;
function _extends$8v() { _extends$8v = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8v.apply(this, arguments); }
var SvgChevronUpSquare = function SvgChevronUpSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8v({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8u || (_path$8u = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm9.293 4.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L12 11.414l-3.293 3.293a1 1 0 0 1-1.414-1.414l4-4Z",
    clipRule: "evenodd"
  })));
};

var _path$8t;
function _extends$8u() { _extends$8u = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8u.apply(this, arguments); }
var SvgChevronsDownUp = function SvgChevronsDownUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8u({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8t || (_path$8t = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.293 3.293a1 1 0 0 1 1.414 0L12 7.586l4.293-4.293a1 1 0 1 1 1.414 1.414l-5 5a1 1 0 0 1-1.414 0l-5-5a1 1 0 0 1 0-1.414ZM12 16.414l-4.293 4.293a1 1 0 0 1-1.414-1.414l5-5a1 1 0 0 1 1.414 0l5 5a1 1 0 0 1-1.414 1.414L12 16.414Z",
    clipRule: "evenodd"
  })));
};

var _path$8s;
function _extends$8t() { _extends$8t = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8t.apply(this, arguments); }
var SvgChevronsLeftRight = function SvgChevronsLeftRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8t({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8s || (_path$8s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.707 6.293a1 1 0 0 1 0 1.414L5.414 12l4.293 4.293a1 1 0 0 1-1.414 1.414l-5-5a1 1 0 0 1 0-1.414l5-5a1 1 0 0 1 1.414 0Zm4.586 0a1 1 0 0 1 1.414 0l5 5a1 1 0 0 1 0 1.414l-5 5a1 1 0 0 1-1.414-1.414L18.586 12l-4.293-4.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$8r;
function _extends$8s() { _extends$8s = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8s.apply(this, arguments); }
var SvgChevronsRightLeft = function SvgChevronsRightLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8s({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8r || (_path$8r = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.293 6.293a1 1 0 0 1 1.414 0l5 5a1 1 0 0 1 0 1.414l-5 5a1 1 0 0 1-1.414-1.414L7.586 12 3.293 7.707a1 1 0 0 1 0-1.414Zm17.414 0a1 1 0 0 1 0 1.414L16.414 12l4.293 4.293a1 1 0 0 1-1.414 1.414l-5-5a1 1 0 0 1 0-1.414l5-5a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$8q;
function _extends$8r() { _extends$8r = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8r.apply(this, arguments); }
var SvgCircleCrossFilled = function SvgCircleCrossFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8r({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8q || (_path$8q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2Zm1.005 4.005h-1.993c-.261 0-.48.08-.66.238a1.121 1.121 0 0 0-.352.586V10H7c-.289 0-.528.1-.719.303a.966.966 0 0 0-.28.697v2a.954.954 0 0 0 .28.694A.948.948 0 0 0 7 14h3v2.998c0 .242.095.47.284.683.19.213.428.319.715.319H13a.914.914 0 0 0 .728-.32c.184-.212.274-.437.272-.675V14h3a.954.954 0 0 0 .722-.306A.941.941 0 0 0 18 13v-2a.97.97 0 0 0-.278-.697A1 1 0 0 0 17 10h-3V6.843c-.077-.27-.192-.47-.347-.6-.154-.13-.37-.21-.648-.238Z",
    clipRule: "evenodd"
  })));
};

var _path$8p, _path2$y;
function _extends$8q() { _extends$8q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8q.apply(this, arguments); }
var SvgCircleCrossOutline = function SvgCircleCrossOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8p || (_path$8p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2Zm0 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16Z",
    clipRule: "evenodd"
  })), _path2$y || (_path2$y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.012 6.005h1.993c.278.029.494.108.648.238.155.13.27.33.347.6V10h3a1 1 0 0 1 .722.303A.97.97 0 0 1 18 11v2a.942.942 0 0 1-.278.694.954.954 0 0 1-.723.306h-3v3.005c.003.238-.087.463-.27.676A.914.914 0 0 1 13 18h-2.001a.916.916 0 0 1-.715-.32c-.19-.212-.284-.44-.284-.682V14H7a.948.948 0 0 1-.719-.306.954.954 0 0 1-.28-.694v-2a.966.966 0 0 1 .28-.697A.948.948 0 0 1 7 10h3V6.829c.056-.232.174-.427.352-.586a.957.957 0 0 1 .66-.238Z",
    clipRule: "evenodd"
  })));
};

var _path$8o;
function _extends$8p() { _extends$8p = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8p.apply(this, arguments); }
var SvgCircleDollarSign = function SvgCircleDollarSign(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8p({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8o || (_path$8o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-7a1 1 0 0 1 1 1v1h3a1 1 0 1 1 0 2h-3v2h1a3 3 0 0 1 0 6h-1v1a1 1 0 1 1-2 0v-1H8a1 1 0 1 1 0-2h3v-2h-1a3 3 0 0 1 0-6h1V6a1 1 0 0 1 1-1Zm-1 4h-1a1 1 0 0 0 0 2h1V9Zm2 4v2h1a1 1 0 0 0 0-2h-1Z",
    clipRule: "evenodd"
  })));
};

var _path$8n;
function _extends$8o() { _extends$8o = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8o.apply(this, arguments); }
var SvgCircleEllipsis = function SvgCircleEllipsis(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8o({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8n || (_path$8n = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm5 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1Zm5 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Zm5 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H17a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$8m;
function _extends$8n() { _extends$8n = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8n.apply(this, arguments); }
var SvgCircleEmpty = function SvgCircleEmpty(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8n({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8m || (_path$8m = /*#__PURE__*/React.createElement("path", {
    d: "M12 2c5.523 0 10 4.478 10 10s-4.477 10-10 10S2 17.522 2 12 6.477 2 12 2Zm0 18c4.411 0 8-3.589 8-8s-3.589-8-8-8-8 3.589-8 8 3.589 8 8 8Z"
  })));
};

var _path$8l;
function _extends$8m() { _extends$8m = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8m.apply(this, arguments); }
var SvgCircleError = function SvgCircleError(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8m({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8l || (_path$8l = /*#__PURE__*/React.createElement("path", {
    d: "M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 5.522 4.477 10 10 10s10-4.478 10-10Zm-8.5 4.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM11 6.875c-.002-.583.33-.875.998-.875.668 0 1.002.292 1.002.875v5.25c0 .583-.335.875-1.004.875s-1.001-.292-.996-.875v-5.25Z"
  })));
};

var _path$8k;
function _extends$8l() { _extends$8l = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8l.apply(this, arguments); }
var SvgCircleInProgress = function SvgCircleInProgress(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8l({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8k || (_path$8k = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Zm-5-8.75a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5Zm5 0a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5ZM18.25 12a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0Z",
    clipRule: "evenodd"
  })));
};

var _path$8j;
function _extends$8k() { _extends$8k = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8k.apply(this, arguments); }
var SvgCircleStop = function SvgCircleStop(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8k({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8j || (_path$8j = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Zm5-10.25a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v.5a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-.5Z",
    clipRule: "evenodd"
  })));
};

var _path$8i;
function _extends$8j() { _extends$8j = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8j.apply(this, arguments); }
var SvgClearFormat = function SvgClearFormat(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8j({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8i || (_path$8i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 17h8a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2Zm4.378-3.004C8.28 14.666 7.904 15 7.25 15c-.654 0-.945-.335-.872-1.004l1.626-10H5.707c-.51-.107-.743-.46-.702-1.056.04-.596.388-.91 1.042-.94h6.183c.544.063.8.393.766.99-.034.596-.36.932-.979 1.006h-2.013l-1.626 10Zm6.596.61 2.168 2.169 2.168-2.168c.448-.464.904-.464 1.368 0 .463.463.463.919 0 1.367l-2.169 2.168 2.169 2.168c.455.456.455.912 0 1.368-.456.455-.912.455-1.368 0l-2.168-2.169-2.168 2.169c-.445.466-.901.466-1.367 0-.467-.467-.467-.922 0-1.368l2.168-2.168-2.168-2.168c-.457-.455-.457-.911 0-1.367.456-.457.912-.457 1.367 0v-.001Z",
    clipRule: "evenodd"
  })));
};

var _path$8h;
function _extends$8i() { _extends$8i = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8i.apply(this, arguments); }
var SvgClip = function SvgClip(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8i({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8h || (_path$8h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M17.995 3.878A3.207 3.207 0 0 0 15.776 3c-.832 0-1.629.318-2.217.882l-8.943 8.57a4.9 4.9 0 0 0-1.529 3.539 4.9 4.9 0 0 0 1.53 3.538 5.338 5.338 0 0 0 3.69 1.465A5.338 5.338 0 0 0 12 19.53l9.59-9.19a1.076 1.076 0 0 1 1.476 0 .97.97 0 0 1 0 1.414l-9.59 9.19c-1.37 1.313-3.229 2.051-5.167 2.051a7.476 7.476 0 0 1-5.168-2.051C1.77 19.629 1 17.848 1 15.99c0-1.858.77-3.639 2.14-4.952l8.942-8.57A5.342 5.342 0 0 1 15.774 1a5.341 5.341 0 0 1 3.695 1.463 4.903 4.903 0 0 1 1.534 3.538 4.902 4.902 0 0 1-1.527 3.541l-8.964 8.57a3.202 3.202 0 0 1-2.215.88c-.83 0-1.627-.316-2.214-.88a2.94 2.94 0 0 1-.917-2.121c0-.796.33-1.56.917-2.122l8.86-8.48a1.076 1.076 0 0 1 1.475 0 .97.97 0 0 1 0 1.414l-8.86 8.48a.982.982 0 0 0-.305.708c0 .265.11.52.306.708a1.077 1.077 0 0 0 1.478 0l8.962-8.57a2.944 2.944 0 0 0 .917-2.126 2.947 2.947 0 0 0-.921-2.125Z",
    clipRule: "evenodd"
  })));
};

var _path$8g;
function _extends$8h() { _extends$8h = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8h.apply(this, arguments); }
var SvgClipboard = function SvgClipboard(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8h({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8g || (_path$8g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 3a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2h1a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h1Zm0 2H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-1a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2Zm8 0H9V3h6v2Z",
    clipRule: "evenodd"
  })));
};

var _path$8f;
function _extends$8g() { _extends$8g = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8g.apply(this, arguments); }
var SvgClipboardCheck = function SvgClipboardCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8g({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8f || (_path$8f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 3a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2h1a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h1Zm0 2H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-1a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2Zm8 0H9V3h6v2Zm.707 6.293a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 1 1 1.414-1.414L11 14.586l3.293-3.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$8e;
function _extends$8f() { _extends$8f = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8f.apply(this, arguments); }
var SvgClipboardCopy = function SvgClipboardCopy(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8f({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8e || (_path$8e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 3a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2h1a3 3 0 0 1 3 3v4a1 1 0 1 1-2 0V6a1 1 0 0 0-1-1h-1a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2a1 1 0 1 1 2 0v2a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h1Zm2 2h6V3H9v2Zm6.707 4.293a1 1 0 0 1 0 1.414L13.414 13H21a1 1 0 1 1 0 2h-7.586l2.293 2.293a1 1 0 0 1-1.414 1.414l-4-4a1 1 0 0 1 0-1.414l4-4a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$8d;
function _extends$8e() { _extends$8e = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8e.apply(this, arguments); }
var SvgClipboardEdit = function SvgClipboardEdit(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8e({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8d || (_path$8d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 3a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2h1a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3h-5.5a1 1 0 1 1 0-2H18a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-1a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2H6a1 1 0 0 0-1 1v7.5a1 1 0 1 1-2 0V6a3 3 0 0 1 3-3h1Zm2 2h6V3H9v2Zm2.905 7.995a1.1 1.1 0 0 0-.777.322L5.893 18.56l-.516 2.059 2.062-.522 5.244-5.225a1.104 1.104 0 0 0 .238-1.199 1.099 1.099 0 0 0-1.016-.68Zm-1.186-1.764a3.1 3.1 0 0 1 3.378 5.056l-.001.001-5.44 5.42a1 1 0 0 1-.46.261l-3.95 1a1 1 0 0 1-1.216-1.212l.99-3.95a1 1 0 0 1 .262-.463l5.43-5.44c.288-.288.63-.517 1.007-.673Z",
    clipRule: "evenodd"
  })));
};

var _path$8c;
function _extends$8d() { _extends$8d = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8d.apply(this, arguments); }
var SvgClipboardList = function SvgClipboardList(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8d({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8c || (_path$8c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 3a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2h1a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h1Zm0 2H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-1a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2Zm8 0H9V3h6v2Zm-8 6a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm4 0a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1Zm-4 5a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm4 0a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$8b;
function _extends$8c() { _extends$8c = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8c.apply(this, arguments); }
var SvgClipboardPaste = function SvgClipboardPaste(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8c({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8b || (_path$8b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7.586 1.586A2 2 0 0 1 9 1h6c.527 0 1.044.18 1.432.568.388.388.568.905.568 1.432h1a3 3 0 0 1 3 3v2a1 1 0 1 1-2 0V6a1 1 0 0 0-1-1h-1c0 .527-.18 1.044-.568 1.432C16.044 6.82 15.527 7 15 7H9c-.527 0-1.044-.18-1.432-.568C7.18 6.044 7 5.527 7 5H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1 1 1 0 1 1 2 0 3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h1a2 2 0 0 1 .586-1.414ZM9 5h6V3H9v2Zm7.293 5.707a1 1 0 0 1 1.414-1.414l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414-1.414L18.586 15H11a1 1 0 1 1 0-2h7.586l-2.293-2.293Z",
    clipRule: "evenodd"
  })));
};

var _path$8a;
function _extends$8b() { _extends$8b = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8b.apply(this, arguments); }
var SvgClipboardSignature = function SvgClipboardSignature(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8b({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8a || (_path$8a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 3a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2h1.001a3 3 0 0 1 2.595 1.5 1 1 0 1 1-1.732 1 1 1 0 0 0-.865-.5H17a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.5a1 1 0 1 1 2 0v.5a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h1Zm2 2h6V3H9v2Zm10.905 4.995a1.1 1.1 0 0 0-.777.322l-4.235 4.244-.516 2.059 2.062-.522 4.244-4.225a1.106 1.106 0 0 0 .238-1.199 1.1 1.1 0 0 0-1.016-.68ZM18.719 8.23a3.1 3.1 0 0 1 3.378 5.056l-.002.002-4.44 4.42a1 1 0 0 1-.46.26l-3.95 1a1 1 0 0 1-1.215-1.212l.99-3.95a1 1 0 0 1 .262-.463l4.43-4.44v-.001a3.1 3.1 0 0 1 1.007-.672ZM7 18a1 1 0 0 1 1-1h1a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$89;
function _extends$8a() { _extends$8a = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8a.apply(this, arguments); }
var SvgClipboardType = function SvgClipboardType(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8a({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$89 || (_path$89 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 3a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2h1a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h1Zm0 2H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-1a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2Zm8 0H9V3h6v2Zm-7 6a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0h-1v4a1 1 0 1 1 0 2h-2a1 1 0 1 1 0-2v-4h-1a1 1 0 1 1-2 0v-1Z",
    clipRule: "evenodd"
  })));
};

var _path$88;
function _extends$89() { _extends$89 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$89.apply(this, arguments); }
var SvgClipboardX = function SvgClipboardX(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$89({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$88 || (_path$88 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 3a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2h1a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h1Zm0 2H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-1a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2Zm8 0H9V3h6v2Zm-6.707 6.707a1 1 0 1 1 1.414-1.414L12 12.586l2.293-2.293a1 1 0 1 1 1.414 1.414L13.414 14l2.293 2.293a1 1 0 0 1-1.414 1.414L12 15.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L10.586 14l-2.293-2.293Z",
    clipRule: "evenodd"
  })));
};

var _path$87;
function _extends$88() { _extends$88 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$88.apply(this, arguments); }
var SvgClock = function SvgClock(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$88({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$87 || (_path$87 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-7a1 1 0 0 1 1 1v5.382l3.447 1.724a1 1 0 1 1-.894 1.788l-4-2A1 1 0 0 1 11 12V6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$86;
function _extends$87() { _extends$87 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$87.apply(this, arguments); }
var SvgClock1 = function SvgClock1(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$87({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$86 || (_path$86 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-7a1 1 0 0 1 1 1v2.513l.652-1.043a1 1 0 0 1 1.696 1.06l-2.5 4A1 1 0 0 1 11 12V6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$85;
function _extends$86() { _extends$86 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$86.apply(this, arguments); }
var SvgClock10 = function SvgClock10(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$86({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$85 || (_path$85 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-7a1 1 0 0 1 1 1v6a1 1 0 0 1-1.447.894l-4-2a1 1 0 1 1 .894-1.788L11 10.382V6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$84;
function _extends$85() { _extends$85 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$85.apply(this, arguments); }
var SvgClock11 = function SvgClock11(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$85({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$84 || (_path$84 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-7a1 1 0 0 1 1 1v6a1 1 0 0 1-1.848.53l-2.5-4a1 1 0 1 1 1.696-1.06L11 8.513V6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$83;
function _extends$84() { _extends$84 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$84.apply(this, arguments); }
var SvgClock12 = function SvgClock12(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$84({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$83 || (_path$83 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-7a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0V6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$82;
function _extends$83() { _extends$83 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$83.apply(this, arguments); }
var SvgClock2 = function SvgClock2(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$83({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$82 || (_path$82 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-7a1 1 0 0 1 1 1v4.382l2.553-1.276a1 1 0 1 1 .894 1.788l-4 2A1 1 0 0 1 11 12V6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$81;
function _extends$82() { _extends$82 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$82.apply(this, arguments); }
var SvgClock3 = function SvgClock3(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$82({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$81 || (_path$81 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-7a1 1 0 0 1 1 1v5h3.5a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$80;
function _extends$81() { _extends$81 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$81.apply(this, arguments); }
var SvgClock4 = function SvgClock4(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$81({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$80 || (_path$80 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-7a1 1 0 0 1 1 1v5.382l3.447 1.724a1 1 0 1 1-.894 1.788l-4-2A1 1 0 0 1 11 12V6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7$;
function _extends$80() { _extends$80 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$80.apply(this, arguments); }
var SvgClock6 = function SvgClock6(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$80({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7$ || (_path$7$ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-7a1 1 0 0 1 1 1v10.5a1 1 0 1 1-2 0V6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7_;
function _extends$7$() { _extends$7$ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7$.apply(this, arguments); }
var SvgClock7 = function SvgClock7(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7$({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7_ || (_path$7_ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-7a1 1 0 0 1 1 1v6a1 1 0 0 1-.152.53l-2.5 4a1 1 0 0 1-1.696-1.06L11 11.713V6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7Z;
function _extends$7_() { _extends$7_ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7_.apply(this, arguments); }
var SvgClock8 = function SvgClock8(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7_({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7Z || (_path$7Z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-7a1 1 0 0 1 1 1v6a1 1 0 0 1-.553.894l-4 2a1 1 0 1 1-.894-1.788L11 11.382V6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7Y;
function _extends$7Z() { _extends$7Z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7Z.apply(this, arguments); }
var SvgClock9 = function SvgClock9(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7Z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7Y || (_path$7Y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-7a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H7.5a1 1 0 1 1 0-2H11V6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7X;
function _extends$7Y() { _extends$7Y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7Y.apply(this, arguments); }
var SvgClockFilled = function SvgClockFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7Y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7X || (_path$7X = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm12-6a1 1 0 1 0-2 0v6a1 1 0 0 0 .152.53l2.5 4a1 1 0 0 0 1.696-1.06L13 11.713V6Z",
    clipRule: "evenodd"
  })));
};

var _path$7W;
function _extends$7X() { _extends$7X = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7X.apply(this, arguments); }
var SvgClockOutline = function SvgClockOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7X({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7W || (_path$7W = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-7a1 1 0 0 1 1 1v5.713l2.348 3.757a1 1 0 0 1-1.696 1.06l-2.5-4A1 1 0 0 1 11 12V6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7V;
function _extends$7W() { _extends$7W = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7W.apply(this, arguments); }
var SvgCloud = function SvgCloud(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7W({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7V || (_path$7V = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.049 6.092A6 6 0 1 0 8.999 18S9 18 9 18h8.5a3.5 3.5 0 1 0 0-7h-1.79a1 1 0 0 1-.957-.714 6 6 0 0 0-4.703-4.194Zm-3.995-1.53A8 8 0 0 1 16.418 9H17.5a5.5 5.5 0 1 1 0 11H9A8.001 8.001 0 0 1 6.054 4.563Z",
    clipRule: "evenodd"
  })));
};

var _path$7U;
function _extends$7V() { _extends$7V = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7V.apply(this, arguments); }
var SvgCloudCog = function SvgCloudCog(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7V({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7U || (_path$7U = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.358 2.014A8 8 0 0 1 16.409 7h1.105a5.5 5.5 0 0 1 3.055 10.022 1 1 0 1 1-1.138-1.644A3.5 3.5 0 0 0 17.493 9H15.7a1 1 0 0 1-.959-.716 6 6 0 1 0-10.028 5.914 1 1 0 0 1-1.426 1.403 8 8 0 0 1 5.07-13.587ZM12 12a1 1 0 0 1 1 1v.126c.367.095.714.24 1.032.428l.261-.261a1 1 0 0 1 1.414 1.414l-.26.261c.187.318.333.665.427 1.032H16a1 1 0 1 1 0 2h-.126c-.095.367-.24.714-.428 1.032l.261.26a1 1 0 0 1-1.414 1.415l-.261-.26a3.976 3.976 0 0 1-1.032.427V21a1 1 0 1 1-2 0v-.126a3.977 3.977 0 0 1-1.032-.428l-.26.261a1 1 0 0 1-1.415-1.414l.26-.261A3.976 3.976 0 0 1 8.127 18H8a1 1 0 1 1 0-2h.126a3.96 3.96 0 0 1 .428-1.032l-.261-.26a1 1 0 1 1 1.414-1.415l.261.26A3.977 3.977 0 0 1 11 13.127V13a1 1 0 0 1 1-1Zm-1.45 3.622a.99.99 0 0 0 .072-.071A1.989 1.989 0 0 1 12 15a1.993 1.993 0 0 1 1.449.621c.341.359.551.844.551 1.379a1.993 1.993 0 0 1-.62 1.448A1.994 1.994 0 0 1 12 19a1.993 1.993 0 0 1-1.45-.622A1.993 1.993 0 0 1 10 17c0-.534.21-1.02.55-1.378Z",
    clipRule: "evenodd"
  })));
};

var _path$7T;
function _extends$7U() { _extends$7U = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7U.apply(this, arguments); }
var SvgCloudDrizzle = function SvgCloudDrizzle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7U({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7T || (_path$7T = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.195 4.418a6 6 0 0 0-6.48 9.782 1 1 0 0 1-1.43 1.399A8 8 0 1 1 16.418 7H17.5a5.5 5.5 0 0 1 3.055 10.074 1 1 0 0 1-1.11-1.664A3.5 3.5 0 0 0 17.5 9h-1.79a1 1 0 0 1-.958-.714 5.999 5.999 0 0 0-3.557-3.868ZM8 13a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0v-1a1 1 0 0 1 1-1Zm8 0a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0v-1a1 1 0 0 1 1-1Zm-4 2a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0v-1a1 1 0 0 1 1-1Zm-4 3a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0v-1a1 1 0 0 1 1-1Zm8 0a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0v-1a1 1 0 0 1 1-1Zm-4 2a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0v-1a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7S;
function _extends$7T() { _extends$7T = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7T.apply(this, arguments); }
var SvgCloudFilled = function SvgCloudFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7T({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7S || (_path$7S = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 18.993h11c2.551 0 4-2.1 4-4 0-2.44-2.004-4.008-4-4.008C18 8.35 16 5 12 5 8.098 5 6.353 8.394 6.353 9.035A5.001 5.001 0 0 0 7 18.993Z",
    clipRule: "evenodd"
  })));
};

var _path$7R;
function _extends$7S() { _extends$7S = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7S.apply(this, arguments); }
var SvgCloudFog = function SvgCloudFog(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7S({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7R || (_path$7R = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.195 4.418a6 6 0 0 0-6.48 9.782 1 1 0 0 1-1.43 1.399A8 8 0 1 1 16.418 7H17.5a5.5 5.5 0 0 1 3.055 10.074 1 1 0 0 1-1.11-1.664A3.5 3.5 0 0 0 17.5 9h-1.79a1 1 0 0 1-.958-.714 5.999 5.999 0 0 0-3.557-3.868ZM6 17a1 1 0 0 1 1-1h9a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1Zm2 4a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7Q;
function _extends$7R() { _extends$7R = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7R.apply(this, arguments); }
var SvgCloudHail = function SvgCloudHail(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7R({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7Q || (_path$7Q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.195 4.418a6 6 0 0 0-6.48 9.782 1 1 0 0 1-1.43 1.399A8 8 0 1 1 16.418 7H17.5a5.5 5.5 0 0 1 3.055 10.074 1 1 0 0 1-1.11-1.664A3.5 3.5 0 0 0 17.5 9h-1.79a1 1 0 0 1-.958-.714 5.999 5.999 0 0 0-3.557-3.868ZM8 13a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1Zm8 0a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1Zm-4 2a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1Zm-5 5a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm8 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H16a1 1 0 0 1-1-1Zm-4 2a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7P, _path2$x;
function _extends$7Q() { _extends$7Q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7Q.apply(this, arguments); }
var SvgCloudLightning = function SvgCloudLightning(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7Q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7P || (_path$7P = /*#__PURE__*/React.createElement("path", {
    d: "M8.528 4.021a6 6 0 0 1 6.224 4.265A1 1 0 0 0 15.71 9h1.79a3.5 3.5 0 0 1 1.944 6.41 1 1 0 1 0 1.111 1.664A5.5 5.5 0 0 0 17.5 7h-1.082a8 8 0 1 0-13.133 8.598 1 1 0 1 0 1.43-1.399A6 6 0 0 1 8.528 4.021Z"
  })), _path2$x || (_path2$x = /*#__PURE__*/React.createElement("path", {
    d: "M13.857 12.514a1 1 0 0 0-1.714-1.028l-3 5A1 1 0 0 0 10 18h2.234l-2.091 3.485a1 1 0 1 0 1.714 1.03l3-5A1 1 0 0 0 14 16h-2.234l2.091-3.486Z"
  })));
};

var _path$7O;
function _extends$7P() { _extends$7P = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7P.apply(this, arguments); }
var SvgCloudMoon = function SvgCloudMoon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7P({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7O || (_path$7O = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.924 3.616a1 1 0 0 1-.217 1.091 3.243 3.243 0 0 0 4.586 4.586A1 1 0 0 1 23 10a7 7 0 0 1-3.5 6.063 1 1 0 1 1-1-1.732 5 5 0 0 0 2.084-2.333 5.239 5.239 0 0 1-5.291-1.29A5.243 5.243 0 0 1 14 5.424a4.997 4.997 0 0 0-2.914 3.742 1 1 0 1 1-1.972-.334A7 7 0 0 1 15.997 3a1 1 0 0 1 .927.616Zm-8.965 9.5A4 4 0 1 0 7 21h6a2 2 0 0 0 0-4h-1.1a1 1 0 0 1-.98-.8 4 4 0 0 0-2.961-3.084ZM5.102 11.31A6 6 0 0 1 12.657 15H13a4 4 0 0 1 0 8H7a6 6 0 0 1-1.899-11.691Z",
    clipRule: "evenodd"
  })));
};

var _path$7N, _path2$w;
function _extends$7O() { _extends$7O = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7O.apply(this, arguments); }
var SvgCloudMoonRain = function SvgCloudMoonRain(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7O({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7N || (_path$7N = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 13a4 4 0 0 0-3.04 6.599 1 1 0 0 1-1.52 1.3 6 6 0 1 1 10.167-6.04.296.296 0 0 0 .087.126c.013.01.021.014.025.015H13a4 4 0 0 1 1.79 7.578 1 1 0 1 1-.896-1.788A2 2 0 0 0 13 17h-.283c-.98 0-1.696-.688-1.979-1.427A4.002 4.002 0 0 0 7 13Zm5.72 2h-.001.002Z",
    clipRule: "evenodd"
  })), _path2$w || (_path2$w = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14.033 5.403a5.002 5.002 0 0 0-2.964 3.764 1 1 0 1 1-1.972-.334A7.002 7.002 0 0 1 16 3a1 1 0 0 1 .716 1.698 3.243 3.243 0 0 0 4.586 4.586A1 1 0 0 1 23 10a6.996 6.996 0 0 1-3.5 6.063 1 1 0 0 1-1-1.732 5.016 5.016 0 0 0 2.099-2.364 5.242 5.242 0 0 1-6.566-6.564ZM7 18a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1Zm4 1a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7M;
function _extends$7N() { _extends$7N = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7N.apply(this, arguments); }
var SvgCloudOff = function SvgCloudOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7N({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7M || (_path$7M = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 1.414 0l3.764 3.764a1 1 0 0 1 .035.035L19.5 18.085l.031.03 3.177 3.178a1 1 0 0 1-1.414 1.414l-2.797-2.797c-.329.06-.662.09-.997.09H9A8.002 8.002 0 0 1 4.19 5.605l1.434 1.433A6 6 0 0 0 9 18h7.586L5.624 7.038M4.19 5.605 1.293 2.707a1 1 0 0 1 0-1.414M9.01 4.927a1 1 0 0 1 1.133-.847A8.008 8.008 0 0 1 16.418 9H17.5a5.501 5.501 0 0 1 4.928 7.945 1 1 0 0 1-1.792-.89A3.5 3.5 0 0 0 17.5 11h-1.79a1 1 0 0 1-.958-.714A6.007 6.007 0 0 0 9.857 6.06a1 1 0 0 1-.847-1.133Z",
    clipRule: "evenodd"
  })));
};

var _path$7L;
function _extends$7M() { _extends$7M = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7M.apply(this, arguments); }
var SvgCloudOutline = function SvgCloudOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7M({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7L || (_path$7L = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 18.993a5 5 0 0 1-.647-9.958C6.353 8.395 8.098 5 12 5c4 0 6 3.35 6 5.985 1.996 0 4 1.567 4 4.008 0 1.9-1.449 4-4 4H7ZM7.009 17c1.54.007 9.936-.014 10.991-.007 1.055.007 2-.797 2-1.993 0-1.196-.945-2-2-2h-1.987v-2c0-2.08-1.665-4-3.988-3.999-2.323.002-4.03 2.002-4.03 4H7.01A2.99 2.99 0 0 0 4 13.993c0 1.689 1.47 3 3.009 3.007Z",
    clipRule: "evenodd"
  })));
};

var _path$7K;
function _extends$7L() { _extends$7L = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7L.apply(this, arguments); }
var SvgCloudRain = function SvgCloudRain(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7L({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7K || (_path$7K = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.195 4.418a6 6 0 0 0-6.48 9.782 1 1 0 0 1-1.43 1.399A8 8 0 1 1 16.418 7H17.5a5.5 5.5 0 0 1 3.055 10.074 1 1 0 0 1-1.11-1.664A3.5 3.5 0 0 0 17.5 9h-1.79a1 1 0 0 1-.958-.714 5.999 5.999 0 0 0-3.557-3.868ZM8 13a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1Zm8 0a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1Zm-4 2a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7J;
function _extends$7K() { _extends$7K = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7K.apply(this, arguments); }
var SvgCloudRainWind = function SvgCloudRainWind(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7K({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7J || (_path$7J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.195 4.418a6 6 0 0 0-6.48 9.782 1 1 0 0 1-1.43 1.399A8 8 0 1 1 16.418 7H17.5a5.5 5.5 0 0 1 3.055 10.074 1 1 0 0 1-1.11-1.664A3.5 3.5 0 0 0 17.5 9h-1.79a1 1 0 0 1-.958-.714 5.999 5.999 0 0 0-3.557-3.868ZM9.394 12.08a1 1 0 0 1 .525 1.313l-3 7a1 1 0 0 1-1.838-.788l3-7a1 1 0 0 1 1.313-.525Zm8 0a1 1 0 0 1 .525 1.313l-3 7a1 1 0 1 1-1.838-.788l3-7a1 1 0 0 1 1.313-.525Zm-4.8 2a1 1 0 0 1 .525 1.313l-3 7a1 1 0 1 1-1.838-.788l3-7a1 1 0 0 1 1.313-.525Z",
    clipRule: "evenodd"
  })));
};

var _path$7I;
function _extends$7J() { _extends$7J = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7J.apply(this, arguments); }
var SvgCloudSnow = function SvgCloudSnow(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7J({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7I || (_path$7I = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.195 4.418a6 6 0 0 0-6.48 9.782 1 1 0 0 1-1.43 1.399A8 8 0 1 1 16.418 7H17.5a5.5 5.5 0 0 1 3.055 10.074 1 1 0 0 1-1.11-1.664A3.5 3.5 0 0 0 17.5 9h-1.79a1 1 0 0 1-.958-.714 5.999 5.999 0 0 0-3.557-3.868ZM7 15a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm8 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H16a1 1 0 0 1-1-1Zm-4 2a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Zm-4 2a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm8 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H16a1 1 0 0 1-1-1Zm-4 2a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7H;
function _extends$7I() { _extends$7I = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7I.apply(this, arguments); }
var SvgCloudSun = function SvgCloudSun(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7I({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7H || (_path$7H = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 1a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0V2a1 1 0 0 1 1-1ZM4.223 4.223a1 1 0 0 1 1.414 0l1.41 1.41a1 1 0 1 1-1.414 1.414l-1.41-1.41a1 1 0 0 1 0-1.414Zm15.554 0a1 1 0 0 1 0 1.414l-1.41 1.41a1 1 0 1 1-1.414-1.414l1.41-1.41a1 1 0 0 1 1.414 0Zm-10.25 3.43a5 5 0 0 1 7.407 5.16 1 1 0 1 1-1.974-.326 3 3 0 0 0-4.443-3.096 1 1 0 0 1-.99-1.738ZM7.96 13.116A4 4 0 1 0 7 21h6a2 2 0 0 0 0-4h-1.1a1 1 0 0 1-.98-.8 4 4 0 0 0-2.961-3.084ZM5.102 11.31A6 6 0 0 1 12.657 15H13a4 4 0 0 1 0 8H7a6 6 0 0 1-1.899-11.691ZM19 12a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2h-2a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7G, _path2$v;
function _extends$7H() { _extends$7H = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7H.apply(this, arguments); }
var SvgCloudSunRain = function SvgCloudSunRain(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7H({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7G || (_path$7G = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 1a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0V2a1 1 0 0 1 1-1ZM4.223 4.223a1 1 0 0 1 1.414 0l1.41 1.41a1 1 0 1 1-1.414 1.414l-1.41-1.41a1 1 0 0 1 0-1.414Zm15.554 0a1 1 0 0 1 0 1.414l-1.41 1.41a1 1 0 0 1-1.414-1.414l1.41-1.41a1 1 0 0 1 1.414 0Zm-10.25 3.43a5 5 0 0 1 7.407 5.16 1 1 0 1 1-1.974-.326 3 3 0 0 0-4.443-3.096 1 1 0 0 1-.99-1.738ZM19 12a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2h-2a1 1 0 0 1-1-1ZM7 18a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1Zm4 1a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })), _path2$v || (_path2$v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 1a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0V2a1 1 0 0 1 1-1ZM4.223 4.223a1 1 0 0 1 1.414 0l1.41 1.41a1 1 0 1 1-1.414 1.414l-1.41-1.41a1 1 0 0 1 0-1.414Zm15.554 0a1 1 0 0 1 0 1.414l-1.41 1.41a1 1 0 0 1-1.414-1.414l1.41-1.41a1 1 0 0 1 1.414 0Zm-10.25 3.43a5 5 0 0 1 7.407 5.16 1 1 0 1 1-1.974-.326 3 3 0 0 0-4.443-3.096 1 1 0 0 1-.99-1.738ZM19 12a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2h-2a1 1 0 0 1-1-1ZM7 13a4 4 0 0 0-3.04 6.599 1 1 0 0 1-1.52 1.3 6 6 0 1 1 10.167-6.04.296.296 0 0 0 .087.126c.013.01.021.014.025.015H13a4 4 0 0 1 1.79 7.578 1 1 0 1 1-.896-1.788A2 2 0 0 0 13 17h-.283c-.98 0-1.696-.688-1.979-1.427A4.002 4.002 0 0 0 7 13Zm5.72 2h-.001.002Z",
    clipRule: "evenodd"
  })));
};

var _path$7F;
function _extends$7G() { _extends$7G = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7G.apply(this, arguments); }
var SvgCloudy = function SvgCloudy(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7G({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7F || (_path$7F = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.197 2.01A6.502 6.502 0 0 1 17.501 6H19a4 4 0 0 1 4 4 1 1 0 1 1-2 0 2 2 0 0 0-2-2h-2.207a1 1 0 0 1-.962-.727A4.502 4.502 0 0 0 7.689 6.11a8 8 0 0 1 8.73 4.89H17.5a5.5 5.5 0 0 1 0 11H9A8.001 8.001 0 0 1 5.182 6.971a6.502 6.502 0 0 1 6.015-4.962Zm-1.148 6.082A6 6 0 1 0 9 20h8.5a3.5 3.5 0 1 0 0-7h-1.79a1 1 0 0 1-.958-.714 6 6 0 0 0-4.703-4.194Z",
    clipRule: "evenodd"
  })));
};

var _path$7E;
function _extends$7F() { _extends$7F = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7F.apply(this, arguments); }
var SvgCode = function SvgCode(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7F({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7E || (_path$7E = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.707 16.213a1 1 0 0 1-1.414 1.414l-5-5a1 1 0 0 1 0-1.414L7.294 6.21a1 1 0 1 1 1.414 1.414L4.414 11.92l4.293 4.293Zm6.587 0 4.293-4.293-4.294-4.295a1 1 0 1 1 1.414-1.414l5.001 5.002a1 1 0 0 1 0 1.414l-5 5a1 1 0 0 1-1.414-1.414Zm-4.647 3.57c-.656-.118-.926-.504-.81-1.158l2.43-13.788c.113-.657.5-.927 1.16-.81.662.116.932.502.81 1.158l-2.43 13.785c-.117.66-.503.93-1.16.813Z",
    clipRule: "evenodd"
  })));
};

var _path$7D;
function _extends$7E() { _extends$7E = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7E.apply(this, arguments); }
var SvgConciergeBell = function SvgConciergeBell(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7E({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7D || (_path$7D = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9 4a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2h-1v2.056a9 9 0 0 1 7.96 8.102A2.999 2.999 0 0 1 23 18v2a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1v-2a3 3 0 0 1 2.04-2.842A9 9 0 0 1 11 7.056V5h-1a1 1 0 0 1-1-1ZM5.072 15h13.856a6.999 6.999 0 0 0-13.856 0ZM4 17a1 1 0 0 0-1 1v1h18v-1a1 1 0 0 0-1-1H4Z",
    clipRule: "evenodd"
  })));
};

var _path$7C;
function _extends$7D() { _extends$7D = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7D.apply(this, arguments); }
var SvgContrast = function SvgContrast(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7D({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7C || (_path$7C = /*#__PURE__*/React.createElement("path", {
    d: "M12 20V4c4.411 0 8 3.588 8 8s-3.589 8-8 8Zm0-18C6.486 2 2 6.485 2 12c0 5.514 4.486 10 10 10s10-4.486 10-10c0-5.515-4.486-10-10-10Z"
  })));
};

var _path$7B;
function _extends$7C() { _extends$7C = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7C.apply(this, arguments); }
var SvgContrastActive = function SvgContrastActive(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7C({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7B || (_path$7B = /*#__PURE__*/React.createElement("path", {
    d: "M12 4v16c-4.411 0-8-3.588-8-8s3.589-8 8-8Zm0 18c5.514 0 10-4.485 10-10 0-5.514-4.486-10-10-10S2 6.486 2 12c0 5.515 4.486 10 10 10Z"
  })));
};

var _path$7A;
function _extends$7B() { _extends$7B = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7B.apply(this, arguments); }
var SvgCopyCheck = function SvgCopyCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7B({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7A || (_path$7A = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 3c-.548 0-1 .452-1 1v10c0 .548.452 1 1 1a1 1 0 1 1 0 2c-1.652 0-3-1.348-3-3V4c0-1.652 1.348-3 3-3h10c1.652 0 3 1.348 3 3a1 1 0 1 1-2 0c0-.548-.452-1-1-1H4Zm6 6a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V10a1 1 0 0 0-1-1H10Zm-3 1a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H10a3 3 0 0 1-3-3V10Zm11.707 2.293a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L14 15.586l3.293-3.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$7z, _path2$u;
function _extends$7A() { _extends$7A = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7A.apply(this, arguments); }
var SvgCopyFilled = function SvgCopyFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7A({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7z || (_path$7z = /*#__PURE__*/React.createElement("path", {
    d: "M4 1a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h2v-7a4 4 0 0 1 4-4h7V4a3 3 0 0 0-3-3H4Z"
  })), _path2$u || (_path2$u = /*#__PURE__*/React.createElement("path", {
    d: "M8 20a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2H10a2 2 0 0 0-2 2v10Z"
  })));
};

var _path$7y;
function _extends$7z() { _extends$7z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7z.apply(this, arguments); }
var SvgCopyLeft = function SvgCopyLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7y || (_path$7y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm12.059-2.805a3 3 0 0 0-3.31.817 1 1 0 1 1-1.498-1.325 5 5 0 1 1 0 6.626 1 1 0 0 1 1.498-1.325 3 3 0 1 0 3.31-4.793Z",
    clipRule: "evenodd"
  })));
};

var _path$7x;
function _extends$7y() { _extends$7y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7y.apply(this, arguments); }
var SvgCopyMinus = function SvgCopyMinus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7x || (_path$7x = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 3c-.548 0-1 .452-1 1v10c0 .548.452 1 1 1a1 1 0 1 1 0 2c-1.652 0-3-1.348-3-3V4c0-1.652 1.348-3 3-3h10c1.652 0 3 1.348 3 3a1 1 0 1 1-2 0c0-.548-.452-1-1-1H4Zm6 6a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V10a1 1 0 0 0-1-1H10Zm-3 1a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H10a3 3 0 0 1-3-3V10Zm4 5a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2h-6a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7w;
function _extends$7x() { _extends$7x = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7x.apply(this, arguments); }
var SvgCopyOutline = function SvgCopyOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7x({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7w || (_path$7w = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 3c-.548 0-1 .452-1 1v10c0 .548.452 1 1 1a1 1 0 1 1 0 2c-1.652 0-3-1.348-3-3V4c0-1.652 1.348-3 3-3h10c1.652 0 3 1.348 3 3a1 1 0 1 1-2 0c0-.548-.452-1-1-1H4Zm6 6a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V10a1 1 0 0 0-1-1H10Zm-3 1a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H10a3 3 0 0 1-3-3V10Z",
    clipRule: "evenodd"
  })));
};

var _path$7v;
function _extends$7w() { _extends$7w = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7w.apply(this, arguments); }
var SvgCopyPlus = function SvgCopyPlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7w({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7v || (_path$7v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 3c-.548 0-1 .452-1 1v10c0 .548.452 1 1 1a1 1 0 1 1 0 2c-1.652 0-3-1.348-3-3V4c0-1.652 1.348-3 3-3h10c1.652 0 3 1.348 3 3a1 1 0 1 1-2 0c0-.548-.452-1-1-1H4Zm6 6a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V10a1 1 0 0 0-1-1H10Zm-3 1a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H10a3 3 0 0 1-3-3V10Zm8 1a1 1 0 0 1 1 1v2h2a1 1 0 1 1 0 2h-2v2a1 1 0 1 1-2 0v-2h-2a1 1 0 1 1 0-2h2v-2a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7u;
function _extends$7v() { _extends$7v = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7v.apply(this, arguments); }
var SvgCopyRight = function SvgCopyRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7v({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7u || (_path$7u = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm9.232-4.677a5 5 0 0 1 5.518 1.37 1 1 0 1 1-1.5 1.322 3 3 0 1 0 0 3.97 1 1 0 1 1 1.5 1.322 5 5 0 1 1-5.518-7.984Z",
    clipRule: "evenodd"
  })));
};

var _path$7t;
function _extends$7u() { _extends$7u = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7u.apply(this, arguments); }
var SvgCopySlash = function SvgCopySlash(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7u({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7t || (_path$7t = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 3c-.548 0-1 .452-1 1v10c0 .548.452 1 1 1a1 1 0 1 1 0 2c-1.652 0-3-1.348-3-3V4c0-1.652 1.348-3 3-3h10c1.652 0 3 1.348 3 3a1 1 0 1 1-2 0c0-.548-.452-1-1-1H4Zm6 6a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V10a1 1 0 0 0-1-1H10Zm-3 1a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H10a3 3 0 0 1-3-3V10Zm11.707 1.293a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1-1.414-1.414l6-6a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$7s;
function _extends$7t() { _extends$7t = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7t.apply(this, arguments); }
var SvgCopyX = function SvgCopyX(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7t({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7s || (_path$7s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 3c-.548 0-1 .452-1 1v10c0 .548.452 1 1 1a1 1 0 1 1 0 2c-1.652 0-3-1.348-3-3V4c0-1.652 1.348-3 3-3h10c1.652 0 3 1.348 3 3a1 1 0 1 1-2 0c0-.548-.452-1-1-1H4Zm6 6a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V10a1 1 0 0 0-1-1H10Zm-3 1a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H10a3 3 0 0 1-3-3V10Zm4.293 1.293a1 1 0 0 1 1.414 0L15 13.586l2.293-2.293a1 1 0 0 1 1.414 1.414L16.414 15l2.293 2.293a1 1 0 0 1-1.414 1.414L15 16.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L13.586 15l-2.293-2.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$7r;
function _extends$7s() { _extends$7s = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7s.apply(this, arguments); }
var SvgCreditCardFilled = function SvgCreditCardFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7s({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7r || (_path$7r = /*#__PURE__*/React.createElement("path", {
    d: "M1 7a3 3 0 0 1 3-3h16a3 3 0 0 1 3 3v2H1V7Zm0 4v6a3 3 0 0 0 3 3h16a3 3 0 0 0 3-3v-6H1Z"
  })));
};

var _path$7q;
function _extends$7r() { _extends$7r = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7r.apply(this, arguments); }
var SvgCreditCardOutline = function SvgCreditCardOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7r({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7q || (_path$7q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 6a1 1 0 0 0-1 1v2h18V7a1 1 0 0 0-1-1H4Zm19 1a3 3 0 0 0-3-3H4a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h16a3 3 0 0 0 3-3V7Zm-2 4H3v6a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-6Z",
    clipRule: "evenodd"
  })));
};

var _path$7p;
function _extends$7q() { _extends$7q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7q.apply(this, arguments); }
var SvgCrossFilled = function SvgCrossFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7p || (_path$7p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10 7H6c-1.267.001-1.901.668-1.901 2.001 0 1.333.634 1.999 1.9 1.999H10v9c.003 1.2.67 1.8 2.002 1.802C13.334 21.804 14 21.202 14 20v-9h4c1.267-.05 1.901-.716 1.901-1.999 0-1.283-.634-1.95-1.901-2.001h-4V4c0-1.333-.666-2-1.998-2-1.332 0-2 .667-2.002 2v3Z",
    clipRule: "evenodd"
  })));
};

var _path$7o;
function _extends$7p() { _extends$7p = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7p.apply(this, arguments); }
var SvgCrossOutline = function SvgCrossOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7p({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7o || (_path$7o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9 4c0-2.191 1.623-2 2.97-2h.032C13.433 2 15 1.809 15 4v2h3c2.155 0 1.901 2 1.901 3.001S20.155 12 18 12h-3v8c0 2.154-2.343 1.797-2.998 1.802C11.347 21.807 9 22.15 9 20v-8H6c-2.112 0-1.9-1.994-1.9-2.999l-.002-.097C4.085 7.874 3.906 6 6 6h3V4Zm4 0h-1.999L11 8H6v2h5v10h2V10h5V8h-5V4Z",
    clipRule: "evenodd"
  })));
};

var _path$7n;
function _extends$7o() { _extends$7o = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7o.apply(this, arguments); }
var SvgDataFilled = function SvgDataFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7o({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7n || (_path$7n = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M22 14H2v-4h20v4Zm0 2v3.92c0 1.387-.667 2.08-2 2.08H4c-1.333 0-2-.693-2-2.08V16h20Zm0-8H2V4c0-1.333.667-2 2-2h15.99C21.33 2 22 2.668 22 4v4ZM6 7a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm0 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm0 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z",
    clipRule: "evenodd"
  })));
};

var _path$7m;
function _extends$7n() { _extends$7n = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7n.apply(this, arguments); }
var SvgDataOutline = function SvgDataOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7n({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7m || (_path$7m = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20 14v-3.999H5.6L4 10v4h16Zm0-6V4.001H5.6L4 4v4h16ZM4 2h15.99C21.33 2 22 2.668 22 4v15.92c0 1.387-.667 2.08-2 2.08H4c-1.333 0-2-.693-2-2.08V4c0-1.333.667-2 2-2Zm1.6 14H4v4h16v-3.999H5.6V16ZM6 7a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm0 6a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm0 6a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z",
    clipRule: "evenodd"
  })));
};

var _path$7l;
function _extends$7m() { _extends$7m = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7m.apply(this, arguments); }
var SvgDatabase = function SvgDatabase(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7m({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7l || (_path$7l = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 5c0 .001 0 .006.003.015a.296.296 0 0 0 .032.066c.043.07.13.174.291.301.33.26.87.539 1.626.79C7.454 6.674 9.592 7 12 7s4.546-.327 6.048-.827c.756-.252 1.296-.53 1.626-.79.162-.128.248-.232.29-.302a.293.293 0 0 0 .033-.066A.052.052 0 0 0 20 5l-.003-.015a.293.293 0 0 0-.032-.066 1.235 1.235 0 0 0-.291-.301c-.33-.26-.87-.539-1.626-.79C16.546 3.326 14.408 3 12 3s-4.546.327-6.048.827c-.756.252-1.296.53-1.626.79a1.236 1.236 0 0 0-.29.302.296.296 0 0 0-.033.066L4 5Zm16 2.527a9.536 9.536 0 0 1-1.32.543C16.925 8.655 14.563 9 12 9c-2.563 0-4.925-.345-6.68-.93A9.536 9.536 0 0 1 4 7.527v4.475l.002.009a.3.3 0 0 0 .03.063 1.2 1.2 0 0 0 .28.295c.321.258.858.543 1.64.804C7.51 13.692 9.686 14 12 14s4.49-.308 6.048-.827c.782-.261 1.319-.546 1.64-.804.158-.127.24-.229.28-.295a.284.284 0 0 0 .031-.07v-.002L20 12V7.527ZM22 5c0-.852-.519-1.504-1.088-1.953-.581-.458-1.36-.826-2.232-1.117C16.925 1.345 14.563 1 12 1c-2.563 0-4.925.345-6.68.93-.873.29-1.651.66-2.232 1.117C2.518 3.496 2 4.148 2 5v14c0 .825.492 1.472 1.058 1.927.575.463 1.356.841 2.262 1.143 1.818.606 4.22.93 6.68.93s4.862-.324 6.68-.93c.906-.302 1.687-.68 2.262-1.143C21.508 20.472 22 19.825 22 19V5Zm-2 9.526a9.475 9.475 0 0 1-1.32.544c-1.818.606-4.22.93-6.68.93s-4.862-.324-6.68-.93A9.476 9.476 0 0 1 4 14.526v4.476l.002.009a.3.3 0 0 0 .03.063 1.2 1.2 0 0 0 .28.295c.321.258.858.543 1.64.804C7.51 20.692 9.686 21 12 21s4.49-.308 6.048-.827c.782-.261 1.319-.546 1.64-.804.158-.127.24-.229.28-.295a.284.284 0 0 0 .032-.072v-4.476Z",
    clipRule: "evenodd"
  })));
};

var _path$7k;
function _extends$7l() { _extends$7l = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7l.apply(this, arguments); }
var SvgDatabaseBackup = function SvgDatabaseBackup(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7l({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7k || (_path$7k = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 5c0 .001 0 .006.003.015a.296.296 0 0 0 .032.066c.043.07.13.174.291.301.33.26.87.539 1.626.79C7.454 6.674 9.592 7 12 7s4.546-.327 6.048-.827c.756-.252 1.296-.53 1.626-.79.162-.128.248-.232.29-.302a.293.293 0 0 0 .033-.066A.052.052 0 0 0 20 5l-.003-.015a.293.293 0 0 0-.032-.066 1.235 1.235 0 0 0-.291-.301c-.33-.26-.87-.539-1.626-.79C16.546 3.326 14.408 3 12 3s-4.546.327-6.048.827c-.756.252-1.296.53-1.626.79a1.236 1.236 0 0 0-.29.302.296.296 0 0 0-.033.066L4 5Zm16 2.527V9a1 1 0 1 0 2 0V5c0-.852-.519-1.504-1.088-1.953-.581-.458-1.36-.826-2.232-1.117C16.925 1.345 14.563 1 12 1c-2.563 0-4.925.345-6.68.93-.873.29-1.651.66-2.232 1.117C2.518 3.496 2 4.148 2 5v14c0 .729.391 1.318.846 1.745.459.432 1.073.791 1.759 1.086 1.379.593 3.244 1.01 5.32 1.166a1 1 0 0 0 .15-1.994c-1.924-.145-3.559-.527-4.68-1.01-.564-.242-.95-.489-1.178-.704-.19-.179-.215-.277-.217-.292v-4.294c.996.58 2.32 1.03 3.836 1.283a1 1 0 1 0 .328-1.972c-1.408-.235-2.527-.65-3.266-1.104C4.09 12.414 4 12.05 4 12V7.527a9.59 9.59 0 0 0 1.32.543C7.075 8.655 9.437 9 12 9c2.563 0 4.925-.345 6.68-.93A9.536 9.536 0 0 0 20 7.527ZM12 11a1 1 0 0 1 1 1v1.586l.383-.383A5.814 5.814 0 0 1 17.5 11.5 5.5 5.5 0 0 1 23 17a6 6 0 0 1-10.8 3.6 1 1 0 0 1 1.6-1.2A4 4 0 0 0 21 17a3.5 3.5 0 0 0-3.5-3.5 3.815 3.815 0 0 0-2.703 1.117l-.383.383H16a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7j;
function _extends$7k() { _extends$7k = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7k.apply(this, arguments); }
var SvgDeaf = function SvgDeaf(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7k({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7j || (_path$7j = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.003 2c2.566 0 4.587 1.078 5.955 2.637l1.914-1.915.364-.364c.465-.46.934-.46 1.405 0 .472.46.475.93.009 1.41l-2.553 2.551A8.16 8.16 0 0 1 20.003 10c0 3.12-1.712 4.188-1.835 5.186-.165 1.334-.42 3.105-.907 3.862C16.32 20.514 14.511 22 12.002 22c0 0-1.012.088-1.012-.979 0-1.066 1.012-1.021 1.013-1.021 1.295 0 2.57-.571 3.708-2.252.635-.94.262-2.152.615-3.538.29-1.135 1.676-2.254 1.676-4.21 0-2.818-2.195-6-6-6-3.804 0-6 3.182-6 6 0 0 0 1-1 1s-1-1-1-1c0-4 2.937-8 8-8ZM9.177 7.176a4 4 0 0 1 5.657 5.657s-.71.71-1.416.003c-.636-.636-.126-1.276-.018-1.397l.02-.02c.683-.68.897-1.931 0-2.83-.899-.897-2.147-.681-2.83 0 0 0-.709.71-1.417.003-.708-.709.004-1.416.004-1.416Zm1.058 5.178 1.415 1.414-7.88 7.878c-.455.477-.924.474-1.406-.008-.483-.482-.485-.951-.008-1.406l7.88-7.878Z",
    clipRule: "evenodd"
  })));
};

var _path$7i;
function _extends$7j() { _extends$7j = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7j.apply(this, arguments); }
var SvgDecreaseFont = function SvgDecreaseFont(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7j({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7i || (_path$7i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.112 15H4.913c-.32 1.35-.592 2.509-.734 3.116-.068.289-.43 1.05-1.388.852-.639-.132-.89-.554-.756-1.266l2.89-10.538C5.259 5.721 5.955 5 7.013 5c1.059 0 1.755.721 2.088 2.164l2.879 10.541c.098.743-.167 1.164-.796 1.263-.943.147-1.256-.563-1.325-.852A785.69 785.69 0 0 0 9.112 15Zm-.488-2c-.727-2.962-1.5-6-1.61-6-.137 0-.904 3.038-1.621 6h3.231ZM15 11h6c.667.002 1 .336 1 1.002 0 .665-.333.998-1 .998h-6c-.667 0-1-.333-1-1s.333-1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7h;
function _extends$7i() { _extends$7i = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7i.apply(this, arguments); }
var SvgDelete = function SvgDelete(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7i({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7h || (_path$7h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.293 4.293A1 1 0 0 1 9 4h11a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H9a1 1 0 0 1-.707-.293l-7-7a1 1 0 0 1 0-1.414l7-7ZM9.414 6l-6 6 6 6H20a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1H9.414Zm1.879 2.293a1 1 0 0 1 1.414 0L15 10.586l2.293-2.293a1 1 0 1 1 1.414 1.414L16.414 12l2.293 2.293a1 1 0 0 1-1.414 1.414L15 13.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L13.586 12l-2.293-2.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$7g;
function _extends$7h() { _extends$7h = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7h.apply(this, arguments); }
var SvgDesktopFilled = function SvgDesktopFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7h({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7g || (_path$7g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9 18.007H4.014C2.671 18.007 2 17.342 2 16.01v-1.995h20v-1.997H2V4.033c0-1.322.667-1.987 2-1.996 1.25-.01 6.596-.077 16.04-.002 1.307.01 1.96.676 1.96 1.998V16.01c0 1.33-.667 1.996-2 1.996h-5l1 1.997h1c.667 0 1 .332 1 .998 0 .665-.333.998-1 .998H7c-.667 0-1-.333-1-.998 0-.666.333-.998 1-.998h1l1-1.997v.001Z",
    clipRule: "evenodd"
  })));
};

var _path$7f;
function _extends$7g() { _extends$7g = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7g.apply(this, arguments); }
var SvgDesktopOutline = function SvgDesktopOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7g({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7f || (_path$7f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "m15 18.007 1 1.997h1c.667 0 1 .332 1 .998 0 .665-.333.998-1 .998H7c-.667 0-1-.333-1-.998 0-.666.333-.998 1-.998h1l1-1.997H4.014C2.671 18.007 2 17.342 2 16.01V4.033c0-1.322.667-1.987 2-1.996 1.25-.01 6.596-.077 16.04-.002 1.307.01 1.96.676 1.96 1.998V16.01c0 1.33-.667 1.996-2 1.996h-5v.001ZM4 4.033v7.985h16V4.033H4Zm0 9.982v1.996h16v-1.996H4Zm7 3.992-1 1.997h4l-1-1.997h-2Z",
    clipRule: "evenodd"
  })));
};

var _path$7e;
function _extends$7f() { _extends$7f = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7f.apply(this, arguments); }
var SvgDogLeashed = function SvgDogLeashed(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7f({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7e || (_path$7e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 8c.667 0 1 .334 1 1.003L5 9h8v6H8v5c0 1.333-.666 2-1.999 2-1.332 0-2-.667-2-2v-8.997c-1.334 0-2.001-.667-2.001-2C2 9 2.001 8 3 8Zm11 .987 6 4.662V20c0 1.333-.667 2-2 2s-2-.667-2-2v-5h-2V8.987ZM15 2c1 0 .459 2 1 2h1.993c.507 0 .675.05.852.2.177.15.226.348.37.514.11.127.372.222.785.286h1.004C22 5 22 5.76 22 7c0 1-2 1-2 2.321v3.13l-6-4.682V3c0-.666.333-.999 1-.999Zm-9.7.076 3.257.033 4.404 3.415.018 2.497L5.3 2.076Z",
    clipRule: "evenodd"
  })));
};

var _path$7d;
function _extends$7e() { _extends$7e = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7e.apply(this, arguments); }
var SvgDollarSign = function SvgDollarSign(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7e({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7d || (_path$7d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 1a1 1 0 0 1 1 1v2h4a1 1 0 1 1 0 2h-4v5h1.5a4.5 4.5 0 1 1 0 9H13v2a1 1 0 1 1-2 0v-2H6a1 1 0 1 1 0-2h5v-5H9.5a4.5 4.5 0 1 1 0-9H11V2a1 1 0 0 1 1-1Zm-1 5H9.5a2.5 2.5 0 1 0 0 5H11V6Zm2 7v5h1.5a2.5 2.5 0 0 0 0-5H13Z",
    clipRule: "evenodd"
  })));
};

var _path$7c;
function _extends$7d() { _extends$7d = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7d.apply(this, arguments); }
var SvgDots = function SvgDots(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7d({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7c || (_path$7c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10 5a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm0 7a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm0 7a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z",
    clipRule: "evenodd"
  })));
};

var _path$7b;
function _extends$7c() { _extends$7c = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7c.apply(this, arguments); }
var SvgDownload = function SvgDownload(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7c({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7b || (_path$7b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2a1 1 0 0 1 1 1v9.586l3.293-3.293a1 1 0 1 1 1.414 1.414l-5 5a1 1 0 0 1-1.414 0l-5-5a1 1 0 0 1 1.414-1.414L11 12.586V3a1 1 0 0 1 1-1ZM3 14a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4a1 1 0 1 1 2 0v4a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3v-4a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7a;
function _extends$7b() { _extends$7b = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7b.apply(this, arguments); }
var SvgDownloadCloud = function SvgDownloadCloud(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7b({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7a || (_path$7a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.195 4.418a6 6 0 0 0-6.48 9.782 1 1 0 0 1-1.43 1.399A8 8 0 1 1 16.418 7H17.5a5.5 5.5 0 0 1 3.055 10.074 1 1 0 0 1-1.11-1.664A3.5 3.5 0 0 0 17.5 9h-1.79a1 1 0 0 1-.958-.714 5.999 5.999 0 0 0-3.557-3.868ZM12 11a1 1 0 0 1 1 1v6.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L11 18.586V12a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$79;
function _extends$7a() { _extends$7a = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7a.apply(this, arguments); }
var SvgDragDrop = function SvgDragDrop(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7a({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$79 || (_path$79 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 5a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm6 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm-6 7a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm6 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm-6 7a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm6 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z",
    clipRule: "evenodd"
  })));
};

var _path$78;
function _extends$79() { _extends$79 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$79.apply(this, arguments); }
var SvgDribbble = function SvgDribbble(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$79({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$78 || (_path$78 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 11.891V12c0 2.232.813 4.274 2.158 5.847 2.276-2.654 4.91-4.557 7.864-5.568a32.425 32.425 0 0 0-.851-1.897c-2.652.824-5.681 1.257-9.17 1.51Zm.254-2.024c3.09-.24 5.707-.627 7.963-1.286-.842-1.482-1.84-2.986-3.081-4.712a9.02 9.02 0 0 0-4.882 5.998Zm6.86-6.67c1.193 1.683 2.178 3.196 3.025 4.72 1.744-.706 3.25-1.634 4.59-2.858A8.963 8.963 0 0 0 12 3c-.647 0-1.278.068-1.886.198Zm9.007 3.299C17.617 7.88 15.943 8.918 14.063 9.7a33.392 33.392 0 0 1 .904 2.053c1.894-.382 3.906-.416 6.027-.072a8.957 8.957 0 0 0-1.873-5.185Zm1.722 7.187c-1.815-.312-3.539-.316-5.168-.028.567 1.649 1.088 3.476 1.622 5.622a9.001 9.001 0 0 0 3.546-5.594ZM15.489 20.3c-.591-2.423-1.15-4.403-1.759-6.148-2.649.891-5.022 2.609-7.11 5.064A8.96 8.96 0 0 0 12 21c1.237 0 2.416-.25 3.49-.701ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Z",
    clipRule: "evenodd"
  })));
};

var _path$77;
function _extends$78() { _extends$78 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$78.apply(this, arguments); }
var SvgDropFilled = function SvgDropFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$78({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$77 || (_path$77 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.419 3.297a.722.722 0 0 1 .95-.195c.08.049.16.122.212.195.37.514.766 1.076 1.215 1.688C15.883 7.82 19 12.075 19 14.52c0 1.785-.793 3.4-2.06 4.573A7.257 7.257 0 0 1 12 21a7.355 7.355 0 0 1-4.94-1.883C5.792 17.944 5 16.33 5 14.545c0-2.445 3.117-6.724 5.204-9.56.475-.636.871-1.174 1.215-1.688ZM15.999 14c-.998 0-.998 1-.998 1A2.004 2.004 0 0 1 13 17s-1 .005-1 1.002S13 19 13 19a4 4 0 0 0 4-4s-.001-1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$76;
function _extends$77() { _extends$77 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$77.apply(this, arguments); }
var SvgDropOutline = function SvgDropOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$77({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$76 || (_path$76 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.419 3.297a.722.722 0 0 1 .95-.195c.08.049.16.122.212.195.37.514.766 1.076 1.215 1.688C15.883 7.82 19 12.075 19 14.52c0 1.785-.793 3.4-2.06 4.573A7.257 7.257 0 0 1 12 21a7.355 7.355 0 0 1-4.94-1.883C5.792 17.944 5 16.33 5 14.545c0-2.445 3.117-6.724 5.204-9.56.475-.636.871-1.174 1.215-1.688Zm.583 2.622-.187.25C8.608 10.53 7 13.204 7 14.546c0 1.16.508 2.261 1.404 3.09A5.355 5.355 0 0 0 12 19c1.366 0 2.64-.503 3.581-1.375.911-.843 1.419-1.944 1.419-3.105 0-1.337-1.574-3.945-4.815-8.35a59.009 59.009 0 0 1-.183-.251ZM15 13c.999 0 1 1 1 1a4 4 0 0 1-4 4s-1 0-1-.997S12 16 12 16c1.124 0 2-.92 2-2.001 0 0 0-1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$75;
function _extends$76() { _extends$76 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$76.apply(this, arguments); }
var SvgDroplet = function SvgDroplet(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$76({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$75 || (_path$75 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2a1 1 0 0 1 .98.804c.453 2.26 1.82 4.456 3.645 5.915C18.805 10.463 20 12.63 20 15a8 8 0 0 1-16 0c0-2.37 1.196-4.537 3.375-6.28C9.2 7.26 10.567 5.063 11.02 2.803A1 1 0 0 1 12 2Zm0 4.053a12.558 12.558 0 0 1-3.375 4.228C6.805 11.737 6 13.37 6 15a6 6 0 1 0 12 0c0-1.63-.804-3.263-2.625-4.72A12.558 12.558 0 0 1 12 6.054Z",
    clipRule: "evenodd"
  })));
};

var _path$74;
function _extends$75() { _extends$75 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$75.apply(this, arguments); }
var SvgDroplets = function SvgDroplets(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$75({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$74 || (_path$74 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14.004 2.02a1 1 0 0 1 .976.804c.452 2.26 1.82 4.456 3.645 5.915 2.178 1.743 3.374 3.909 3.375 6.279v.002h-1l1-.004v.002a7.98 7.98 0 0 1-13.616 5.68 1 1 0 1 1 1.412-1.416A5.98 5.98 0 0 0 20 15.024v-.004c0-1.63-.805-3.263-2.625-4.72a12.56 12.56 0 0 1-3.377-4.23c-.178.363-.375.717-.59 1.06a1 1 0 1 1-1.696-1.06 9.972 9.972 0 0 0 1.309-3.254 1 1 0 0 1 .983-.796ZM7 4.3a1 1 0 0 1 .98.804c.243 1.212.973 2.39 1.942 3.181C11.241 9.361 12 10.726 12 12.25c0 2.76-2.235 5.05-5 5.05s-5-2.29-5-5.05c0-1.506.742-2.896 2.085-3.97A5.586 5.586 0 0 0 6.02 5.103 1 1 0 0 1 7 4.3Zm.003 3.645a7.466 7.466 0 0 1-1.668 1.896C4.378 10.606 4 11.456 4 12.25c0 1.68 1.365 3.05 3 3.05s3-1.37 3-3.05c0-.796-.38-1.63-1.342-2.415a7.7 7.7 0 0 1-1.655-1.89Z",
    clipRule: "evenodd"
  })));
};

var _path$73;
function _extends$74() { _extends$74 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$74.apply(this, arguments); }
var SvgDumbbellFilled = function SvgDumbbellFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$74({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$73 || (_path$73 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 16a3.998 3.998 0 0 0 3.465-2h5.077c.198.388 1.263 2 3.458 2 2.427 0 4-2 4-4 0-2.44-2.026-4-4-4-1.522 0-2.938.934-3.458 2.007 0 0-5.073-.007-5.073 0A4 4 0 1 0 6 16Z",
    clipRule: "evenodd"
  })));
};

var _path$72;
function _extends$73() { _extends$73 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$73.apply(this, arguments); }
var SvgDumbbellOutline = function SvgDumbbellOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$73({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$72 || (_path$72 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18 8c1.974 0 4 1.56 4 4 0 2-1.573 4-4 4-1.625 0-2.63-.884-3.124-1.5H9.123a4 4 0 1 1 .005-4.994l1.485-.001h4.245C15.52 8.658 16.722 8 18 8Zm0 2c-1.267 0-1.874 1.085-1.936 1.502H7.938a2 2 0 1 0 0 .997l8.113.001h.013c.065.374.608 1.5 1.936 1.5 1.147 0 2-.922 2-2 0-1.078-.854-2-2-2Z",
    clipRule: "evenodd"
  })));
};

var _path$71, _path2$t;
function _extends$72() { _extends$72 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$72.apply(this, arguments); }
var SvgEmailFilled = function SvgEmailFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$72({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$71 || (_path$71 = /*#__PURE__*/React.createElement("path", {
    d: "M1.116 5.172A3.001 3.001 0 0 1 4 3h16a3 3 0 0 1 2.7 1.69l-10.22 7.174a.94.94 0 0 1-.975-.009L1.115 5.172Z"
  })), _path2$t || (_path2$t = /*#__PURE__*/React.createElement("path", {
    d: "M1 7.476V18a3 3 0 0 0 3 3h16a3 3 0 0 0 3-3V6.924l-9.396 6.595a2.94 2.94 0 0 1-3.165.029l-.01-.007L1 7.476Z"
  })));
};

var _path$70;
function _extends$71() { _extends$71 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$71.apply(this, arguments); }
var SvgEmailOutline = function SvgEmailOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$71({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$70 || (_path$70 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 5a1 1 0 0 0-1 1v.45l8.503 5.404a.94.94 0 0 0 .996-.001L21 6.45V6a1 1 0 0 0-1-1H4Zm19 1.983V6a3 3 0 0 0-3-3H4a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h16a3 3 0 0 0 3-3V6.983ZM21 8.82l-7.439 4.727a2.94 2.94 0 0 1-3.122 0l-.005-.003L3 8.82V18a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V8.82Z",
    clipRule: "evenodd"
  })));
};

var _path$6$;
function _extends$70() { _extends$70 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$70.apply(this, arguments); }
var SvgEuro = function SvgEuro(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$70({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6$ || (_path$6$ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13.797 3a8.7 8.7 0 0 1 5.875 2.26 1 1 0 0 1-1.344 1.48A6.7 6.7 0 0 0 13.808 5a6.9 6.9 0 0 0-6.17 4H16a1 1 0 1 1 0 2H7.058a6.901 6.901 0 0 0-.058.987V12c0 .34.024.674.07 1H13a1 1 0 1 1 0 2H7.659c1.098 2.367 3.45 4 6.141 4 1.725 0 3.301-.69 4.55-1.76a1 1 0 0 1 1.3 1.52C18.1 20.09 16.076 21 13.8 21c-3.838 0-7.086-2.523-8.295-6H4a1 1 0 1 1 0-2h1.054A9.208 9.208 0 0 1 5 12.006 8.902 8.902 0 0 1 5.045 11H4a1 1 0 1 1 0-2h1.485a8.91 8.91 0 0 1 2.042-3.314A8.9 8.9 0 0 1 13.787 3h.01Z",
    clipRule: "evenodd"
  })));
};

var _path$6_;
function _extends$6$() { _extends$6$ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6$.apply(this, arguments); }
var SvgExchange = function SvgExchange(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6$({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6_ || (_path$6_ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.707 2.293a1 1 0 0 1 0 1.414L6.414 6H20a1 1 0 1 1 0 2H6.414l2.293 2.293a1 1 0 1 1-1.414 1.414l-4-4a1 1 0 0 1 0-1.414l4-4a1 1 0 0 1 1.414 0Zm6.586 10a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414-1.414L17.586 18H4a1 1 0 1 1 0-2h13.586l-2.293-2.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$6Z;
function _extends$6_() { _extends$6_ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6_.apply(this, arguments); }
var SvgExclamationDefault = function SvgExclamationDefault(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6_({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6Z || (_path$6Z = /*#__PURE__*/React.createElement("path", {
    d: "M12.5 14V5c0-.667.333-1 1-1s1 .333 1 1v9c0 .666-.332 1-.997 1s-1-.333-1.003-1Zm1 3a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z"
  })));
};

var _path$6Y;
function _extends$6Z() { _extends$6Z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6Z.apply(this, arguments); }
var SvgExclamationTriangleFilled = function SvgExclamationTriangleFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6Z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6Y || (_path$6Y = /*#__PURE__*/React.createElement("path", {
    d: "m12.922 2.622 8.911 17.354c.453.88-.068 2.024-.922 2.024H3.09c-.854 0-1.375-1.144-.922-2.024l8.911-17.354c.426-.83 1.418-.83 1.844 0h-.001ZM12 20a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm-1-5.875c0 .583.334.875 1 .875.667 0 1-.292 1-.875v-5.25C13 8.29 12.667 8 12 8c-.666 0-1 .291-1 .875v5.25Z"
  })));
};

var _path$6X;
function _extends$6Y() { _extends$6Y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6Y.apply(this, arguments); }
var SvgExclamationTriangleOutline = function SvgExclamationTriangleOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6Y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6X || (_path$6X = /*#__PURE__*/React.createElement("path", {
    d: "M12.922 2.622c-.426-.83-1.418-.83-1.844 0l-8.91 17.354c-.454.88.067 2.024.921 2.024h17.822c.854 0 1.375-1.144.922-2.024l-8.91-17.354h-.001Zm-.915 2.88L19.5 20H4.502l7.505-14.498ZM10.5 17.5a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0ZM11 14c0 .666.334 1 1 1 .667 0 1-.334 1-1v-4c0-.667-.333-1-1-1-.666 0-1 .333-1 1v4Z"
  })));
};

var _path$6W;
function _extends$6X() { _extends$6X = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6X.apply(this, arguments); }
var SvgExpand = function SvgExpand(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6X({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6W || (_path$6W = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15 4a1 1 0 1 1 0-2h6a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0V5.414l-5.293 5.293a1 1 0 0 1-1.414-1.414L18.586 4H15Zm-5.707 9.293a1 1 0 0 1 1.414 1.414L5.414 20H9a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1v-6a1 1 0 1 1 2 0v3.586l5.293-5.293Z",
    clipRule: "evenodd"
  })));
};

var _path$6V;
function _extends$6W() { _extends$6W = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6W.apply(this, arguments); }
var SvgExternalLink = function SvgExternalLink(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6W({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6V || (_path$6V = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15 4a1 1 0 1 1 0-2h6a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0V5.414l-9.293 9.293a1 1 0 0 1-1.414-1.414L18.586 4H15ZM5 7a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-6a1 1 0 1 1 2 0v6a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3h6a1 1 0 1 1 0 2H5Z",
    clipRule: "evenodd"
  })));
};

var _path$6U;
function _extends$6V() { _extends$6V = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6V.apply(this, arguments); }
var SvgEyeFilled = function SvgEyeFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6V({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6U || (_path$6U = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "m22.917 12.398.002-.003a1.004 1.004 0 0 0 0-.79l-.002-.003-.003-.007-.009-.021a9.013 9.013 0 0 0-.153-.322c-.105-.21-.262-.506-.47-.857a15.384 15.384 0 0 0-1.924-2.575C18.615 5.943 15.868 4 12 4 8.132 4 5.385 5.943 3.642 7.82a15.386 15.386 0 0 0-1.923 2.575 13.438 13.438 0 0 0-.591 1.107l-.033.072-.01.02-.002.008-.001.002v.001a1.003 1.003 0 0 0-.001.789v.002l.002.002.003.007.01.021.032.072a13.438 13.438 0 0 0 .591 1.107 15.387 15.387 0 0 0 1.923 2.575C5.385 18.057 8.132 20 12 20c3.868 0 6.615-1.943 8.358-3.82a15.385 15.385 0 0 0 1.923-2.575 13.415 13.415 0 0 0 .591-1.107l.033-.072.01-.02.002-.008ZM10 12a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm2-4a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z",
    clipRule: "evenodd"
  })));
};

var _path$6T;
function _extends$6U() { _extends$6U = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6U.apply(this, arguments); }
var SvgEyeHiddenFilled = function SvgEyeHiddenFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6U({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6T || (_path$6T = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.707 1.293a1 1 0 0 0-1.414 1.414L5.64 6.054A13.17 13.17 0 0 0 3.642 7.82a15.385 15.385 0 0 0-1.923 2.575 13.443 13.443 0 0 0-.592 1.107l-.032.072-.01.02-.002.008-.002.002v.001a1.003 1.003 0 0 0 0 .789v.002l.002.002.003.007.009.021.032.072a13.443 13.443 0 0 0 .591 1.107 15.386 15.386 0 0 0 1.924 2.575C5.385 18.057 8.132 20 12 20a10.72 10.72 0 0 0 5.865-1.72l4.428 4.427a1 1 0 0 0 1.414-1.414l-20-20Zm10.907 13.735-1.422-1.422a2 2 0 0 1-2.798-2.798L8.972 9.386a4 4 0 0 0 5.642 5.642Zm7.667-1.423a15.463 15.463 0 0 1-1.672 2.297L9.096 4.39C9.983 4.143 10.95 4 12 4c3.868 0 6.615 1.943 8.358 3.82a15.385 15.385 0 0 1 1.923 2.575 13.466 13.466 0 0 1 .591 1.107l.033.072.01.02.002.008.002.003c.107.252.107.538 0 .79l-.002.003-.003.007-.01.021a9.013 9.013 0 0 1-.153.322c-.105.21-.26.506-.47.857Z",
    clipRule: "evenodd"
  })));
};

var _path$6S;
function _extends$6T() { _extends$6T = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6T.apply(this, arguments); }
var SvgEyeHiddenOutline = function SvgEyeHiddenOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6T({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6S || (_path$6S = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.707 1.293a1 1 0 0 0-1.414 1.414L5.64 6.054A13.17 13.17 0 0 0 3.642 7.82a15.385 15.385 0 0 0-1.923 2.575 13.443 13.443 0 0 0-.592 1.107l-.032.072-.01.02-.002.008-.002.002v.001a1.002 1.002 0 0 0 0 .789v.002l.002.002.003.007.009.021.032.072a13.443 13.443 0 0 0 .591 1.107 15.386 15.386 0 0 0 1.924 2.575C5.385 18.057 8.132 20 12 20a10.72 10.72 0 0 0 5.865-1.72l4.428 4.427a1 1 0 0 0 1.414-1.414l-20-20ZM16.41 16.825l-1.796-1.797a4 4 0 0 1-5.642-5.642L7.078 7.492a10.976 10.976 0 0 0-1.97 1.688A13.389 13.389 0 0 0 3.112 12a13.388 13.388 0 0 0 1.996 2.82C6.615 16.442 8.868 18 12 18a8.718 8.718 0 0 0 4.41-1.175Zm-6.016-6.017a2 2 0 0 0 2.798 2.798l-2.798-2.798Zm-9.313.797L2 12l-.919-.395Zm19.481.977c-.204.344-.468.751-.794 1.186l1.427 1.427c.456-.586.816-1.137 1.086-1.59a13.466 13.466 0 0 0 .591-1.107l.033-.072.01-.02.002-.008.002-.003a1.004 1.004 0 0 0 0-.79l-.002-.003-.003-.007-.01-.021a9.013 9.013 0 0 0-.153-.322c-.105-.21-.26-.506-.47-.857a15.385 15.385 0 0 0-1.923-2.575C18.615 5.943 15.868 4 12 4c-.647 0-1.262.054-1.847.154L12 6c3.132 0 5.385 1.557 6.892 3.18A13.386 13.386 0 0 1 20.888 12c-.082.157-.19.355-.326.582Zm2.357-.977-.92.395.92-.395Zm-.92.395.92.395-.92-.395Z",
    clipRule: "evenodd"
  })));
};

var _path$6R;
function _extends$6S() { _extends$6S = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6S.apply(this, arguments); }
var SvgEyeOutline = function SvgEyeOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6S({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6R || (_path$6R = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.112 12a13.388 13.388 0 0 0 1.996 2.82C6.615 16.442 8.868 18 12 18c3.132 0 5.385-1.557 6.892-3.18A13.39 13.39 0 0 0 20.888 12a13.39 13.39 0 0 0-1.996-2.82C17.385 7.557 15.132 6 12 6 8.868 6 6.615 7.557 5.108 9.18A13.388 13.388 0 0 0 3.112 12ZM22 12c.92-.394.919-.394.919-.395l-.002-.003-.003-.007-.009-.021a9.013 9.013 0 0 0-.153-.322c-.105-.21-.262-.506-.47-.857a15.384 15.384 0 0 0-1.924-2.575C18.615 5.943 15.868 4 12 4 8.132 4 5.385 5.943 3.642 7.82a15.386 15.386 0 0 0-1.923 2.575 13.438 13.438 0 0 0-.591 1.107l-.033.072-.01.02-.002.008-.001.002v.001S1.08 11.606 2 12l-.919-.395a1.002 1.002 0 0 0 0 .789L2 12l-.92.394v.002l.002.002.003.007.01.021.032.072a13.438 13.438 0 0 0 .591 1.107 15.387 15.387 0 0 0 1.923 2.575C5.385 18.057 8.132 20 12 20c3.868 0 6.615-1.943 8.358-3.82a15.385 15.385 0 0 0 1.923-2.575 13.415 13.415 0 0 0 .591-1.107l.033-.072.01-.02.002-.008.002-.003L22 12Zm0 0 .919.395a1.003 1.003 0 0 0 0-.79L22 12Zm-10-2a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-4 2a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z",
    clipRule: "evenodd"
  })));
};

var _path$6Q;
function _extends$6R() { _extends$6R = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6R.apply(this, arguments); }
var SvgFacebook = function SvgFacebook(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6R({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6Q || (_path$6Q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.757 2.757A6 6 0 0 1 15 1h3a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-3v2h3a1 1 0 0 1 .97 1.242l-1 4A1 1 0 0 1 17 15h-2v7a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-7H7a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1h2V7a6 6 0 0 1 1.757-4.243ZM15 3a4 4 0 0 0-4 4v3a1 1 0 0 1-1 1H8v2h2a1 1 0 0 1 1 1v7h2v-7a1 1 0 0 1 1-1h2.22l.5-2H14a1 1 0 0 1-1-1V7a2 2 0 0 1 2-2h2V3h-2Z",
    clipRule: "evenodd"
  })));
};

var _path$6P;
function _extends$6Q() { _extends$6Q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6Q.apply(this, arguments); }
var SvgFaucetFilled = function SvgFaucetFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6Q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6P || (_path$6P = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20 17c1.333 1.264 2 2.264 2 3a2 2 0 1 1-4 0c0-.736.667-1.736 2-3ZM3.5 8c.249 0 .455.229.493.53L4 8.631v6.736c0 .314-.181.575-.419.624L3.5 16h-1c-.249 0-.455-.229-.493-.53L2 15.369V8.632c0-.314.181-.575.419-.624L2.5 8h1ZM13 6a1 1 0 0 1 1 1v1a2 2 0 0 1 2 2h2a4 4 0 0 1 4 4v1h-4v-1h-2a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2H7a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1h1a2 2 0 0 1 2-2V7a1 1 0 0 1 1-1h2Zm1.526-4c.233 0 .426.177.466.41L15 2.5v1c0 .245-.168.45-.389.492L14.526 4H9.474a.482.482 0 0 1-.466-.41L9 3.5v-1c0-.245.168-.45.389-.492L9.474 2h5.052Z",
    clipRule: "evenodd"
  })));
};

var _path$6O;
function _extends$6P() { _extends$6P = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6P.apply(this, arguments); }
var SvgFaucetOutline = function SvgFaucetOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6P({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6O || (_path$6O = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M19 17c1.333 1.264 2 2.264 2 3a2 2 0 1 1-4 0c0-.736.667-1.736 2-3ZM12 6a1 1 0 0 1 1 1v1a2 2 0 0 1 1.742 1.017c.084-.011.17-.017.258-.017h2a5 5 0 0 1 5 5v1a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1h-1a2.01 2.01 0 0 1-.259-.017A1.997 1.997 0 0 1 13 16H9a2 2 0 0 1-2-2H6a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1h1a2 2 0 0 1 2-2V7a1 1 0 0 1 1-1h2ZM3.5 8c.249 0 .455.229.493.53L4 8.631v6.736c0 .314-.181.575-.419.624L3.5 16h-1c-.249 0-.455-.229-.493-.53L2 15.369V8.632c0-.314.181-.575.419-.624L2.5 8h1ZM12 9h-2v1H9v1H8v2h1v1h4v-1h4a1 1 0 0 1 1 1h2a3 3 0 0 0-3-3h-4v-1h-1V9Zm1.526-7c.233 0 .426.177.466.41L14 2.5v1c0 .245-.168.45-.389.492L13.526 4H8.474a.482.482 0 0 1-.466-.41L8 3.5v-1c0-.245.168-.45.389-.492L8.474 2h5.052Z",
    clipRule: "evenodd"
  })));
};

var _path$6N;
function _extends$6O() { _extends$6O = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6O.apply(this, arguments); }
var SvgFileArchive = function SvgFileArchive(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6O({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6N || (_path$6N = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 3a.976.976 0 0 0-.693.307A.976.976 0 0 0 5 4v18a1 1 0 1 1-2 0V4c0-.774.311-1.526.893-2.107A2.974 2.974 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a2.97 2.97 0 0 1-.893 2.107A2.973 2.973 0 0 1 18 23h-2a1 1 0 1 1 0-2h2a.976.976 0 0 0 .693-.307A.976.976 0 0 0 19 20V9h-5a1 1 0 0 1-1-1V3H6Zm9 .914V7h3.086L15 3.914ZM10 5a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0V6a1 1 0 0 1 1-1Zm0 5a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0v-1a1 1 0 0 1 1-1Zm0 5a1 1 0 0 1 1 1v1.17a3.001 3.001 0 1 1-2 0V16a1 1 0 0 1 1-1Zm0 4a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z",
    clipRule: "evenodd"
  })));
};

var _path$6M;
function _extends$6N() { _extends$6N = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6N.apply(this, arguments); }
var SvgFileAudio = function SvgFileAudio(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6N({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6M || (_path$6M = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6a1 1 0 0 0-1 1v2a1 1 0 0 1-2 0V4a3 3 0 0 1 .879-2.121ZM15 3.914V7h3.086L15 3.914ZM6 11a3 3 0 0 0-3 3v1a2 2 0 1 1-2 2v-3a5 5 0 1 1 10 0v3a2 2 0 1 1-2-2v-1a3 3 0 0 0-3-3Z",
    clipRule: "evenodd"
  })));
};

var _path$6L;
function _extends$6M() { _extends$6M = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6M.apply(this, arguments); }
var SvgFileAxis3D = function SvgFileAxis3D(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6M({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6L || (_path$6L = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7ZM8 9a1 1 0 0 1 1 1v5.586l2.293-2.293a1 1 0 0 1 1.414 1.414L10.414 17H16a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$6K;
function _extends$6L() { _extends$6L = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6L.apply(this, arguments); }
var SvgFileBadge = function SvgFileBadge(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6L({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6K || (_path$6K = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 3a1 1 0 0 0-1 1v3a1 1 0 1 1-2 0V4a3 3 0 0 1 3-3h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3h-6a1 1 0 1 1 0-2h6a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6Zm9 .914V7h3.086L15 3.914ZM2.172 11.172a4 4 0 0 1 5.864 5.433l.948 5.216a1 1 0 0 1-1.3 1.128L5 22.054l-2.684.895a1 1 0 0 1-1.3-1.128l.949-5.216a4 4 0 0 1 .207-5.433Zm1.606 6.637-.492 2.708 1.398-.466a1 1 0 0 1 .632 0l1.398.466-.492-2.708a4 4 0 0 1-2.444 0ZM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z",
    clipRule: "evenodd"
  })));
};

var _path$6J;
function _extends$6K() { _extends$6K = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6K.apply(this, arguments); }
var SvgFileBadgeInside = function SvgFileBadgeInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6K({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6J || (_path$6J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM6 3a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7.914L14.086 3H6Zm3.172 4.172a4 4 0 0 1 5.864 5.433l.948 5.216a1 1 0 0 1-1.3 1.128L12 18.054l-2.684.895a1 1 0 0 1-1.3-1.128l.949-5.216a4 4 0 0 1 .207-5.433Zm1.606 6.637-.492 2.708 1.398-.466a1 1 0 0 1 .632 0l1.398.466-.492-2.708a4 4 0 0 1-2.444 0ZM12 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z",
    clipRule: "evenodd"
  })));
};

var _path$6I;
function _extends$6J() { _extends$6J = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6J.apply(this, arguments); }
var SvgFileBarChart = function SvgFileBarChart(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6J({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6I || (_path$6I = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Zm-1 8a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1Zm4 3a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0v-3a1 1 0 0 1 1-1Zm-8 2a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0v-1a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$6H;
function _extends$6I() { _extends$6I = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6I.apply(this, arguments); }
var SvgFileBarChartUpTrend = function SvgFileBarChartUpTrend(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6I({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6H || (_path$6H = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Zm3 8a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1Zm-4 2a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0v-4a1 1 0 0 1 1-1Zm-4 2a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$6G;
function _extends$6H() { _extends$6H = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6H.apply(this, arguments); }
var SvgFileBox = function SvgFileBox(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6H({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6G || (_path$6G = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3h-3.5a1 1 0 1 1 0-2H18a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6a1 1 0 0 0-1 1v4a1 1 0 0 1-2 0V4a3 3 0 0 1 .879-2.121ZM15 3.914V7h3.086L15 3.914Zm-9.552 6.523a2.97 2.97 0 0 1 3.104 0l2.996 1.828A3.023 3.023 0 0 1 13 14.86v3.28c0 1.06-.545 2.05-1.452 2.595l-.004.003-2.988 1.822-.003.002a2.96 2.96 0 0 1-3.106 0l-.003-.002-2.989-1.822-.003-.003A3.022 3.022 0 0 1 1 18.14v-3.28c0-1.06.545-2.05 1.452-2.595l2.995-1.827.001-.001ZM7 11.999a.97.97 0 0 0-.507.143l-.002.002-2.794 1.704L7 15.833l3.302-1.986-2.793-1.703-.002-.002A.97.97 0 0 0 7 12Zm4 3.763-3 1.804v2.991l2.515-1.534c.291-.175.485-.505.485-.883v-2.378Zm-5 4.795v-2.991l-3-1.804v2.378c0 .378.193.708.485.883l.006.003L6 20.556Z",
    clipRule: "evenodd"
  })));
};

var _path$6F;
function _extends$6G() { _extends$6G = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6G.apply(this, arguments); }
var SvgFileCheck = function SvgFileCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6G({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6F || (_path$6F = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6a1 1 0 0 0-1 1v4a1 1 0 0 1-2 0V4a3 3 0 0 1 .879-2.121ZM15 3.914V7h3.086L15 3.914Zm-5.293 8.379a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 1 1 1.414-1.414L5 15.586l3.293-3.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$6E;
function _extends$6F() { _extends$6F = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6F.apply(this, arguments); }
var SvgFileCheckInside = function SvgFileCheckInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6F({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6E || (_path$6E = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Zm2.707 9.293a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 1 1 1.414-1.414L11 15.586l3.293-3.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$6D;
function _extends$6E() { _extends$6E = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6E.apply(this, arguments); }
var SvgFileClock = function SvgFileClock(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6E({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6D || (_path$6D = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.893 1.893A2.974 2.974 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a2.97 2.97 0 0 1-.893 2.107A2.973 2.973 0 0 1 18 23h-2a1 1 0 1 1 0-2h2a.976.976 0 0 0 .693-.307A.976.976 0 0 0 19 20V9h-5a1 1 0 0 1-1-1V3H6a.976.976 0 0 0-.693.307A.976.976 0 0 0 5 4v3a1 1 0 0 1-2 0V4c0-.774.311-1.526.893-2.107ZM15 3.914V7h3.086L15 3.914ZM8 11a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm-7 5a7 7 0 1 1 14 0 7 7 0 0 1-14 0Zm7-3a1 1 0 0 1 1 1v1.782l1.14.95a1 1 0 1 1-1.28 1.536l-1.5-1.25A1 1 0 0 1 7 16.25V14a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$6C;
function _extends$6D() { _extends$6D = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6D.apply(this, arguments); }
var SvgFileCode = function SvgFileCode(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6D({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6C || (_path$6C = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6a1 1 0 0 0-1 1v4a1 1 0 0 1-2 0V4a3 3 0 0 1 .879-2.121ZM15 3.914V7h3.086L15 3.914Zm-9.293 7.379a1 1 0 0 1 0 1.414L3.414 15l2.293 2.293a1 1 0 1 1-1.414 1.414l-3-3a1 1 0 0 1 0-1.414l3-3a1 1 0 0 1 1.414 0Zm2.586 0a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1 0 1.414l-3 3a1 1 0 0 1-1.414-1.414L10.586 15l-2.293-2.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$6B;
function _extends$6C() { _extends$6C = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6C.apply(this, arguments); }
var SvgFileCodeInside = function SvgFileCodeInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6C({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6B || (_path$6B = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Zm-2.293 9.293a1 1 0 0 1 0 1.414L9.414 15l1.293 1.293a1 1 0 0 1-1.414 1.414l-2-2a1 1 0 0 1 0-1.414l2-2a1 1 0 0 1 1.414 0Zm2.586 1.414a1 1 0 0 1 1.414-1.414l2 2a1 1 0 0 1 0 1.414l-2 2a1 1 0 0 1-1.414-1.414L14.586 15l-1.293-1.293Z",
    clipRule: "evenodd"
  })));
};

var _path$6A;
function _extends$6B() { _extends$6B = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6B.apply(this, arguments); }
var SvgFileCog = function SvgFileCog(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6B({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6A || (_path$6A = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 3a1 1 0 0 0-1 1v2a1 1 0 0 1-2 0V4a3 3 0 0 1 3-3h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6Zm9 .914V7h3.086L15 3.914ZM6 9a1 1 0 0 1 1 1v.126c.367.095.714.24 1.032.428l.261-.261a1 1 0 1 1 1.414 1.414l-.26.261c.187.318.333.665.427 1.032H10a1 1 0 1 1 0 2h-.126a3.96 3.96 0 0 1-.428 1.032l.261.26a1 1 0 0 1-1.414 1.415l-.261-.26A3.974 3.974 0 0 1 7 17.873V18a1 1 0 1 1-2 0v-.126a3.977 3.977 0 0 1-1.032-.428l-.26.261a1 1 0 0 1-1.415-1.414l.26-.261A3.976 3.976 0 0 1 2.127 15H2a1 1 0 1 1 0-2h.126a3.96 3.96 0 0 1 .428-1.032l-.261-.26a1 1 0 1 1 1.414-1.415l.261.26A3.977 3.977 0 0 1 5 10.127V10a1 1 0 0 1 1-1Zm-1.45 3.622a1.04 1.04 0 0 0 .072-.071A1.989 1.989 0 0 1 6 12a1.993 1.993 0 0 1 1.449.621C7.79 12.98 8 13.465 8 14a1.993 1.993 0 0 1-.62 1.448A1.993 1.993 0 0 1 6 16a1.993 1.993 0 0 1-1.45-.622A1.993 1.993 0 0 1 4 14c0-.534.21-1.02.55-1.378Z",
    clipRule: "evenodd"
  })));
};

var _path$6z;
function _extends$6A() { _extends$6A = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6A.apply(this, arguments); }
var SvgFileCogInside = function SvgFileCogInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6A({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6z || (_path$6z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Zm-1 8a1 1 0 0 1 1 1v.17c.351.125.673.313.95.55l.152-.087a1 1 0 0 1 .996 1.734l-.148.085a3.014 3.014 0 0 1 0 1.096l.148.085a1 1 0 0 1-.996 1.734l-.152-.087a2.997 2.997 0 0 1-.95.55V18a1 1 0 1 1-2 0v-.17a3 3 0 0 1-.95-.55l-.152.087a1 1 0 0 1-.996-1.734l.148-.085a3.018 3.018 0 0 1 0-1.096l-.148-.085a1 1 0 1 1 .996-1.734l.152.087a3 3 0 0 1 .95-.55V12a1 1 0 0 1 1-1Zm-.887 3.538a1.035 1.035 0 0 0 .043-.075 1 1 0 1 1-.043.075Z",
    clipRule: "evenodd"
  })));
};

var _path$6y;
function _extends$6z() { _extends$6z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6z.apply(this, arguments); }
var SvgFileDiff = function SvgFileDiff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6y || (_path$6y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM6 3a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7.914L14.086 3H6Zm6 3a1 1 0 0 1 1 1v2h2a1 1 0 1 1 0 2h-2v2a1 1 0 1 1-2 0v-2H9a1 1 0 1 1 0-2h2V7a1 1 0 0 1 1-1ZM8 17a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$6x;
function _extends$6y() { _extends$6y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6y.apply(this, arguments); }
var SvgFileDigit = function SvgFileDigit(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6x || (_path$6x = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6a1 1 0 0 0-1 1v4a1 1 0 0 1-2 0V4a3 3 0 0 1 .879-2.121ZM15 3.914V7h3.086L15 3.914ZM4 13a1 1 0 0 0-1 1v2a1 1 0 1 0 2 0v-2a1 1 0 0 0-1-1Zm-3 1a3 3 0 1 1 6 0v2a3 3 0 1 1-6 0v-2Zm8-2a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5h1a1 1 0 1 1 0 2h-4a1 1 0 1 1 0-2h1v-4h-1a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$6w;
function _extends$6x() { _extends$6x = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6x.apply(this, arguments); }
var SvgFileDown = function SvgFileDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6x({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6w || (_path$6w = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Zm-1 8a1 1 0 0 1 1 1v3.586l1.293-1.293a1 1 0 0 1 1.414 1.414l-3 3a1 1 0 0 1-1.414 0l-3-3a1 1 0 1 1 1.414-1.414L11 15.586V12a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$6v;
function _extends$6w() { _extends$6w = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6w.apply(this, arguments); }
var SvgFileEdit = function SvgFileEdit(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6w({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6v || (_path$6v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 3a1 1 0 0 0-1 1v9.5a1 1 0 1 1-2 0V4a3 3 0 0 1 3-3h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3h-5.5a1 1 0 1 1 0-2H18a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6Zm9 .914V7h3.086L15 3.914Zm-3.095 9.08a1.1 1.1 0 0 0-.777.323L5.893 18.56l-.516 2.059 2.062-.522 5.244-5.225a1.104 1.104 0 0 0 .238-1.199 1.099 1.099 0 0 0-1.016-.68Zm-1.186-1.763a3.1 3.1 0 0 1 3.378 5.056l-.001.001-5.44 5.42a1 1 0 0 1-.46.261l-3.95 1a1 1 0 0 1-1.216-1.212l.99-3.95a1 1 0 0 1 .262-.463l5.43-5.44c.288-.288.63-.517 1.007-.673Z",
    clipRule: "evenodd"
  })));
};

var _path$6u, _path2$s;
function _extends$6v() { _extends$6v = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6v.apply(this, arguments); }
var SvgFileEmptyFilled = function SvgFileEmptyFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6v({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6u || (_path$6u = /*#__PURE__*/React.createElement("path", {
    d: "M13 1H6a3 3 0 0 0-3 3v16a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3V9h-7a1 1 0 0 1-1-1V1Z"
  })), _path2$s || (_path2$s = /*#__PURE__*/React.createElement("path", {
    d: "M20.866 7a1 1 0 0 0-.159-.207l-5.5-5.5A1 1 0 0 0 15 1.134V7h5.866Z"
  })));
};

var _path$6t;
function _extends$6u() { _extends$6u = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6u.apply(this, arguments); }
var SvgFileEmptyOutline = function SvgFileEmptyOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6u({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6t || (_path$6t = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Z",
    clipRule: "evenodd"
  })));
};

var _path$6s;
function _extends$6t() { _extends$6t = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6t.apply(this, arguments); }
var SvgFileHeart = function SvgFileHeart(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6t({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6s || (_path$6s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 3a1 1 0 0 0-1 1v2a1 1 0 0 1-2 0V4a3 3 0 0 1 3-3h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6Zm9 .914V7h3.086L15 3.914ZM7.245 9.257a3.43 3.43 0 0 1 3.752.735.997.997 0 0 1 .088.1c1.288 1.374 1.24 3.534-.275 5.062l-.008.008-3.6 3.55a1 1 0 0 1-1.405 0L2.2 15.153C.656 13.596.62 11.372 2.005 10a3.43 3.43 0 0 1 4.492-.312c.232-.178.485-.323.748-.432Zm1.603 1.76a1.43 1.43 0 0 0-.83.085l-.005.002a1.301 1.301 0 0 0-.466.323l-.35.34a1 1 0 0 1-1.394 0l-.35-.34-.011-.011a1.43 1.43 0 0 0-1.56-.312l-.01.005c-.16.063-.309.162-.455.308l-.003.004c-.514.508-.649 1.46.203 2.322l2.884 2.852 2.893-2.853c.846-.856.71-1.809.193-2.321a.995.995 0 0 1-.067-.073 1.43 1.43 0 0 0-.672-.332Z",
    clipRule: "evenodd"
  })));
};

var _path$6r;
function _extends$6s() { _extends$6s = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6s.apply(this, arguments); }
var SvgFileImage = function SvgFileImage(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6s({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6r || (_path$6r = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM12.414 21H18a1 1 0 0 0 1-1v-2.586l-.795-.795a1 1 0 0 0-1.41 0L12.415 21ZM19 14.73a3 3 0 0 0-3.615.47l-.002.003L9.586 21H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7v5a1 1 0 0 0 1 1h5v5.73ZM18.086 7 15 3.914V7h3.086ZM10 12a1 1 0 1 0 0 2 1 1 0 0 0 0-2Zm-3 1a3 3 0 1 1 6 0 3 3 0 0 1-6 0Z",
    clipRule: "evenodd"
  })));
};

var _path$6q;
function _extends$6r() { _extends$6r = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6r.apply(this, arguments); }
var SvgFileInput = function SvgFileInput(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6r({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6q || (_path$6q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6a1 1 0 0 0-1 1v4a1 1 0 0 1-2 0V4a3 3 0 0 1 .879-2.121ZM15 3.914V7h3.086L15 3.914Zm-6.707 7.379a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1 0 1.414l-3 3a1 1 0 0 1-1.414-1.414L9.586 16H2a1 1 0 1 1 0-2h7.586l-1.293-1.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$6p;
function _extends$6q() { _extends$6q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6q.apply(this, arguments); }
var SvgFileJson = function SvgFileJson(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6p || (_path$6p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6a1 1 0 0 0-1 1v4a1 1 0 0 1-2 0V4a3 3 0 0 1 .879-2.121ZM15 3.914V7h3.086L15 3.914ZM2.586 11.586A2 2 0 0 1 4 11a1 1 0 1 1 0 2v1a2 2 0 0 1-.268 1A2 2 0 0 1 4 16v1a1 1 0 1 1 0 2 2 2 0 0 1-2-2v-1a1 1 0 1 1 0-2v-1a2 2 0 0 1 .586-1.414ZM7 12a1 1 0 0 1 1-1 2 2 0 0 1 2 2v1a1 1 0 1 1 0 2v1a2 2 0 0 1-2 2 1 1 0 1 1 0-2v-1a2 2 0 0 1 .268-1A2 2 0 0 1 8 14v-1a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$6o;
function _extends$6p() { _extends$6p = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6p.apply(this, arguments); }
var SvgFileJsonInside = function SvgFileJsonInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6p({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6o || (_path$6o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Zm-4.414 8.586A2 2 0 0 1 10 11a1 1 0 1 1 0 2v1a2 2 0 0 1-.268 1A2 2 0 0 1 10 16v1a1 1 0 1 1 0 2 2 2 0 0 1-2-2v-1a1 1 0 1 1 0-2v-1a2 2 0 0 1 .586-1.414ZM13 12a1 1 0 0 1 1-1 2 2 0 0 1 2 2v1a1 1 0 1 1 0 2v1a2 2 0 0 1-2 2 1 1 0 1 1 0-2v-1a2 2 0 0 1 .268-1A2 2 0 0 1 14 14v-1a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$6n;
function _extends$6o() { _extends$6o = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6o.apply(this, arguments); }
var SvgFileKey = function SvgFileKey(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6o({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6n || (_path$6n = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 3a1 1 0 0 0-1 1v6a1 1 0 1 1-2 0V4a3 3 0 0 1 3-3h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6Zm9 .914V7h3.086L15 3.914ZM9.293 9.293a1 1 0 0 1 1.414 1.414l-.293.293.293.293a1 1 0 0 1-1.414 1.414L9 12.414l-2.292 2.293a3 3 0 1 1-1.414-1.414l3.999-4ZM4 15a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z",
    clipRule: "evenodd"
  })));
};

var _path$6m;
function _extends$6n() { _extends$6n = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6n.apply(this, arguments); }
var SvgFileKeyInside = function SvgFileKeyInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6n({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6m || (_path$6m = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM6 3a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7.914L14.086 3H6Zm10.707 6.293a1 1 0 0 1 0 1.414l-.293.293.293.293a1 1 0 0 1-1.414 1.414L15 12.414l-2.292 2.293a3 3 0 1 1-1.414-1.414l3.999-4a1 1 0 0 1 1.414 0ZM10 15a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z",
    clipRule: "evenodd"
  })));
};

var _path$6l;
function _extends$6m() { _extends$6m = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6m.apply(this, arguments); }
var SvgFileLineChart = function SvgFileLineChart(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6m({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6l || (_path$6l = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Zm3.707 9.293a1 1 0 0 1 0 1.414l-3.5 3.5a1 1 0 0 1-1.414 0L10.5 15.914l-1.793 1.793a1 1 0 0 1-1.414-1.414l2.5-2.5a1 1 0 0 1 1.414 0l1.293 1.293 2.793-2.793a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$6k;
function _extends$6l() { _extends$6l = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6l.apply(this, arguments); }
var SvgFileLock = function SvgFileLock(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6l({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6k || (_path$6k = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 3a1 1 0 0 0-1 1v1a1 1 0 0 1-2 0V4a3 3 0 0 1 3-3h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6Zm9 .914V7h3.086L15 3.914ZM3.879 8.88A3 3 0 0 1 9 11v1a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2v-1a3 3 0 0 1 .879-2.121ZM3 14v3h6v-3H3Zm4-2H5v-1a1 1 0 1 1 2 0v1Z",
    clipRule: "evenodd"
  })));
};

var _path$6j;
function _extends$6k() { _extends$6k = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6k.apply(this, arguments); }
var SvgFileLockInside = function SvgFileLockInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6k({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6j || (_path$6j = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM6 3a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7.914L14.086 3H6Zm3.172 4.172A4 4 0 0 1 16 10v1.268A2 2 0 0 1 17 13v4a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-4a2 2 0 0 1 1-1.732V10a4 4 0 0 1 1.172-2.828ZM10 11h4v-1a2 2 0 1 0-4 0v1Zm-1 2v4h6v-4H9Z",
    clipRule: "evenodd"
  })));
};

var _path$6i;
function _extends$6j() { _extends$6j = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6j.apply(this, arguments); }
var SvgFileMinus = function SvgFileMinus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6j({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6i || (_path$6i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6a1 1 0 0 0-1 1v4a1 1 0 0 1-2 0V4a3 3 0 0 1 .879-2.121ZM15 3.914V7h3.086L15 3.914ZM2 15a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$6h;
function _extends$6i() { _extends$6i = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6i.apply(this, arguments); }
var SvgFileMinusInside = function SvgFileMinusInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6i({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6h || (_path$6h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7ZM8 15a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$6g;
function _extends$6h() { _extends$6h = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6h.apply(this, arguments); }
var SvgFileOutput = function SvgFileOutput(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6h({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6g || (_path$6g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6a1 1 0 0 0-1 1v4a1 1 0 0 1-2 0V4a3 3 0 0 1 .879-2.121ZM15 3.914V7h3.086L15 3.914Zm-9.293 7.379a1 1 0 0 1 0 1.414L4.414 14H12a1 1 0 1 1 0 2H4.414l1.293 1.293a1 1 0 1 1-1.414 1.414l-3-3a1 1 0 0 1 0-1.414l3-3a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$6f;
function _extends$6g() { _extends$6g = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6g.apply(this, arguments); }
var SvgFilePieChart = function SvgFilePieChart(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6g({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6f || (_path$6f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3h-2a1 1 0 1 1 0-2h2a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6a1 1 0 0 0-1 1v3a1 1 0 0 1-2 0V4a3 3 0 0 1 .879-2.121ZM15 3.914V7h3.086L15 3.914ZM7.293 9.463A1 1 0 0 1 8 9.17 6.83 6.83 0 0 1 14.83 16a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-5.83a1 1 0 0 1 .293-.707ZM9 11.275V15h3.725A4.83 4.83 0 0 0 9 11.275Zm-4.21-.226a1 1 0 0 1-.089 1.412 4.84 4.84 0 1 0 6.796 6.87 1 1 0 1 1 1.486 1.338 6.84 6.84 0 1 1-9.604-9.71 1 1 0 0 1 1.412.09Z",
    clipRule: "evenodd"
  })));
};

var _path$6e;
function _extends$6f() { _extends$6f = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6f.apply(this, arguments); }
var SvgFilePlus = function SvgFilePlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6f({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6e || (_path$6e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6a1 1 0 0 0-1 1v4a1 1 0 0 1-2 0V4a3 3 0 0 1 .879-2.121ZM15 3.914V7h3.086L15 3.914ZM6 11a1 1 0 0 1 1 1v2h2a1 1 0 1 1 0 2H7v2a1 1 0 1 1-2 0v-2H3a1 1 0 1 1 0-2h2v-2a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$6d;
function _extends$6e() { _extends$6e = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6e.apply(this, arguments); }
var SvgFilePlusInside = function SvgFilePlusInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6e({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6d || (_path$6d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Zm-1 8a1 1 0 0 1 1 1v2h2a1 1 0 1 1 0 2h-2v2a1 1 0 1 1-2 0v-2H9a1 1 0 1 1 0-2h2v-2a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$6c;
function _extends$6d() { _extends$6d = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6d.apply(this, arguments); }
var SvgFileQuestion = function SvgFileQuestion(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6d({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6c || (_path$6c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM6 3a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7.914L14.086 3H6Zm6.118 7.03a1.1 1.1 0 0 0-.718.136.975.975 0 0 1-.053.028c-.121.061-.296.24-.453.553a1 1 0 1 1-1.788-.894c.24-.48.656-1.09 1.319-1.433a3.1 3.1 0 0 1 3.813.605c.022.024.042.049.062.075.366.488.7 1.097.7 1.9 0 1.184-.891 1.946-1.44 2.316a5.889 5.889 0 0 1-1.19.613l-.025.01-.009.003-.003.001h-.002L12 13l.33.944a1 1 0 0 1-.662-1.887h.001-.002l.007-.003.043-.016a3.883 3.883 0 0 0 .723-.38c.451-.304.56-.542.56-.658 0-.188-.06-.37-.268-.657a1.1 1.1 0 0 0-.614-.312ZM11 17a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$6b;
function _extends$6c() { _extends$6c = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6c.apply(this, arguments); }
var SvgFileScan = function SvgFileScan(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6c({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6b || (_path$6b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V10a1 1 0 1 1-2 0V9h-5a1 1 0 0 1-1-1V3H6a1 1 0 0 0-1 1v16c0 .548.452 1 1 1h4.5a1 1 0 1 1 0 2H6c-1.652 0-3-1.348-3-3V4a3 3 0 0 1 .879-2.121ZM15 3.914V7h3.086L15 3.914Zm-1.121 9.965A3 3 0 0 1 16 13a1 1 0 1 1 0 2 1 1 0 0 0-1 1 1 1 0 1 1-2 0 3 3 0 0 1 .879-2.121Zm6.828 1.414A1 1 0 0 0 20 15a1 1 0 1 1 0-2 3 3 0 0 1 3 3 1 1 0 1 1-2 0 1 1 0 0 0-.293-.707ZM14 19a1 1 0 0 1 1 1 1 1 0 0 0 1 1 1 1 0 1 1 0 2 3 3 0 0 1-3-3 1 1 0 0 1 1-1Zm8 0a1 1 0 0 1 1 1 3 3 0 0 1-3 3 1 1 0 1 1 0-2 1 1 0 0 0 1-1 1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$6a;
function _extends$6b() { _extends$6b = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6b.apply(this, arguments); }
var SvgFileSearch = function SvgFileSearch(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6b({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6a || (_path$6a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Zm-1.5 10a1.5 1.5 0 1 0 .93 2.677 1.006 1.006 0 0 1 .247-.246A1.5 1.5 0 0 0 11.5 13Zm3.08 3.165a3.5 3.5 0 1 0-1.414 1.414l1.127 1.128a1 1 0 0 0 1.414-1.414l-1.128-1.128Z",
    clipRule: "evenodd"
  })));
};

var _path$69;
function _extends$6a() { _extends$6a = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6a.apply(this, arguments); }
var SvgFileSignature = function SvgFileSignature(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6a({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$69 || (_path$69 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 3a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.5a1 1 0 1 1 2 0v.5a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 3-3h8.5a1 1 0 0 1 .707.293l3.5 3.5a1 1 0 0 1-1.414 1.414L14.086 3H6Zm13.905 6.995a1.1 1.1 0 0 0-.778.322l-4.234 4.244-.516 2.059 2.062-.522 4.244-4.225a1.1 1.1 0 0 0-.778-1.878ZM18.719 8.23a3.1 3.1 0 0 1 3.378 5.056l-.002.002-4.44 4.42a1 1 0 0 1-.46.26l-3.95 1a1 1 0 0 1-1.215-1.212l.99-3.95a1 1 0 0 1 .262-.463l4.43-4.44v-.001a3.1 3.1 0 0 1 1.007-.672ZM7 18a1 1 0 0 1 1-1h1a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$68;
function _extends$69() { _extends$69 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$69.apply(this, arguments); }
var SvgFileStack = function SvgFileStack(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$69({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$68 || (_path$68 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.5 3c-.248 0-.5.252-.5.5v9c0 .248.252.5.5.5h7c.248 0 .5-.252.5-.5V8h-4a1 1 0 0 1-1-1V3h-2.5Zm0-2C11.148 1 10 2.148 10 3.5v9c0 1.352 1.148 2.5 2.5 2.5h7c1.352 0 2.5-1.148 2.5-2.5V6a1 1 0 0 0-.293-.707l-4-4A1 1 0 0 0 17 1h-4.5Zm7.086 5L17 3.414V6h2.586ZM7 7a1 1 0 0 1 1 1v8.753l.003.006a.733.733 0 0 0 .238.238l.006.003H15a1 1 0 1 1 0 2H8.2c-.699 0-1.242-.428-1.507-.693C6.428 18.042 6 17.5 6 16.8V8a1 1 0 0 1 1-1Zm-4 4a1 1 0 0 1 1 1v8.753l.003.006a.733.733 0 0 0 .238.238l.006.003H11a1 1 0 1 1 0 2H4.2c-.699 0-1.242-.428-1.507-.693C2.428 22.042 2 21.5 2 20.8V12a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$67;
function _extends$68() { _extends$68 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$68.apply(this, arguments); }
var SvgFileSymlink = function SvgFileSymlink(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$68({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$67 || (_path$67 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6a1 1 0 0 0-1 1v7a1 1 0 1 1-2 0V4a3 3 0 0 1 .879-2.121ZM15 3.914V7h3.086L15 3.914Zm-5.707 7.379a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1 0 1.414l-3 3a1 1 0 0 1-1.414-1.414L10.586 16H6a1 1 0 0 0-1 1v1a1 1 0 1 1-2 0v-1a3 3 0 0 1 3-3h4.586l-1.293-1.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$66;
function _extends$67() { _extends$67 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$67.apply(this, arguments); }
var SvgFileTerminal = function SvgFileTerminal(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$67({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$66 || (_path$66 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Zm-5.707 8.293a1 1 0 0 1 1.414 0l2 2a1 1 0 0 1 0 1.414l-2 2a1 1 0 0 1-1.414-1.414L8.586 14l-1.293-1.293a1 1 0 0 1 0-1.414ZM11 18a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$65;
function _extends$66() { _extends$66 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$66.apply(this, arguments); }
var SvgFileText = function SvgFileText(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$66({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$65 || (_path$65 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.848 1.848A3.043 3.043 0 0 1 6 .957h8.5c.277 0 .542.11.738.305l5.5 5.5c.196.196.306.461.306.738V20A3.044 3.044 0 0 1 18 23.044H6A3.044 3.044 0 0 1 2.957 20V4c0-.807.32-1.581.891-2.152Zm14.133 5.109-2.938-2.938v2.938h2.938Zm-5.025-3.914V8c0 .576.468 1.043 1.044 1.043h4.956V20a.956.956 0 0 1-.956.956H6A.957.957 0 0 1 5.043 20V4A.957.957 0 0 1 6 3.043h6.957ZM6.957 9c0-.576.468-1.043 1.044-1.043h2a1.043 1.043 0 1 1 0 2.087H8A1.044 1.044 0 0 1 6.957 9Zm0 4c0-.576.468-1.043 1.044-1.043h8a1.044 1.044 0 0 1 0 2.086H8A1.043 1.043 0 0 1 6.957 13Zm0 4c0-.576.468-1.043 1.044-1.043h8a1.044 1.044 0 0 1 0 2.087H8A1.043 1.043 0 0 1 6.957 17Z",
    clipRule: "evenodd"
  })));
};

var _path$64;
function _extends$65() { _extends$65 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$65.apply(this, arguments); }
var SvgFileType = function SvgFileType(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$65({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$64 || (_path$64 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6a1 1 0 0 0-1 1v4a1 1 0 0 1-2 0V4a3 3 0 0 1 .879-2.121ZM15 3.914V7h3.086L15 3.914ZM1 12a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0H6v4a1 1 0 1 1 0 2H4a1 1 0 1 1 0-2v-4H3a1 1 0 1 1-2 0v-1Z",
    clipRule: "evenodd"
  })));
};

var _path$63;
function _extends$64() { _extends$64 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$64.apply(this, arguments); }
var SvgFileTypeInside = function SvgFileTypeInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$64({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$63 || (_path$63 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Zm-5 9a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0h-1v4a1 1 0 1 1 0 2h-2a1 1 0 1 1 0-2v-4h-1a1 1 0 1 1-2 0v-1Z",
    clipRule: "evenodd"
  })));
};

var _path$62;
function _extends$63() { _extends$63 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$63.apply(this, arguments); }
var SvgFileUp = function SvgFileUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$63({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$62 || (_path$62 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Zm-1.707 8.293a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1-1.414 1.414L13 14.414V18a1 1 0 1 1-2 0v-3.586l-1.293 1.293a1 1 0 0 1-1.414-1.414l3-3Z",
    clipRule: "evenodd"
  })));
};

var _path$61;
function _extends$62() { _extends$62 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$62.apply(this, arguments); }
var SvgFileVideo = function SvgFileVideo(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$62({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$61 || (_path$61 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 3a1 1 0 0 0-1 1v4a1 1 0 0 1-2 0V4a3 3 0 0 1 3-3h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6Zm9 .914V7h3.086L15 3.914Zm-.515 7.211A1 1 0 0 1 15 12v6a1 1 0 0 1-1.53.848l-2.491-1.557A2 2 0 0 1 9 19H3a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h6a2 2 0 0 1 1.979 1.709l2.491-1.557a1 1 0 0 1 1.015-.027ZM9 14.485V17H3v-4h6v1.485Zm2.087.515L13 16.196v-2.392L11.087 15Z",
    clipRule: "evenodd"
  })));
};

var _path$60;
function _extends$61() { _extends$61 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$61.apply(this, arguments); }
var SvgFileVideoInside = function SvgFileVideoInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$61({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$60 || (_path$60 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Zm-3.493 7.13a1 1 0 0 1 1.008.012l5 3a1 1 0 0 1 0 1.716l-5 3A1 1 0 0 1 9 17v-6a1 1 0 0 1 .507-.87ZM11 12.766v2.468L13.056 14 11 12.766Z",
    clipRule: "evenodd"
  })));
};

var _path$5$;
function _extends$60() { _extends$60 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$60.apply(this, arguments); }
var SvgFileVisualizationFilled = function SvgFileVisualizationFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$60({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5$ || (_path$5$ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 1h7v7a1 1 0 0 0 1 1h7v11a3 3 0 0 1-3 3H4a1 1 0 0 1-1-1V4a3 3 0 0 1 3-3Zm14.707 5.793a1 1 0 0 1 .159.207H15V1.134a1 1 0 0 1 .207.159l5.5 5.5ZM9 11a4 4 0 1 0 2.031 7.446l1.262 1.261a1 1 0 0 0 1.414-1.414l-1.261-1.261A3.982 3.982 0 0 0 13 15a4 4 0 0 0-4-4Zm-1.414 2.586a2 2 0 1 1 2.828 2.828 2 2 0 0 1-2.828-2.828Z",
    clipRule: "evenodd"
  })));
};

var _path$5_;
function _extends$5$() { _extends$5$ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5$.apply(this, arguments); }
var SvgFileVisualizationOutline = function SvgFileVisualizationOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5$({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5_ || (_path$5_ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.879 1.879A3 3 0 0 1 7 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 22 7.5V20a3 3 0 0 1-3 3H5a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H7a1 1 0 0 0-1 1v3a1 1 0 0 1-2 0V4a3 3 0 0 1 .879-2.121ZM16 3.914V7h3.086L16 3.914ZM3.172 11.172a4 4 0 0 1 6.274 4.86l1.261 1.26a1 1 0 0 1-1.414 1.415l-1.261-1.261A3.988 3.988 0 0 1 6 18a4 4 0 0 1-2.828-6.828ZM6 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z",
    clipRule: "evenodd"
  })));
};

var _path$5Z;
function _extends$5_() { _extends$5_ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5_.apply(this, arguments); }
var SvgFileVolume = function SvgFileVolume(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5_({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5Z || (_path$5Z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6a1 1 0 0 0-1 1v3a1 1 0 0 1-2 0V4a3 3 0 0 1 .879-2.121ZM15 3.914V7h3.086L15 3.914ZM7.472 9.118A1 1 0 0 1 8 10v8a1 1 0 0 1-1.555.832L3.697 17H2a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1h1.697l2.748-1.832a1 1 0 0 1 1.027-.05ZM6 11.868l-1.445.964A1 1 0 0 1 4 13H3v2h1a1 1 0 0 1 .555.168L6 16.13v-4.263Zm4.375-1.649a1 1 0 0 1 1.406.156C12.576 11.37 13 12.665 13 14c0 1.336-.424 2.631-1.22 3.625a1 1 0 1 1-1.56-1.25c.484-.606.78-1.45.78-2.375 0-.924-.296-1.769-.78-2.375a1 1 0 0 1 .155-1.406Z",
    clipRule: "evenodd"
  })));
};

var _path$5Y;
function _extends$5Z() { _extends$5Z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5Z.apply(this, arguments); }
var SvgFileVolumeInside = function SvgFileVolumeInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5Z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5Y || (_path$5Y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Zm1.375 8.22a1 1 0 0 1 1.406.155C16.576 12.37 17 13.665 17 15c0 1.336-.424 2.631-1.22 3.625a1 1 0 1 1-1.56-1.25c.484-.606.78-1.45.78-2.375 0-.924-.296-1.769-.78-2.375a1 1 0 0 1 .155-1.406Zm-3.5 1.5a1 1 0 0 1 1.406.155c.475.595.719 1.36.719 2.125 0 .764-.244 1.53-.72 2.125a1 1 0 1 1-1.56-1.25c.164-.206.28-.52.28-.875s-.116-.67-.28-.875a1 1 0 0 1 .155-1.406ZM7 15a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$5X;
function _extends$5Y() { _extends$5Y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5Y.apply(this, arguments); }
var SvgFileWarning = function SvgFileWarning(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5Y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5X || (_path$5X = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM6 3a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7.914L14.086 3H6Zm6 5a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0V9a1 1 0 0 1 1-1Zm-1 9a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$5W, _path2$r;
function _extends$5X() { _extends$5X = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5X.apply(this, arguments); }
var SvgFileWithItemsFilled = function SvgFileWithItemsFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5X({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5W || (_path$5W = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13 1H6a3 3 0 0 0-3 3v16a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3V9h-7a1 1 0 0 1-1-1V1ZM8 12a1 1 0 1 0 0 2h2a1 1 0 1 0 0-2H8Zm6 0a1 1 0 1 0 0 2h2a1 1 0 1 0 0-2h-2Zm-6 4a1 1 0 1 0 0 2h2a1 1 0 1 0 0-2H8Zm6 0a1 1 0 1 0 0 2h2a1 1 0 1 0 0-2h-2Z",
    clipRule: "evenodd"
  })), _path2$r || (_path2$r = /*#__PURE__*/React.createElement("path", {
    d: "M20.866 7a1 1 0 0 0-.159-.207l-5.5-5.5A1 1 0 0 0 15 1.134V7h5.866Z"
  })));
};

var _path$5V;
function _extends$5W() { _extends$5W = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5W.apply(this, arguments); }
var SvgFileWithItemsOutline = function SvgFileWithItemsOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5W({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5V || (_path$5V = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7ZM7 13a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm6 0a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2h-2a1 1 0 0 1-1-1Zm-6 4a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm6 0a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2h-2a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$5U;
function _extends$5V() { _extends$5V = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5V.apply(this, arguments); }
var SvgFileX = function SvgFileX(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5V({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5U || (_path$5U = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H4a1 1 0 1 1 0-2h14a1 1 0 0 0 1-1V9h-5a1 1 0 0 1-1-1V3H6a1 1 0 0 0-1 1v4a1 1 0 0 1-2 0V4a3 3 0 0 1 .879-2.121ZM15 3.914V7h3.086L15 3.914ZM2.293 13.207a1 1 0 1 1 1.414-1.414L5.5 13.586l1.793-1.793a1 1 0 0 1 1.414 1.414L6.914 15l1.793 1.793a1 1 0 1 1-1.414 1.414L5.5 16.414l-1.793 1.793a1 1 0 0 1-1.414-1.414L4.086 15l-1.793-1.793Z",
    clipRule: "evenodd"
  })));
};

var _path$5T;
function _extends$5U() { _extends$5U = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5U.apply(this, arguments); }
var SvgFileXInside = function SvgFileXInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5U({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5T || (_path$5T = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.879 1.879A3 3 0 0 1 6 1h8.5a1 1 0 0 1 .707.293l5.5 5.5A1 1 0 0 1 21 7.5V20a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V4a3 3 0 0 1 .879-2.121ZM18.086 7 15 3.914V7h3.086ZM13 3v5a1 1 0 0 0 1 1h5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h7Zm-4.207 8.793a1 1 0 0 1 1.414 0L12 13.586l1.793-1.793a1 1 0 0 1 1.414 1.414L13.414 15l1.793 1.793a1 1 0 0 1-1.414 1.414L12 16.414l-1.793 1.793a1 1 0 0 1-1.414-1.414L10.586 15l-1.793-1.793a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$5S;
function _extends$5T() { _extends$5T = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5T.apply(this, arguments); }
var SvgFiles = function SvgFiles(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5T({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5S || (_path$5S = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.793 1.793C7.233 1.353 7.875 1 8.6 1h6.9a1 1 0 0 1 .707.293l4.5 4.5A1 1 0 0 1 21 6.5v9.9c0 .725-.352 1.367-.793 1.807-.44.44-1.082.793-1.807.793H8.6c-.725 0-1.367-.352-1.807-.793C6.353 17.767 6 17.125 6 16.4V3.6c0-.725.353-1.367.793-1.807ZM18.086 6 16 3.914V6h2.086ZM14 3v4a1 1 0 0 0 1 1h4v8.4c0 .075-.047.233-.207.393-.16.16-.318.207-.393.207H8.6c-.075 0-.233-.047-.393-.207-.16-.16-.207-.318-.207-.393V3.6c0-.075.047-.233.207-.393.16-.16.318-.207.393-.207H14ZM3 6.6a1 1 0 0 1 1 1v12.8c0 .075.047.233.207.393.16.16.318.207.393.207h9.8a1 1 0 1 1 0 2H4.6c-.725 0-1.367-.353-1.807-.793C2.353 21.767 2 21.125 2 20.4V7.6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$5R;
function _extends$5S() { _extends$5S = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5S.apply(this, arguments); }
var SvgFilterFilled = function SvgFilterFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5S({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5R || (_path$5R = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 2a1 1 0 0 0-.764 1.646L9 12.826V19a1 1 0 0 0 .553.894l4 2A1 1 0 0 0 15 21v-8.174l7.764-9.18A1 1 0 0 0 22 2H2Z",
    clipRule: "evenodd"
  })));
};

var _path$5Q;
function _extends$5R() { _extends$5R = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5R.apply(this, arguments); }
var SvgFilterOutline = function SvgFilterOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5R({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5Q || (_path$5Q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.093 2.58A1 1 0 0 1 2 2h20a1 1 0 0 1 .764 1.646L15 12.826V21a1 1 0 0 1-1.447.894l-4-2A1 1 0 0 1 9 19v-6.174l-7.764-9.18a1 1 0 0 1-.143-1.067ZM4.155 4l6.609 7.814a1 1 0 0 1 .236.646v5.922l2 1V12.46a1 1 0 0 1 .236-.646L19.845 4H4.155Z",
    clipRule: "evenodd"
  })));
};

var _path$5P;
function _extends$5Q() { _extends$5Q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5Q.apply(this, arguments); }
var SvgFilterX = function SvgFilterX(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5Q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5P || (_path$5P = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.093 2.58A1 1 0 0 1 2 2h11.013a1 1 0 1 1 0 2H4.155l6.609 7.814a1 1 0 0 1 .236.646v5.922l2 1V12.46a1 1 0 0 1 .24-.649l.9-1.055a1 1 0 0 1 1.52 1.298l-.66.775V21a1 1 0 0 1-1.447.894l-4-2A1 1 0 0 1 9 19v-6.174l-7.764-9.18a1 1 0 0 1-.143-1.067Zm15.2-.287a1 1 0 0 1 1.414 0L19.5 4.086l1.793-1.793a1 1 0 1 1 1.414 1.414L20.914 5.5l1.793 1.793a1 1 0 0 1-1.414 1.414L19.5 6.914l-1.793 1.793a1 1 0 1 1-1.414-1.414L18.086 5.5l-1.793-1.793a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$5O, _path2$q;
function _extends$5P() { _extends$5P = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5P.apply(this, arguments); }
var SvgFlag = function SvgFlag(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5P({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5O || (_path$5O = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 1.414 0l20 20a1 1 0 0 1-1.414 1.414l-5.719-5.719c-1.505-.083-2.774-.591-3.89-1.037l-.055-.023C10.36 15.421 9.28 15 8 15c-1.387 0-2.26.23-2.754.428a3.32 3.32 0 0 0-.246.11V22a1 1 0 1 1-2 0V4.414L1.293 2.707a1 1 0 0 1 0-1.414ZM5 6.414l7.845 7.846-.474-.188-.055-.023C11.096 13.562 9.693 13 8 13c-1.292 0-2.272.173-3 .397V6.414Z",
    clipRule: "evenodd"
  })), _path2$q || (_path2$q = /*#__PURE__*/React.createElement("path", {
    d: "M7 2a1 1 0 0 1 1-1c1.693 0 3.097.561 4.316 1.05l.055.022C13.64 2.579 14.72 3 16 3c1.387 0 2.26-.23 2.754-.428.248-.1.405-.192.488-.248a.96.96 0 0 0 .073-.053A.995.995 0 0 1 20 2h.005a.997.997 0 0 1 .7.29l.004.005a.995.995 0 0 1 .291.702V14a1 1 0 1 1-2 0V4.603c-.728.224-1.708.397-3 .397-1.693 0-3.097-.561-4.316-1.05l-.055-.022C10.36 3.421 9.28 3 8 3a1 1 0 0 1-1-1Z"
  })));
};

var _path$5N;
function _extends$5O() { _extends$5O = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5O.apply(this, arguments); }
var SvgFlashlight = function SvgFlashlight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5O({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5N || (_path$5N = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 2a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v4c0 1.415-.756 2.17-1.267 2.68l-.026.027C17.217 9.197 17 9.44 17 10v10a3 3 0 0 1-3 3h-4a3 3 0 0 1-3-3V10c0-.56-.218-.803-.707-1.293l-.026-.026C5.757 8.17 5 7.415 5 6V2Zm2 1v2h10V3H7Zm9.577 4H7.423c.082.09.177.185.284.293l.026.026C8.243 7.83 9 8.585 9 10v10a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V10c0-1.415.756-2.17 1.267-2.68l.026-.027c.107-.108.202-.203.284-.293Z",
    clipRule: "evenodd"
  })));
};

var _path$5M;
function _extends$5N() { _extends$5N = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5N.apply(this, arguments); }
var SvgFlashlightOff = function SvgFlashlightOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5N({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5M || (_path$5M = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 1.414 0l20 20a1 1 0 0 1-1.414 1.414L17 18.414V20a3 3 0 0 1-3 3h-4a3 3 0 0 1-3-3V10c0-.56-.218-.803-.707-1.293l-.026-.026c-.455-.455-1.105-1.104-1.241-2.241L1.293 2.707a1 1 0 0 1 0-1.414ZM9 10.414V20a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-3.586l-6-6ZM6 2a1 1 0 0 1 1-1h11a1 1 0 0 1 1 1v4c0 1.415-.756 2.17-1.267 2.68l-.026.027C17.217 9.197 17 9.44 17 10v1a1 1 0 1 1-2 0v-1c0-1.415.756-2.17 1.267-2.68l.026-.027c.107-.108.202-.203.284-.293H11a1 1 0 1 1 0-2h6V3H7a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$5L;
function _extends$5M() { _extends$5M = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5M.apply(this, arguments); }
var SvgFoldHorizontal = function SvgFoldHorizontal(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5M({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5L || (_path$5L = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 1a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0V2a1 1 0 0 1 1-1Zm0 6a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0V8a1 1 0 0 1 1-1ZM4.293 8.293a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1 0 1.414l-3 3a1 1 0 0 1-1.414-1.414L5.586 13H2a1 1 0 1 1 0-2h3.586L4.293 9.707a1 1 0 0 1 0-1.414Zm15.414 0a1 1 0 0 1 0 1.414L18.414 11H22a1 1 0 1 1 0 2h-3.586l1.293 1.293a1 1 0 0 1-1.414 1.414l-3-3a1 1 0 0 1 0-1.414l3-3a1 1 0 0 1 1.414 0ZM12 13a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1Zm0 6a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$5K;
function _extends$5L() { _extends$5L = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5L.apply(this, arguments); }
var SvgFoldVertical = function SvgFoldVertical(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5L({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5K || (_path$5K = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 1a1 1 0 0 1 1 1v3.586l1.293-1.293a1 1 0 1 1 1.414 1.414l-3 3a1 1 0 0 1-1.414 0l-3-3a1 1 0 0 1 1.414-1.414L11 5.586V2a1 1 0 0 1 1-1ZM1 12a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2H2a1 1 0 0 1-1-1Zm6 0a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm6 0a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2h-2a1 1 0 0 1-1-1Zm6 0a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2h-2a1 1 0 0 1-1-1ZM8.293 18.293l3-3a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1-1.414 1.414L13 18.414V22a1 1 0 1 1-2 0v-3.586l-1.293 1.293a1 1 0 0 1-1.414-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$5J;
function _extends$5K() { _extends$5K = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5K.apply(this, arguments); }
var SvgFolder = function SvgFolder(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5K({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5J || (_path$5J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 2.879A3 3 0 0 1 4 2h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4c-1.652 0-3-1.348-3-3V5a3 3 0 0 1 .879-2.121ZM4 4a1 1 0 0 0-1 1v13c0 .548.452 1 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Z",
    clipRule: "evenodd"
  })));
};

var _path$5I;
function _extends$5J() { _extends$5J = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5J.apply(this, arguments); }
var SvgFolderArchive = function SvgFolderArchive(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5J({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5I || (_path$5I = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 2.879A3 3 0 0 1 4 2h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v12a1 1 0 1 1-2 0V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4a1 1 0 0 0-1 1v13c0 .548.452 1 1 1h6a1 1 0 1 1 0 2H4c-1.652 0-3-1.348-3-3V5a3 3 0 0 1 .879-2.121ZM16 9a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0v-1a1 1 0 0 1 1-1Zm0 5a1 1 0 0 1 1 1v1.17a3.001 3.001 0 1 1-2 0V15a1 1 0 0 1 1-1Zm0 4a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z",
    clipRule: "evenodd"
  })));
};

var _path$5H;
function _extends$5I() { _extends$5I = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5I.apply(this, arguments); }
var SvgFolderCheck = function SvgFolderCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5I({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5H || (_path$5H = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 2.879A3 3 0 0 1 4 2h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4c-1.652 0-3-1.348-3-3V5a3 3 0 0 1 .879-2.121ZM4 4a1 1 0 0 0-1 1v13c0 .548.452 1 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm11.707 6.293a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 1 1 1.414-1.414L11 13.586l3.293-3.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$5G;
function _extends$5H() { _extends$5H = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5H.apply(this, arguments); }
var SvgFolderClock = function SvgFolderClock(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5H({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5G || (_path$5G = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 4c-.548 0-1 .452-1 1v13a1 1 0 0 0 1 1h3a1 1 0 1 1 0 2H4a3 3 0 0 1-3-3V5c0-1.652 1.348-3 3-3h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3 1 1 0 1 1-2 0 1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm-7 5a7 7 0 1 1 14 0 7 7 0 0 1-14 0Zm7-3a1 1 0 0 1 1 1v1.586l.707.707a1 1 0 0 1-1.414 1.414l-1-1A1 1 0 0 1 15 16v-2a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$5F;
function _extends$5G() { _extends$5G = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5G.apply(this, arguments); }
var SvgFolderClosed = function SvgFolderClosed(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5G({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5F || (_path$5F = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 2.879A3 3 0 0 1 4 2h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4c-1.652 0-3-1.348-3-3V5a3 3 0 0 1 .879-2.121ZM3 11v7c0 .548.452 1 1 1h16a1 1 0 0 0 1-1v-7H3Zm18-2H3V5a1 1 0 0 1 1-1h3.927a1 1 0 0 1 .828.45l.01.014.815 1.194A3 3 0 0 0 12.065 7H20a1 1 0 0 1 1 1v1Z",
    clipRule: "evenodd"
  })));
};

var _path$5E;
function _extends$5F() { _extends$5F = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5F.apply(this, arguments); }
var SvgFolderCog = function SvgFolderCog(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5F({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5E || (_path$5E = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 4c-.548 0-1 .452-1 1v13a1 1 0 0 0 1 1h6.5a1 1 0 1 1 0 2H4a3 3 0 0 1-3-3V5c0-1.652 1.348-3 3-3h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v3a1 1 0 1 1-2 0V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm14 9a1 1 0 0 1 1 1v.126c.367.095.714.24 1.032.428l.261-.261a1 1 0 0 1 1.414 1.414l-.26.261c.187.318.332.665.427 1.032H22a1 1 0 1 1 0 2h-.126c-.095.367-.24.714-.428 1.032l.261.26a1 1 0 0 1-1.414 1.415l-.261-.26a3.976 3.976 0 0 1-1.032.427V22a1 1 0 1 1-2 0v-.126a3.977 3.977 0 0 1-1.032-.428l-.26.261a1 1 0 0 1-1.415-1.414l.26-.261A3.977 3.977 0 0 1 14.127 19H14a1 1 0 1 1 0-2h.126c.095-.367.24-.714.428-1.032l-.261-.26a1 1 0 0 1 1.414-1.415l.261.26A3.977 3.977 0 0 1 17 14.127V14a1 1 0 0 1 1-1Zm-1.45 3.622a.99.99 0 0 0 .072-.071A1.989 1.989 0 0 1 18 16a1.993 1.993 0 0 1 1.449.621c.341.359.551.844.551 1.379a1.993 1.993 0 0 1-.62 1.448A1.994 1.994 0 0 1 18 20a1.993 1.993 0 0 1-1.45-.622A1.993 1.993 0 0 1 16 18c0-.534.21-1.02.55-1.378Z",
    clipRule: "evenodd"
  })));
};

var _path$5D;
function _extends$5E() { _extends$5E = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5E.apply(this, arguments); }
var SvgFolderCogInside = function SvgFolderCogInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5E({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5D || (_path$5D = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 2.879A3 3 0 0 1 4 2h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4c-1.652 0-3-1.348-3-3V5a3 3 0 0 1 .879-2.121ZM4 4a1 1 0 0 0-1 1v13c0 .548.452 1 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm8 5a1 1 0 0 1 1 1v.17c.351.125.673.313.95.55l.152-.087a1 1 0 0 1 .996 1.734l-.148.085a3.014 3.014 0 0 1 0 1.096l.148.085a1 1 0 0 1-.996 1.734l-.152-.087a2.996 2.996 0 0 1-.95.55V16a1 1 0 1 1-2 0v-.17a3 3 0 0 1-.95-.55l-.152.087a1 1 0 0 1-.996-1.734l.148-.085a3.017 3.017 0 0 1 0-1.096l-.148-.085a1 1 0 1 1 .996-1.734l.152.087a3 3 0 0 1 .95-.55V10a1 1 0 0 1 1-1Zm-.887 3.538a1.124 1.124 0 0 0 .043-.075 1 1 0 1 1-.043.075Z",
    clipRule: "evenodd"
  })));
};

var _path$5C;
function _extends$5D() { _extends$5D = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5D.apply(this, arguments); }
var SvgFolderDot = function SvgFolderDot(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5D({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5C || (_path$5C = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 2.879A3 3 0 0 1 4 2h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4c-1.652 0-3-1.348-3-3V5a3 3 0 0 1 .879-2.121ZM4 4a1 1 0 0 0-1 1v13c0 .548.452 1 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm6 9a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z",
    clipRule: "evenodd"
  })));
};

var _path$5B;
function _extends$5C() { _extends$5C = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5C.apply(this, arguments); }
var SvgFolderDown = function SvgFolderDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5C({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5B || (_path$5B = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 2.879A3 3 0 0 1 4 2h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4c-1.652 0-3-1.348-3-3V5a3 3 0 0 1 .879-2.121ZM4 4a1 1 0 0 0-1 1v13c0 .548.452 1 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm8 5a1 1 0 0 1 1 1v3.586l1.293-1.293a1 1 0 0 1 1.414 1.414l-3 3a1 1 0 0 1-1.414 0l-3-3a1 1 0 1 1 1.414-1.414L11 13.586V10a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$5A;
function _extends$5B() { _extends$5B = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5B.apply(this, arguments); }
var SvgFolderEdit = function SvgFolderEdit(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5B({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5A || (_path$5A = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 4c-.548 0-1 .452-1 1v6.5a1 1 0 1 1-2 0V5c0-1.652 1.348-3 3-3h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3h-9.5a1 1 0 0 1 0-2H20a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm5.905 6.995a1.102 1.102 0 0 0-.777.322L3.893 16.56l-.516 2.059 2.062-.522 5.244-5.225a1.104 1.104 0 0 0 .238-1.199 1.1 1.1 0 0 0-1.016-.68ZM8.719 9.23a3.1 3.1 0 0 1 3.378 5.056l-.001.001-5.44 5.42a1 1 0 0 1-.46.261l-3.95 1a1 1 0 0 1-1.216-1.212l.99-3.95a1 1 0 0 1 .262-.463l5.43-5.44c.288-.288.63-.517 1.007-.673Z",
    clipRule: "evenodd"
  })));
};

var _path$5z;
function _extends$5A() { _extends$5A = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5A.apply(this, arguments); }
var SvgFolderFilled = function SvgFolderFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5A({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5z || (_path$5z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 2.879A3 3 0 0 1 4 2h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4c-1.652 0-3-1.348-3-3V5a3 3 0 0 1 .879-2.121Z",
    clipRule: "evenodd"
  })));
};

var _path$5y;
function _extends$5z() { _extends$5z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5z.apply(this, arguments); }
var SvgFolderGit = function SvgFolderGit(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5y || (_path$5y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.39 4.117A1 1 0 0 0 7.91 4H4a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h5a1 1 0 1 1 0 2H4a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3h3.896a3 3 0 0 1 2.526 1.345l.813 1.205a1 1 0 0 0 .835.45H20a3 3 0 0 1 3 3v5a1 1 0 1 1-2 0V8a1 1 0 0 0-1-1h-7.93a3 3 0 0 1-2.502-1.345L8.755 4.45a1 1 0 0 0-.365-.333ZM13 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2Zm-3 1a3 3 0 1 1 4.076 2.801 3.926 3.926 0 0 0 3.123 3.123 3.001 3.001 0 1 1-.048 2.018A5.953 5.953 0 0 1 14 18.5V22a1 1 0 1 1-2 0v-7.17A3.001 3.001 0 0 1 10 12Zm10 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z",
    clipRule: "evenodd"
  })));
};

var _path$5x;
function _extends$5y() { _extends$5y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5y.apply(this, arguments); }
var SvgFolderGitInside = function SvgFolderGitInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5x || (_path$5x = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 2.879A3 3 0 0 1 4 2h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4c-1.652 0-3-1.348-3-3V5a3 3 0 0 1 .879-2.121ZM4 4a1 1 0 0 0-1 1v13c0 .548.452 1 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm8 8a1 1 0 1 0 0 2 1 1 0 0 0 0-2Zm-2.83 0a3.001 3.001 0 0 1 5.66 0H17a1 1 0 1 1 0 2h-2.17a3.001 3.001 0 0 1-5.66 0H7a1 1 0 1 1 0-2h2.17Z",
    clipRule: "evenodd"
  })));
};

var _path$5w;
function _extends$5x() { _extends$5x = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5x.apply(this, arguments); }
var SvgFolderHeart = function SvgFolderHeart(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5x({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5w || (_path$5w = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 4c-.548 0-1 .452-1 1v13a1 1 0 0 0 1 1h7a1 1 0 1 1 0 2H4a3 3 0 0 1-3-3V5c0-1.652 1.348-3 3-3h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v1.5a1 1 0 1 1-2 0V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm14.262 8.254a3.43 3.43 0 0 1 3.733.737.966.966 0 0 1 .09.102c1.288 1.373 1.24 3.533-.275 5.061l-.008.008-3.6 3.55a1 1 0 0 1-1.405-.001L13.2 18.154c-1.544-1.557-1.58-3.781-.195-5.153a3.43 3.43 0 0 1 4.49-.313c.234-.176.491-.323.767-.434Zm1.59 1.765a1.43 1.43 0 0 0-.827.084l-.013.005a1.379 1.379 0 0 0-.465.31l-.34.34a1 1 0 0 1-1.404.01l-.35-.34-.011-.012a1.43 1.43 0 0 0-1.56-.312l-.01.005c-.16.063-.309.162-.455.308l-.003.004c-.514.508-.649 1.46.203 2.322l2.884 2.852 2.893-2.853c.846-.856.71-1.809.193-2.321a.983.983 0 0 1-.066-.072 1.43 1.43 0 0 0-.67-.33Z",
    clipRule: "evenodd"
  })));
};

var _path$5v;
function _extends$5w() { _extends$5w = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5w.apply(this, arguments); }
var SvgFolderInput = function SvgFolderInput(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5w({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5v || (_path$5v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 4c-.548 0-1 .452-1 1v4a1 1 0 0 1-2 0V5c0-1.652 1.348-3 3-3h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4a3 3 0 0 1-3-3v-1a1 1 0 1 1 2 0v1a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm4.293 5.293a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1 0 1.414l-3 3a1 1 0 0 1-1.414-1.414L9.586 14H2a1 1 0 1 1 0-2h7.586l-1.293-1.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$5u;
function _extends$5v() { _extends$5v = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5v.apply(this, arguments); }
var SvgFolderKanban = function SvgFolderKanban(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5v({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5u || (_path$5u = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 2.879A3 3 0 0 1 4 2h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4c-1.652 0-3-1.348-3-3V5a3 3 0 0 1 .879-2.121ZM4 4a1 1 0 0 0-1 1v13c0 .548.452 1 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm4 5a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0v-4a1 1 0 0 1 1-1Zm4 0a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1Zm4 0a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$5t;
function _extends$5u() { _extends$5u = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5u.apply(this, arguments); }
var SvgFolderKey = function SvgFolderKey(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5u({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5t || (_path$5t = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 4c-.548 0-1 .452-1 1v13a1 1 0 0 0 1 1h6a1 1 0 1 1 0 2H4a3 3 0 0 1-3-3V5c0-1.652 1.348-3 3-3h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v2a1 1 0 1 1-2 0V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm18.707 9.293a1 1 0 0 1 0 1.414l-.293.293.293.293a1 1 0 0 1-1.414 1.414L21 16.414l-2.292 2.293a3 3 0 1 1-1.414-1.414l2.998-3h.001l1-1a1 1 0 0 1 1.414 0ZM16 19a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z",
    clipRule: "evenodd"
  })));
};

var _path$5s;
function _extends$5t() { _extends$5t = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5t.apply(this, arguments); }
var SvgFolderLock = function SvgFolderLock(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5t({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5s || (_path$5s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 4c-.548 0-1 .452-1 1v13a1 1 0 0 0 1 1h6a1 1 0 1 1 0 2H4a3 3 0 0 1-3-3V5c0-1.652 1.348-3 3-3h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v2.5a1 1 0 1 1-2 0V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm11.879 8.879A3 3 0 0 1 21 15v1a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-6a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2v-1a3 3 0 0 1 .879-2.121ZM15 18v3h6v-3h-6Zm4-2h-2v-1a1 1 0 0 1 2 0v1Z",
    clipRule: "evenodd"
  })));
};

var _path$5r;
function _extends$5s() { _extends$5s = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5s.apply(this, arguments); }
var SvgFolderMinus = function SvgFolderMinus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5s({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5r || (_path$5r = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 2.879A3 3 0 0 1 4 2h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4c-1.652 0-3-1.348-3-3V5a3 3 0 0 1 .879-2.121ZM4 4a1 1 0 0 0-1 1v13c0 .548.452 1 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm4 9a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$5q, _path2$p;
function _extends$5r() { _extends$5r = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5r.apply(this, arguments); }
var SvgFolderOpen = function SvgFolderOpen(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5r({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5q || (_path$5q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 2a1 1 0 0 0-1 1v15a3 3 0 0 0 3 3h15a.996.996 0 0 0 .5-.133.996.996 0 0 0 .403-.188c.527-.407 1.289-1.284 1.455-1.929l1.55-6A3 3 0 0 0 21 9.17V8a3 3 0 0 0-3-3h-5.927a1 1 0 0 1-.828-.45l-.01-.014-.815-1.194A3 3 0 0 0 7.935 2H2Zm1 2v14a1 1 0 0 0 1 1h14.794c.221-.199.582-.572.628-.75l1.55-6a1.001 1.001 0 0 0-.97-1.25A1 1 0 0 1 19 10V8a1 1 0 0 0-1-1h-5.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H3Z",
    clipRule: "evenodd"
  })), _path2$p || (_path2$p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.238 11a1 1 0 0 0-.894.549l-.001.001-1.449 2.897a1 1 0 1 1-1.788-.894l1.45-2.903a3 3 0 0 1 1.11-1.206C8.138 9.153 8.443 9 9 9h11a1 1 0 1 1 0 2H9.238Z",
    clipRule: "evenodd"
  })));
};

var _path$5p;
function _extends$5q() { _extends$5q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5q.apply(this, arguments); }
var SvgFolderOutput = function SvgFolderOutput(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5p || (_path$5p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 4c-.548 0-1 .452-1 1v2.5a1 1 0 0 1-2 0V5c0-1.652 1.348-3 3-3h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H2a1 1 0 1 1 0-2h18a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm1.707 5.293a1 1 0 0 1 0 1.414L4.414 12H12a1 1 0 1 1 0 2H4.414l1.293 1.293a1 1 0 1 1-1.414 1.414l-3-3a1 1 0 0 1 0-1.414l3-3a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$5o;
function _extends$5p() { _extends$5p = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5p.apply(this, arguments); }
var SvgFolderPlus = function SvgFolderPlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5p({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5o || (_path$5o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 2.879A3 3 0 0 1 4 2h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4c-1.652 0-3-1.348-3-3V5a3 3 0 0 1 .879-2.121ZM4 4a1 1 0 0 0-1 1v13c0 .548.452 1 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm8 5a1 1 0 0 1 1 1v2h2a1 1 0 1 1 0 2h-2v2a1 1 0 1 1-2 0v-2H9a1 1 0 1 1 0-2h2v-2a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$5n;
function _extends$5o() { _extends$5o = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5o.apply(this, arguments); }
var SvgFolderRoot = function SvgFolderRoot(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5o({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5n || (_path$5n = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 2.879A3 3 0 0 1 4 2h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4c-1.652 0-3-1.348-3-3V5a3 3 0 0 1 .879-2.121ZM13 19h7a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4a1 1 0 0 0-1 1v13c0 .548.452 1 1 1h7v-3.17a3.001 3.001 0 1 1 2 0V19Zm-1-7a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z",
    clipRule: "evenodd"
  })));
};

var _path$5m;
function _extends$5n() { _extends$5n = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5n.apply(this, arguments); }
var SvgFolderSearch = function SvgFolderSearch(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5n({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5m || (_path$5m = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 4c-.548 0-1 .452-1 1v13a1 1 0 0 0 1 1h7a1 1 0 1 1 0 2H4a3 3 0 0 1-3-3V5c0-1.652 1.348-3 3-3h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v4a1 1 0 1 1-2 0V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm13 11a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-4 2a4 4 0 1 1 7.446 2.032l1.261 1.26a1 1 0 0 1-1.414 1.415l-1.261-1.26A4 4 0 0 1 13 17Z",
    clipRule: "evenodd"
  })));
};

var _path$5l;
function _extends$5m() { _extends$5m = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5m.apply(this, arguments); }
var SvgFolderSearchInside = function SvgFolderSearchInside(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5m({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5l || (_path$5l = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 2.879A3 3 0 0 1 4 2h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4c-1.652 0-3-1.348-3-3V5a3 3 0 0 1 .879-2.121ZM4 4a1 1 0 0 0-1 1v13c0 .548.452 1 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm7.5 7a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3ZM8 12.5a3.5 3.5 0 1 1 6.58 1.665l1.127 1.128a1 1 0 1 1-1.414 1.414l-1.128-1.128A3.5 3.5 0 0 1 8 12.5Z",
    clipRule: "evenodd"
  })));
};

var _path$5k;
function _extends$5l() { _extends$5l = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5l.apply(this, arguments); }
var SvgFolderSymlink = function SvgFolderSymlink(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5l({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5k || (_path$5k = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 4c-.548 0-1 .452-1 1v4a1 1 0 0 1-2 0V5c0-1.652 1.348-3 3-3h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H2a1 1 0 1 1 0-2h18a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm3.293 5.293a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1 0 1.414l-3 3a1 1 0 0 1-1.414-1.414L8.586 14H4a1 1 0 0 0-1 1v1a1 1 0 1 1-2 0v-1a3 3 0 0 1 3-3h4.586l-1.293-1.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$5j;
function _extends$5k() { _extends$5k = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5k.apply(this, arguments); }
var SvgFolderSync = function SvgFolderSync(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5k({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5j || (_path$5j = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.39 4.117A1 1 0 0 0 7.91 4H4a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h5a1 1 0 1 1 0 2H4a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3h3.896a3 3 0 0 1 2.526 1.345l.813 1.205a1 1 0 0 0 .835.45H20a3 3 0 0 1 3 3v1a1 1 0 1 1-2 0V8a1 1 0 0 0-1-1h-7.93a3 3 0 0 1-2.502-1.345L8.755 4.45a1 1 0 0 0-.365-.333ZM12 9a1 1 0 0 1 1 1v1.595C14.055 10.637 15.507 10 17 10c1.593 0 3.14.726 4.207 1.793.49.49.969 1.204 1.221 1.836a1 1 0 1 1-1.857.742 3.936 3.936 0 0 0-.778-1.164C19.06 12.474 18.007 12 17 12c-.909 0-1.855.386-2.57 1H16a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1Zm.129 8.072a1 1 0 0 1 1.3.557c.147.367.469.855.778 1.164C14.95 19.534 15.904 20 17 20c.99 0 1.866-.38 2.572-1H18a1 1 0 1 1 0-2h4a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0v-1.595C19.963 21.356 18.595 22 17 22c-1.704 0-3.149-.734-4.207-1.793a5.917 5.917 0 0 1-1.221-1.836 1 1 0 0 1 .557-1.3Z",
    clipRule: "evenodd"
  })));
};

var _path$5i;
function _extends$5j() { _extends$5j = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5j.apply(this, arguments); }
var SvgFolderTree = function SvgFolderTree(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5j({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5i || (_path$5i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 2a1 1 0 0 1 1 1v2c0 .548.452 1 1 1h3a1 1 0 0 1 0 2H5c-.35 0-.687-.06-1-.172V16c0 .548.452 1 1 1h3a1 1 0 1 1 0 2H5c-1.652 0-3-1.348-3-3V3a1 1 0 0 1 1-1Zm8.586.586A2 2 0 0 1 13 2h2a2 2 0 0 1 1.6.8l.9 1.2H20a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2V4a2 2 0 0 1 .586-1.414ZM15 4h-2v5h7V6h-2.5a2 2 0 0 1-1.6-.8L15 4Zm-.123 9a2 2 0 0 1 1.796 1.1l.005.01.435.89H20a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h1.877Zm.003 2H13v5h7v-3h-2.877m-2.243-2h.007-.007Z",
    clipRule: "evenodd"
  })));
};

var _path$5h;
function _extends$5i() { _extends$5i = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5i.apply(this, arguments); }
var SvgFolderUp = function SvgFolderUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5i({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5h || (_path$5h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 2.879A3 3 0 0 1 4 2h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4c-1.652 0-3-1.348-3-3V5a3 3 0 0 1 .879-2.121ZM4 4a1 1 0 0 0-1 1v13c0 .548.452 1 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm7.293 5.293a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1-1.414 1.414L13 12.414V16a1 1 0 1 1-2 0v-3.586l-1.293 1.293a1 1 0 0 1-1.414-1.414l3-3Z",
    clipRule: "evenodd"
  })));
};

var _path$5g;
function _extends$5h() { _extends$5h = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5h.apply(this, arguments); }
var SvgFolderX = function SvgFolderX(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5h({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5g || (_path$5g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 2.879A3 3 0 0 1 4 2h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4c-1.652 0-3-1.348-3-3V5a3 3 0 0 1 .879-2.121ZM4 4a1 1 0 0 0-1 1v13c0 .548.452 1 1 1h16a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-7.935A3 3 0 0 1 9.58 5.658l-.816-1.194-.01-.014A1 1 0 0 0 7.928 4H4Zm4.793 5.793a1 1 0 0 1 1.414 0L12 11.586l1.793-1.793a1 1 0 1 1 1.414 1.414L13.414 13l1.793 1.793a1 1 0 0 1-1.414 1.414L12 14.414l-1.793 1.793a1 1 0 0 1-1.414-1.414L10.586 13l-1.793-1.793a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$5f;
function _extends$5g() { _extends$5g = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5g.apply(this, arguments); }
var SvgFolders = function SvgFolders(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5g({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5f || (_path$5f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.879 3.879A3 3 0 0 1 8 3h3.935a3 3 0 0 1 2.485 1.342l.816 1.194.01.014a1 1 0 0 0 .827.45H20a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H8c-1.652 0-3-1.348-3-3V6a3 3 0 0 1 .879-2.121ZM8 5a1 1 0 0 0-1 1v9c0 .548.452 1 1 1h12a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1h-3.935a3 3 0 0 1-2.485-1.342l-.816-1.194-.01-.014a1 1 0 0 0-.827-.45H8ZM2 7a1 1 0 0 1 1 1v11c0 .548.452 1 1 1h14a1 1 0 1 1 0 2H4c-1.652 0-3-1.348-3-3V8a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$5e;
function _extends$5f() { _extends$5f = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5f.apply(this, arguments); }
var SvgFrown = function SvgFrown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5f({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5e || (_path$5e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm7-3a1 1 0 0 1 1-1h.01a1 1 0 0 1 0 2H9a1 1 0 0 1-1-1Zm6 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H15a1 1 0 0 1-1-1Zm-5.526 5.191c.798-.58 2-1.191 3.526-1.191 1.525 0 2.728.61 3.526 1.191a6.944 6.944 0 0 1 1.17 1.08c.03.035.053.064.07.086l.022.027.007.01.003.003.001.002S16.8 15.4 16 16l.8-.6a1 1 0 0 1-1.597 1.204l-.005-.006a3.709 3.709 0 0 0-.194-.219 4.953 4.953 0 0 0-.655-.57C13.772 15.389 12.975 15 12 15c-.975 0-1.772.39-2.35.809a4.952 4.952 0 0 0-.815.748l-.034.041-.004.006.001-.002H8.8m0 0-.002.002A1 1 0 0 1 7.2 15.4l.8.6-.8-.6.002-.003.003-.004.007-.009.021-.027.07-.086a6.942 6.942 0 0 1 1.171-1.08",
    clipRule: "evenodd"
  })));
};

var _path$5d;
function _extends$5e() { _extends$5e = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5e.apply(this, arguments); }
var SvgFullScreen = function SvgFullScreen(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5e({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5d || (_path$5d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 3a1 1 0 0 1 1-1h4.8a1 1 0 0 1 0 2H5.414l4.293 4.293a1 1 0 0 1-1.414 1.414L4 5.414V7.8a1 1 0 0 1-2 0V3Zm14.2-1H21a1 1 0 0 1 1 1v4.8a1 1 0 1 1-2 0V5.414l-4.293 4.293a1 1 0 0 1-1.414-1.414L18.586 4H16.2a1 1 0 1 1 0-2ZM9.707 14.293a1 1 0 0 1 0 1.414L5.414 20H7.8a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1v-4.8a1 1 0 1 1 2 0v2.386l4.293-4.293a1 1 0 0 1 1.414 0Zm4.586 0a1 1 0 0 1 1.414 0L20 18.586V16.2a1 1 0 1 1 2 0V21a1 1 0 0 1-1 1h-4.8a1 1 0 1 1 0-2h2.386l-4.293-4.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$5c;
function _extends$5d() { _extends$5d = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5d.apply(this, arguments); }
var SvgFullScreenOff = function SvgFullScreenOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5d({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5c || (_path$5c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 2a1 1 0 0 1 1 1v3a3 3 0 0 1-3 3H3a1 1 0 0 1 0-2h3a1 1 0 0 0 1-1V3a1 1 0 0 1 1-1Zm8 0a1 1 0 0 1 1 1v3a1 1 0 0 0 1 1h3a1 1 0 1 1 0 2h-3a3 3 0 0 1-3-3V3a1 1 0 0 1 1-1ZM2 16a1 1 0 0 1 1-1h3a3 3 0 0 1 3 3v3a1 1 0 1 1-2 0v-3a1 1 0 0 0-1-1H3a1 1 0 0 1-1-1Zm16 1a1 1 0 0 0-1 1v3a1 1 0 1 1-2 0v-3a3 3 0 0 1 3-3h3a1 1 0 1 1 0 2h-3Z",
    clipRule: "evenodd"
  })));
};

var _path$5b;
function _extends$5c() { _extends$5c = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5c.apply(this, arguments); }
var SvgGauge = function SvgGauge(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5c({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5b || (_path$5b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 5a9 9 0 0 0-7.794 13.5 1 1 0 0 1-1.732 1 11 11 0 1 1 19.052 0 1 1 0 0 1-1.732-1A9 9 0 0 0 12 5Zm4.707 4.293a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414-1.414l4-4a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$5a;
function _extends$5b() { _extends$5b = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5b.apply(this, arguments); }
var SvgGaugeCircle = function SvgGaugeCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5b({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5a || (_path$5a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.611 1.55a11 11 0 0 1 7.35.218 1 1 0 0 1-.723 1.864 9 9 0 1 0 5.13 5.13 1 1 0 0 1 1.864-.724A11 11 0 1 1 8.61 1.549Zm11.096 2.743a1 1 0 0 1 0 1.414l-5 5a3 3 0 1 1-1.414-1.414l5-5a1 1 0 0 1 1.414 0Zm-7.108 6.906a1 1 0 1 0 .201.201.994.994 0 0 1-.2-.2Z",
    clipRule: "evenodd"
  })));
};

var _path$59, _path2$o;
function _extends$5a() { _extends$5a = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5a.apply(this, arguments); }
var SvgGearFilled = function SvgGearFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5a({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$59 || (_path$59 = /*#__PURE__*/React.createElement("path", {
    d: "M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"
  })), _path2$o || (_path2$o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "m20.555 10.49-.176.088c-.353.175-.53.525-.486.963v.962c-.044.394.133.788.486.963l.132.088a1.9 1.9 0 0 1 .883 1.137c.132.482.088.963-.177 1.4l-1.015 1.752c-.485.919-1.676 1.225-2.559.7l-.176-.088c-.353-.219-.75-.175-1.104.044-.083.047-.164.095-.243.142a5.514 5.514 0 0 1-.639.34 1.02 1.02 0 0 0-.574.918v.22c0 1.05-.838 1.881-1.897 1.881h-2.074a1.876 1.876 0 0 1-1.897-1.882V19.9c0-.393-.22-.744-.574-.919a5.664 5.664 0 0 1-.882-.481 1.106 1.106 0 0 0-1.103-.044l-.177.088a1.95 1.95 0 0 1-1.412.175 1.91 1.91 0 0 1-1.147-.875l-1.015-1.751c-.485-.875-.176-2.013.706-2.538l.177-.088c.309-.175.53-.569.485-.963v-.963c.044-.393-.132-.787-.485-.962l-.133-.132c-.926-.481-1.235-1.663-.706-2.538l1.015-1.75a1.91 1.91 0 0 1 1.147-.876c.486-.131.971-.087 1.412.175l.177.088c.353.219.75.175 1.103-.044.084-.047.164-.095.244-.142.212-.125.413-.244.638-.34a1.02 1.02 0 0 0 .574-.918v-.22C9.083 2.832 9.921 2 10.98 2h2.074c1.06 0 1.898.832 1.898 1.882v.262c0 .394.22.744.573.92.309.13.618.306.883.48.353.22.75.22 1.103.045l.176-.088a1.938 1.938 0 0 1 2.56.7l1.014 1.75c.22.395.31.92.177 1.401a1.898 1.898 0 0 1-.883 1.138Zm-8.56 5.383a3.858 3.858 0 0 0 2.736-1.138c.75-.7 1.147-1.707 1.147-2.713 0-1.007-.441-1.97-1.147-2.713-.706-.744-1.677-1.138-2.736-1.138A3.858 3.858 0 0 0 9.26 9.309c-.75.7-1.148 1.706-1.148 2.713 0 1.006.397 1.97 1.148 2.713.706.744 1.676 1.138 2.735 1.138Z",
    clipRule: "evenodd"
  })));
};

var _path$58, _path2$n;
function _extends$59() { _extends$59 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$59.apply(this, arguments); }
var SvgGearOutline = function SvgGearOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$59({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$58 || (_path$58 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0-2a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z",
    clipRule: "evenodd"
  })), _path2$n || (_path2$n = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M21.09 10.032c.16-.198.278-.427.348-.68a1.905 1.905 0 0 0-.177-1.4l-1.014-1.75a1.936 1.936 0 0 0-2.56-.7l-.176.087c-.353.175-.75.175-1.103-.044a5.667 5.667 0 0 0-.883-.482 1.02 1.02 0 0 1-.573-.919v-.262c0-1.05-.839-1.882-1.898-1.882H10.98a1.876 1.876 0 0 0-1.897 1.882V4.1c0 .394-.22.744-.574.919a5.451 5.451 0 0 0-.638.34c-.08.046-.16.094-.244.141-.353.219-.75.263-1.103.044l-.177-.088c-.44-.262-.926-.306-1.412-.175a1.91 1.91 0 0 0-1.147.876l-1.015 1.75c-.53.875-.22 2.057.706 2.538l.133.132c.353.175.53.569.485.963v.962c.044.394-.176.788-.485.963l-.177.088c-.882.525-1.191 1.663-.706 2.538l1.015 1.75a1.91 1.91 0 0 0 1.147.876 1.95 1.95 0 0 0 1.412-.175l.177-.088c.353-.175.75-.175 1.103.044.265.175.573.35.882.481.353.175.574.526.574.92v.218c0 1.05.838 1.882 1.897 1.882h2.074a1.876 1.876 0 0 0 1.898-1.882V19.9c0-.393.22-.744.573-.919.225-.095.427-.214.639-.34.079-.046.16-.094.244-.141.353-.219.75-.263 1.103-.044l.176.088c.883.525 2.074.219 2.56-.7l1.014-1.751c.265-.438.31-.92.177-1.4a1.898 1.898 0 0 0-.883-1.138l-.132-.088c-.317-.157-.492-.49-.492-.842.13-1.397.473-1.746.802-2.083a2.14 2.14 0 0 0 .4-.509Zm-3.24 2.527a.76.76 0 0 1-.006-.059c-.051-1 .458-2.998 1.63-3.5l.05-.08-.035.02-.942-1.624-.116.057a3.178 3.178 0 0 1-3.113-.137l-.025-.016-.025-.017a3.628 3.628 0 0 0-.554-.304l-.055-.024-.054-.027a3.016 3.016 0 0 1-1.692-2.704V4h-1.79v.1a3.016 3.016 0 0 1-1.694 2.705l-.053.026-.056.024c-.099.042-.154.075-.282.15-.081.049-.193.115-.364.212-.823.494-2.013.72-3.136.08l-.05-.025-.871 1.502.093.048.184.183c.932.64 1.3 1.698 1.242 2.614v.805a3.006 3.006 0 0 1-1.504 2.774l-.05.028-.007.003.869 1.499.116-.058a3.179 3.179 0 0 1 3.112.138l.026.016.025.016c.186.124.385.234.553.305l.055.024.054.026a3.016 3.016 0 0 1 1.693 2.704V20h1.79v-.1c0-1.146.651-2.188 1.693-2.704l.054-.027.055-.024c.1-.042.155-.075.282-.15.082-.049.193-.115.364-.212.823-.494 2.013-.72 3.136-.08l.05.025.877-1.511-.063-.042c-1.045-.589-1.483-1.663-1.467-2.616Z",
    clipRule: "evenodd"
  })));
};

var _path$57;
function _extends$58() { _extends$58 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$58.apply(this, arguments); }
var SvgGlucometer = function SvgGlucometer(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$58({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$57 || (_path$57 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.5 2c.441.018.797.167 1.066.447.27.28.414.63.434 1.053v13c0 .404-.145.754-.434 1.049-.29.295-.655.445-1.096.451H13v4h-2v-4H7.5c-.416 0-.77-.15-1.064-.451A1.44 1.44 0 0 1 6 16.5v-13c.016-.419.162-.77.436-1.053.275-.283.63-.432 1.064-.447h9ZM11 14H9v2h2v-2Zm4 0h-2v2h2v-2Zm1-10H8v8h8V4Zm-4 1.5c1.333 1.264 2 2.264 2 3a2 2 0 1 1-4 0c0-.736.667-1.736 2-3Z",
    clipRule: "evenodd"
  })));
};

var _path$56;
function _extends$57() { _extends$57 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$57.apply(this, arguments); }
var SvgGripHorizontal = function SvgGripHorizontal(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$57({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$56 || (_path$56 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 9a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm7 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm7 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0ZM3 15a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm7 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm7 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z",
    clipRule: "evenodd"
  })));
};

var _path$55;
function _extends$56() { _extends$56 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$56.apply(this, arguments); }
var SvgHamburgerMenu = function SvgHamburgerMenu(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$56({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$55 || (_path$55 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 6a1 1 0 0 1 1-1h16a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1Zm0 6a1 1 0 0 1 1-1h16a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1Zm0 6a1 1 0 0 1 1-1h16a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$54;
function _extends$55() { _extends$55 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$55.apply(this, arguments); }
var SvgHand = function SvgHand(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$55({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$54 || (_path$54 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.879 1.879a3 3 0 0 1 5.015 1.332 3 3 0 0 1 4 2A3 3 0 0 1 23 8v6a9 9 0 0 1-9 9h-2c-3.069 0-5.02-.968-6.695-2.63l-.002-.003-3.6-3.6A3 3 0 0 1 5 11.931V6a3 3 0 0 1 4.106-2.789 3 3 0 0 1 .773-1.332ZM9 6v4.5a1 1 0 1 0 2 0V4a1 1 0 0 1 2 0v6a1 1 0 1 0 2 0V6a1 1 0 0 1 2 0v5a1 1 0 1 0 2 0V8a1 1 0 0 1 2 0v6a7 7 0 0 1-7 7h-2c-2.53 0-3.978-.752-5.284-2.048l-.001-.002-3.577-3.576a1 1 0 0 1 1.415-1.407l.731.731a.838.838 0 0 0 .018.018l.991.991a1 1 0 0 0 1.414-1.414L7 13.586V6a1 1 0 0 1 2 0Z",
    clipRule: "evenodd"
  })));
};

var _path$53;
function _extends$54() { _extends$54 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$54.apply(this, arguments); }
var SvgHandMetal = function SvgHandMetal(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$54({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$53 || (_path$53 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.879 2.879A3 3 0 0 1 11 5v1.172a3 3 0 0 1 3.529 1.214A3 3 0 0 1 17 7.172V7a3 3 0 0 1 6 0v7a9 9 0 0 1-9 9h-2c-3.069 0-5.02-.968-6.695-2.63l-.002-.003-3.6-3.6A3 3 0 0 1 5 11.931V5a3 3 0 0 1 .879-2.121Zm-.577 11.837.991.991a1 1 0 0 0 1.414-1.414L7 13.586V5a1 1 0 0 1 2 0v6a1 1 0 1 0 2 0V9a1 1 0 0 1 2 0v2.4a1 1 0 1 0 2 0V10a1 1 0 0 1 2 0v2.5a1 1 0 1 0 2 0V7a1 1 0 0 1 2 0v7a7 7 0 0 1-7 7h-2c-2.53 0-3.978-.752-5.284-2.048l-.001-.002-3.577-3.576a1 1 0 0 1 1.415-1.407l.731.731a.838.838 0 0 0 .018.018Z",
    clipRule: "evenodd"
  })));
};

var _path$52;
function _extends$53() { _extends$53 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$53.apply(this, arguments); }
var SvgHeadphones = function SvgHeadphones(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$53({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$52 || (_path$52 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 0 0-8 8v1h2a3 3 0 0 1 3 3v3a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3v-7a10 10 0 0 1 20 0v7a3 3 0 0 1-3 3h-1a3 3 0 0 1-3-3v-3a3 3 0 0 1 3-3h2v-1a8 8 0 0 0-8-8Zm8 11h-2a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-4ZM4 15v4a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1H4Z",
    clipRule: "evenodd"
  })));
};

var _path$51;
function _extends$52() { _extends$52 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$52.apply(this, arguments); }
var SvgHeartCrack = function SvgHeartCrack(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$52({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$51 || (_path$51 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.904 3.904A6.5 6.5 0 0 1 7.5 2c.98 0 1.873.14 2.747.52.613.268 1.185.64 1.753 1.12.568-.48 1.14-.852 1.753-1.12C14.627 2.14 15.52 2 16.5 2A6.5 6.5 0 0 1 23 8.5c0 2.742-1.81 4.753-3.297 6.21l-6.996 6.997a1 1 0 0 1-1.414 0l-6.994-6.994C2.794 13.258 1 11.25 1 8.5a6.5 6.5 0 0 1 1.904-4.596Zm7.646 1.132a4.902 4.902 0 0 0-1.102-.682C8.887 4.11 8.28 4 7.5 4A4.5 4.5 0 0 0 3 8.5c0 1.847 1.2 3.336 2.695 4.781l.012.012L12 19.586l6.3-6.3c1.492-1.463 2.7-2.95 2.7-4.786A4.5 4.5 0 0 0 16.5 4c-.78 0-1.387.11-1.948.354-.568.248-1.152.66-1.845 1.353L11.414 7l2.293 2.293a1 1 0 0 1 0 1.414L12.414 12l.293.293a1 1 0 0 1-1.414 1.414l-1-1a1 1 0 0 1 0-1.414L11.586 10 9.293 7.707a1 1 0 0 1 0-1.414l1.257-1.257Z",
    clipRule: "evenodd"
  })));
};

var _path$50;
function _extends$51() { _extends$51 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$51.apply(this, arguments); }
var SvgHeartFilled = function SvgHeartFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$51({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$50 || (_path$50 = /*#__PURE__*/React.createElement("path", {
    d: "M7.5 2A6.5 6.5 0 0 0 1 8.5c0 2.75 1.794 4.758 3.299 6.213l6.994 6.994a1 1 0 0 0 1.414 0l6.996-6.996C21.191 13.253 23 11.24 23 8.5A6.5 6.5 0 0 0 16.5 2c-.98 0-1.873.14-2.747.52-.613.268-1.185.64-1.753 1.12-.568-.48-1.14-.852-1.753-1.12C9.373 2.14 8.48 2 7.5 2Z"
  })));
};

var _path$4$;
function _extends$50() { _extends$50 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$50.apply(this, arguments); }
var SvgHeartHandshake = function SvgHeartHandshake(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$50({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4$ || (_path$4$ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.904 3.904A6.5 6.5 0 0 1 7.5 2c.98 0 1.873.14 2.747.52.613.268 1.185.64 1.753 1.12.568-.48 1.14-.852 1.753-1.12C14.627 2.14 15.52 2 16.5 2A6.5 6.5 0 0 1 23 8.5c0 1.499-.54 2.78-1.274 3.87a.998.998 0 0 1-.083.122c-.592.853-1.296 1.588-1.94 2.219l-6.996 6.996a1 1 0 0 1-1.414 0l-6.994-6.994C2.794 13.258 1 11.25 1 8.5a6.5 6.5 0 0 1 1.904-4.596ZM15 16.586l-1.293-1.293a1 1 0 0 0-1.414 1.414L13.586 18 12 19.586l-6.293-6.293-.012-.012C4.201 11.836 3 10.347 3 8.5A4.5 4.5 0 0 1 7.5 4c.78 0 1.387.11 1.948.354.354.154.713.372 1.102.682L8.336 7.25l-.002.002a3.17 3.17 0 0 0 0 4.497 3.175 3.175 0 0 0 4.374.106l2.076-1.906a1.82 1.82 0 0 1 2.444.002l2.278 2.046c-.363.437-.773.864-1.206 1.289l-.3.3-1.293-1.293a1 1 0 0 0-1.414 1.414L16.586 15 15 16.586Zm5.604-6.29A4.386 4.386 0 0 0 21 8.5 4.5 4.5 0 0 0 16.5 4c-.78 0-1.387.11-1.948.354-.568.248-1.152.66-1.845 1.353l-2.96 2.96-.002.003a1.17 1.17 0 0 0 0 1.66l.002.003a1.175 1.175 0 0 0 1.622.036l2.07-1.9a3.82 3.82 0 0 1 5.131-.001l.002.001 2.032 1.826Z",
    clipRule: "evenodd"
  })));
};

var _path$4_;
function _extends$4$() { _extends$4$ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4$.apply(this, arguments); }
var SvgHeartOff = function SvgHeartOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4$({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4_ || (_path$4_ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 1.414 0L17.205 15.79a.028.028 0 0 0 .004.004l5.498 5.498a1 1 0 0 1-1.414 1.414L16.5 17.914l-3.793 3.793a1 1 0 0 1-1.414 0l-6.994-6.994C2.794 13.258 1 11.25 1 8.5a6.5 6.5 0 0 1 1.703-4.382l-1.41-1.41a1 1 0 0 1 0-1.415Zm2.827 4.24A4.5 4.5 0 0 0 3 8.5c0 1.847 1.2 3.337 2.695 4.781l.012.012L12 19.586l3.086-3.086L4.12 5.534Zm10.432-1.179c-.568.248-1.152.66-1.845 1.353a1 1 0 0 1-1.418-.003c-1.007-1.017-1.815-1.45-2.717-1.622a1 1 0 1 1 .376-1.964c1.123.215 2.089.702 3.054 1.52a7.09 7.09 0 0 1 1.751-1.117C14.627 2.14 15.52 2 16.5 2A6.5 6.5 0 0 1 23 8.5c0 2.547-1.566 4.46-2.958 5.872a1 1 0 0 1-1.424-1.404C19.965 11.601 21 10.193 21 8.5A4.5 4.5 0 0 0 16.5 4c-.78 0-1.387.11-1.948.354Z",
    clipRule: "evenodd"
  })));
};

var _path$4Z;
function _extends$4_() { _extends$4_ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4_.apply(this, arguments); }
var SvgHeartOutline = function SvgHeartOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4_({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4Z || (_path$4Z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.904 3.904A6.5 6.5 0 0 1 7.5 2c.98 0 1.873.14 2.747.52.613.268 1.185.64 1.753 1.12.568-.48 1.14-.852 1.753-1.12C14.627 2.14 15.52 2 16.5 2A6.5 6.5 0 0 1 23 8.5c0 2.742-1.81 4.753-3.297 6.21l-6.996 6.997a1 1 0 0 1-1.414 0l-6.994-6.994C2.794 13.258 1 11.25 1 8.5a6.5 6.5 0 0 1 1.904-4.596ZM7.5 4A4.5 4.5 0 0 0 3 8.5c0 1.847 1.2 3.336 2.695 4.781l.012.012L12 19.586l6.3-6.3c1.492-1.463 2.7-2.95 2.7-4.786A4.5 4.5 0 0 0 16.5 4c-.78 0-1.387.11-1.948.354-.568.248-1.152.66-1.845 1.353a1 1 0 0 1-1.414 0c-.693-.693-1.277-1.105-1.845-1.353C8.887 4.11 8.28 4 7.5 4Z",
    clipRule: "evenodd"
  })));
};

var _path$4Y;
function _extends$4Z() { _extends$4Z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4Z.apply(this, arguments); }
var SvgHeartPulse = function SvgHeartPulse(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4Z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4Y || (_path$4Y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.904 3.904A6.5 6.5 0 0 1 7.5 2c.98 0 1.873.14 2.747.52.613.268 1.185.64 1.753 1.12.568-.48 1.14-.852 1.753-1.12C14.627 2.14 15.52 2 16.5 2A6.5 6.5 0 0 1 23 8.5c0 1.563-.588 2.89-1.37 4.01a1.01 1.01 0 0 1-.092.13c-.569.79-1.228 1.477-1.835 2.07l-6.996 6.997a1 1 0 0 1-1.414 0l-6.994-6.994c-.614-.594-1.276-1.28-1.845-2.07a1.002 1.002 0 0 1-.096-.136C1.581 11.388 1 10.064 1 8.5a6.5 6.5 0 0 1 1.904-4.596ZM5.409 13c.094.094.19.188.286.281l.012.012L12 19.586l6.3-6.3c.097-.095.193-.19.287-.286H15.5a1 1 0 0 1-.919-.606l-.392-.915-1.227 4.296a1 1 0 0 1-1.876.131l-1.309-2.945A1 1 0 0 1 9.5 13H5.41Zm3.473-2h-5.12C3.29 10.228 3 9.407 3 8.5A4.5 4.5 0 0 1 7.5 4c.78 0 1.387.11 1.948.354.568.248 1.152.66 1.845 1.353a1 1 0 0 0 1.414 0c.693-.693 1.277-1.105 1.845-1.353C15.113 4.11 15.72 4 16.5 4A4.5 4.5 0 0 1 21 8.5c0 .905-.294 1.726-.77 2.5h-4.07l-1.24-2.894a1 1 0 0 0-1.881.12l-1.244 4.35-.881-1.982a1 1 0 0 0-1.808-.041L8.882 11Z",
    clipRule: "evenodd"
  })));
};

var _path$4X;
function _extends$4Y() { _extends$4Y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4Y.apply(this, arguments); }
var SvgHeartRateFilled = function SvgHeartRateFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4Y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4X || (_path$4X = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 20.994c-.26 0-.591-.123-.996-.369-1.027-.552-6.365-4.442-7.793-7.178h3.4l2.48 2.243c.287.235.567.34.84.313.273-.027.508-.183.703-.466l1.583-3.503 1.834 2.628c.168.2.365.313.591.338a.988.988 0 0 0 .615-.12l2.103-1.44h3.43c-1.423 2.75-6.857 6.566-7.792 7.185-.405.246-.738.37-.997.37L12 20.994Zm7.424-9.533-2.041-.008c-.328 0-.56.022-.696.068-.136.045-.302.145-.497.3l-1.1.767-2.155-3.008c-.217-.387-.53-.582-.935-.585-.407-.004-.7.191-.883.585l-1.665 3.744-1.68-1.546a1.276 1.276 0 0 0-.378-.257c-.103-.038-.276-.06-.519-.069H2.391A7.83 7.83 0 0 1 2 8.995C2 5.627 4.42 3 7.5 3c1.74 0 3.41.883 4.5 2.267C13.09 3.883 14.76 3 16.5 3 19.58 3 22 5.627 22 8.995c0 .82-.135 1.634-.393 2.466h-2.183Z",
    clipRule: "evenodd"
  })));
};

var _path$4W;
function _extends$4X() { _extends$4X = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4X.apply(this, arguments); }
var SvgHeartRateOutline = function SvgHeartRateOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4X({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4W || (_path$4W = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 20.994c-.438 0-.996-.369-.996-.369C5.77 16.992 2 13.81 2 8.995 2 5.627 4.42 3 7.5 3c1.74 0 3.41.883 4.5 2.267C13.09 3.883 14.76 3 16.5 3 19.58 3 22 5.627 22 8.995c0 4.815-3.803 7.997-9.003 11.63 0 0-.559.37-.997.37v-.001Zm0-2.002c1.904-1.499 4.524-2.913 6.394-5.55l-1.034.005-1.986 1.363c-.238.156-.481.22-.732.19-.25-.03-.464-.16-.642-.392l-1.783-2.574-1.47 3.29c-.136.353-.371.57-.707.654-.335.084-.652-.012-.95-.288l-2.479-2.243h-.998C7.423 16.079 10 17.477 12 18.992Zm7.424-7.54a5.602 5.602 0 0 0 .578-2.457C20.002 7 18.602 5 16.502 5c-2.231 0-3.41 1.616-4.5 3-1.09-1.384-2.345-3-4.5-3-2.032 0-3.5 2.001-3.5 3.995 0 .73.218 1.567.589 2.457h2.406c.192.012.34.043.444.093.104.051.27.175.495.371l1.516 1.408 1.67-3.783c.195-.364.488-.546.88-.546s.693.182.904.546l2.183 3.047 1.08-.762c.232-.156.429-.257.59-.304.162-.046.402-.07.721-.07h1.944Z",
    clipRule: "evenodd"
  })));
};

var _path$4V;
function _extends$4W() { _extends$4W = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4W.apply(this, arguments); }
var SvgHelpingHand = function SvgHelpingHand(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4W({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4V || (_path$4V = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20.62 8.005a.88.88 0 0 0-1.2.086l-4.065 4.768A3 3 0 0 1 13 14h-2.5a1 1 0 1 1 0-2H13a1 1 0 0 0 .74-.329l.062-.073A1 1 0 0 0 13 10h-2.76a2 2 0 0 0-1.412.587L4.414 15 7 17.586l.7-.701.002-.001A3 3 0 0 1 9.83 16h5.125-.003l.007 1v-1h-.004a2 2 0 0 0 1.617-.847l4.206-5.949a.88.88 0 0 0-.16-1.2ZM7.292 20.707a1 1 0 0 0 1.414-1.414L8.414 19l.703-.703.002-.002A1.001 1.001 0 0 1 9.83 18h5.138a4 4 0 0 0 3.24-1.693l4.197-5.936a2.88 2.88 0 0 0-4.495-3.59l-2.314 2.714A2.996 2.996 0 0 0 13 8h-2.76c-1.06 0-2.078.423-2.828 1.173L3 13.586l-.293-.293a1 1 0 0 0-1.414 1.414l6 6Z",
    clipRule: "evenodd"
  })));
};

var _path$4U;
function _extends$4V() { _extends$4V = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4V.apply(this, arguments); }
var SvgHighlighter = function SvgHighlighter(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4V({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4U || (_path$4U = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14.707 3.293a1 1 0 0 1 0 1.414L10.11 9.303a1 1 0 0 0 0 1.394l5.192 5.192a1 1 0 0 0 1.394 0l4.596-4.596a1 1 0 0 1 1.414 1.414L18.1 17.314a3 3 0 0 1-2.78.78l-2.613 2.613A1 1 0 0 1 12 21H3a1 1 0 0 1-1-1v-3a1 1 0 0 1 .293-.707l5.613-5.613a3 3 0 0 1 .78-2.78l.007-.007 4.6-4.6a1 1 0 0 1 1.414 0ZM9 12.414l-5 5V19h7.586l2-2L9 12.414Z",
    clipRule: "evenodd"
  })));
};

var _path$4T;
function _extends$4U() { _extends$4U = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4U.apply(this, arguments); }
var SvgHistory = function SvgHistory(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4U({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4T || (_path$4T = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.996 2H12A10 10 0 1 1 2 12a1 1 0 1 1 2 0 8 8 0 1 0 8.002-8A8.75 8.75 0 0 0 5.96 6.453L5.414 7H8a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1V3a1 1 0 1 1 2 0v2.586l.553-.553.012-.012A10.75 10.75 0 0 1 11.996 2ZM12 6a1 1 0 0 1 1 1v4.382l3.447 1.724a1 1 0 1 1-.894 1.788l-4-2A1 1 0 0 1 11 12V7a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$4S, _path2$m;
function _extends$4T() { _extends$4T = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4T.apply(this, arguments); }
var SvgHomeFilled = function SvgHomeFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4T({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4S || (_path$4S = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 4.293a1 1 0 0 1 1.414 0l7 7a1 1 0 0 1-1.414 1.414L12 6.414l-6.293 6.293a1 1 0 0 1-1.414-1.414l7-7Z",
    clipRule: "evenodd"
  })), _path2$m || (_path2$m = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "m7 13 5-5 5 5v6a1 1 0 0 1-1 1h-2.5v-2.5a1.5 1.5 0 0 0-3 0V20H8a1 1 0 0 1-1-1v-6Z",
    clipRule: "evenodd"
  })));
};

var _path$4R;
function _extends$4S() { _extends$4S = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4S.apply(this, arguments); }
var SvgHomeOutline = function SvgHomeOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4S({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4R || (_path$4R = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 4.293a1 1 0 0 1 1.414 0l5 5 2 2a1 1 0 0 1-1.414 1.414L18 12.414v6.336c0 .69-.56 1.25-1.25 1.25h-9.5C6.56 20 6 19.44 6 18.75v-6.336l-.293.293a1 1 0 0 1-1.414-1.414l2-2 5-5ZM12 6.414l4 4V18h-2.5v-2.5a1.5 1.5 0 0 0-3 0V18H8v-7.586l4-4Z",
    clipRule: "evenodd"
  })));
};

var _path$4Q;
function _extends$4R() { _extends$4R = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4R.apply(this, arguments); }
var SvgHospital = function SvgHospital(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4R({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4Q || (_path$4Q = /*#__PURE__*/React.createElement("path", {
    d: "M18 6h2a2 2 0 0 1 2 2v12c0 1.333-.667 2-2 2h-4c-1.333 0-2-.667-2-2v-5h-4v5c0 1.333-.667 2-2 2H4c-1.333 0-2-.667-2-2V8a2 2 0 0 1 2-2h2V4c0-1.333.667-2 2-2h8c1.333 0 2 .667 2 2v2Zm-3 3c.667-.005 1-.338 1-1 0-.662-.334-.995-1-1h-2V5c0-.667-.333-1-1-1s-1 .333-1 1v2H9c-.667.002-1 .335-1 .998S8.333 8.995 9 9h2v2c.006.663.341.997 1.005 1 .663.003.995-.33.995-1V9h2Zm-9.5 6h1c.667 0 1-.334 1-1.001V13c.004-.667-.327-1-.996-1H5.5c-.667 0-1 .333-1 1v1c0 .667.333 1 1 1Zm0 5h1c.667 0 1-.334 1-1.001V18c.004-.667-.327-1-.996-1H5.5c-.667 0-1 .333-1 1v1c0 .667.333 1 1 1Zm12-5h1c.667 0 1-.334 1-1.001V13c.004-.667-.327-1-.996-1H17.5c-.667 0-1 .333-1 1v1c0 .667.333 1 1 1Zm0 5h1c.667 0 1-.334 1-1.001V18c.004-.667-.327-1-.996-1H17.5c-.667 0-1 .333-1 1v1c0 .667.333 1 1 1Z"
  })));
};

var _path$4P;
function _extends$4Q() { _extends$4Q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4Q.apply(this, arguments); }
var SvgHourglass = function SvgHourglass(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4Q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4P || (_path$4P = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 2a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2h-1v3.172a3 3 0 0 1-.879 2.121L13.414 12l3.707 3.707a3 3 0 0 1 .879 2.12V21h1a1 1 0 1 1 0 2H5a1 1 0 1 1 0-2h1v-3.172a3 3 0 0 1 .879-2.121L10.586 12 6.879 8.293A3 3 0 0 1 6 6.173V3H5a1 1 0 0 1-1-1Zm4 1v3.172a1 1 0 0 0 .293.707L12 10.586l3.707-3.707A1 1 0 0 0 16 6.172V3H8Zm4 10.414-3.707 3.707a1 1 0 0 0-.293.707V21h8v-3.172a1 1 0 0 0-.293-.707L12 13.414Z",
    clipRule: "evenodd"
  })));
};

var _path$4O;
function _extends$4P() { _extends$4P = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4P.apply(this, arguments); }
var SvgHouseFilled = function SvgHouseFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4P({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4O || (_path$4O = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2c.492 0 .938.167 1.338.502l8.328 6.743c.329.349.415.726.258 1.132-.156.407-.465.61-.927.612l-.985.011v9c0 1.333-.667 2-2.001 2h-2.002c-1.334-.01-2.001-.676-2.001-2v-6h-4.003v5.986c0 1.324-.667 1.99-2.001 2H6.002c-1.334.01-2.001-.653-2.001-1.986l-.004-9-1.029-.011c-.483 0-.786-.204-.911-.612-.125-.407-.043-.786.246-1.136l8.358-6.739A2.033 2.033 0 0 1 12 2Z",
    clipRule: "evenodd"
  })));
};

var _path$4N;
function _extends$4O() { _extends$4O = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4O.apply(this, arguments); }
var SvgHouseOutline = function SvgHouseOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4O({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4N || (_path$4N = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2c.492 0 .938.167 1.338.502l8.328 6.743c.329.349.415.726.258 1.132-.156.407-.465.61-.927.612l-.985.011v9c0 1.333-.667 2-2.001 2h-2.002c-1.334-.01-2.001-.676-2.001-2v-6h-4.003v5.986c0 1.324-.667 1.99-2.001 2H6.002c-1.334.01-2.001-.653-2.001-1.986l-.004-9-1.029-.011c-.483 0-.786-.204-.911-.612-.125-.407-.043-.786.246-1.136l8.358-6.739A2.033 2.033 0 0 1 12 2ZM6.002 9v11h2.002v-6c0-1.333.667-2 2.001-2h4.003c1.334 0 2.001.667 2.001 2v6h2V9l-6.002-4.581L6.002 9Z",
    clipRule: "evenodd"
  })));
};

var _path$4M, _path2$l, _path3$5;
function _extends$4N() { _extends$4N = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4N.apply(this, arguments); }
var SvgImageFilled = function SvgImageFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4N({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4M || (_path$4M = /*#__PURE__*/React.createElement("path", {
    d: "M8 9a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z"
  })), _path2$l || (_path2$l = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v9.586l-3.379-3.38a3 3 0 0 0-4.242 0L3.825 21.762A3 3 0 0 1 2 19V5Zm7 1a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z",
    clipRule: "evenodd"
  })), _path3$5 || (_path3$5 = /*#__PURE__*/React.createElement("path", {
    d: "M6.414 22H19a3 3 0 0 0 3-3v-1.586l-4.793-4.793a1 1 0 0 0-1.414 0L6.414 22Z"
  })));
};

var _path$4L;
function _extends$4M() { _extends$4M = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4M.apply(this, arguments); }
var SvgImageMinus = function SvgImageMinus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4M({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4L || (_path$4L = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h.586l8.793-8.793a3 3 0 0 1 4.242 0L20 12.586V9a1 1 0 1 1 2 0v10a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3h7a1 1 0 1 1 0 2H5Zm3.414 16H19a1 1 0 0 0 1-1v-3.586l-2.793-2.793a1 1 0 0 0-1.414 0L8.414 20ZM16 6a1 1 0 1 1 0-2h6a1 1 0 1 1 0 2h-6ZM9 8a1 1 0 1 0 0 2 1 1 0 0 0 0-2ZM6 9a3 3 0 1 1 6 0 3 3 0 0 1-6 0Z",
    clipRule: "evenodd"
  })));
};

var _path$4K;
function _extends$4L() { _extends$4L = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4L.apply(this, arguments); }
var SvgImageOff = function SvgImageOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4L({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4K || (_path$4K = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 1.414 0L4.295 2.88a.885.885 0 0 1 .006.005L21.115 19.7a.149.149 0 0 0 .004.004l1.588 1.588a1 1 0 0 1-1.414 1.414l-1-1A2.938 2.938 0 0 1 19 22H5a3 3 0 0 1-3-3V4.998a2.99 2.99 0 0 1 .295-1.289L1.293 2.707a1 1 0 0 1 0-1.414ZM4 5.414V19a1 1 0 0 0 1 1h.586l6.5-6.5-1.797-1.797A3.001 3.001 0 0 1 6.287 7.7L4 5.414Zm4.252 4.252a.996.996 0 0 0 .071.072l-.07-.072Zm5.248 5.248L8.414 20h10.172L13.5 14.914ZM8 3a1 1 0 0 1 1-1h10a3 3 0 0 1 3 3v10a1 1 0 0 1-1.707.707l-3-3a1 1 0 0 1 1.414-1.414L20 12.586V5a1 1 0 0 0-1-1H9a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$4J;
function _extends$4K() { _extends$4K = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4K.apply(this, arguments); }
var SvgImageOutline = function SvgImageOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4K({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4J || (_path$4J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h.586l8.793-8.793a3 3 0 0 1 4.242 0L20 12.586V5a1 1 0 0 0-1-1H5Zm17 10.999V5a3 3 0 0 0-3-3H5a3 3 0 0 0-3 3v14a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3v-4.001Zm-2 .415-2.793-2.793a1 1 0 0 0-1.414 0L8.414 20H19a1 1 0 0 0 1-1v-3.586ZM9 8a1 1 0 1 0 0 2 1 1 0 0 0 0-2ZM6 9a3 3 0 1 1 6 0 3 3 0 0 1-6 0Z",
    clipRule: "evenodd"
  })));
};

var _path$4I;
function _extends$4J() { _extends$4J = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4J.apply(this, arguments); }
var SvgImagePlus = function SvgImagePlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4J({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4I || (_path$4I = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M19 1a1 1 0 0 1 1 1v2h2a1 1 0 1 1 0 2h-2v2a1 1 0 1 1-2 0V6h-2a1 1 0 1 1 0-2h2V2a1 1 0 0 1 1-1ZM5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h.586l8.793-8.793a3 3 0 0 1 4.242 0L20 12.586V12a1 1 0 1 1 2 0v7a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3h7a1 1 0 1 1 0 2H5Zm3.414 16H19a1 1 0 0 0 1-1v-3.586l-2.793-2.793a1 1 0 0 0-1.414 0L8.414 20ZM9 8a1 1 0 1 0 0 2 1 1 0 0 0 0-2ZM6 9a3 3 0 1 1 6 0 3 3 0 0 1-6 0Z",
    clipRule: "evenodd"
  })));
};

var _path$4H;
function _extends$4I() { _extends$4I = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4I.apply(this, arguments); }
var SvgImcFilled = function SvgImcFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4I({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4H || (_path$4H = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.816 14.142v1.733H2v-1.733h.906V9.858H2V8.125h3.816v1.733H4.91v4.284h.906Zm6.538-2.105-1.58 2.973-.208-.399-1.344-2.575-.113-.242v4.08H7.196V8.126H9.2l1.585 3.139 1.566-3.139h2.014v7.75h-1.913v-4.041l-.098.203v-.001ZM19.048 16c-.507 0-.967-.084-1.38-.252a2.793 2.793 0 0 1-1.065-.763 3.482 3.482 0 0 1-.67-1.24c-.156-.487-.233-1.051-.233-1.693 0-.634.077-1.201.232-1.701.156-.505.379-.932.668-1.28.292-.352.647-.62 1.064-.8A3.415 3.415 0 0 1 19.048 8c.694 0 1.276.149 1.74.45.465.302.832.772 1.105 1.403l.085.197-1.763.948-.085-.275a1.403 1.403 0 0 0-.373-.632c-.15-.139-.382-.213-.71-.213-.384 0-.68.125-.906.376-.228.254-.347.638-.347 1.162v1.168c0 .524.119.908.347 1.162.226.251.522.376.907.376.318 0 .56-.086.738-.254.198-.186.343-.41.438-.675l.098-.271L22 13.932l-.09.19c-.279.592-.648 1.054-1.11 1.38-.468.333-1.055.498-1.752.498Z",
    clipRule: "evenodd"
  })));
};

var _path$4G;
function _extends$4H() { _extends$4H = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4H.apply(this, arguments); }
var SvgImport = function SvgImport(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4H({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4G || (_path$4G = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2a1 1 0 0 1 1 1v9.586l2.293-2.293a1 1 0 1 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L11 12.586V3a1 1 0 0 1 1-1ZM1.879 4.879A3 3 0 0 1 4 4h4a1 1 0 0 1 0 2H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-4a1 1 0 1 1 0-2h4a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H4a3 3 0 0 1-3-3V7a3 3 0 0 1 .879-2.121Z",
    clipRule: "evenodd"
  })));
};

var _path$4F;
function _extends$4G() { _extends$4G = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4G.apply(this, arguments); }
var SvgInbox = function SvgInbox(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4G({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4F || (_path$4F = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7.24 3h9.52a3.002 3.002 0 0 1 2.685 1.665l3.45 6.887A1 1 0 0 1 23 12v6a3 3 0 0 1-3 3H4a3 3 0 0 1-3-3v-6a1 1 0 0 1 .106-.448l3.448-6.887.001-.001A3 3 0 0 1 7.24 3Zm0 2a1 1 0 0 0-.894.555l-.002.003L3.62 11H8a1 1 0 0 1 .832.445L10.535 14h2.93l1.703-2.555A1 1 0 0 1 16 11h4.38l-2.724-5.442-.002-.003A1 1 0 0 0 16.76 5H7.24ZM21 13h-4.465l-1.703 2.555A1 1 0 0 1 14 16h-4a1 1 0 0 1-.832-.445L7.465 13H3v5a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-5Z",
    clipRule: "evenodd"
  })));
};

var _path$4E;
function _extends$4F() { _extends$4F = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4F.apply(this, arguments); }
var SvgIncreaseFont = function SvgIncreaseFont(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4F({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4E || (_path$4E = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M17 11V9c0-.667.333-1 1-1s1 .333 1 1v2h2c.667.002 1 .336 1 1.002 0 .665-.333.998-1 .998h-2v2c-.002.667-.336 1-1.002 1-.665 0-.998-.333-.998-1v-2h-2c-.667 0-1-.333-1-1s.333-1 1-1h2Zm-7.888 4H4.913c-.32 1.35-.592 2.509-.734 3.116-.068.289-.43 1.05-1.388.852-.639-.132-.89-.554-.756-1.266l2.89-10.538C5.259 5.721 5.955 5 7.013 5c1.059 0 1.755.721 2.088 2.164l2.879 10.541c.098.743-.167 1.164-.796 1.263-.943.147-1.256-.563-1.325-.852A785.69 785.69 0 0 0 9.112 15Zm-.488-2c-.727-2.962-1.5-6-1.61-6-.137 0-.904 3.038-1.621 6h3.231Z",
    clipRule: "evenodd"
  })));
};

var _path$4D;
function _extends$4E() { _extends$4E = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4E.apply(this, arguments); }
var SvgInfoCircleFilled = function SvgInfoCircleFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4E({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4D || (_path$4D = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10ZM12 7a1 1 0 1 0 0 2h.01a1 1 0 1 0 0-2H12Zm1 5a1 1 0 1 0-2 0v4a1 1 0 1 0 2 0v-4Z",
    clipRule: "evenodd"
  })));
};

var _path$4C;
function _extends$4D() { _extends$4D = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4D.apply(this, arguments); }
var SvgInfoCircleOutline = function SvgInfoCircleOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4D({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4C || (_path$4C = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20 12a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8 10c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Zm0-15a1 1 0 1 0 0 2h.01a1 1 0 1 0 0-2H12Zm1 5a1 1 0 1 0-2 0v4a1 1 0 1 0 2 0v-4Z",
    clipRule: "evenodd"
  })));
};

var _path$4B;
function _extends$4C() { _extends$4C = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4C.apply(this, arguments); }
var SvgInfoDefault = function SvgInfoDefault(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4C({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4B || (_path$4B = /*#__PURE__*/React.createElement("path", {
    d: "M11.5 19v-9c0-.667.333-1 1-1s1 .333 1 1v9c0 .666-.332 1-.997 1s-1-.333-1.003-1Zm1-15a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z"
  })));
};

var _path$4A;
function _extends$4B() { _extends$4B = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4B.apply(this, arguments); }
var SvgInstagram = function SvgInstagram(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4B({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4A || (_path$4A = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 3a4 4 0 0 0-4 4v10a4 4 0 0 0 4 4h10a4 4 0 0 0 4-4V7a4 4 0 0 0-4-4H7ZM1 7a6 6 0 0 1 6-6h10a6 6 0 0 1 6 6v10a6 6 0 0 1-6 6H7a6 6 0 0 1-6-6V7Zm15.5-.5a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2h-.01a1 1 0 0 1-1-1Zm-4.017 2.49a3 3 0 1 0-.88 5.934 3 3 0 0 0 .88-5.935ZM9.741 7.518a5 5 0 1 1 4.605 8.876A5 5 0 0 1 9.741 7.52Z",
    clipRule: "evenodd"
  })));
};

var _path$4z;
function _extends$4A() { _extends$4A = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4A.apply(this, arguments); }
var SvgItalic = function SvgItalic(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4A({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4z || (_path$4z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13 20H7c-.666 0-1-.333-1-.999 0-.665.334-.999 1-1.001h2l4-12h-2c-.662 0-.994-.334-.994-1 0-.668.332-1 .995-1h6.004c.663.04.995.374.995 1 0 .625-.332.958-.995 1h-2.003L11 18h2c.668 0 1.002.335 1.002 1.001 0 .667-.334 1-1.002.998V20Z",
    clipRule: "evenodd"
  })));
};

var _path$4y;
function _extends$4z() { _extends$4z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4z.apply(this, arguments); }
var SvgJapaneseYen = function SvgJapaneseYen(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4y || (_path$4y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.322 2.265a1 1 0 0 1 1.413.057L12 8.026l5.265-5.704a1 1 0 0 1 1.47 1.356L13 9.891V10h5a1 1 0 1 1 0 2h-5v2h5a1 1 0 1 1 0 2h-5v5a1 1 0 1 1-2 0v-5H6a1 1 0 1 1 0-2h5v-2H6a1 1 0 1 1 0-2h5v-.109L5.265 3.678a1 1 0 0 1 .057-1.413Z",
    clipRule: "evenodd"
  })));
};

var _path$4x;
function _extends$4y() { _extends$4y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4y.apply(this, arguments); }
var SvgKeyFilled = function SvgKeyFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4x || (_path$4x = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 21v-3l7.4-7.4a6.5 6.5 0 1 1 4 4L12 16h-2v3H7v3H3c-.6 0-1-.4-1-1ZM17 7.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0ZM16.5 6a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z",
    clipRule: "evenodd"
  })));
};

var _path$4w;
function _extends$4x() { _extends$4x = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4x.apply(this, arguments); }
var SvgKeyOutline = function SvgKeyOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4x({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4w || (_path$4w = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.552 2.109a7.5 7.5 0 1 1 2.133 13.62l-.978.978A1 1 0 0 1 12 17h-1v2a1 1 0 0 1-1 1H8v2a1 1 0 0 1-1 1H3c-.527 0-1.044-.18-1.432-.568C1.18 22.045 1 21.527 1 21v-3a1 1 0 0 1 .293-.707l6.978-6.978a7.5 7.5 0 0 1 3.281-8.206Zm4.604.888a5.5 5.5 0 0 0-5.812 7.274 1 1 0 0 1-.237 1.036L3 18.414V21h3v-2a1 1 0 0 1 1-1h2v-2a1 1 0 0 1 1-1h1.586l1.107-1.107a1 1 0 0 1 1.036-.237 5.5 5.5 0 1 0 2.427-10.66ZM16.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1ZM15 7.5a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Z",
    clipRule: "evenodd"
  })));
};

var _path$4v;
function _extends$4w() { _extends$4w = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4w.apply(this, arguments); }
var SvgLaugh = function SvgLaugh(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4w({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4v || (_path$4v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm7-3a1 1 0 0 1 1-1h.01a1 1 0 0 1 0 2H9a1 1 0 0 1-1-1Zm6 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H15a1 1 0 0 1-1-1Zm-8.764 3.354A1 1 0 0 1 6 12h12a1 1 0 0 1 .986 1.167A7 7 0 0 1 12 19a7 7 0 0 1-6.986-5.833 1 1 0 0 1 .222-.813ZM7.333 14a5 5 0 0 0 4.681 3 5 5 0 0 0 4.653-3H7.333Z",
    clipRule: "evenodd"
  })));
};

var _path$4u;
function _extends$4v() { _extends$4v = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4v.apply(this, arguments); }
var SvgLayout = function SvgLayout(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4v({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4u || (_path$4u = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v3h16V5a1 1 0 0 0-1-1H5Zm17 1a3 3 0 0 0-3-3H5a3 3 0 0 0-3 3v14a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3V5Zm-2 5H10v10h9a1 1 0 0 0 1-1v-9ZM8 20V10H4v9a1 1 0 0 0 1 1h3Z",
    clipRule: "evenodd"
  })));
};

var _path$4t;
function _extends$4u() { _extends$4u = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4u.apply(this, arguments); }
var SvgLayoutDashboard = function SvgLayoutDashboard(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4u({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4t || (_path$4t = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 4a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4Zm7 0H4v7h5V4Zm4 0a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-5a2 2 0 0 1-2-2V4Zm7 0h-5v3h5V4Zm-7 9a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-5a2 2 0 0 1-2-2v-7Zm7 0h-5v7h5v-7ZM2 17a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-3Zm7 0H4v3h5v-3Z",
    clipRule: "evenodd"
  })));
};

var _path$4s;
function _extends$4t() { _extends$4t = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4t.apply(this, arguments); }
var SvgLayoutGrid = function SvgLayoutGrid(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4t({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4s || (_path$4s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 4a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4Zm7 0H4v5h5V4Zm4 0a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-5a2 2 0 0 1-2-2V4Zm7 0h-5v5h5V4ZM2 15a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-5Zm7 0H4v5h5v-5Zm4 0a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-5a2 2 0 0 1-2-2v-5Zm7 0h-5v5h5v-5Z",
    clipRule: "evenodd"
  })));
};

var _path$4r;
function _extends$4s() { _extends$4s = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4s.apply(this, arguments); }
var SvgLayoutList = function SvgLayoutList(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4s({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4r || (_path$4r = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 4a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4Zm7 0H4v5h5V4Zm4 0a1 1 0 0 1 1-1h7a1 1 0 1 1 0 2h-7a1 1 0 0 1-1-1Zm0 5a1 1 0 0 1 1-1h7a1 1 0 1 1 0 2h-7a1 1 0 0 1-1-1ZM2 15a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-5Zm7 0H4v5h5v-5Zm4 0a1 1 0 0 1 1-1h7a1 1 0 1 1 0 2h-7a1 1 0 0 1-1-1Zm0 5a1 1 0 0 1 1-1h7a1 1 0 1 1 0 2h-7a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$4q;
function _extends$4r() { _extends$4r = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4r.apply(this, arguments); }
var SvgLayoutPanelLeft = function SvgLayoutPanelLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4r({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4q || (_path$4q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 4a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4Zm7 0H4v16h5V4Zm4 0a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-5a2 2 0 0 1-2-2V4Zm7 0h-5v5h5V4Zm-7 11a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-5a2 2 0 0 1-2-2v-5Zm7 0h-5v5h5v-5Z",
    clipRule: "evenodd"
  })));
};

var _path$4p;
function _extends$4q() { _extends$4q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4q.apply(this, arguments); }
var SvgLayoutPanelTop = function SvgLayoutPanelTop(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4p || (_path$4p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4Zm18 0H4v5h16V4ZM2 15a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-5Zm7 0H4v5h5v-5Zm4 0a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-5a2 2 0 0 1-2-2v-5Zm7 0h-5v5h5v-5Z",
    clipRule: "evenodd"
  })));
};

var _path$4o;
function _extends$4p() { _extends$4p = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4p.apply(this, arguments); }
var SvgLayoutTemplate = function SvgLayoutTemplate(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4p({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4o || (_path$4o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4Zm18 0H4v5h16V4ZM2 15a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-5Zm9 0H4v5h7v-5Zm4 0a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-3a2 2 0 0 1-2-2v-5Zm5 0h-3v5h3v-5Z",
    clipRule: "evenodd"
  })));
};

var _path$4n;
function _extends$4o() { _extends$4o = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4o.apply(this, arguments); }
var SvgLightbulbFilled = function SvgLightbulbFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4o({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4n || (_path$4n = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 1a7 7 0 0 0-7 7c0 1.196.261 2.676 1.793 4.207.644.644 1.083 1.273 1.226 1.99A1 1 0 0 0 9 15h6a1 1 0 0 0 .98-.804c.144-.719.488-1.247 1.21-1.972C18.412 11.113 19 9.538 19 8a7 7 0 0 0-7-7ZM9 17a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2H9Zm1 4a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2h-4Z",
    clipRule: "evenodd"
  })));
};

var _path$4m;
function _extends$4n() { _extends$4n = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4n.apply(this, arguments); }
var SvgLightbulbOff = function SvgLightbulbOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4n({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4m || (_path$4m = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 1.414 0l4.286 4.286.028.027 15.686 15.687a1 1 0 0 1-1.414 1.414L6.982 8.397a3.67 3.67 0 0 0 1.225 2.396c.756.756 1.517 1.727 1.773 3.01a1 1 0 0 1-1.96.393c-.143-.71-.576-1.335-1.211-1.973a5.67 5.67 0 0 1-1.652-5.651L1.293 2.707a1 1 0 0 1 0-1.414Zm10.396-.275A7 7 0 0 1 19 7.998V8c0 1.435-.483 2.774-1.452 3.864a1 1 0 0 1-1.495-1.328c.63-.71.947-1.57.947-2.535a5 5 0 0 0-7.75-4.166 1 1 0 1 1-1.1-1.67 7 7 0 0 1 3.539-1.147ZM8 18a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1Zm2 5a1 1 0 1 1 0-2h4a1 1 0 1 1 0 2h-4Z",
    clipRule: "evenodd"
  })));
};

var _path$4l;
function _extends$4m() { _extends$4m = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4m.apply(this, arguments); }
var SvgLightbulbOutline = function SvgLightbulbOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4m({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4l || (_path$4l = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7.05 3.05A7 7 0 0 1 19 8c0 1.538-.588 3.113-1.81 4.224-.722.725-1.066 1.253-1.21 1.972a1 1 0 0 1-1.96-.392c.254-1.275.907-2.145 1.773-3.011a1 1 0 0 1 .038-.036C16.595 10.069 17 9.053 17 8A5 5 0 0 0 7 8c0 .804.139 1.724 1.207 2.793.756.756 1.517 1.727 1.774 3.01a1 1 0 1 1-1.962.393c-.143-.716-.582-1.345-1.226-1.989C5.26 10.676 5 9.196 5 8a7 7 0 0 1 2.05-4.95ZM8 18a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1Zm1 4a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$4k;
function _extends$4l() { _extends$4l = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4l.apply(this, arguments); }
var SvgLightningBolt = function SvgLightningBolt(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4l({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4k || (_path$4k = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13.398 1.083a1 1 0 0 1 .594 1.041L13.132 9H21a1 1 0 0 1 .768 1.64l-10 12a1 1 0 0 1-1.76-.764l.86-6.876H3a1 1 0 0 1-.768-1.64l10-12a1 1 0 0 1 1.166-.277Z",
    clipRule: "evenodd"
  })));
};

var _path$4j;
function _extends$4k() { _extends$4k = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4k.apply(this, arguments); }
var SvgLink = function SvgLink(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4k({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4j || (_path$4j = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 8a4 4 0 0 0 0 8h2a1 1 0 1 1 0 2H7A6 6 0 1 1 7 6h2a1 1 0 0 1 0 2H7Zm7-1a1 1 0 0 1 1-1h2a6 6 0 1 1 0 12h-2a1 1 0 1 1 0-2h2a4 4 0 1 0 0-8h-2a1 1 0 0 1-1-1Zm-7 5a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$4i;
function _extends$4j() { _extends$4j = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4j.apply(this, arguments); }
var SvgLinkOff = function SvgLinkOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4j({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4i || (_path$4i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 1.414 0l20 20a1 1 0 0 1-1.414 1.414L11.586 13H8a1 1 0 1 1 0-2h1.586l-2.98-2.98A4 4 0 0 0 7 16h2a1 1 0 1 1 0 2H7A6 6 0 0 1 4.948 6.362L1.293 2.707a1 1 0 0 1 0-1.414ZM15 8a1 1 0 1 1 0-2h2a6 6 0 0 1 4.8 9.6 1 1 0 0 1-1.6-1.2A4 4 0 0 0 17 8h-2Z",
    clipRule: "evenodd"
  })));
};

var _path$4h;
function _extends$4i() { _extends$4i = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4i.apply(this, arguments); }
var SvgLinkedin = function SvgLinkedin(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4i({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4h || (_path$4h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 3a1 1 0 1 0 0 2 1 1 0 0 0 0-2ZM1 4a3 3 0 1 1 6 0 3 3 0 0 1-6 0Zm15 5a5 5 0 0 0-5 5v6h2v-6a3 3 0 0 1 6 0v6h2v-6a5 5 0 0 0-5-5Zm-4.95.05A7 7 0 0 1 23 14v7a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-7a1 1 0 0 0-2 0v7a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-7a7 7 0 0 1 2.05-4.95ZM1 9a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V9Zm2 1v10h2V10H3Z",
    clipRule: "evenodd"
  })));
};

var _path$4g;
function _extends$4h() { _extends$4h = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4h.apply(this, arguments); }
var SvgList = function SvgList(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4h({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4g || (_path$4g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 8c-.667 0-1-.333-1-1s.333-1 1-1h2c.667 0 1 .333 1 1s-.333 1-1 1H3Zm0 5c-.667 0-1-.333-1-1s.333-1 1-1h2c.667 0 1 .333 1 1s-.333 1-1 1H3Zm0 5c-.667 0-1-.333-1-1s.333-1 1-1h2c.667 0 1 .333 1 1s-.333 1-1 1H3Zm6 0c-.667 0-1-.333-1-1s.333-1 1-1h12c.667 0 1 .333 1 1s-.333 1-1 1H9Zm0-5c-.667 0-1-.333-1-1s.333-1 1-1h12c.667 0 1 .333 1 1s-.333 1-1 1H9Zm0-5c-.667 0-1-.333-1-1s.333-1 1-1h12c.667 0 1 .333 1 1s-.333 1-1 1H9Z",
    clipRule: "evenodd"
  })));
};

var _path$4f;
function _extends$4g() { _extends$4g = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4g.apply(this, arguments); }
var SvgListChecks = function SvgListChecks(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4g({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4f || (_path$4f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.707 4.293a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L5 7.586l3.293-3.293a1 1 0 0 1 1.414 0ZM12 6a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2h-8a1 1 0 0 1-1-1Zm0 6a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2h-8a1 1 0 0 1-1-1Zm-2.293 2.293a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 1 1 1.414-1.414L5 17.586l3.293-3.293a1 1 0 0 1 1.414 0ZM12 18a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2h-8a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$4e;
function _extends$4f() { _extends$4f = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4f.apply(this, arguments); }
var SvgListFilter = function SvgListFilter(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4f({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4e || (_path$4e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 6a1 1 0 0 1 1-1h18a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1Zm4 6a1 1 0 0 1 1-1h10a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1Zm3 6a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$4d;
function _extends$4e() { _extends$4e = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4e.apply(this, arguments); }
var SvgListStart = function SvgListStart(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4e({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4d || (_path$4d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.707 3.293a1 1 0 0 1 0 1.414L16.414 5H19a3 3 0 0 1 3 3v10a1 1 0 1 1-2 0V8a1 1 0 0 0-1-1h-2.586l.293.293a1 1 0 0 1-1.414 1.414l-2-2a1 1 0 0 1 0-1.414l2-2a1 1 0 0 1 1.414 0ZM2 6a1 1 0 0 1 1-1h7a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1Zm0 6a1 1 0 0 1 1-1h13a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1Zm0 6a1 1 0 0 1 1-1h13a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$4c, _path2$k, _path3$4;
function _extends$4d() { _extends$4d = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4d.apply(this, arguments); }
var SvgLlp = function SvgLlp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4d({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4c || (_path$4c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 5h14a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3Zm0 2a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1H5Z",
    clipRule: "evenodd"
  })), _path2$k || (_path2$k = /*#__PURE__*/React.createElement("path", {
    d: "M5.377 14.521c0 .147.082.229.229.229h3.403c.147 0 .229-.082.229-.229v-.63c0-.155-.082-.237-.23-.237H6.564V9.188c0-.148-.09-.23-.237-.23h-.72c-.147 0-.23.074-.23.23v5.333Zm4.721 0c0 .147.082.229.229.229h3.403c.147 0 .229-.082.229-.229v-.63c0-.155-.082-.237-.23-.237h-2.445V9.188c0-.148-.09-.23-.237-.23h-.72c-.147 0-.23.074-.23.23v5.333Zm4.95.229h.728c.155 0 .229-.082.229-.229v-1.628h.892c1.292 0 2.176-.81 2.176-1.98 0-1.153-.892-1.954-2.176-1.954h-1.85c-.146 0-.228.073-.228.229v5.333c0 .147.082.229.229.229Zm2.847-3.836c0 .548-.377.883-.998.883h-.892v-1.742h.892c.613 0 .998.335.998.859Z"
  })), _path3$4 || (_path3$4 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.262 8.836a.482.482 0 0 1 .344-.127h.72c.118 0 .246.033.346.128a.474.474 0 0 1 .14.35v4.217H9.01c.119 0 .249.034.348.136.099.1.13.232.13.351v.63a.48.48 0 0 1-.132.346.48.48 0 0 1-.346.133H5.606a.48.48 0 0 1-.347-.133.48.48 0 0 1-.132-.346V9.188c0-.12.032-.252.135-.352Zm.365.373V14.5h3.36v-.596H6.314V9.209h-.686Zm4.356-.373a.482.482 0 0 1 .344-.127h.72c.118 0 .246.033.346.128a.474.474 0 0 1 .14.35v4.217h2.197c.119 0 .25.034.348.136.099.1.13.232.13.351v.63a.48.48 0 0 1-.132.346.48.48 0 0 1-.346.133h-3.403a.48.48 0 0 1-.347-.133.48.48 0 0 1-.132-.346V9.188c0-.12.032-.252.135-.352Zm.365.373V14.5h3.36v-.596h-2.674V9.209h-.686Zm4.356-.373a.482.482 0 0 1 .344-.127h1.849c.693 0 1.3.217 1.736.609.439.393.69.95.69 1.596 0 .65-.248 1.214-.685 1.613-.436.397-1.043.616-1.741.616h-.642v1.378a.482.482 0 0 1-.127.344.478.478 0 0 1-.352.135h-.728a.48.48 0 0 1-.347-.133.48.48 0 0 1-.132-.346V9.188c0-.12.032-.252.135-.352Zm.365.373V14.5h.686v-1.857h1.142c.594 0 1.074-.186 1.404-.486.327-.298.522-.725.522-1.243 0-.509-.195-.93-.524-1.224-.33-.298-.812-.481-1.402-.481h-1.828Zm.686.596h1.142c.35 0 .663.096.892.294.233.201.355.487.355.815 0 .334-.116.627-.35.834-.229.203-.544.299-.897.299h-1.142V9.805Zm.5.5v1.242h.642c.267 0 .452-.072.566-.173.11-.098.181-.247.181-.46a.55.55 0 0 0-.182-.437c-.116-.1-.302-.172-.565-.172h-.642Z",
    clipRule: "evenodd"
  })));
};

var _path$4b;
function _extends$4c() { _extends$4c = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4c.apply(this, arguments); }
var SvgLoader = function SvgLoader(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4c({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4b || (_path$4b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 1a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0V2a1 1 0 0 1 1-1ZM4.223 4.223a1 1 0 0 1 1.414 0l2.83 2.83a1 1 0 1 1-1.414 1.414l-2.83-2.83a1 1 0 0 1 0-1.414Zm15.554 0a1 1 0 0 1 0 1.414l-2.83 2.83a1 1 0 1 1-1.414-1.414l2.83-2.83a1 1 0 0 1 1.414 0ZM1 12a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2H2a1 1 0 0 1-1-1Zm16 0a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1Zm-8.533 3.533a1 1 0 0 1 0 1.414l-2.83 2.83a1 1 0 0 1-1.414-1.414l2.83-2.83a1 1 0 0 1 1.414 0Zm7.066 0a1 1 0 0 1 1.414 0l2.83 2.83a1 1 0 1 1-1.414 1.414l-2.83-2.83a1 1 0 0 1 0-1.414ZM12 17a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0v-4a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$4a;
function _extends$4b() { _extends$4b = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4b.apply(this, arguments); }
var SvgLoading = function SvgLoading(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4b({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$4a || (_path$4a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14.472 4.391A8 8 0 1 0 20 12.002a1 1 0 1 1 2 0 10 10 0 1 1-6.91-9.512 1 1 0 1 1-.618 1.902Z",
    clipRule: "evenodd"
  })));
};

var _path$49;
function _extends$4a() { _extends$4a = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4a.apply(this, arguments); }
var SvgLockCloseFilled = function SvgLockCloseFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4a({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$49 || (_path$49 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a4 4 0 0 0-4 4v3h8V7a4 4 0 0 0-4-4Zm6 7V7A6 6 0 1 0 6 7v3H5a3 3 0 0 0-3 3v7a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3v-7a3 3 0 0 0-3-3h-1Z",
    clipRule: "evenodd"
  })));
};

var _path$48;
function _extends$49() { _extends$49 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$49.apply(this, arguments); }
var SvgLockCloseOutline = function SvgLockCloseOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$49({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$48 || (_path$48 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a4 4 0 0 0-4 4v3h8V7a4 4 0 0 0-4-4Zm6 7V7A6 6 0 1 0 6 7v3H5a3 3 0 0 0-3 3v7a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3v-7a3 3 0 0 0-3-3h-1ZM5 12a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7a1 1 0 0 0-1-1H5Z",
    clipRule: "evenodd"
  })));
};

var _path$47;
function _extends$48() { _extends$48 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$48.apply(this, arguments); }
var SvgLockOpenFilled = function SvgLockOpenFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$48({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$47 || (_path$47 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.172 4.172A4 4 0 0 1 16 7a1 1 0 1 0 2 0A6 6 0 1 0 6 7v3H5a3 3 0 0 0-3 3v7a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3v-7a3 3 0 0 0-3-3H8V7a4 4 0 0 1 1.172-2.828Z",
    clipRule: "evenodd"
  })));
};

var _path$46;
function _extends$47() { _extends$47 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$47.apply(this, arguments); }
var SvgLockOpenOutline = function SvgLockOpenOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$47({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$46 || (_path$46 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.172 4.172A4 4 0 0 1 16 7a1 1 0 1 0 2 0A6 6 0 1 0 6 7v3H5a3 3 0 0 0-3 3v7a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3v-7a3 3 0 0 0-3-3H8V7a4 4 0 0 1 1.172-2.828ZM7 12H5a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7a1 1 0 0 0-1-1H7Z",
    clipRule: "evenodd"
  })));
};

var _path$45;
function _extends$46() { _extends$46 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$46.apply(this, arguments); }
var SvgLogIn = function SvgLogIn(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$46({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$45 || (_path$45 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14 3a1 1 0 0 1 1-1h4a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3h-4a1 1 0 1 1 0-2h4a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-4a1 1 0 0 1-1-1ZM9.293 6.293a1 1 0 0 1 1.414 0l5 5a1 1 0 0 1 0 1.414l-5 5a1 1 0 0 1-1.414-1.414L12.586 13H3a1 1 0 1 1 0-2h9.586L9.293 7.707a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _g, _defs;
function _extends$45() { _extends$45 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$45.apply(this, arguments); }
var SvgLungs = function SvgLungs(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$45({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _g || (_g = /*#__PURE__*/React.createElement("g", {
    clipPath: "url(#lungs_svg__a)"
  }, /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11 4h2v6a2 2 0 0 0 1.012 1.74C14.36-3.486 22 6.071 22 20.427c-1.116-.366-4.666-1.719-6.64-2.477-.656-.251-1.36-1.278-1.36-1.98v-1.395c-.555-.65-1.277-1.276-2-1.276-.723 0-1.445.627-2 1.276v1.395c0 .702-.703 1.729-1.36 1.98-1.974.758-5.524 2.111-6.64 2.477C2 6.072 9.641-3.485 9.988 11.74A2 2 0 0 0 11 10V4Z",
    clipRule: "evenodd"
  }))), _defs || (_defs = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("clipPath", {
    id: "lungs_svg__a"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M0 0h24v24H0z"
  })))));
};

var _path$44;
function _extends$44() { _extends$44 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$44.apply(this, arguments); }
var SvgMailCheck = function SvgMailCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$44({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$44 || (_path$44 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 3.879A3 3 0 0 1 4 3h16a3 3 0 0 1 3 3v7a1 1 0 1 1-2 0V8.82l-7.44 4.727a2.94 2.94 0 0 1-3.12 0l-.006-.003L3 8.82V18c0 .548.452 1 1 1h8a1 1 0 1 1 0 2H4c-1.652 0-3-1.348-3-3V6a3 3 0 0 1 .879-2.121ZM3 6.45l8.503 5.403a.94.94 0 0 0 .994 0l.002-.001L21 6.45V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v.45Zm19.707 9.842a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L18 19.586l3.293-3.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$43;
function _extends$43() { _extends$43 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$43.apply(this, arguments); }
var SvgMailMinus = function SvgMailMinus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$43({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$43 || (_path$43 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 3.879A3 3 0 0 1 4 3h16a3 3 0 0 1 3 3v9a1 1 0 1 1-2 0V8.82l-7.44 4.727a2.94 2.94 0 0 1-3.12 0l-.006-.003L3 8.82V18c0 .548.452 1 1 1h8a1 1 0 1 1 0 2H4c-1.652 0-3-1.348-3-3V6a3 3 0 0 1 .879-2.121ZM3 6.45l8.503 5.403a.94.94 0 0 0 .994 0l.002-.001L21 6.45V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v.45ZM15 19a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2h-6a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$42;
function _extends$42() { _extends$42 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$42.apply(this, arguments); }
var SvgMailOpen = function SvgMailOpen(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$42({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$42 || (_path$42 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a1 1 0 0 0-.6.2l-8 6a1 1 0 0 0-.28.326l8.383 5.328a.94.94 0 0 0 .994 0l.002-.001 8.38-5.326a1.036 1.036 0 0 0-.282-.33h-.002L12.6 3.2A1 1 0 0 0 12 3Zm9 8.82-7.44 4.727a2.94 2.94 0 0 1-3.12 0l-.006-.003L3 11.82V20a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-8.18ZM10.2 1.6a3 3 0 0 1 3.6 0l8.005 6.004A3.013 3.013 0 0 1 23 10v10a3 3 0 0 1-3 3H4a3 3 0 0 1-3-3V10a3 3 0 0 1 1.2-2.4l8-6Z",
    clipRule: "evenodd"
  })));
};

var _path$41;
function _extends$41() { _extends$41 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$41.apply(this, arguments); }
var SvgMailPlus = function SvgMailPlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$41({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$41 || (_path$41 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 3.879A3 3 0 0 1 4 3h16a3 3 0 0 1 3 3v7a1 1 0 1 1-2 0V8.82l-7.44 4.727a2.94 2.94 0 0 1-3.12 0l-.006-.003L3 8.82V18c0 .548.452 1 1 1h8a1 1 0 1 1 0 2H4c-1.652 0-3-1.348-3-3V6a3 3 0 0 1 .879-2.121ZM3 6.45l8.503 5.403a.94.94 0 0 0 .994 0l.002-.001L21 6.45V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v.45ZM19 15a1 1 0 0 1 1 1v2h2a1 1 0 1 1 0 2h-2v2a1 1 0 1 1-2 0v-2h-2a1 1 0 1 1 0-2h2v-2a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$40;
function _extends$40() { _extends$40 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$40.apply(this, arguments); }
var SvgMailQuestion = function SvgMailQuestion(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$40({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$40 || (_path$40 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 3.879A3 3 0 0 1 4 3h16a3 3 0 0 1 3 3v4.5a1 1 0 1 1-2 0V8.82l-7.44 4.727a2.94 2.94 0 0 1-3.12 0l-.006-.003L3 8.82V18c0 .548.452 1 1 1h12.5a1 1 0 1 1 0 2H4c-1.652 0-3-1.348-3-3V6a3 3 0 0 1 .879-2.121ZM3 6.45l8.503 5.403a.94.94 0 0 0 .994 0l.002-.001L21 6.45V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v.45Zm17.118 8.56a1.1 1.1 0 0 0-.717.135.975.975 0 0 1-.054.028c-.121.061-.296.24-.453.553a1 1 0 1 1-1.788-.894c.24-.48.656-1.09 1.319-1.433a3.1 3.1 0 0 1 3.813.605c.022.024.042.049.062.075.366.488.7 1.097.7 1.9 0 1.184-.891 1.946-1.44 2.316a5.889 5.889 0 0 1-1.19.613l-.025.01-.009.003-.003.001h-.002L20 17.98l.33.944a1 1 0 0 1-.663-1.887h.002m0 0 .005-.002.043-.017a3.877 3.877 0 0 0 .723-.38c.451-.304.56-.542.56-.658 0-.188-.06-.37-.268-.657a1.1 1.1 0 0 0-.614-.312M20 21a1 1 0 0 1 1 1v.01a1 1 0 1 1-2 0V22a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3$, _path2$j;
function _extends$3$() { _extends$3$ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3$.apply(this, arguments); }
var SvgMailSearch = function SvgMailSearch(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3$({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3$ || (_path$3$ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 3.879A3 3 0 0 1 3.999 3h16a3 3 0 0 1 3 3v6.5a1 1 0 1 1-2 0V8.82l-7.438 4.727a2.94 2.94 0 0 1-3.122 0l-.005-.003L3 8.82V18c0 .548.452 1 1 1h7.5a1 1 0 1 1 0 2H4c-1.652 0-3-1.348-3-3V6a3 3 0 0 1 .879-2.121Zm1.12 2.572 8.504 5.403a.94.94 0 0 0 .994 0l.002-.001 8.5-5.402V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v.45Z",
    clipRule: "evenodd"
  })), _path2$j || (_path2$j = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.57 19.4a2 2 0 1 1 .03.03m-1.407-4.28A3.987 3.987 0 0 1 18 14c1.094 0 2.085.439 2.807 1.15l.021.022A3.987 3.987 0 0 1 22 18c0 .742-.202 1.436-.554 2.032l1.261 1.26a1 1 0 0 1-1.414 1.415l-1.261-1.26A3.981 3.981 0 0 1 18 22a3.987 3.987 0 0 1-2.828-1.172l-.022-.021A3.987 3.987 0 0 1 14 18c0-1.094.439-2.085 1.15-2.807l.022-.021.021-.022Z",
    clipRule: "evenodd"
  })));
};

var _path$3_;
function _extends$3_() { _extends$3_ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3_.apply(this, arguments); }
var SvgMailWarning = function SvgMailWarning(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3_({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3_ || (_path$3_ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 3.879A3 3 0 0 1 3.999 3h16a3 3 0 0 1 3 3v4.5a1 1 0 1 1-2 0V8.82l-7.438 4.727a2.94 2.94 0 0 1-3.122 0l-.005-.003L3 8.82V18c0 .548.452 1 1 1h12.5a1 1 0 1 1 0 2H4c-1.652 0-3-1.348-3-3V6a3 3 0 0 1 .879-2.121Zm1.12 2.572 8.504 5.403a.94.94 0 0 0 .994 0l.002-.001 8.5-5.402V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v.45ZM20 13a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0v-4a1 1 0 0 1 1-1Zm0 8a1 1 0 0 1 1 1v.01a1 1 0 1 1-2 0V22a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3Z;
function _extends$3Z() { _extends$3Z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3Z.apply(this, arguments); }
var SvgMailX = function SvgMailX(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3Z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3Z || (_path$3Z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 3.879A3 3 0 0 1 3.999 3h16a3 3 0 0 1 3 3v7a1 1 0 1 1-2 0V8.82l-7.438 4.727a2.94 2.94 0 0 1-3.122 0l-.005-.003L3 8.82V18c0 .548.452 1 1 1h9a1 1 0 1 1 0 2H4c-1.652 0-3-1.348-3-3V6a3 3 0 0 1 .879-2.121Zm1.12 2.572 8.504 5.403a.94.94 0 0 0 .994 0l.002-.001 8.5-5.402V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v.45Zm13.294 9.842a1 1 0 0 1 1.414 0L19 17.586l1.293-1.293a1 1 0 0 1 1.414 1.414L20.414 19l1.293 1.293a1 1 0 0 1-1.414 1.414L19 20.414l-1.293 1.293a1 1 0 0 1-1.414-1.414L17.586 19l-1.293-1.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$3Y;
function _extends$3Y() { _extends$3Y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3Y.apply(this, arguments); }
var SvgMailbox = function SvgMailbox(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3Y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3Y || (_path$3Y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.5 6A3.481 3.481 0 0 0 3 9.5V17a1 1 0 0 0 1 1h5a1 1 0 0 0 1-1V9.5C10 7.552 8.448 6 6.5 6Zm4.253 0A5.486 5.486 0 0 1 12 9.5V17a3 3 0 0 1-.172 1H20a1 1 0 0 0 1-1V9c0-1.648-1.352-3-3-3h-7.247ZM20 20a3 3 0 0 0 3-3V9c0-2.752-2.248-5-5-5H6.5A5.481 5.481 0 0 0 1 9.5V17a3 3 0 0 0 3 3h16ZM14 9a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-1h-2a1 1 0 0 1-1-1Zm-9 1a1 1 0 0 1 1-1h1a1 1 0 0 1 0 2H6a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3X;
function _extends$3X() { _extends$3X = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3X.apply(this, arguments); }
var SvgMails = function SvgMails(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3X({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3X || (_path$3X = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 5a1 1 0 0 0-1 1v.4l6.566 3.495.002.001a.913.913 0 0 0 .864 0h.002L21 6.398V6a1 1 0 0 0-1-1H8Zm15 1.98V6a3 3 0 0 0-3-3H8a3 3 0 0 0-3 3v9a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3V6.979Zm-2 1.685-5.634 3a2.912 2.912 0 0 1-2.732 0l-.004-.002L7 8.665V15a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8.665ZM2 7a1 1 0 0 1 1 1v11c0 .548.452 1 1 1h14a1 1 0 1 1 0 2H4c-1.652 0-3-1.348-3-3V8a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3W;
function _extends$3W() { _extends$3W = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3W.apply(this, arguments); }
var SvgMapFilled = function SvgMapFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3W({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3W || (_path$3W = /*#__PURE__*/React.createElement("path", {
    d: "M15 5.365v14.608a5.632 5.632 0 0 1-.684-.113L9 18.606V4.009c.28.026.614.089 1 .188l4.95 1.182.05-.014Zm2-.574 2.469-.709a2.09 2.09 0 0 1 1.792.302c.47.34.739.851.739 1.4v11.338c0 .783-.548 1.467-1.364 1.702L17 19.736V4.791ZM7 19.19l-2.469.721c-.19.054-.386.082-.584.082-.433 0-.86-.132-1.208-.385-.47-.34-.739-.85-.739-1.4V7.082c0-.785.548-1.469 1.365-1.703L7 4.33v14.86Z"
  })));
};

var _path$3V;
function _extends$3V() { _extends$3V = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3V.apply(this, arguments); }
var SvgMapMarkerFilled = function SvgMapMarkerFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3V({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3V || (_path$3V = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "m11.4 22.8-.003-.002-.006-.005-.02-.015-.073-.056c-.063-.05-.154-.12-.268-.212a32.358 32.358 0 0 1-3.777-3.596C5.23 16.638 3 13.395 3 10a9 9 0 1 1 18 0c0 3.395-2.23 6.638-4.253 8.914a32.365 32.365 0 0 1-4.045 3.808l-.073.056-.02.015-.006.005-.002.001S12.6 22.8 12 22l.6.8a1 1 0 0 1-1.2 0Zm.6-.8-.6.8.6-.8Zm3-12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    clipRule: "evenodd"
  })));
};

var _path$3U;
function _extends$3U() { _extends$3U = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3U.apply(this, arguments); }
var SvgMapMarkerOutline = function SvgMapMarkerOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3U({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3U || (_path$3U = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a7 7 0 0 0-7 7c0 2.605 1.77 5.362 3.747 7.586A30.35 30.35 0 0 0 12 20.72a30.348 30.348 0 0 0 3.253-3.135C17.23 15.363 19 12.606 19 10a7 7 0 0 0-7-7Zm0 19-.6.8h-.001l-.002-.002-.006-.005-.02-.015-.073-.056c-.063-.05-.154-.12-.268-.212a32.358 32.358 0 0 1-3.777-3.596C5.23 16.638 3 13.395 3 10a9 9 0 1 1 18 0c0 3.395-2.23 6.638-4.253 8.914a32.365 32.365 0 0 1-4.045 3.808l-.073.056-.02.015-.006.005-.002.001S12.6 22.8 12 22Zm0 0 .6.8a1 1 0 0 1-1.2 0l.6-.8Zm0-14a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-4 2a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z",
    clipRule: "evenodd"
  })));
};

var _path$3T;
function _extends$3T() { _extends$3T = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3T.apply(this, arguments); }
var SvgMapOutline = function SvgMapOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3T({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3T || (_path$3T = /*#__PURE__*/React.createElement("path", {
    d: "M8.704 4c.332-.001.764.067 1.296.203l4.95 1.183 4.519-1.298a2.09 2.09 0 0 1 1.792.302c.47.34.739.851.739 1.4v11.338c0 .783-.548 1.467-1.364 1.702l-4.32 1.084a4.453 4.453 0 0 1-.898.086c-.332.001-.7-.043-1.102-.134L9 18.612l-4.469 1.307c-.19.054-.387.081-.584.081-.433 0-.86-.132-1.208-.385-.47-.34-.739-.85-.739-1.4V7.088c0-.785.548-1.469 1.365-1.702l4.086-1.183c.503-.134.92-.202 1.253-.203ZM4 7.125v10.79l4-1.099V6L4 7.125Zm12 0v10.79l4-1.099V6l-4 1.125Zm-2 0L10 6v10.816l4 1.1V7.124v.001Z"
  })));
};

var _path$3S;
function _extends$3S() { _extends$3S = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3S.apply(this, arguments); }
var SvgMapPinOff = function SvgMapPinOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3S({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3S || (_path$3S = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.036 1.924A8.88 8.88 0 0 1 12 1a9 9 0 0 1 9 9v.019a9.659 9.659 0 0 1-.915 3.926 1 1 0 1 1-1.81-.85A7.66 7.66 0 0 0 19 9.99 7 7 0 0 0 12 3h-.002a6.88 6.88 0 0 0-3.074.716 1 1 0 1 1-.888-1.792Zm-6.743-.631a1 1 0 0 1 1.414 0L6.125 4.71l.026.027L9.82 8.405a.981.981 0 0 1 .035.035l3.706 3.706a.904.904 0 0 1 .035.035l4.103 4.103a.967.967 0 0 1 .02.02l4.99 4.989a1 1 0 0 1-1.415 1.414l-4.218-4.218a30.936 30.936 0 0 1-4.456 4.297 1 1 0 0 1-1.219.014l.6-.8-.6.8H11.4l-.002-.002-.006-.005-.02-.015a11.849 11.849 0 0 1-.341-.268 32.347 32.347 0 0 1-3.777-3.596C5.23 16.638 3 13.395 3 10v-.002a9.06 9.06 0 0 1 1.161-4.423L1.293 2.707a1 1 0 0 1 0-1.414ZM5.647 7.06A7.06 7.06 0 0 0 5 10.001c0 2.604 1.77 5.36 3.747 7.585a30.347 30.347 0 0 0 3.249 3.13 28.94 28.94 0 0 0 3.66-3.647l-3.106-3.105A4 4 0 0 1 8 10.005V10h.586l-.55-.55m0 0-2.39-2.389 2.39 2.39Zm3.96 3.96L8.59 10.004l3.406 3.406Zm-.214-6.572a1 1 0 0 1 1.22-.716 4 4 0 0 1 2.867 2.88 1 1 0 1 1-1.938.496 2 2 0 0 0-1.433-1.44 1 1 0 0 1-.716-1.22Z",
    clipRule: "evenodd"
  })));
};

var _path$3R;
function _extends$3R() { _extends$3R = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3R.apply(this, arguments); }
var SvgMaximize = function SvgMaximize(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3R({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3R || (_path$3R = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.879 2.879A3 3 0 0 1 5 2h3a1 1 0 0 1 0 2H5a1 1 0 0 0-1 1v3a1 1 0 0 1-2 0V5a3 3 0 0 1 .879-2.121ZM15 3a1 1 0 0 1 1-1h3a3 3 0 0 1 3 3v3a1 1 0 1 1-2 0V5a1 1 0 0 0-1-1h-3a1 1 0 0 1-1-1ZM3 15a1 1 0 0 1 1 1v3a1 1 0 0 0 1 1h3a1 1 0 1 1 0 2H5a3 3 0 0 1-3-3v-3a1 1 0 0 1 1-1Zm18 0a1 1 0 0 1 1 1v3a3 3 0 0 1-3 3h-3a1 1 0 1 1 0-2h3a1 1 0 0 0 1-1v-3a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3Q;
function _extends$3Q() { _extends$3Q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3Q.apply(this, arguments); }
var SvgMedicineBottleFilled = function SvgMedicineBottleFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3Q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3Q || (_path$3Q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 7.994h18v11.99c0 1.306-.667 1.972-2 1.997-1.333.025-6 .025-14 0-1.333 0-2-.666-2-1.998V7.994ZM4.038 2H20c1.333 0 2 .666 2 1.998V6H2V3.998C2 2.666 2.68 2 4.038 2ZM11 12v1.996L8.996 14C8.332 14 8 14.333 8 15s.332 1 .996 1H11v2c0 .667.333 1 1 1s1-.333 1-1v-2h2.002c.665 0 .998-.333.998-1s-.333-1.001-.998-1.004L13 14v-2c0-.667-.333-1-1-1s-1 .333-1 1Z",
    clipRule: "evenodd"
  })));
};

var _path$3P;
function _extends$3P() { _extends$3P = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3P.apply(this, arguments); }
var SvgMedicineBottleOutline = function SvgMedicineBottleOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3P({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3P || (_path$3P = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.038 2H20c1.333 0 2 .666 2 1.998v2.997c0 .666-.33 1-.993 1L21 20c0 1.333-.668 2-2.005 2H5c-1.334 0-2-.667-2-2V7.994c-.667 0-1-.333-1-.999V3.998C2 2.666 2.68 2 4.038 2ZM4 3.998v1.998h16V3.998H4Zm1 3.996v11.99h14V7.993H5ZM11 11c0-.667.334-1 1-1 .667 0 1 .333 1 1v2l2.002-.004C15.668 13 16 13.333 16 14s-.332 1-.998 1H13v2c0 .667-.333 1-1 1-.666 0-1-.333-1-1v-2H8.997C8.332 15 8 14.667 8 14s.332-1 .997-1L11 12.996V11Z",
    clipRule: "evenodd"
  })));
};

var _path$3O;
function _extends$3O() { _extends$3O = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3O.apply(this, arguments); }
var SvgMegaphone = function SvgMegaphone(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3O({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3O || (_path$3O = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M21.605 5.204A1 1 0 0 1 22 6v12a1 1 0 0 1-1.217.976l-8.241-1.831a4.01 4.01 0 0 1-7.806-1.735l-1.953-.434A1 1 0 0 1 2 14v-3a1 1 0 0 1 .732-.963l18-5a1 1 0 0 1 .873.167ZM6.698 15.846a2.008 2.008 0 0 0 3.881.863l-3.881-.863ZM4 11.76v1.438l16 3.555V7.316L4 11.76Z",
    clipRule: "evenodd"
  })));
};

var _path$3N;
function _extends$3N() { _extends$3N = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3N.apply(this, arguments); }
var SvgMegaphoneOff = function SvgMegaphoneOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3N({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3N || (_path$3N = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 1.414 0L9.955 8.54a.993.993 0 0 1 .026.026l7.855 7.854.021.022 4.85 4.85a1 1 0 0 1-1.414 1.414l-4.654-4.654-4.097-.91a4.007 4.007 0 0 1-7.806-1.733l-1.953-.434A1 1 0 0 1 2 14v-3a1 1 0 0 1 .732-.963l4.61-1.282-6.05-6.048a1 1 0 0 1 0-1.414Zm7.673 9.087L4 11.76v1.438l10.005 2.221-5.04-5.04Zm-2.268 5.465a2.008 2.008 0 0 0 3.882.863l-3.882-.862ZM21.605 5.204A1 1 0 0 1 22 6v9.34a1 1 0 1 1-2 0V7.316l-6.043 1.678a1 1 0 0 1-.535-1.928l7.31-2.03a1 1 0 0 1 .873.168Z",
    clipRule: "evenodd"
  })));
};

var _path$3M;
function _extends$3M() { _extends$3M = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3M.apply(this, arguments); }
var SvgMeh = function SvgMeh(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3M({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3M || (_path$3M = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm7-3a1 1 0 0 1 1-1h.01a1 1 0 0 1 0 2H9a1 1 0 0 1-1-1Zm6 0a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H15a1 1 0 0 1-1-1Zm-7 6a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3L;
function _extends$3L() { _extends$3L = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3L.apply(this, arguments); }
var SvgMenuSquare = function SvgMenuSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3L({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3L || (_path$3L = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm4 3a1 1 0 0 1 1-1h10a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1Zm0 4a1 1 0 0 1 1-1h10a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1Zm0 4a1 1 0 0 1 1-1h10a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3K;
function _extends$3K() { _extends$3K = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3K.apply(this, arguments); }
var SvgMessageCircle = function SvgMessageCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3K({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3K || (_path$3K = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13.105 4.022a7.5 7.5 0 0 0-7.31 10.83 1 1 0 0 1 .054.764L4.58 19.42l3.803-1.268a1 1 0 0 1 .763.055 7.5 7.5 0 1 0 3.958-14.184Zm-6.158-.23a9.5 9.5 0 1 1 1.689 16.384l-5.32 1.773a1 1 0 0 1-1.265-1.265l1.774-5.32A9.5 9.5 0 0 1 6.947 3.791Z",
    clipRule: "evenodd"
  })));
};

var _path$3J;
function _extends$3J() { _extends$3J = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3J.apply(this, arguments); }
var SvgMessageSquare = function SvgMessageSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3J({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3J || (_path$3J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v13.586l2.293-2.293A1 1 0 0 1 7 16h12a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2.879 2.879A3 3 0 0 1 5 2h14a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H7.414l-3.707 3.707A1 1 0 0 1 2 21V5a3 3 0 0 1 .879-2.121Z",
    clipRule: "evenodd"
  })));
};

var _path$3I;
function _extends$3I() { _extends$3I = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3I.apply(this, arguments); }
var SvgMessageSquareDashed = function SvgMessageSquareDashed(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3I({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3I || (_path$3I = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4c-.548 0-1 .452-1 1v1a1 1 0 0 1-2 0V5c0-1.652 1.348-3 3-3h2a1 1 0 0 1 0 2H5Zm5-1a1 1 0 0 1 1-1h3a1 1 0 1 1 0 2h-3a1 1 0 0 1-1-1Zm7 0a1 1 0 0 1 1-1h1c1.652 0 3 1.348 3 3a1 1 0 1 1-2 0c0-.548-.452-1-1-1h-1a1 1 0 0 1-1-1Zm4 5a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0V9a1 1 0 0 1 1-1ZM3 9a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1Zm18 5a1 1 0 0 1 1 1c0 1.652-1.348 3-3 3h-1a1 1 0 1 1 0-2h1c.548 0 1-.452 1-1a1 1 0 0 1 1-1ZM3 15a1 1 0 0 1 1 1v2.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4A1 1 0 0 1 2 21v-5a1 1 0 0 1 1-1Zm7 2a1 1 0 0 1 1-1h3a1 1 0 1 1 0 2h-3a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3H;
function _extends$3H() { _extends$3H = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3H.apply(this, arguments); }
var SvgMessageSquarePlus = function SvgMessageSquarePlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3H({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3H || (_path$3H = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v13.586l2.293-2.293A1 1 0 0 1 7 16h12a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2.879 2.879A3 3 0 0 1 5 2h14a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H7.414l-3.707 3.707A1 1 0 0 1 2 21V5a3 3 0 0 1 .879-2.121ZM12 6a1 1 0 0 1 1 1v2h2a1 1 0 1 1 0 2h-2v2a1 1 0 1 1-2 0v-2H9a1 1 0 1 1 0-2h2V7a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3G;
function _extends$3G() { _extends$3G = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3G.apply(this, arguments); }
var SvgMessagesSquare = function SvgMessagesSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3G({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3G || (_path$3G = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 3c-.548 0-1 .452-1 1v8.586l2.293-2.293A1 1 0 0 1 6 10h6a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H4ZM1 4c0-1.652 1.348-3 3-3h8a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3H6.414l-3.707 3.707A1 1 0 0 1 1 15V4Zm16 5a1 1 0 0 1 1-1h2a3 3 0 0 1 3 3v11a1 1 0 0 1-1.707.707L17.586 19H12a3 3 0 0 1-3-3v-1a1 1 0 1 1 2 0v1a1 1 0 0 0 1 1h6a1 1 0 0 1 .707.293L21 19.586V11a1 1 0 0 0-1-1h-2a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3F;
function _extends$3F() { _extends$3F = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3F.apply(this, arguments); }
var SvgMicrophoneFilled = function SvgMicrophoneFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3F({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3F || (_path$3F = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 7h3V5H8V4c0-1.334.667-2 2-2h4c1.333 0 2 .666 2 2v9.024c0 1.317-.667 1.975-2 1.975h-4c-1.333 0-2-.667-2-2V7Zm11 3a1 1 0 0 1 1 1c0 4.071-3.061 7.435-7 7.93V20h3a1 1 0 0 1 0 2H8a1 1 0 0 1 0-2h3v-1.07c-3.939-.495-7-3.859-7-7.93a1 1 0 1 1 2 0c0 3.308 2.691 6 6 6s6-2.692 6-6a1 1 0 0 1 1-1ZM8 10v2h3v-2H8Z",
    clipRule: "evenodd"
  })));
};

var _path$3E;
function _extends$3E() { _extends$3E = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3E.apply(this, arguments); }
var SvgMicrophoneOutline = function SvgMicrophoneOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3E({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3E || (_path$3E = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M19 10a1 1 0 0 1 1 1c0 4.071-3.061 7.435-7 7.93V20h3a1 1 0 0 1 0 2H8a1 1 0 0 1 0-2h3v-1.07c-3.939-.495-7-3.859-7-7.93a1 1 0 1 1 2 0c0 3.308 2.691 6 6 6s6-2.692 6-6a1 1 0 0 1 1-1Zm-9-8h4c1.333 0 2 .666 2 2v9.024c0 1.317-.667 1.975-2 1.975h-4c-1.333 0-2-.667-2-2v-9C8 2.666 8.667 2 10 2Zm0 2v9h4V4h-4Zm0 2h2v2h-2V6Zm0 3h2v2h-2V9Z",
    clipRule: "evenodd"
  })));
};

var _path$3D;
function _extends$3D() { _extends$3D = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3D.apply(this, arguments); }
var SvgMinimize = function SvgMinimize(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3D({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3D || (_path$3D = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20.293 2.293a1 1 0 1 1 1.414 1.414L16.414 9H20a1 1 0 1 1 0 2h-6a1 1 0 0 1-1-1V4a1 1 0 1 1 2 0v3.586l5.293-5.293ZM3 14a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-3.586l-5.293 5.293a1 1 0 0 1-1.414-1.414L7.586 15H4a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3C;
function _extends$3C() { _extends$3C = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3C.apply(this, arguments); }
var SvgMinus = function SvgMinus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3C({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3C || (_path$3C = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 12a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3B;
function _extends$3B() { _extends$3B = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3B.apply(this, arguments); }
var SvgMinusCircle = function SvgMinusCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3B({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3B || (_path$3B = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm6 0a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3A;
function _extends$3A() { _extends$3A = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3A.apply(this, arguments); }
var SvgMinusSquare = function SvgMinusSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3A({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3A || (_path$3A = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm5 7a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3z;
function _extends$3z() { _extends$3z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3z.apply(this, arguments); }
var SvgModule = function SvgModule(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3z || (_path$3z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 5a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm7 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm7 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0ZM3 12a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm7 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm7 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0ZM3 19a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm7 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm7 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z",
    clipRule: "evenodd"
  })));
};

var _path$3y;
function _extends$3y() { _extends$3y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3y.apply(this, arguments); }
var SvgMonitorCheck = function SvgMonitorCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3y || (_path$3y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H4Zm9 14h7a3 3 0 0 0 3-3V5a3 3 0 0 0-3-3H4a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h7v2H8a1 1 0 1 0 0 2h8a1 1 0 1 0 0-2h-3v-2Zm2.707-10.707a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L11 10.586l3.293-3.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$3x;
function _extends$3x() { _extends$3x = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3x.apply(this, arguments); }
var SvgMonitorPlay = function SvgMonitorPlay(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3x({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3x || (_path$3x = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H4Zm9 14h7a3 3 0 0 0 3-3V5a3 3 0 0 0-3-3H4a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h7v2H8a1 1 0 1 0 0 2h8a1 1 0 1 0 0-2h-3v-2ZM9.507 6.13a1 1 0 0 1 1.008.013l5 3a1 1 0 0 1 0 1.714l-5 3A1 1 0 0 1 9 13V7a1 1 0 0 1 .507-.87ZM11 8.766v2.468L13.056 10 11 8.766Z",
    clipRule: "evenodd"
  })));
};

var _path$3w;
function _extends$3w() { _extends$3w = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3w.apply(this, arguments); }
var SvgMonitorSmartphone = function SvgMonitorSmartphone(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3w({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3w || (_path$3w = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.879 3.879A3 3 0 0 1 4 3h12a3 3 0 0 1 3 3v2a1 1 0 1 1-2 0V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h8a1 1 0 1 1 0 2h-1v2h1a1 1 0 1 1 0 2H7a1 1 0 1 1 0-2h2v-2H4a3 3 0 0 1-3-3V6a3 3 0 0 1 .879-2.121ZM18 13a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-6a1 1 0 0 0-1-1h-2Zm-3 1a3 3 0 0 1 3-3h2a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3h-2a3 3 0 0 1-3-3v-6Z",
    clipRule: "evenodd"
  })));
};

var _path$3v;
function _extends$3v() { _extends$3v = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3v.apply(this, arguments); }
var SvgMoreHorizontal = function SvgMoreHorizontal(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3v({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3v || (_path$3v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 12a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm7 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm7 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z",
    clipRule: "evenodd"
  })));
};

var _path$3u;
function _extends$3u() { _extends$3u = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3u.apply(this, arguments); }
var SvgMouse = function SvgMouse(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3u({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3u || (_path$3u = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a6 6 0 0 0-6 6v6a6 6 0 0 0 12 0V9a6 6 0 0 0-6-6ZM4 9a8 8 0 1 1 16 0v6a8 8 0 1 1-16 0V9Zm8-4a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0V6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3t;
function _extends$3t() { _extends$3t = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3t.apply(this, arguments); }
var SvgMousePointer = function SvgMousePointer(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3t({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3t || (_path$3t = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.293 2.293a1 1 0 0 1 1.092-.216l16.97 7.07a1 1 0 0 1-.063 1.87l-5.868 1.993 5.283 5.283a1 1 0 1 1-1.414 1.414l-5.283-5.283-1.993 5.868a1 1 0 0 1-1.87.063l-7.07-16.97a1 1 0 0 1 .216-1.092Zm2.564 2.564L9.975 17.14l1.658-4.883a1 1 0 0 1 .625-.625l4.883-1.658L4.857 4.857Z",
    clipRule: "evenodd"
  })));
};

var _path$3s;
function _extends$3s() { _extends$3s = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3s.apply(this, arguments); }
var SvgMousePointerClick = function SvgMousePointerClick(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3s({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3s || (_path$3s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.929 1.273a1 1 0 0 1 1.225.707l.777 2.897a1 1 0 1 1-1.932.518l-.777-2.897a1 1 0 0 1 .707-1.225Zm7.728 2.07a1 1 0 0 1 0 1.414L12.535 6.88a1 1 0 1 1-1.414-1.414l2.122-2.122a1 1 0 0 1 1.414 0ZM1.272 6.929a1 1 0 0 1 1.225-.707L5.395 7a1 1 0 0 1-.518 1.932l-2.898-.777a1 1 0 0 1-.707-1.225Zm7.02 1.364a1 1 0 0 1 1.093-.216l12 5a1 1 0 0 1-.064 1.87l-3.703 1.257 3.403 3.403a1 1 0 0 1-1.414 1.414l-3.403-3.403-1.257 3.703a1 1 0 0 1-1.87.064l-5-12a1 1 0 0 1 .216-1.092Zm2.565 2.564 3.047 7.314.923-2.718a1 1 0 0 1 .626-.626l2.718-.923-7.314-3.047Zm-3.98.264a1 1 0 0 1 .001 1.414l-2.12 2.122a1 1 0 0 1-1.415-1.414l2.12-2.122a1 1 0 0 1 1.415 0Z",
    clipRule: "evenodd"
  })));
};

var _path$3r;
function _extends$3r() { _extends$3r = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3r.apply(this, arguments); }
var SvgMove = function SvgMove(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3r({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3r || (_path$3r = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 1.293a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1-1.414 1.414L13 4.414V11h6.586l-1.293-1.293a1 1 0 0 1 1.414-1.414l3 3a1 1 0 0 1 0 1.414l-3 3a1 1 0 0 1-1.414-1.414L19.586 13H13v6.586l1.293-1.293a1 1 0 0 1 1.414 1.414l-3 3a1 1 0 0 1-1.414 0l-3-3a1 1 0 1 1 1.414-1.414L11 19.586V13H4.414l1.293 1.293a1 1 0 1 1-1.414 1.414l-3-3a1 1 0 0 1 0-1.414l3-3a1 1 0 0 1 1.414 1.414L4.414 11H11V4.414L9.707 5.707a1 1 0 0 1-1.414-1.414l3-3Z",
    clipRule: "evenodd"
  })));
};

var _path$3q;
function _extends$3q() { _extends$3q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3q.apply(this, arguments); }
var SvgMoveDiagonalL2R = function SvgMoveDiagonalL2R(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3q || (_path$3q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0V7.414L7.414 18H11a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1v-6a1 1 0 1 1 2 0v3.586L16.586 6H13a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3p;
function _extends$3p() { _extends$3p = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3p.apply(this, arguments); }
var SvgMoveDiagonalR2L = function SvgMoveDiagonalR2L(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3p({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3p || (_path$3p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 5a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H7.414L18 16.586V13a1 1 0 1 1 2 0v6a1 1 0 0 1-1 1h-6a1 1 0 1 1 0-2h3.586L6 7.414V11a1 1 0 1 1-2 0V5Z",
    clipRule: "evenodd"
  })));
};

var _path$3o;
function _extends$3o() { _extends$3o = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3o.apply(this, arguments); }
var SvgMoveDown = function SvgMoveDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3o({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3o || (_path$3o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 1a1 1 0 0 1 1 1v17.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L11 19.586V2a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3n;
function _extends$3n() { _extends$3n = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3n.apply(this, arguments); }
var SvgMoveDownLeft = function SvgMoveDownLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3n({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3n || (_path$3n = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M19.707 4.293a1 1 0 0 1 0 1.414L7.414 18H11a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1v-6a1 1 0 1 1 2 0v3.586L18.293 4.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$3m;
function _extends$3m() { _extends$3m = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3m.apply(this, arguments); }
var SvgMoveDownRight = function SvgMoveDownRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3m({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3m || (_path$3m = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.293 4.293a1 1 0 0 1 1.414 0L18 16.586V13a1 1 0 1 1 2 0v6a1 1 0 0 1-1 1h-6a1 1 0 1 1 0-2h3.586L4.293 5.707a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$3l;
function _extends$3l() { _extends$3l = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3l.apply(this, arguments); }
var SvgMoveHorizontal = function SvgMoveHorizontal(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3l({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3l || (_path$3l = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.707 7.293a1 1 0 0 1 0 1.414L4.414 11h15.172l-2.293-2.293a1 1 0 0 1 1.414-1.414l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414-1.414L19.586 13H4.414l2.293 2.293a1 1 0 1 1-1.414 1.414l-4-4a1 1 0 0 1 0-1.414l4-4a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$3k;
function _extends$3k() { _extends$3k = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3k.apply(this, arguments); }
var SvgMoveLeft = function SvgMoveLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3k({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3k || (_path$3k = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.707 7.293a1 1 0 0 1 0 1.414L4.414 11H22a1 1 0 1 1 0 2H4.414l2.293 2.293a1 1 0 1 1-1.414 1.414l-4-4a1 1 0 0 1 0-1.414l4-4a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$3j;
function _extends$3j() { _extends$3j = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3j.apply(this, arguments); }
var SvgMoveRight = function SvgMoveRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3j({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3j || (_path$3j = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M17.293 7.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414-1.414L19.586 13H2a1 1 0 1 1 0-2h17.586l-2.293-2.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$3i;
function _extends$3i() { _extends$3i = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3i.apply(this, arguments); }
var SvgMoveUp = function SvgMoveUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3i({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3i || (_path$3i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 1.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L13 4.414V22a1 1 0 1 1-2 0V4.414L8.707 6.707a1 1 0 0 1-1.414-1.414l4-4Z",
    clipRule: "evenodd"
  })));
};

var _path$3h;
function _extends$3h() { _extends$3h = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3h.apply(this, arguments); }
var SvgMoveUpLeft = function SvgMoveUpLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3h({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3h || (_path$3h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 5a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H7.414l12.293 12.293a1 1 0 0 1-1.414 1.414L6 7.414V11a1 1 0 1 1-2 0V5Z",
    clipRule: "evenodd"
  })));
};

var _path$3g;
function _extends$3g() { _extends$3g = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3g.apply(this, arguments); }
var SvgMoveUpRight = function SvgMoveUpRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3g({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3g || (_path$3g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0V7.414L5.707 19.707a1 1 0 0 1-1.414-1.414L16.586 6H13a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3f;
function _extends$3f() { _extends$3f = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3f.apply(this, arguments); }
var SvgMoveVertical = function SvgMoveVertical(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3f({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3f || (_path$3f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 1.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L13 4.414v15.172l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L11 19.586V4.414L8.707 6.707a1 1 0 0 1-1.414-1.414l4-4Z",
    clipRule: "evenodd"
  })));
};

var _path$3e;
function _extends$3e() { _extends$3e = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3e.apply(this, arguments); }
var SvgMugFilled = function SvgMugFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3e({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3e || (_path$3e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14 8c1.333 0 2 .667 2 2v1c2.667 0 4 1.333 4 3.999v1C20 18.667 18.667 20 16 20c0 1.333-.666 2-1.999 2H5c-1.333 0-2-.667-2-2V10c0-1.333.667-2 2-2h9Zm2 5v5c1.273 0 1.938-.607 1.996-1.822L18 16v-.997C18 13.668 17.333 13 16 13ZM7.005 2C7.668 2 8 2.333 8 3v3c0 .667-.334 1-1 1-.666 0-1-.333-1-1V3c.006-.667.341-1 1.005-1ZM11 4c.666 0 1 .333 1 1v1c0 .667-.332 1-.996 1-.663 0-.998-.333-1.004-1V5c0-.667.334-1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3d;
function _extends$3d() { _extends$3d = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3d.apply(this, arguments); }
var SvgMugOutline = function SvgMugOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3d({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3d || (_path$3d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14.5 8c1.333 0 2 .667 2 2v1c2.667 0 4 1.333 4 3.999v1c0 2.667-1.333 4.001-4 4.001 0 1.333-.666 2-1.999 2H5.5c-1.333 0-2-.667-2-2V10c0-1.333.667-2 2-2h9Zm0 2h-9v10h9V10Zm2 3v5c1.273 0 1.938-.607 1.996-1.822L18.5 16v-.997c0-1.335-.667-2.003-2-2.003ZM7.505 2c.663 0 .995.333.995 1v3c0 .667-.334 1-1 1-.666 0-1-.333-1-1V3c.006-.667.341-1 1.005-1ZM11.5 4c.666 0 1 .333 1 1v1c0 .667-.332 1-.996 1-.663 0-.998-.333-1.004-1V5c0-.667.334-1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3c, _path2$i;
function _extends$3c() { _extends$3c = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3c.apply(this, arguments); }
var SvgMultipleUsersFilled = function SvgMultipleUsersFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3c({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3c || (_path$3c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.25 12.8a5 5 0 1 0-6.5 0A6.997 6.997 0 0 0 2 19a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1 7 7 0 0 0-3.75-6.2Z",
    clipRule: "evenodd"
  })), _path2$i || (_path2$i = /*#__PURE__*/React.createElement("path", {
    d: "M16.105 8.688c0 .926-.241 1.79-.658 2.517-.331.578-.238 1.384.247 1.84.127.12.25.245.369.377 1.105 1.23 1.727 2.9 1.727 4.64 0 .518-.377.938-.843.938h4.21c.466 0 .843-.42.843-.938 0-1.74-.621-3.41-1.727-4.64a6.178 6.178 0 0 0-.369-.377c-.485-.456-.578-1.262-.247-1.84a5.058 5.058 0 0 0 .659-2.518C20.316 6.1 18.43 4 16.106 4c-.23 0-.453.02-.672.06-.716.127-.743 1.042-.307 1.623.61.814.978 1.861.978 3.005Z"
  })));
};

var _path$3b;
function _extends$3b() { _extends$3b = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3b.apply(this, arguments); }
var SvgMultipleUsersOutline = function SvgMultipleUsersOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3b({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3b || (_path$3b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 6a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm3.25 6.8a5 5 0 1 0-6.5 0A6.997 6.997 0 0 0 1 19a1 1 0 1 0 2 0 5 5 0 1 1 10 0 1 1 0 1 0 2 0 7 7 0 0 0-3.75-6.2ZM15 5a1 1 0 0 1 1-1 5 5 0 0 1 3.25 8.8A6.995 6.995 0 0 1 23 19a1 1 0 1 1-2 0 5 5 0 0 0-5-5 1 1 0 1 1 0-2 3 3 0 0 0 0-6 1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$3a;
function _extends$3a() { _extends$3a = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3a.apply(this, arguments); }
var SvgNeedleFilled = function SvgNeedleFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3a({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$3a || (_path$3a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18.87 13.212c.942.943.942 1.886 0 2.828l-.708.708 1.414 1.414 1.415-1.414c.471-.472.942-.472 1.414 0 .471.471.471.942 0 1.414l-4.243 4.242c-.47.472-.943.472-1.414 0-.472-.47-.472-.942 0-1.414l1.414-1.414-1.414-1.414-.707.707c-.943.943-1.886.943-2.829 0-1.413-1.415-4.9-4.9-7.07-7.07-.943-.944-.943-1.887 0-2.83l.706-.707L2.81 4.247h2.83l2.624 2.601.707-.707c.943-.943 1.885-.943 2.828 0l7.071 7.071Zm-9.897-1.41 1.414 1.414.707-.707c.472-.47.472-.943 0-1.414-.47-.472-.942-.472-1.414 0l-.707.707Zm2.83 2.828 1.413 1.415.707-.708c.471-.47.471-.942 0-1.414-.472-.47-.943-.47-1.414 0l-.707.707Z",
    clipRule: "evenodd"
  })));
};

var _path$39;
function _extends$39() { _extends$39 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$39.apply(this, arguments); }
var SvgNeedleOutline = function SvgNeedleOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$39({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$39 || (_path$39 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18.87 13.212c.942.943.942 1.886 0 2.828l-.708.708 1.414 1.414 1.415-1.414c.471-.472.942-.472 1.414 0 .471.471.471.942 0 1.414l-4.243 4.242c-.47.472-.943.472-1.414 0-.472-.47-.472-.942 0-1.414l1.414-1.414-1.414-1.414-.707.707c-.943.943-1.886.943-2.829 0-1.413-1.415-4.9-4.9-7.07-7.07-.943-.944-.943-1.887 0-2.83l.706-.707L2.81 4.247h2.83l2.624 2.601.707-.707c.943-.943 1.885-.943 2.828 0l7.071 7.071Zm-1.415 1.414-7.07-7.07-2.829 2.828 1.414 1.414.707-.707c.471-.472.943-.472 1.414 0 .472.471.472.943 0 1.414l-.707.707 1.414 1.414.707-.707c.476-.467.947-.467 1.415 0 .467.468.467.94 0 1.414l-.708.707 1.415 1.415 2.828-2.829Z",
    clipRule: "evenodd"
  })));
};

var _path$38;
function _extends$38() { _extends$38 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$38.apply(this, arguments); }
var SvgNurse = function SvgNurse(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$38({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$38 || (_path$38 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16 16c4 0 6 1.333 6 4 0 1.333-.667 2-2 2H4c-1.333 0-2-.667-2-2 0-2.667 2.001-4 6.004-4H16Zm0 2H8c-2.667 0-4 .667-4 2h16c0-1.333-1.333-2-4-2ZM11.998 2c1.76 0 3.522.258 5.284.775a1 1 0 0 1 .718.96V9.6a1 1 0 0 1-1 1h-.018l-.035-.001c0 1.175-1.434 4.4-4.95 4.4-3.422 0-4.873-3.085-4.947-4.305l-.003-.095A.989.989 0 0 1 6 9.613V3.734a1 1 0 0 1 .718-.96A18.654 18.654 0 0 1 11.998 2ZM12 10c-1.64 0-2.99.248-2.99.265a3 3 0 0 0 5.979-.017S13.64 10 12 10Zm-.024-6c-1.17 0-2.332.133-3.484.4L8 4.521V8.5a16.47 16.47 0 0 1 4.002-.5c1.167 0 2.333.128 3.498.383l.5.117V4.522A15.99 15.99 0 0 0 11.977 4Zm.523.5v1.002l1.004.002v.998H12.5V7.5h-1v-.998h-1.002v-.998H11.5V4.5h1Z",
    clipRule: "evenodd"
  })));
};

var _path$37;
function _extends$37() { _extends$37 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$37.apply(this, arguments); }
var SvgNurseView = function SvgNurseView(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$37({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$37 || (_path$37 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.072 16c.123.758.269 1.296.603 2h-.671c-2.645 0-3.98.667-4.004 2h6.092c1.28 1.21 2.725 1.837 4.508 2H4c-1.333 0-2-.667-2-2 .045-2.667 2.07-4 6.072-4ZM15 10a5 5 0 0 1 4.178 7.747l.095.096 2.483 2.492c.393.447.243 1.013-.087 1.342-.33.328-.896.469-1.316.054l-.044-.043-2.532-2.53A5 5 0 1 1 15 10Zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6ZM11.998 2c1.76 0 3.522.258 5.284.775a1 1 0 0 1 .718.96V8.68a7.076 7.076 0 0 0-2-.619V4.52A15.84 15.84 0 0 0 12 4a15.85 15.85 0 0 0-4 .52v3.978c2.972-.792 5.81-.397 5.81-.397-1.875.302-3.25 1.393-3.81 2.003 0 0-.61.064-.992.147.004.349.197.828.197.828-.692 1-.985 2.167-.985 2.167-.757-.856-1.122-1.943-1.168-2.529l-.005-.117A.989.989 0 0 1 6 9.613V3.734a1 1 0 0 1 .718-.96A18.654 18.654 0 0 1 11.998 2Zm.502 2.5v1.002l1.004.002v.998H12.5V7.5h-1v-.998h-1.002v-.998H11.5V4.5h1Z",
    clipRule: "evenodd"
  })));
};

var _path$36;
function _extends$36() { _extends$36 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$36.apply(this, arguments); }
var SvgO2Filled = function SvgO2Filled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$36({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$36 || (_path$36 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7.912 17.552a5.338 5.338 0 0 1-2.016-.368 4.059 4.059 0 0 1-1.552-1.096c-.427-.485-.757-1.088-.992-1.808S3 12.725 3 11.776c0-.95.117-1.784.352-2.504s.565-1.323.992-1.808c.427-.485.944-.85 1.552-1.096A5.34 5.34 0 0 1 7.912 6a5.34 5.34 0 0 1 2.016.368 4.05 4.05 0 0 1 1.552 1.096c.427.485.757 1.088.992 1.808s.352 1.555.352 2.504c0 .95-.117 1.784-.352 2.504s-.565 1.323-.992 1.808c-.427.485-.944.85-1.552 1.096a5.338 5.338 0 0 1-2.016.368Zm0-2.16c.736 0 1.31-.24 1.72-.72.41-.48.616-1.168.616-2.064v-1.664c0-.896-.205-1.584-.616-2.064-.41-.48-.984-.72-1.72-.72s-1.31.24-1.72.72c-.41.48-.616 1.168-.616 2.064v1.664c0 .896.205 1.584.616 2.064.41.48.984.72 1.72.72Zm12.088.21.1.1V17.3l-.1.1h-5.823l-.1-.1.032-1.81 2.534-2.332c.226-.21.417-.4.572-.571.153-.17.276-.332.371-.491.094-.157.16-.311.202-.463.04-.152.06-.316.06-.494v-.177a.888.888 0 0 0-.083-.4.857.857 0 0 0-.222-.284.952.952 0 0 0-.315-.172 1.17 1.17 0 0 0-.364-.058c-.182 0-.339.03-.47.092a1.1 1.1 0 0 0-.346.25c-.1.107-.182.23-.249.368a2.212 2.212 0 0 0-.157.455l-.136.07-1.544-.634-.058-.122c.097-.312.23-.61.4-.89.17-.285.384-.534.64-.748a2.89 2.89 0 0 1 .909-.506 3.546 3.546 0 0 1 1.176-.183c.46 0 .87.07 1.227.21.359.14.663.333.912.58.25.246.44.539.572.877.13.338.195.703.195 1.095s-.061.75-.183 1.075a3.806 3.806 0 0 1-.49.904c-.211.288-.45.554-.715.795a45.98 45.98 0 0 1-.84.75l-1.323 1.117H20v-.001Z",
    clipRule: "evenodd"
  })));
};

var _path$35;
function _extends$35() { _extends$35 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$35.apply(this, arguments); }
var SvgOpenDoor = function SvgOpenDoor(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$35({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$35 || (_path$35 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.999 2h11.99c1.334 0 2 .667 2.005 2v15.996c-.003 1.33-.671 1.998-2.004 2.004-1.998.009-9.982.009-11.996 0-1.342-.006-2.007-.674-1.995-2.004V4c0-1.333.667-2 2-2Zm7.73 13.754c-.171.311-.454.577-.847.797L6 20.005h12v-16h-3.972l-.043 10.766a1.97 1.97 0 0 1-.255.983ZM10.996 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z",
    clipRule: "evenodd"
  })));
};

var _path$34;
function _extends$34() { _extends$34 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$34.apply(this, arguments); }
var SvgPackageCheck = function SvgPackageCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$34({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$34 || (_path$34 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.502 1.403a3 3 0 0 1 2.998.001l7 4A3 3 0 0 1 22 7.999V10a1 1 0 1 1-2 0V8.56l-7 4.019v8l1.505-.858a1 1 0 0 1 .99 1.738L13.5 22.596l-.003.002a3 3 0 0 1-2.995 0l-.002-.002-6.996-3.998-.004-.002A3 3 0 0 1 2 16.001V7.999a3 3 0 0 1 1.5-2.595l.004-.002L7.006 3.4a.974.974 0 0 1 .095-.054l3.401-1.943ZM7.55 5.393 5.019 6.84 12 10.847l2.51-1.44-6.961-4.013Zm8.967 2.862L9.56 4.245l1.936-1.107.004-.002a1 1 0 0 1 1 0l6.481 3.703-2.465 1.416ZM11 12.579 4 8.561v7.438a1 1 0 0 0 .498.864l.002.001 6.5 3.714V12.58Zm11.707.714a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L18 16.586l3.293-3.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$33;
function _extends$33() { _extends$33 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$33.apply(this, arguments); }
var SvgPackageOpen = function SvgPackageOpen(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$33({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$33 || (_path$33 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.91 3.113h-.001l-1.79.958 5.897 3.156.478-.27a1.12 1.12 0 0 0-.06-1.942l-.002-.001-3.651-1.9a.93.93 0 0 0-.87-.001Zm-.942-1.765L12 2.937 9.032 1.348l-.003-.001a2.93 2.93 0 0 0-2.744-.002L2.638 3.243l-.018.01a3.12 3.12 0 0 0-.553 5.086 3.12 3.12 0 0 0 .412 5.024l.012.008.509.3v3.198a3.06 3.06 0 0 0 1.649 2.718l6.505 3.346c.168.086.348.12.524.108A.998.998 0 0 0 12.5 23l6.961-3.413a3.059 3.059 0 0 0 1.65-2.718v-3.262l.398-.236.013-.008a3.12 3.12 0 0 0 .433-5.005 3.12 3.12 0 0 0-.587-5.112l-3.653-1.901a2.93 2.93 0 0 0-2.744.002l-.003.001Zm4.142 13.44-1.391.823c-.008.005-.016.01-.024.013a3 3 0 0 1-2.91 0l-.008-.004L13 14.612v5.916l5.54-2.716a1.06 1.06 0 0 0 .57-.941v-2.082ZM5 16.872v-2.017l1.281.757a3 3 0 0 0 2.934.014L11 14.615v5.989l-5.43-2.793A1.06 1.06 0 0 1 5 16.87ZM3.55 6.954l.459.26L9.88 4.07l-1.79-.958a.93.93 0 0 0-.872 0l-.007.004-3.64 1.894a1.12 1.12 0 0 0-.022 1.943ZM12 5.205 6.067 8.381l5.94 3.37 5.947-3.36L12 5.206ZM3.566 9.72l.415-.222 5.995 3.4-1.734.98a1 1 0 0 1-.957.004l-3.769-2.228a1.12 1.12 0 0 1 .05-1.934Zm12.193 4.158-1.722-.976 6.005-3.392.392.21a1.12 1.12 0 0 1 .05 1.934l-3.769 2.228a1 1 0 0 1-.956-.004Z",
    clipRule: "evenodd"
  })));
};

var _path$32;
function _extends$32() { _extends$32 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$32.apply(this, arguments); }
var SvgPackageSearch = function SvgPackageSearch(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$32({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$32 || (_path$32 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.502 1.403a3 3 0 0 1 2.998.001l7 4A3 3 0 0 1 22 7.999V10a1 1 0 1 1-2 0V8.56l-7 4.019v8l1.505-.858a1 1 0 0 1 .99 1.738L13.5 22.596l-.003.002a3 3 0 0 1-2.995 0l-.002-.002-6.996-3.998-.004-.002A3 3 0 0 1 2 16.001V7.999a3 3 0 0 1 1.5-2.595l.004-.002L7.006 3.4a.974.974 0 0 1 .095-.054l3.401-1.943ZM7.55 5.393 5.019 6.84 12 10.847l2.51-1.44-6.961-4.013Zm8.967 2.862L9.56 4.245l1.936-1.107.004-.002a1 1 0 0 1 1 0l6.481 3.703-2.465 1.416ZM11 12.579 4 8.561v7.438a1 1 0 0 0 .498.864l.002.001 6.5 3.714V12.58ZM18.5 14a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3ZM15 15.5a3.5 3.5 0 1 1 6.58 1.665l1.127 1.128a1 1 0 1 1-1.414 1.414l-1.128-1.128A3.5 3.5 0 0 1 15 15.5Z",
    clipRule: "evenodd"
  })));
};

var _path$31;
function _extends$31() { _extends$31 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$31.apply(this, arguments); }
var SvgPartyPopper = function SvgPartyPopper(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$31({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$31 || (_path$31 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M22.948 1.683a1 1 0 0 1-.63 1.265l-2.242.75a1.9 1.9 0 0 0-1.284 2.045l.001.011A2.461 2.461 0 0 1 16.35 8.5h-.38c-.392 0-.71.27-.778.627l-.001.007-.21 1.06a1 1 0 0 1-1.962-.388l.21-1.057A2.782 2.782 0 0 1 15.97 6.5h.38a.461.461 0 0 0 .457-.51 3.9 3.9 0 0 1 2.636-4.19l2.24-.75a1 1 0 0 1 1.265.632Zm-12.321-.61a1 1 0 0 1 1.3.554l.333.825a2.467 2.467 0 0 1-1.899 3.338.443.443 0 0 0-.361.44V7a1 1 0 1 1-2 0v-.77c0-1.181.857-2.234 2.056-2.417a.468.468 0 0 0 .345-.623l-.329-.817a1 1 0 0 1 .555-1.3ZM14 2a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H15a1 1 0 0 1-1-1ZM3 3a1 1 0 0 1 1-1h.01a1 1 0 0 1 0 2H4a1 1 0 0 1-1-1Zm18 5a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H22a1 1 0 0 1-1-1ZM4.783 11.175c.09-.315.25-.622.51-.882.447-.447 1.027-.599 1.56-.602.529-.003 1.08.135 1.606.346 1.059.423 2.211 1.219 3.248 2.256 1.037 1.037 1.832 2.19 2.256 3.248.212.527.35 1.078.346 1.605-.003.534-.155 1.114-.602 1.561a1.941 1.941 0 0 1-.466.344 1.049 1.049 0 0 1-.207.102l-10.7 3.79a1 1 0 0 1-1.276-1.278l3.725-10.49Zm.88 3.501-2.018 5.68 5.686-2.014a11.51 11.51 0 0 1-2.038-1.635 11.519 11.519 0 0 1-1.63-2.03Zm6.627 2.614a.503.503 0 0 0 .02-.156c0-.186-.052-.472-.203-.85-.301-.751-.921-1.684-1.814-2.577s-1.825-1.513-2.577-1.814c-.378-.151-.664-.203-.85-.202a.532.532 0 0 0-.15.017l-.017.048a.7.7 0 0 0-.008.11c-.001.186.051.472.202.85.302.752.921 1.684 1.814 2.577s1.825 1.512 2.577 1.814c.378.151.664.203.85.202a.503.503 0 0 0 .156-.02Zm5.925-3.683a2.468 2.468 0 0 1 3.333-1.867l.005.002.82.33a1 1 0 0 1-.746 1.856l-.815-.328h-.002a.468.468 0 0 0-.624.348A2.45 2.45 0 0 1 17.77 16H17a1 1 0 1 1 0-2h.77a.45.45 0 0 0 .442-.375l.003-.018ZM21 20a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H22a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$30;
function _extends$30() { _extends$30 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$30.apply(this, arguments); }
var SvgPause = function SvgPause(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$30({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$30 || (_path$30 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4Zm2 1v14h2V5H7Zm6-1a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1V4Zm2 1v14h2V5h-2Z",
    clipRule: "evenodd"
  })));
};

var _path$2$;
function _extends$2$() { _extends$2$ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2$.apply(this, arguments); }
var SvgPauseCircle = function SvgPauseCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2$({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2$ || (_path$2$ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm9-4a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0V9a1 1 0 0 1 1-1Zm4 0a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0V9a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$2_;
function _extends$2_() { _extends$2_ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2_.apply(this, arguments); }
var SvgPauseFilled = function SvgPauseFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2_({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2_ || (_path$2_ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 3.5a.5.5 0 0 0-.5.5v16a.5.5 0 0 0 .5.5h4a.5.5 0 0 0 .5-.5V4a.5.5 0 0 0-.5-.5H6Zm8 0a.5.5 0 0 0-.5.5v16a.5.5 0 0 0 .5.5h4a.5.5 0 0 0 .5-.5V4a.5.5 0 0 0-.5-.5h-4Z",
    clipRule: "evenodd"
  })));
};

var _path$2Z;
function _extends$2Z() { _extends$2Z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2Z.apply(this, arguments); }
var SvgPen = function SvgPen(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2Z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2Z || (_path$2Z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M19.86 3.118a1.868 1.868 0 0 0-2.097.528.998.998 0 0 1-.056.061L4.395 17.02l-.97 3.556 3.556-.97L20.293 6.293a.88.88 0 0 1 .072-.065 1.833 1.833 0 0 0 .665-1.319 1.812 1.812 0 0 0-.533-1.375 1.852 1.852 0 0 0-.637-.416ZM19.022.994a3.868 3.868 0 0 1 2.89 1.125 3.828 3.828 0 0 1 1.115 2.888 3.813 3.813 0 0 1-1.352 2.732L8.207 21.207a1 1 0 0 1-.444.258l-5.5 1.5a1 1 0 0 1-1.228-1.228l1.5-5.5a1 1 0 0 1 .258-.444L16.266 2.32A3.848 3.848 0 0 1 19.022.994Z",
    clipRule: "evenodd"
  })));
};

var _path$2Y;
function _extends$2Y() { _extends$2Y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2Y.apply(this, arguments); }
var SvgPenFilled = function SvgPenFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2Y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2Y || (_path$2Y = /*#__PURE__*/React.createElement("path", {
    d: "M19.043 1.003a3.368 3.368 0 0 1 2.515.98 3.329 3.329 0 0 1 .97 2.509 3.315 3.315 0 0 1-1.19 2.387L20.06 8.156l-4.707-4.707 1.278-1.279a3.347 3.347 0 0 1 2.411-1.167Zm-5.103 3.86L3.145 15.656a.5.5 0 0 0-.128.222l-1.5 5.5a.5.5 0 0 0 .614.614l5.5-1.5a.5.5 0 0 0 .222-.129L18.647 9.57l-4.708-4.707Z"
  })));
};

var _path$2X;
function _extends$2X() { _extends$2X = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2X.apply(this, arguments); }
var SvgPenOutline = function SvgPenOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2X({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2X || (_path$2X = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M19.86 3.127a1.868 1.868 0 0 0-2.097.528.998.998 0 0 1-.056.062l-1.293 1.292L19 7.595l1.293-1.293a1.11 1.11 0 0 1 .072-.065 1.833 1.833 0 0 0 .665-1.318 1.812 1.812 0 0 0-.533-1.376 1.852 1.852 0 0 0-.637-.416Zm-.151 6.588-.002.002-.002.001L8.207 21.216a1 1 0 0 1-.444.258l-5.5 1.5a1 1 0 0 1-1.228-1.228l1.5-5.5a1 1 0 0 1 .258-.444L16.266 2.33a3.848 3.848 0 0 1 2.756-1.326 3.868 3.868 0 0 1 2.89 1.126 3.828 3.828 0 0 1 1.115 2.887 3.813 3.813 0 0 1-1.352 2.732L19.71 9.715ZM15 6.424 4.395 17.029l-.97 3.555 3.556-.97L17.586 9.01 15 6.424Z",
    clipRule: "evenodd"
  })));
};

var _path$2W;
function _extends$2W() { _extends$2W = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2W.apply(this, arguments); }
var SvgPenSquare = function SvgPenSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2W({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2W || (_path$2W = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20 2.879c-.297 0-.583.118-.793.328l-9.304 9.304-.529 2.115 2.115-.529 9.304-9.304A1.121 1.121 0 0 0 20 2.879Zm-2.207-1.086a3.121 3.121 0 0 1 4.414 4.414l-9.5 9.5a1 1 0 0 1-.465.263l-4 1a1 1 0 0 1-1.212-1.213l1-4a1 1 0 0 1 .263-.464l9.5-9.5ZM1.879 3.879A3 3 0 0 1 4 3h7a1 1 0 1 1 0 2H4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7a1 1 0 1 1 2 0v7a3 3 0 0 1-3 3H4a3 3 0 0 1-3-3V6a3 3 0 0 1 .879-2.121Z",
    clipRule: "evenodd"
  })));
};

var _path$2V;
function _extends$2V() { _extends$2V = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2V.apply(this, arguments); }
var SvgPenTool = function SvgPenTool(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2V({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2V || (_path$2V = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 .942-.265l14.5 3.5a1 1 0 0 1 .746.776l1.153 5.765a1 1 0 0 1 1.073.224l3 3a1 1 0 0 1 0 1.414l-7 7a1 1 0 0 1-1.414 0l-3-3a1 1 0 0 1-.224-1.073L5.304 17.48a1 1 0 0 1-.776-.746l-3.5-14.5a1 1 0 0 1 .265-.942ZM12.67 16.915l4.244-4.244-1.27-6.349L5.22 3.806l4.487 4.486a3 3 0 1 1-1.414 1.414L3.805 5.22l2.516 10.425 6.35 1.27Zm-2.39-6.61a1 1 0 1 0 1.439 1.39 1 1 0 0 0-1.44-1.39ZM13.414 19 15 20.586 20.586 15 19 13.414 13.414 19Z",
    clipRule: "evenodd"
  })));
};

var _path$2U;
function _extends$2U() { _extends$2U = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2U.apply(this, arguments); }
var SvgPenToolFilled = function SvgPenToolFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2U({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2U || (_path$2U = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.5 10.12a1 1 0 1 0 1 0V3.016c.04-.645.313-.977.818-.998.505-.021.858.151 1.059.517l2.944 7.458c.447 1.03.674 1.701.68 2.011.005.31-.063.623-.205.939l-2.242 3.94c-.18.302-.396.562-.647.778-.252.217-.632.325-1.141.325h-3.532c-.444 0-.81-.108-1.099-.325a2.519 2.519 0 0 1-.688-.778L6.219 12.95A2.072 2.072 0 0 1 6 12.01c.006-.31.238-.984.697-2.024l2.927-7.45C9.793 2.18 10.138 2 10.658 2c.521 0 .802.34.842 1.02v7.1ZM10 19.986h4c.667 0 1 .333 1 1s-.333 1-1 1h-4c-.666 0-1-.333-1-1s.334-1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$2T;
function _extends$2T() { _extends$2T = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2T.apply(this, arguments); }
var SvgPencilLine = function SvgPencilLine(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2T({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2T || (_path$2T = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18 4.879c-.297 0-.583.118-.793.328L4.903 17.512l-.529 2.114 2.115-.529L18.793 6.793A1.123 1.123 0 0 0 18 4.88Zm-2.207-1.086a3.121 3.121 0 0 1 4.414 4.414l-12.5 12.5a1 1 0 0 1-.464.263l-4 1a1 1 0 0 1-1.213-1.212l1-4a1 1 0 0 1 .263-.465l12.5-12.5ZM11 21a1 1 0 0 1 1-1h9a1 1 0 1 1 0 2h-9a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$2S;
function _extends$2S() { _extends$2S = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2S.apply(this, arguments); }
var SvgPencilRuler = function SvgPencilRuler(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2S({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2S || (_path$2S = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.089 3.403c-.63-.627-1.464-.57-1.946-.034a.994.994 0 0 1-.036.038l-2.6 2.6c-.631.631-.575 1.467-.038 1.95L3.515 8l3.493 3.577 4.586-4.586-1.567-1.605-1.32 1.32a1 1 0 1 1-1.414-1.414l1.336-1.336-.54-.554Zm4.92 2.175L9.514 2l-.008-.008C8.145.63 5.994.568 4.676 2.01L2.093 4.593C.733 5.953.668 8.1 2.105 9.42l3.489 3.572-2.801 2.8a1 1 0 0 0-.258.445l-1.5 5.5a1 1 0 0 0 1.228 1.228l5.5-1.5a1 1 0 0 0 .444-.258l2.801-2.801 3.59 3.506c1.362 1.357 3.509 1.418 4.826-.022l2.583-2.583c1.364-1.363 1.425-3.517-.022-4.835l-3.562-3.48 1.282-1.283.002-.002.002-.002 1.966-1.966a3.832 3.832 0 0 0 1.352-2.732 3.81 3.81 0 0 0-1.116-2.888A3.851 3.851 0 0 0 19.022.994a3.866 3.866 0 0 0-2.756 1.326l-3.258 3.258ZM15 6.414 4.395 17.02l-.97 3.556 3.556-.97L17.586 9 15 6.414Zm4 1.172L16.414 5l1.293-1.293c.02-.02.038-.04.056-.061a1.846 1.846 0 0 1 1.342-.654 1.868 1.868 0 0 1 1.392.542 1.83 1.83 0 0 1 .533 1.375 1.813 1.813 0 0 1-.665 1.319.741.741 0 0 0-.072.065L19 7.586Zm-1.992 4.82-4.585 4.586 3.584 3.5c.631.632 1.467.576 1.95.04a.448.448 0 0 1 .036-.04l2.6-2.6c.631-.63.575-1.466.038-1.949a1.374 1.374 0 0 1-.018-.016l-.012-.012-.557-.544-1.337 1.336a1 1 0 1 1-1.414-1.414l1.32-1.32-1.605-1.567Z",
    clipRule: "evenodd"
  })));
};

var _path$2R;
function _extends$2R() { _extends$2R = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2R.apply(this, arguments); }
var SvgPercent = function SvgPercent(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2R({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2R || (_path$2R = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.5 5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3ZM3 6.5a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0Zm15.293-2.207a1 1 0 1 1 1.414 1.414l-14 14a1 1 0 0 1-1.414-1.414l14-14ZM17.5 16a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3ZM14 17.5a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0Z",
    clipRule: "evenodd"
  })));
};

var _path$2Q;
function _extends$2Q() { _extends$2Q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2Q.apply(this, arguments); }
var SvgPetFilled = function SvgPetFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2Q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2Q || (_path$2Q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13.498 9.548c.138.115.264.246.374.39l4.602 6c.828 1.078.66 2.651-.374 3.514a2.337 2.337 0 0 1-1.498.548H7.398C6.074 20 5 18.88 5 17.5c0-.568.185-1.12.525-1.562l4.603-6c.827-1.078 2.336-1.253 3.37-.39ZM19.5 8a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Zm-15 0a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Zm11-5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Zm-7 0a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Z",
    clipRule: "evenodd"
  })));
};

var _path$2P;
function _extends$2P() { _extends$2P = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2P.apply(this, arguments); }
var SvgPetOutline = function SvgPetOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2P({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2P || (_path$2P = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13.498 9.548c.138.115.264.246.374.39l4.602 6c.828 1.078.66 2.651-.374 3.514a2.337 2.337 0 0 1-1.498.548H7.398C6.074 20 5 18.88 5 17.5c0-.568.185-1.12.525-1.562l4.603-6c.827-1.078 2.336-1.253 3.37-.39Zm-1.784 1.608-4.602 5.999A.57.57 0 0 0 7 17.5c0 .294.197.5.398.5h9.204a.34.34 0 0 0 .217-.084c.207-.173.245-.53.069-.761l-4.603-6a.455.455 0 0 0-.068-.071c-.158-.132-.365-.108-.503.072ZM19.5 8a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Zm-15 0a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Zm11-5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Zm-7 0a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Z",
    clipRule: "evenodd"
  })));
};

var _path$2O, _path2$h, _path3$3;
function _extends$2O() { _extends$2O = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2O.apply(this, arguments); }
var SvgPhoneCall = function SvgPhoneCall(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2O({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2O || (_path$2O = /*#__PURE__*/React.createElement("path", {
    d: "M14.155 1.006a1 1 0 1 0-.21 1.989 8 8 0 0 1 7.111 7.057 1 1 0 0 0 1.988-.224 10 10 0 0 0-8.89-8.822Z"
  })), _path2$h || (_path2$h = /*#__PURE__*/React.createElement("path", {
    d: "M14.243 5.019a1 1 0 1 0-.387 1.962 4 4 0 0 1 3.16 3.2 1 1 0 0 0 1.967-.362 6 6 0 0 0-4.74-4.8Zm-7.216 5.21a.948.948 0 0 0 .025.087l-.025-.087Z"
  })), _path3$3 || (_path3$3 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20.85 22.77c.086-.03.167-.07.24-.12a3.001 3.001 0 0 0 1.844-2.765V16.89a3 3 0 0 0-2.45-2.973.398.398 0 0 0-.073-.018 11.844 11.844 0 0 1-2.59-.645 2.996 2.996 0 0 0-.867-.186 2.995 2.995 0 0 0-2.363.856l-.004.004-.79.79a6.68 6.68 0 0 1-.274-.141c-.567-.309-1.386-.854-2.316-1.784-.94-.94-1.515-1.75-1.85-2.307a7.445 7.445 0 0 1-.194-.345l.792-.793.004-.004a3 3 0 0 0 .676-3.163 11.84 11.84 0 0 1-.645-2.59l-.002-.01A3 3 0 0 0 6.994 1H3.997a3 3 0 0 0-2.985 3.27l.002.016a20.79 20.79 0 0 0 3.222 9.104 20.5 20.5 0 0 0 6.306 6.304l.014.009a20.79 20.79 0 0 0 9.044 3.21l.018.003a3 3 0 0 0 1.231-.146ZM7.67 3.242A1 1 0 0 0 7.008 3h-3.01a1 1 0 0 0-.995 1.084 18.79 18.79 0 0 0 2.852 8.126c.009-.023.031-.008.073.058a18.5 18.5 0 0 0 5.692 5.692l.007.005a18.79 18.79 0 0 0 8.223 2.914 1 1 0 0 0 1.084-.995v-3.01a1 1 0 0 0-.857-.999 13.846 13.846 0 0 1-3.023-.754h-.002a1 1 0 0 0-.245-.059 1 1 0 0 0-.742.287l-1.268 1.267a1 1 0 0 1-.814.288.998.998 0 0 1-.44-.122 6.62 6.62 0 0 1-.976-.45c-.728-.396-1.703-1.055-2.774-2.126-1.06-1.06-1.734-2-2.15-2.692a8.365 8.365 0 0 1-.444-.835 5.043 5.043 0 0 1-.133-.322l-.009-.025-.003-.01-.001-.003v-.002l-.001-.001s.091.236-.025-.087a1.015 1.015 0 0 1-.007-.032 1 1 0 0 1 .251-.994l1.268-1.268a1 1 0 0 0 .223-1.053V6.88a13.84 13.84 0 0 1-.755-3.024 1 1 0 0 0-.337-.614Z",
    clipRule: "evenodd"
  })));
};

var _path$2N;
function _extends$2N() { _extends$2N = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2N.apply(this, arguments); }
var SvgPhoneFilled = function SvgPhoneFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2N({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2N || (_path$2N = /*#__PURE__*/React.createElement("path", {
    d: "M21.888 16.92v3a2 2 0 0 1-2.18 2 19.791 19.791 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.998 2h3a2 2 0 0 1 1.324.484c.368.316.55.767.676 1.236.25.935-.044-.22.7 2.81a2 2 0 0 1-.45 2.11l-1.27 1.27a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c1.142.31 1.142.31 2.81.7a2 2 0 0 1 1.72 2.03Z"
  })));
};

var _path$2M, _path2$g;
function _extends$2M() { _extends$2M = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2M.apply(this, arguments); }
var SvgPhoneForwarded = function SvgPhoneForwarded(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2M({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2M || (_path$2M = /*#__PURE__*/React.createElement("path", {
    d: "M18.707 1.293a1 1 0 1 0-1.414 1.414L19.586 5H14a1 1 0 1 0 0 2h5.586l-2.293 2.293a1 1 0 0 0 1.414 1.414l4-4a1 1 0 0 0 0-1.414l-4-4Zm-11.68 8.936a.948.948 0 0 0 .025.087l-.025-.087Z"
  })), _path2$g || (_path2$g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20.85 22.77a1 1 0 0 0 .24-.12 3.001 3.001 0 0 0 1.844-2.765V16.89a3 3 0 0 0-2.45-2.973.398.398 0 0 0-.073-.018 11.838 11.838 0 0 1-2.59-.645 3 3 0 0 0-.867-.186 2.999 2.999 0 0 0-2.363.856l-.004.004-.79.79a6.68 6.68 0 0 1-.274-.141c-.567-.309-1.386-.854-2.316-1.784-.94-.94-1.515-1.75-1.85-2.307a7.445 7.445 0 0 1-.194-.345l.792-.793.004-.004a3 3 0 0 0 .676-3.164 11.84 11.84 0 0 1-.645-2.59l-.002-.009A3 3 0 0 0 6.994 1H3.997a3 3 0 0 0-2.985 3.27l.002.016a20.79 20.79 0 0 0 3.222 9.104 20.5 20.5 0 0 0 6.306 6.304l.014.009a20.789 20.789 0 0 0 9.044 3.21l.018.003c.416.037.835-.012 1.231-.146ZM7.67 3.242A1 1 0 0 0 7.008 3h-3.01a1 1 0 0 0-.995 1.084 18.79 18.79 0 0 0 2.852 8.126c.009-.023.031-.008.073.058a18.5 18.5 0 0 0 5.692 5.692l.007.005a18.789 18.789 0 0 0 8.223 2.914 1 1 0 0 0 1.084-.995v-3.01a1 1 0 0 0-.857-.999 13.84 13.84 0 0 1-3.023-.754h-.002a1 1 0 0 0-.245-.059 1.004 1.004 0 0 0-.742.287l-1.268 1.267a1 1 0 0 1-.814.288.998.998 0 0 1-.44-.122 7.567 7.567 0 0 1-.976-.45c-.728-.396-1.703-1.055-2.774-2.126-1.06-1.06-1.734-2-2.15-2.692a8.365 8.365 0 0 1-.444-.835 5.005 5.005 0 0 1-.133-.322l-.009-.025-.003-.01-.001-.003v-.002l-.001-.002s.091.237-.025-.086a1.015 1.015 0 0 1-.007-.032 1 1 0 0 1 .251-.994l1.268-1.268a1 1 0 0 0 .223-1.053V6.88a13.84 13.84 0 0 1-.755-3.024 1 1 0 0 0-.337-.614Z",
    clipRule: "evenodd"
  })));
};

var _path$2L, _path2$f;
function _extends$2L() { _extends$2L = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2L.apply(this, arguments); }
var SvgPhoneIncoming = function SvgPhoneIncoming(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2L({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2L || (_path$2L = /*#__PURE__*/React.createElement("path", {
    d: "M17 2a1 1 0 1 0-2 0v6a1 1 0 0 0 1 1h6a1 1 0 1 0 0-2h-3.586l4.293-4.293a1 1 0 0 0-1.414-1.414L17 5.586V2Zm-9.973 8.229a.948.948 0 0 0 .025.087l-.025-.087Z"
  })), _path2$f || (_path2$f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20.85 22.77a1 1 0 0 0 .24-.12 3.001 3.001 0 0 0 1.844-2.765V16.89a3 3 0 0 0-2.45-2.973.398.398 0 0 0-.073-.018 11.838 11.838 0 0 1-2.59-.645 3 3 0 0 0-.867-.186 2.999 2.999 0 0 0-2.363.856l-.004.004-.79.79a6.68 6.68 0 0 1-.274-.141c-.567-.309-1.386-.854-2.316-1.784-.94-.94-1.515-1.75-1.85-2.307a7.445 7.445 0 0 1-.194-.345l.792-.793.004-.004a3 3 0 0 0 .676-3.164 11.84 11.84 0 0 1-.645-2.59l-.002-.009A3 3 0 0 0 6.994 1H3.997a3 3 0 0 0-2.985 3.27l.002.016a20.79 20.79 0 0 0 3.222 9.104 20.5 20.5 0 0 0 6.306 6.304l.014.009a20.789 20.789 0 0 0 9.044 3.21l.018.003c.416.037.835-.012 1.231-.146ZM7.67 3.242A1 1 0 0 0 7.008 3h-3.01a1 1 0 0 0-.995 1.084 18.79 18.79 0 0 0 2.852 8.126c.009-.023.031-.008.073.058a18.5 18.5 0 0 0 5.692 5.692l.007.005a18.789 18.789 0 0 0 8.223 2.914 1 1 0 0 0 1.084-.995v-3.01a1 1 0 0 0-.857-.999 13.84 13.84 0 0 1-3.023-.754h-.002a1 1 0 0 0-.245-.059 1.004 1.004 0 0 0-.742.287l-1.268 1.267a1 1 0 0 1-.814.288.998.998 0 0 1-.44-.122 7.567 7.567 0 0 1-.976-.45c-.728-.396-1.703-1.055-2.774-2.126-1.06-1.06-1.734-2-2.15-2.692a8.365 8.365 0 0 1-.444-.835 5.005 5.005 0 0 1-.133-.322l-.009-.025-.003-.01-.001-.003v-.002l-.001-.002s.091.237-.025-.086a1.015 1.015 0 0 1-.007-.032 1 1 0 0 1 .251-.994l1.268-1.268a1 1 0 0 0 .223-1.053V6.88a13.84 13.84 0 0 1-.755-3.024 1 1 0 0 0-.337-.614Z",
    clipRule: "evenodd"
  })));
};

var _path$2K, _path2$e;
function _extends$2K() { _extends$2K = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2K.apply(this, arguments); }
var SvgPhoneMissed = function SvgPhoneMissed(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2K({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2K || (_path$2K = /*#__PURE__*/React.createElement("path", {
    d: "M16.707 1.293a1 1 0 1 0-1.414 1.414L17.586 5l-2.293 2.293a1 1 0 0 0 1.414 1.414L19 6.414l2.293 2.293a1 1 0 1 0 1.414-1.414L20.414 5l2.293-2.293a1 1 0 0 0-1.414-1.414L19 3.586l-2.293-2.293Zm-9.68 8.936a.948.948 0 0 0 .025.087l-.025-.087Z"
  })), _path2$e || (_path2$e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20.85 22.77a1 1 0 0 0 .24-.12 3.001 3.001 0 0 0 1.844-2.765V16.89a3 3 0 0 0-2.45-2.973.398.398 0 0 0-.073-.018 11.838 11.838 0 0 1-2.59-.645 3 3 0 0 0-.867-.186 2.999 2.999 0 0 0-2.363.856l-.004.004-.79.79a6.68 6.68 0 0 1-.274-.141c-.567-.309-1.386-.854-2.316-1.784-.94-.94-1.515-1.75-1.85-2.307a7.445 7.445 0 0 1-.194-.345l.792-.793.004-.004a3 3 0 0 0 .676-3.164 11.84 11.84 0 0 1-.645-2.59l-.002-.009A3 3 0 0 0 6.994 1H3.997a3 3 0 0 0-2.985 3.27l.002.016a20.79 20.79 0 0 0 3.222 9.104 20.5 20.5 0 0 0 6.306 6.304l.014.009a20.789 20.789 0 0 0 9.044 3.21l.018.003c.416.037.835-.012 1.231-.146ZM7.67 3.242A1 1 0 0 0 7.008 3h-3.01a1 1 0 0 0-.995 1.084 18.79 18.79 0 0 0 2.852 8.126c.009-.023.031-.008.073.058a18.5 18.5 0 0 0 5.692 5.692l.007.005a18.789 18.789 0 0 0 8.223 2.914 1 1 0 0 0 1.084-.995v-3.01a1 1 0 0 0-.857-.999 13.84 13.84 0 0 1-3.023-.754h-.002a1 1 0 0 0-.245-.059 1.004 1.004 0 0 0-.742.287l-1.268 1.267a1 1 0 0 1-.814.288.998.998 0 0 1-.44-.122 7.567 7.567 0 0 1-.976-.45c-.728-.396-1.703-1.055-2.774-2.126-1.06-1.06-1.734-2-2.15-2.692a8.365 8.365 0 0 1-.444-.835 5.005 5.005 0 0 1-.133-.322l-.009-.025-.003-.01-.001-.003v-.002l-.001-.002s.091.237-.025-.086a1.015 1.015 0 0 1-.007-.032 1 1 0 0 1 .251-.994l1.268-1.268a1 1 0 0 0 .223-1.053V6.88a13.84 13.84 0 0 1-.755-3.024 1 1 0 0 0-.337-.614Z",
    clipRule: "evenodd"
  })));
};

var _path$2J;
function _extends$2J() { _extends$2J = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2J.apply(this, arguments); }
var SvgPhoneOff = function SvgPhoneOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2J({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2J || (_path$2J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M22.707 1.293a1 1 0 0 1 0 1.414L12.123 13.291c.569.498 1.175.952 1.813 1.359l.72-.721a3 3 0 0 1 3.165-.675c.836.312 1.705.528 2.59.645h.009a3 3 0 0 1 2.456 2.119.996.996 0 0 1 .124.478c0 .039 0 .129-.002.257.002.049.002.098.002.147a.793.793 0 0 1-.006.098 134.81 134.81 0 0 1-.036 1.639 56.88 56.88 0 0 1-.054 1.392 11.9 11.9 0 0 1-.037.525 3.616 3.616 0 0 1-.027.217v.004c-.007.042-.03.184-.091.325a3 3 0 0 1-3.019 1.786l-.018-.002a20.79 20.79 0 0 1-9.062-3.223 20.421 20.421 0 0 1-2.778-2.119l-5.165 5.165a1 1 0 0 1-1.414-1.414l8.6-8.6a1.029 1.029 0 0 1 .17-.17l11.23-11.23a1 1 0 0 1 1.414 0ZM10.706 14.709l-1.418 1.417c.76.683 1.575 1.302 2.438 1.85l.008.005a18.79 18.79 0 0 0 8.184 2.914 1 1 0 0 0 .956-.511l.003-.024c.01-.105.02-.253.03-.44.02-.37.038-.851.052-1.34.02-.704.033-1.403.038-1.787a1 1 0 0 0-.853-.912 13.84 13.84 0 0 1-3.024-.754h-.002a1 1 0 0 0-1.053.223l-1.268 1.267a1 1 0 0 1-1.201.162 17 17 0 0 1-2.89-2.07ZM7.782 3.242A1 1 0 0 0 7.12 3H4.11a1 1 0 0 0-.995 1.083 18.79 18.79 0 0 0 2.914 8.183 1 1 0 1 1-1.678 1.088 20.79 20.79 0 0 1-3.225-9.066l-.002-.018A3 3 0 0 1 4.109 1h2.997A3 3 0 0 1 10.1 3.58l.001.01c.117.884.333 1.753.645 2.59a2.999 2.999 0 0 1-.675 3.163l-.004.004-1.27 1.27a1 1 0 0 1-1.414-1.414L8.65 7.935a1 1 0 0 0 .224-1.053l-.001-.002a13.84 13.84 0 0 1-.754-3.024 1 1 0 0 0-.337-.614Zm13.083 17.213c-.003.02-.003.016 0-.003v.003Z",
    clipRule: "evenodd"
  })));
};

var _path$2I, _path2$d, _path3$2;
function _extends$2I() { _extends$2I = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2I.apply(this, arguments); }
var SvgPhoneOutgoing = function SvgPhoneOutgoing(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2I({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2I || (_path$2I = /*#__PURE__*/React.createElement("path", {
    d: "M7.027 10.229a.948.948 0 0 0 .025.087l-.025-.087Z"
  })), _path2$d || (_path2$d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20.85 22.77a1 1 0 0 0 .24-.12 3.001 3.001 0 0 0 1.844-2.765V16.89a3 3 0 0 0-2.45-2.973.398.398 0 0 0-.073-.018 11.838 11.838 0 0 1-2.59-.645 3 3 0 0 0-.867-.186 2.999 2.999 0 0 0-2.363.856l-.004.004-.79.79a6.68 6.68 0 0 1-.274-.141c-.567-.309-1.386-.854-2.316-1.784-.94-.94-1.515-1.75-1.85-2.307a7.445 7.445 0 0 1-.194-.345l.792-.793.004-.004a3 3 0 0 0 .676-3.164 11.84 11.84 0 0 1-.645-2.59l-.002-.009A3 3 0 0 0 6.994 1H3.997a3 3 0 0 0-2.985 3.27l.002.016a20.79 20.79 0 0 0 3.222 9.104 20.5 20.5 0 0 0 6.306 6.304l.014.009a20.789 20.789 0 0 0 9.044 3.21l.018.003c.416.037.835-.012 1.231-.146ZM7.67 3.242A1 1 0 0 0 7.008 3h-3.01a1 1 0 0 0-.995 1.084 18.79 18.79 0 0 0 2.852 8.126c.009-.023.031-.008.073.058a18.5 18.5 0 0 0 5.692 5.692l.007.005a18.789 18.789 0 0 0 8.223 2.914 1 1 0 0 0 1.084-.995v-3.01a1 1 0 0 0-.857-.999 13.84 13.84 0 0 1-3.023-.754h-.002a1 1 0 0 0-.245-.059 1.004 1.004 0 0 0-.742.287l-1.268 1.267a1 1 0 0 1-.814.288 1 1 0 0 1-.44-.122l-.093-.035a7.567 7.567 0 0 1-.883-.414c-.728-.397-1.703-1.056-2.774-2.127-1.06-1.06-1.734-2-2.15-2.692a8.365 8.365 0 0 1-.444-.835 5.005 5.005 0 0 1-.133-.322l-.009-.025-.003-.01-.001-.003v-.002l-.001-.002s.091.237-.025-.086a1.015 1.015 0 0 1-.007-.032 1 1 0 0 1 .251-.994l1.268-1.268a1 1 0 0 0 .223-1.053V6.88a13.84 13.84 0 0 1-.755-3.024 1 1 0 0 0-.337-.614Z",
    clipRule: "evenodd"
  })), _path3$2 || (_path3$2 = /*#__PURE__*/React.createElement("path", {
    d: "M15 2a1 1 0 0 0 1 1h3.586l-4.293 4.293a1 1 0 0 0 1.414 1.414L21 4.414V8a1 1 0 1 0 2 0V2a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1Z"
  })));
};

var _path$2H;
function _extends$2H() { _extends$2H = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2H.apply(this, arguments); }
var SvgPhoneOutline = function SvgPhoneOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2H({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2H || (_path$2H = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M21.09 22.65a1 1 0 0 1-.24.12 2.999 2.999 0 0 1-1.232.146l-.018-.002a20.789 20.789 0 0 1-9.044-3.211l-.014-.009a20.5 20.5 0 0 1-6.306-6.304 20.79 20.79 0 0 1-3.222-9.104l-.002-.016A3 3 0 0 1 3.997 1h2.997a3 3 0 0 1 2.994 2.58l.002.01c.116.884.333 1.753.645 2.59a2.999 2.999 0 0 1-.676 3.163l-.004.004-.792.793c.053.1.117.216.195.345.334.557.909 1.367 1.85 2.307.929.93 1.748 1.475 2.315 1.784.1.054.19.1.274.14l.79-.789.004-.004a3 3 0 0 1 2.363-.856 3 3 0 0 1 .867.186c.836.312 1.705.528 2.59.645a.398.398 0 0 1 .074.018 3 3 0 0 1 2.449 2.973v2.996a3 3 0 0 1-1.845 2.766ZM7.007 3a1 1 0 0 1 1 .856A13.84 13.84 0 0 0 8.76 6.88l.001.002a1 1 0 0 1-.223 1.053L7.27 9.203a1 1 0 0 0-.251.994l.007.032c.116.323.025.087.025.087l-.025-.087a.948.948 0 0 0 .025.087v.003l.002.004.003.009.009.025.028.075c.024.061.059.144.105.247.094.205.236.489.444.835.416.693 1.09 1.633 2.15 2.692 1.07 1.071 2.046 1.73 2.774 2.127a7.567 7.567 0 0 0 .977.449.998.998 0 0 0 .44.122 1 1 0 0 0 .813-.288l1.268-1.267a1 1 0 0 1 .742-.287 1 1 0 0 1 .245.058l.002.001c.976.364 1.99.617 3.024.754a1 1 0 0 1 .856 1v3.009a.999.999 0 0 1-1.084.995 18.789 18.789 0 0 1-8.223-2.914l-.007-.005a18.5 18.5 0 0 1-5.692-5.692c-.042-.066-.064-.081-.073-.059a18.79 18.79 0 0 1-2.852-8.125A1 1 0 0 1 4 3h3.009Z",
    clipRule: "evenodd"
  })));
};

var _path$2G;
function _extends$2G() { _extends$2G = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2G.apply(this, arguments); }
var SvgPillFilled = function SvgPillFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2G({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2G || (_path$2G = /*#__PURE__*/React.createElement("path", {
    d: "M3.535 3.535c1.017-1.017 4.243-2.828 7.071 0l9.901 9.9c1.413 1.414 2.497 4.574 0 7.072-2.497 2.497-5.657 1.414-7.071 0l-9.9-9.9c-2.83-2.83-1.018-6.055-.001-7.072Zm1.414 1.414c-1.489 1.49-.707 3.536 0 4.243l4.95 4.95 4.243-4.243-4.95-4.95c-.707-.707-2.753-1.49-4.243 0Z"
  })));
};

var _path$2F;
function _extends$2F() { _extends$2F = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2F.apply(this, arguments); }
var SvgPillOutline = function SvgPillOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2F({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2F || (_path$2F = /*#__PURE__*/React.createElement("path", {
    d: "M3.535 3.535c1.017-1.017 4.243-2.828 7.071 0l9.901 9.9c1.413 1.414 2.497 4.574 0 7.072-2.497 2.497-5.657 1.414-7.071 0l-9.9-9.9c-2.83-2.83-1.018-6.055-.001-7.072Zm1.414 1.414c-1.489 1.49-.707 3.536 0 4.243l4.243 4.242 4.242-4.242-4.242-4.243c-.707-.707-2.753-1.49-4.243 0Zm14.113 14.143c1.49-1.49.707-3.535 0-4.243l-4.242-4.242-4.243 4.242 4.243 4.243c.707.707 2.753 1.49 4.242 0Z"
  })));
};

var _path$2E;
function _extends$2E() { _extends$2E = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2E.apply(this, arguments); }
var SvgPipeFilled = function SvgPipeFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2E({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2E || (_path$2E = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14 6v2h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2h1V6h4ZM3 9a1 1 0 0 1 1 1v1h2v4H4v1a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1Zm18 0a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-1h-2v-4h2v-1a1 1 0 0 1 1-1Zm-6-5a1 1 0 1 1 0 2H9a1 1 0 0 1 0-2h6Z",
    clipRule: "evenodd"
  })));
};

var _path$2D;
function _extends$2D() { _extends$2D = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2D.apply(this, arguments); }
var SvgPipeOutline = function SvgPipeOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2D({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2D || (_path$2D = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15 4a1 1 0 1 1 0 2h-1v2h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2h1V6H9a1 1 0 0 1 0-2h6ZM3 9a1 1 0 0 1 1 1v1h2v4H4v1a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1Zm18 0a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-1h-2v-4h2v-1a1 1 0 0 1 1-1Zm-6 1H9v6h6v-6Z",
    clipRule: "evenodd"
  })));
};

var _path$2C;
function _extends$2C() { _extends$2C = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2C.apply(this, arguments); }
var SvgPlayCircle = function SvgPlayCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2C({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2C || (_path$2C = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm8.528-4.882a1 1 0 0 1 1.027.05l6 4a1 1 0 0 1 0 1.664l-6 4A1 1 0 0 1 9 16V8a1 1 0 0 1 .528-.882ZM11 9.868v4.264L14.197 12 11 9.869Z",
    clipRule: "evenodd"
  })));
};

var _path$2B;
function _extends$2B() { _extends$2B = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2B.apply(this, arguments); }
var SvgPlayFilled = function SvgPlayFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2B({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2B || (_path$2B = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.52 2.122a1 1 0 0 1 1.02.037l14 9a1 1 0 0 1 0 1.682l-14 9A1 1 0 0 1 4 21V3a1 1 0 0 1 .52-.878Z",
    clipRule: "evenodd"
  })));
};

var _path$2A;
function _extends$2A() { _extends$2A = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2A.apply(this, arguments); }
var SvgPlayOutline = function SvgPlayOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2A({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2A || (_path$2A = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.52 2.122a1 1 0 0 1 1.02.037l14 9a1 1 0 0 1 0 1.682l-14 9A1 1 0 0 1 4 21V3a1 1 0 0 1 .52-.878ZM6 4.832v14.336L17.15 12 6 4.832Z",
    clipRule: "evenodd"
  })));
};

var _path$2z;
function _extends$2z() { _extends$2z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2z.apply(this, arguments); }
var SvgPlaySquare = function SvgPlaySquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2z || (_path$2z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm6.528 2.118a1 1 0 0 1 1.027.05l6 4a1 1 0 0 1 0 1.664l-6 4A1 1 0 0 1 8 16V8a1 1 0 0 1 .528-.882ZM10 9.868v4.264L13.197 12 10 9.869Z",
    clipRule: "evenodd"
  })));
};

var _path$2y;
function _extends$2y() { _extends$2y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2y.apply(this, arguments); }
var SvgPlus = function SvgPlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2y || (_path$2y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a1 1 0 0 1 1 1v6h6a1 1 0 1 1 0 2h-6v6a1 1 0 1 1-2 0v-6H5a1 1 0 1 1 0-2h6V5a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$2x;
function _extends$2x() { _extends$2x = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2x.apply(this, arguments); }
var SvgPlusCircle = function SvgPlusCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2x({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2x || (_path$2x = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11-5a1 1 0 0 1 1 1v3h3a1 1 0 1 1 0 2h-3v3a1 1 0 1 1-2 0v-3H8a1 1 0 1 1 0-2h3V8a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$2w;
function _extends$2w() { _extends$2w = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2w.apply(this, arguments); }
var SvgPlusSquare = function SvgPlusSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2w({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2w || (_path$2w = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm10 2a1 1 0 0 1 1 1v3h3a1 1 0 1 1 0 2h-3v3a1 1 0 1 1-2 0v-3H8a1 1 0 1 1 0-2h3V8a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$2v;
function _extends$2v() { _extends$2v = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2v.apply(this, arguments); }
var SvgPointer = function SvgPointer(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2v({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2v || (_path$2v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.879 1.879A3 3 0 0 1 11 4v2.172a3 3 0 0 1 3.529 1.214 3 3 0 0 1 4 1A3 3 0 0 1 23 11v3a9 9 0 0 1-9 9h-2c-3.069 0-5.02-.968-6.695-2.63l-.002-.003-3.6-3.6A3 3 0 0 1 5 11.931V4a3 3 0 0 1 .879-2.121Zm-.577 12.837.991.991a1 1 0 0 0 1.414-1.414L7 13.586V4a1 1 0 0 1 2 0v6a1 1 0 1 0 2 0V9a1 1 0 0 1 2 0v1a1 1 0 1 0 2 0 1 1 0 0 1 2 0v1a1 1 0 1 0 2 0 1 1 0 0 1 2 0v3a7 7 0 0 1-7 7h-2c-2.53 0-3.978-.752-5.284-2.048l-.001-.002-3.577-3.576a1 1 0 0 1 1.415-1.407l.731.731a.838.838 0 0 0 .018.018Z",
    clipRule: "evenodd"
  })));
};

var _path$2u;
function _extends$2u() { _extends$2u = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2u.apply(this, arguments); }
var SvgPollFilled = function SvgPollFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2u({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2u || (_path$2u = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 8c0-1.329.667-1.995 2-2 1.333-.005 2 .662 2 2v8c0 1.333-.667 2-2 2s-2-.667-2-2V8Zm6-4c0-1.333.667-2 2-2s2 .667 2 2v12c0 1.337-.667 2.003-2 2-1.333-.003-2-.67-2-2V4ZM5 20h14c.667 0 1 .333 1 1s-.333 1-1 1H5c-.667 0-1-.333-1-1s.333-1 1-1Zm11-8c0-1.333.667-2 2-2s2 .667 2 2v4c0 1.333-.667 2-2 2s-2-.667-2-2v-4Z",
    clipRule: "evenodd"
  })));
};

var _path$2t;
function _extends$2t() { _extends$2t = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2t.apply(this, arguments); }
var SvgPollOutline = function SvgPollOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2t({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2t || (_path$2t = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 8c0-1.328.667-1.995 2-2h2.001V4c0-1.333.666-2 1.996-2h2.007C14.335 2.005 15 2.67 15 4v6h2c1.333 0 2 .668 2 2.004v4c0 1.33-.667 1.998-2 2.002C15 18.01 7.334 18 7 18c-1.333 0-2-.667-2-2V8Zm2 0v8h2V8H7Zm4-4v12h2V4h-2Zm4 8v4h2v-4h-2ZM5 20h14c.667 0 1 .334 1 1 0 .667-.333 1-1 1H5c-.667 0-1-.333-1-1 0-.666.333-1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$2s;
function _extends$2s() { _extends$2s = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2s.apply(this, arguments); }
var SvgPound = function SvgPound(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2s({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2s || (_path$2s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M17.414 3.253C18.388 4.118 19 5.395 19 7a1 1 0 1 1-2 0c0-1.062-.388-1.785-.914-2.252C15.54 4.264 14.786 4 14 4c-.787 0-1.541.264-2.086.748C11.388 5.215 11 5.938 11 7v5h5a1 1 0 1 1 0 2h-5v6h7a1 1 0 1 1 0 2H6a1 1 0 1 1 0-2h3v-6H6a1 1 0 1 1 0-2h3V7c0-1.605.612-2.882 1.586-3.747C11.54 2.403 12.786 2 14 2c1.213 0 2.459.403 3.414 1.253Z",
    clipRule: "evenodd"
  })));
};

var _path$2r;
function _extends$2r() { _extends$2r = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2r.apply(this, arguments); }
var SvgPresentationFilled = function SvgPresentationFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2r({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2r || (_path$2r = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 4c-.667 0-1-.333-1-1s.333-1 1-1h18c.667 0 1 .333 1 1s-.333 1-1 1v12c0 1.332-.667 1.999-2 2h-4.366l.72 2.689c.173.644-.063 1.052-.707 1.224-.644.173-1.052-.063-1.225-.707L12.563 18h-1.126l-.86 3.206c-.172.644-.58.88-1.224.707-.644-.172-.88-.58-.707-1.224L9.366 18H5c-1.333 0-2-.667-2-2V4Zm8 4.167v4.666c0 .778.333 1.167 1 1.167s1-.389 1-1.167V8.167c0-.78-.333-1.17-1-1.167-.667.003-1 .392-1 1.167Zm4-1.042v5.75c0 .748.333 1.123 1 1.125.667.002 1-.373 1-1.125v-5.75C17 6.375 16.667 6 16 6s-1 .375-1 1.125ZM7 10.25v2.5c0 .833.333 1.25 1 1.25s1-.417 1-1.25v-2.5C9 9.417 8.667 9 8 9s-1 .417-1 1.25Z",
    clipRule: "evenodd"
  })));
};

var _path$2q;
function _extends$2q() { _extends$2q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2q.apply(this, arguments); }
var SvgPresentationOutline = function SvgPresentationOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2q || (_path$2q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M21 4v12c0 1.332-.667 1.999-2 2h-4.366l.72 2.689c.173.644-.063 1.052-.707 1.224-.644.173-1.052-.063-1.225-.707L12.563 18h-1.126l-.86 3.206c-.172.644-.58.88-1.224.707-.644-.172-.88-.58-.707-1.224L9.366 18H5c-1.333 0-2-.667-2-2V4c-.667 0-1-.333-1-1s.333-1 1-1h18c.667 0 1 .333 1 1s-.333 1-1 1Zm-2 0H5v12h14V4Zm-8 4.167c0-.775.333-1.164 1-1.167.667-.003 1 .386 1 1.167v4.666c0 .778-.333 1.167-1 1.167s-1-.389-1-1.167V8.167Zm4-1.042C15 6.375 15.333 6 16 6s1 .375 1 1.125v5.75c0 .752-.333 1.127-1 1.125-.667-.002-1-.377-1-1.125v-5.75ZM7 10.25C7 9.417 7.333 9 8 9s1 .417 1 1.25v2.5C9 13.583 8.667 14 8 14s-1-.417-1-1.25v-2.5Z",
    clipRule: "evenodd"
  })));
};

var _path$2p;
function _extends$2p() { _extends$2p = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2p.apply(this, arguments); }
var SvgPrinterFilled = function SvgPrinterFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2p({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2p || (_path$2p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 2a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v6h1a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3h-1v3a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-3H4a3 3 0 0 1-3-3v-5a3 3 0 0 1 3-3h1V2Zm12 6H7V3h10v5Zm0 7H7v6h10v-6Z",
    clipRule: "evenodd"
  })));
};

var _path$2o;
function _extends$2o() { _extends$2o = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2o.apply(this, arguments); }
var SvgPrinterOutline = function SvgPrinterOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2o({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2o || (_path$2o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 2a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v6h1a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3h-1v3a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-3H4a3 3 0 0 1-3-3v-5a3 3 0 0 1 3-3h1V2Zm-1 8a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h1v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3h1a1 1 0 0 0 1-1v-5a1 1 0 0 0-1-1H4Zm13-2H7V3h10v5Zm0 7H7v6h10v-6Z",
    clipRule: "evenodd"
  })));
};

var _path$2n;
function _extends$2n() { _extends$2n = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2n.apply(this, arguments); }
var SvgQrCode = function SvgQrCode(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2n({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2n || (_path$2n = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 2h9v9H2V2Zm2 2h5v5H4V4Zm4 1H5v3h3V5Zm-6 8h9v9H2v-9Zm2 2h5v5H4v-5Zm4 1H5v3h3v-3ZM22 2h-9v9h9V2Zm-2 2h-5v5h5V4Zm-4 1h3v3h-3V5Zm-3 8h3v1h-1v3h1v1h-1v3h1v1h-3v-9Zm7 0h-3v2h3v-2Zm-3 3h2v1h-1v2h1v1h-2v-4Zm5-3h-1v2h1v-2Zm-5 8h1v1h-1v-1Zm3 0h-1v1h3v-6h-2v5Z",
    clipRule: "evenodd"
  })));
};

var _path$2m;
function _extends$2m() { _extends$2m = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2m.apply(this, arguments); }
var SvgQuestionMarkFilled = function SvgQuestionMarkFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2m({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2m || (_path$2m = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10ZM10.906 8.271a2 2 0 0 1 3.014 1.727V10c0 .47-.365.958-1.055 1.418a6.05 6.05 0 0 1-1.246.628l-.016.006a1 1 0 0 0 .633 1.897l.193-.07-.192.07.002-.001.004-.002.01-.003.036-.012.116-.043.024-.01a8.056 8.056 0 0 0 1.545-.796c.81-.54 1.945-1.55 1.946-3.081a4 4 0 0 0-7.773-1.333 1 1 0 0 0 1.886.664 2 2 0 0 1 .873-1.06ZM12 16a1 1 0 1 0 0 2h.01a1 1 0 1 0 0-2H12Z",
    clipRule: "evenodd"
  })));
};

var _path$2l, _path2$c;
function _extends$2l() { _extends$2l = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2l.apply(this, arguments); }
var SvgQuestionMarkOutline = function SvgQuestionMarkOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2l({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2l || (_path$2l = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm10-8c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    clipRule: "evenodd"
  })), _path2$c || (_path2$c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.258 8.024a2 2 0 0 0-2.225 1.308 1 1 0 1 1-1.886-.664 4 4 0 0 1 7.773 1.333c0 1.53-1.135 2.54-1.945 3.081a8.056 8.056 0 0 1-1.686.848l-.035.013-.011.003-.004.002h-.002l-.073.022.037-.011.035-.01a1 1 0 0 1-.633-1.897l.016-.006a5.802 5.802 0 0 0 .364-.147 6.05 6.05 0 0 0 .882-.481c.69-.46 1.055-.949 1.055-1.418v-.002a2 2 0 0 0-1.662-1.974ZM11 17a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$2k;
function _extends$2k() { _extends$2k = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2k.apply(this, arguments); }
var SvgQuote = function SvgQuote(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2k({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2k || (_path$2k = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10.175 2.82C10.746 3.392 11 4.173 11 5v8c0 3.69-1.061 6.024-2.742 7.399C6.616 21.743 4.582 22 3 22h-.038c-.216 0-.453 0-.65-.022-.202-.022-.584-.086-.894-.396-.31-.31-.374-.692-.396-.893a6.245 6.245 0 0 1-.022-.65v-2.046c0-.222 0-.462.021-.662.022-.202.083-.58.384-.893.307-.319.69-.389.899-.414.204-.024.449-.024.668-.024H3c.175 0 .433-.097.668-.332.235-.235.332-.493.332-.668v-1c-.825 0-1.603-.252-2.176-.824C1.252 12.603 1 11.826 1 11V4.972c0-.817.255-1.588.828-2.155C2.399 2.251 3.175 2 4 2h3.994c.83-.01 1.609.245 2.181.82ZM8.758 4.23c-.115-.115-.333-.236-.744-.23H4c-.425 0-.65.124-.765.238-.115.114-.235.33-.235.734V11c0 .425.123.647.238.762.115.115.338.238.762.238h.038c.216 0 .453 0 .65.022.202.022.584.086.894.396.31.31.374.692.396.894.022.197.022.434.022.65V15c0 .825-.403 1.567-.918 2.082C4.567 17.597 3.825 18 3 18v2c1.418 0 2.884-.243 3.992-1.149C8.062 17.976 9 16.311 9 13V5c0-.424-.124-.651-.242-.77Zm13.416-1.41C22.746 3.392 23 4.172 23 5v8c0 3.69-1.061 6.024-2.742 7.399C18.616 21.743 16.582 22 15 22h-.038c-.216 0-.453 0-.65-.022-.202-.022-.584-.086-.894-.396-.31-.31-.374-.692-.396-.893a6.215 6.215 0 0 1-.022-.65V17a1 1 0 0 1 1-1c.666 0 1.04-.1 1.248-.203a.648.648 0 0 0 .335-.352c.084-.193.139-.48.16-.92.009-.172.012-.347.012-.533-.733-.045-1.415-.3-1.93-.816C13.251 12.603 13 11.826 13 11V4.972c0-.817.255-1.588.828-2.155C14.399 2.251 15.175 2 16 2h3.994c.83-.01 1.608.245 2.18.82Zm-1.416 1.41c-.116-.115-.334-.236-.744-.23H16c-.425 0-.65.124-.765.238-.115.114-.235.33-.235.734V11c0 .425.123.647.238.762.115.115.338.238.762.238h.75a1 1 0 0 1 1 1l.003.444c.003.396.006.808-.012 1.179-.025.52-.095 1.094-.324 1.62a2.644 2.644 0 0 1-1.275 1.343c-.338.17-.72.28-1.142.345V20c1.418 0 2.884-.243 3.992-1.149C20.062 17.976 21 16.311 21 13V5c0-.423-.125-.651-.242-.77Z",
    clipRule: "evenodd"
  })));
};

var _path$2j;
function _extends$2j() { _extends$2j = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2j.apply(this, arguments); }
var SvgQuoteLeft = function SvgQuoteLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2j({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2j || (_path$2j = /*#__PURE__*/React.createElement("path", {
    d: "M21 3c-3 0-7 1-7 8v8c0 1.25.756 2.017 2 2h4c1.25 0 2-.75 2-1.972V13c0-1.25-.75-2-2-2-1 0-1 0-1-1V9c0-1 1-2 2-2s1-.008 1-1.031v-1.97c0-1 0-1-1-1ZM9 3c-3 0-7 1-7 8v8c0 1.25.757 2.017 2 2h4c1.25 0 2-.75 2-1.972V13c0-1.25-.75-2-2-2h-.75C7.25 8.75 7 7 10 7V4c0-1 0-1-1-1Z"
  })));
};

var _path$2i;
function _extends$2i() { _extends$2i = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2i.apply(this, arguments); }
var SvgQuoteRight = function SvgQuoteRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2i({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2i || (_path$2i = /*#__PURE__*/React.createElement("path", {
    d: "M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.03V20c0 1 0 1 1 1Zm12 0c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1Z"
  })));
};

var _path$2h;
function _extends$2h() { _extends$2h = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2h.apply(this, arguments); }
var SvgRedo = function SvgRedo(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2h({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2h || (_path$2h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14.293 3.293a1 1 0 0 1 1.414 0l5 5a1 1 0 0 1 0 1.414l-5 5a1 1 0 0 1-1.414-1.414L17.586 10H9.5a4.5 4.5 0 1 0 0 9H13a1 1 0 1 1 0 2H9.5a6.5 6.5 0 1 1 0-13h8.086l-3.293-3.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$2g;
function _extends$2g() { _extends$2g = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2g.apply(this, arguments); }
var SvgRefreshCcw = function SvgRefreshCcw(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2g({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2g || (_path$2g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.996 2H12a10 10 0 0 1 10 10 1 1 0 1 1-2 0 8 8 0 0 0-7.998-8A8.75 8.75 0 0 0 5.96 6.453L5.414 7H8a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1V3a1 1 0 1 1 2 0v2.586l.553-.553.012-.012A10.75 10.75 0 0 1 11.996 2ZM4.93 19.071A10 10 0 0 1 2 12a1 1 0 1 1 2 0 8 8 0 0 0 7.998 8 8.75 8.75 0 0 0 6.041-2.453l.547-.547H16a1 1 0 1 1 0-2h5a1 1 0 0 1 1 1v5a1 1 0 1 1-2 0v-2.586l-.553.553-.012.012A10.75 10.75 0 0 1 12.004 22H12a10 10 0 0 1-7.071-2.929Z",
    clipRule: "evenodd"
  })));
};

var _path$2f;
function _extends$2f() { _extends$2f = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2f.apply(this, arguments); }
var SvgRefreshCcwDot = function SvgRefreshCcwDot(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2f({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2f || (_path$2f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 1a1 1 0 0 1 1 1v3.755l1.333-1.2A10 10 0 0 1 22 11.999a1 1 0 1 1-2 .002A8 8 0 0 0 6.668 6.044l-.001.001L5.606 7H9a1 1 0 0 1 0 2H3a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1Zm7 11a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm-7-1a1 1 0 0 1 1 1 8 8 0 0 0 13.332 5.956l.001-.001L18.394 17H15a1 1 0 1 1 0-2h6a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-3.755l-1.331 1.198-.002.002A10 10 0 0 1 2 12.001 1 1 0 0 1 3 11Z",
    clipRule: "evenodd"
  })));
};

var _path$2e;
function _extends$2e() { _extends$2e = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2e.apply(this, arguments); }
var SvgRefreshCwOff = function SvgRefreshCwOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2e({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2e || (_path$2e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 1.414 0l3.64 3.639.004.005 16.356 16.356a1 1 0 0 1-1.414 1.414l-2.965-2.965A9.906 9.906 0 0 1 12 22h-.004a10.75 10.75 0 0 1-7.431-3.021l-.012-.012L4 18.414V21a1 1 0 1 1-2 0v-5a1 1 0 0 1 1-1h5a1 1 0 1 1 0 2H5.414l.547.547A8.75 8.75 0 0 0 12 20a7.91 7.91 0 0 0 4.905-1.68L5.68 7.094A7.91 7.91 0 0 0 4 12a1 1 0 1 1-2 0c0-2.402.84-4.608 2.258-6.328L1.293 2.707a1 1 0 0 1 0-1.414ZM21 2a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1h-5a1 1 0 1 1 0-2h2.586l-.547-.547A8.75 8.75 0 0 0 11.998 4a7.81 7.81 0 0 0-2.542.415 1 1 0 1 1-.652-1.89A9.812 9.812 0 0 1 12 2h.004a10.75 10.75 0 0 1 7.43 3.021l.013.012.553.553V3a1 1 0 0 1 1-1Zm0 9a1 1 0 0 1 1 1 9.812 9.812 0 0 1-.525 3.196 1 1 0 0 1-1.89-.652c.272-.79.415-1.65.415-2.544a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$2d;
function _extends$2d() { _extends$2d = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2d.apply(this, arguments); }
var SvgRoadFilled = function SvgRoadFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2d({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2d || (_path$2d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11 7.5h2V3h1.733c1.029 0 1.925.703 2.175 1.704l4.058 14.898A1.126 1.126 0 0 1 19.88 21H13v-4.5h-2V21H4.121a1.125 1.125 0 0 1-1.087-1.398L7.092 4.704A2.243 2.243 0 0 1 9.267 3H11v4.5Zm2 2.25h-2v4.5h2v-4.5Z",
    clipRule: "evenodd"
  })));
};

var _path$2c;
function _extends$2c() { _extends$2c = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2c.apply(this, arguments); }
var SvgRoadOutline = function SvgRoadOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2c({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2c || (_path$2c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11 19v-3h2v5h-2.001v-2H11Zm2-9v4h-2v-4h2Zm2-4.69L14.377 3h.356c1.029 0 1.925.703 2.175 1.704l4.058 14.898A1.126 1.126 0 0 1 19.88 21h-.61l-.54-2L15 5.31ZM13 3v5h-2V3h2ZM4.73 21h-.609a1.125 1.125 0 0 1-1.087-1.398L7.092 4.704A2.243 2.243 0 0 1 9.267 3h.356L9 5.31 5.27 19l-.54 2Z",
    clipRule: "evenodd"
  })));
};

var _path$2b;
function _extends$2b() { _extends$2b = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2b.apply(this, arguments); }
var SvgRocket = function SvgRocket(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2b({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2b || (_path$2b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20.46 3.039a11.88 11.88 0 0 0-9.112 5.542l-.006.008a21 21 0 0 0-1.667 3.172l2.067 2.067a21.345 21.345 0 0 0 3.212-1.666c4.225-2.838 5.3-6.549 5.505-9.123ZM14.84 14.55a23 23 0 0 1-2.341 1.128v3.027c.096-.032.195-.066.295-.102.863-.313 1.582-.724 1.873-1.16.144-.214.25-.546.292-1.001a6.626 6.626 0 0 0-.047-1.422 10.35 10.35 0 0 0-.072-.47ZM11.499 20l.179.983A1 1 0 0 1 10.5 20v-4.585L8.086 13H3.5a1 1 0 0 1-.984-1.178L3.5 12l-.984-.178v-.002l.001-.004.002-.01.006-.032.023-.112a12.627 12.627 0 0 1 .468-1.639c.325-.894.913-2.175 1.928-2.854h.001c.595-.397 1.293-.561 1.927-.62a8.597 8.597 0 0 1 1.863.054 13.16 13.16 0 0 1 1.36.252A13.881 13.881 0 0 1 21.506 1a1 1 0 0 1 .994 1c0 2.81-.788 7.708-5.852 11.417.085.357.182.828.249 1.348a8.6 8.6 0 0 1 .054 1.863c-.059.634-.222 1.332-.619 1.927v.001c-.68 1.015-1.96 1.604-2.855 1.928a12.624 12.624 0 0 1-1.751.491l-.032.006-.01.002h-.003l-.002.001L11.5 20ZM8.967 8.661A23.006 23.006 0 0 0 7.825 11h-3.03c.031-.096.065-.195.101-.295.313-.863.724-1.582 1.16-1.873.214-.143.546-.25 1.001-.292a6.62 6.62 0 0 1 1.423.047c.169.022.332.048.487.075Zm-5.61 7.073a3.18 3.18 0 0 1 4.243.132l.013.012c1.188 1.173 1.173 3.056.152 4.266-.443.526-1.053.913-1.639 1.2-.598.291-1.244.513-1.824.68a17.306 17.306 0 0 1-2.116.459l-.037.005-.011.002h-.005L2 21.501l-.991-.134v-.005l.002-.01.005-.038.02-.134c.019-.113.046-.274.083-.47.074-.392.19-.93.357-1.512.166-.58.388-1.226.68-1.824.287-.586.673-1.196 1.2-1.639ZM2 21.5l-.991-.133a1 1 0 0 0 1.124 1.124L2 21.501Z",
    clipRule: "evenodd"
  })));
};

var _path$2a;
function _extends$2a() { _extends$2a = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2a.apply(this, arguments); }
var SvgRotateCcw = function SvgRotateCcw(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2a({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$2a || (_path$2a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.996 2H12A10 10 0 1 1 2 12a1 1 0 1 1 2 0 8 8 0 1 0 8.002-8A8.75 8.75 0 0 0 5.96 6.453L5.414 7H8a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1V3a1 1 0 1 1 2 0v2.586l.553-.553.012-.012A10.75 10.75 0 0 1 11.996 2Z",
    clipRule: "evenodd"
  })));
};

var _path$29;
function _extends$29() { _extends$29 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$29.apply(this, arguments); }
var SvgRotateCw = function SvgRotateCw(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$29({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$29 || (_path$29 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a8 8 0 1 0 8 8 1 1 0 1 1 2 0A10 10 0 1 1 12 2c2.786 0 5.442 1.105 7.433 3.02l.014.013.553.553V3a1 1 0 1 1 2 0v5a1 1 0 0 1-1 1h-5a1 1 0 1 1 0-2h2.586l-.546-.546A8.74 8.74 0 0 0 12 4Z",
    clipRule: "evenodd"
  })));
};

var _path$28, _path2$b;
function _extends$28() { _extends$28 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$28.apply(this, arguments); }
var SvgRulerFilled = function SvgRulerFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$28({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$28 || (_path$28 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 20a2 2 0 0 0 2 2h3a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v16Zm6-15H7v1h3V5Zm0 2H8v1h2V7Zm0 2H7v1h3V9Zm0 2H8v1h2v-1Zm0 2H7v1h3v-1Zm0 2H8v1h2v-1Zm0 2H7v1h3v-1Zm0 2H8v1h2v-1Z",
    clipRule: "evenodd"
  })), _path2$b || (_path2$b = /*#__PURE__*/React.createElement("path", {
    d: "M19 2h-6v2h2v16h-2v2h6v-2h-2V4h2V2Z"
  })));
};

var _path$27, _path2$a;
function _extends$27() { _extends$27 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$27.apply(this, arguments); }
var SvgRulerOutline = function SvgRulerOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$27({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$27 || (_path$27 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M10 20H7V4h3v16Zm-3 2a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7Z",
    clipRule: "evenodd"
  })), _path2$a || (_path2$a = /*#__PURE__*/React.createElement("path", {
    d: "M8 14h2v-1H8v1Zm1-2h1v-1H9v1Zm0 8h1v-1H9v1ZM8 10h2V9H8v1Zm0 8h2v-1H8v1ZM8 6h2V5H8v1Zm1 2h1V7H9v1Zm0 8h1v-1H9v1Zm5-14h6v2h-2v16h2v2h-6v-2h2V4h-2V2Z"
  })));
};

var _path$26;
function _extends$26() { _extends$26 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$26.apply(this, arguments); }
var SvgSave = function SvgSave(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$26({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$26 || (_path$26 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h1v-7a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v7h1a1 1 0 0 0 1-1V8.414L15.586 4H8v3h7a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1V4H5Zm0-2a3 3 0 0 0-3 3v14a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3V8a1 1 0 0 0-.293-.707l-5-5A1 1 0 0 0 16 2H5Zm11 18v-6H8v6h8Z",
    clipRule: "evenodd"
  })));
};

var _path$25, _path2$9;
function _extends$25() { _extends$25 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$25.apply(this, arguments); }
var SvgSaveAll = function SvgSaveAll(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$25({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$25 || (_path$25 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M19.998 19H20v-1l-.005 1h.003a3 3 0 0 0 2.997-2.7c.003-.033.005-.067.005-.1V6a1 1 0 0 0-.293-.707l-4-4A1 1 0 0 0 18 1H8a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h11.998ZM8 3a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h1v-6a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v6h1.005a1 1 0 0 0 .995-.859V6.414L17.586 3H11v2h5a1 1 0 1 1 0 2h-6a1 1 0 0 1-1-1V3H8Zm3 9v5h6v-5h-6Z",
    clipRule: "evenodd"
  })), _path2$9 || (_path2$9 = /*#__PURE__*/React.createElement("path", {
    d: "M3 6a1 1 0 0 0-2 0v14a3 3 0 0 0 3 3h14a1 1 0 1 0 0-2H4a1 1 0 0 1-1-1V6Z"
  })));
};

var _path$24, _path2$8;
function _extends$24() { _extends$24 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$24.apply(this, arguments); }
var SvgSaveFilled = function SvgSaveFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$24({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$24 || (_path$24 = /*#__PURE__*/React.createElement("path", {
    d: "M6 2H5a3 3 0 0 0-3 3v14a3 3 0 0 0 3 3h1v-9a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v9h1a3 3 0 0 0 3-3V8a1 1 0 0 0-.293-.707l-5-5A1 1 0 0 0 16 2H8v5h7a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1V2Z"
  })), _path2$8 || (_path2$8 = /*#__PURE__*/React.createElement("path", {
    d: "M16 22v-8H8v8h8Z"
  })));
};

var _path$23;
function _extends$23() { _extends$23 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$23.apply(this, arguments); }
var SvgScale = function SvgScale(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$23({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$23 || (_path$23 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2a1 1 0 0 1 1 1v1.37C14.9 5.238 17.388 6 19 6h2a1 1 0 0 1 0 2h-.932l2.868 7.649A1 1 0 0 1 22.6 16.8 6.008 6.008 0 0 1 19 18a6.008 6.008 0 0 1-3.599-1.199 1 1 0 0 1-.337-1.152l2.893-7.716c-1.57-.188-3.4-.747-4.957-1.385V20h4a1 1 0 0 1 0 2H7a1 1 0 1 1 0-2h4V6.548c-1.556.638-3.386 1.197-4.957 1.385l2.893 7.716a1 1 0 0 1-.338 1.152A6.008 6.008 0 0 1 5 18a6.008 6.008 0 0 1-3.599-1.199 1 1 0 0 1-.337-1.152L3.932 8H3a1 1 0 1 1 0-2h2c1.612 0 4.1-.762 6-1.63V3a1 1 0 0 1 1-1Zm-7 8.848-1.776 4.736a3.996 3.996 0 0 0 3.552 0L5 10.848Zm14 0-1.776 4.736a3.997 3.997 0 0 0 3.552 0L19 10.848Z",
    clipRule: "evenodd"
  })));
};

var _path$22;
function _extends$22() { _extends$22 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$22.apply(this, arguments); }
var SvgScale3D = function SvgScale3D(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$22({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$22 || (_path$22 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 1 0 0 2 1 1 0 0 0 0-2ZM2 5a3 3 0 1 1 4 2.83v8.756l4.293-4.293a1 1 0 0 1 1.414 1.414L7.414 18h8.757a3.001 3.001 0 1 1 0 2H5a1 1 0 0 1-1-1V7.83A3.001 3.001 0 0 1 2 5Zm17 13a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z",
    clipRule: "evenodd"
  })));
};

var _path$21;
function _extends$21() { _extends$21 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$21.apply(this, arguments); }
var SvgScan = function SvgScan(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$21({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$21 || (_path$21 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v2a1 1 0 0 1-2 0V5a3 3 0 0 1 3-3h2a1 1 0 0 1 0 2H5Zm11-1a1 1 0 0 1 1-1h2a3 3 0 0 1 3 3v2a1 1 0 1 1-2 0V5a1 1 0 0 0-1-1h-2a1 1 0 0 1-1-1ZM3 16a1 1 0 0 1 1 1v2a1 1 0 0 0 1 1h2a1 1 0 1 1 0 2H5a3 3 0 0 1-3-3v-2a1 1 0 0 1 1-1Zm18 0a1 1 0 0 1 1 1v2a3 3 0 0 1-3 3h-2a1 1 0 1 1 0-2h2a1 1 0 0 0 1-1v-2a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$20;
function _extends$20() { _extends$20 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$20.apply(this, arguments); }
var SvgScanFace = function SvgScanFace(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$20({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$20 || (_path$20 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.293 4.293A1 1 0 0 1 5 4h2a1 1 0 0 0 0-2H5a3 3 0 0 0-3 3v2a1 1 0 0 0 2 0V5a1 1 0 0 1 .293-.707ZM17 2a1 1 0 1 0 0 2h2a1 1 0 0 1 1 1v2a1 1 0 1 0 2 0V5a3 3 0 0 0-3-3h-2ZM9 8a1 1 0 0 0 0 2h.01a1 1 0 0 0 0-2H9Zm6 0a1 1 0 1 0 0 2h.01a1 1 0 1 0 0-2H15ZM4 17a1 1 0 1 0-2 0v2a3 3 0 0 0 3 3h2a1 1 0 1 0 0-2H5a1 1 0 0 1-1-1v-2Zm18 0a1 1 0 1 0-2 0v2a1 1 0 0 1-1 1h-2a1 1 0 1 0 0 2h2a3 3 0 0 0 3-3v-2ZM8.797 13.396A1 1 0 0 0 7.2 14.6l.002.003.003.004.007.009.021.027.07.086c.059.07.142.164.248.276.213.22.522.512.923.804.798.58 2 1.191 3.526 1.191 1.525 0 2.728-.61 3.526-1.191a6.992 6.992 0 0 0 .922-.804 5.661 5.661 0 0 0 .319-.362l.02-.027.008-.01.003-.003.001-.002s-.144.177-.626.675l.627-.676a1 1 0 0 0-1.597-1.204l-.005.006a3.75 3.75 0 0 1-.194.219 4.946 4.946 0 0 1-.655.57c-.577.42-1.374.81-2.349.81-.975 0-1.772-.39-2.35-.81a4.945 4.945 0 0 1-.815-.748 1.9 1.9 0 0 1-.034-.041l-.004-.006Z",
    clipRule: "evenodd"
  })));
};

var _path$1$;
function _extends$1$() { _extends$1$ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1$.apply(this, arguments); }
var SvgScanLine = function SvgScanLine(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1$({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1$ || (_path$1$ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v2a1 1 0 0 1-2 0V5a3 3 0 0 1 3-3h2a1 1 0 0 1 0 2H5Zm11-1a1 1 0 0 1 1-1h2a3 3 0 0 1 3 3v2a1 1 0 1 1-2 0V5a1 1 0 0 0-1-1h-2a1 1 0 0 1-1-1ZM6 12a1 1 0 0 1 1-1h10a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1Zm-3 4a1 1 0 0 1 1 1v2a1 1 0 0 0 1 1h2a1 1 0 1 1 0 2H5a3 3 0 0 1-3-3v-2a1 1 0 0 1 1-1Zm18 0a1 1 0 0 1 1 1v2a3 3 0 0 1-3 3h-2a1 1 0 1 1 0-2h2a1 1 0 0 0 1-1v-2a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$1_;
function _extends$1_() { _extends$1_ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1_.apply(this, arguments); }
var SvgScrollText = function SvgScrollText(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1_({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1_ || (_path$1_ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.848 2.848A3.043 3.043 0 0 1 4 1.957h13A3.044 3.044 0 0 1 20.044 5v10.957H22c.576 0 1.044.467 1.044 1.043v2A3.044 3.044 0 0 1 20 22.044H8A3.044 3.044 0 0 1 4.957 19V9.043H2A1.043 1.043 0 0 1 .957 8V5c0-.807.32-1.581.891-2.152ZM7.043 19a.957.957 0 0 0 1.914 0v-2c0-.576.467-1.043 1.043-1.043h7.956V5A.956.956 0 0 0 17 4.043H6.89c.1.306.154.629.154.957v14ZM4 4.043A.956.956 0 0 1 4.957 5v1.957H3.044V5A.957.957 0 0 1 4 4.043Zm7.043 14V19c0 .328-.052.65-.154.956H20a.957.957 0 0 0 .956-.956v-.956h-9.912ZM8.957 8c0-.576.467-1.043 1.043-1.043h5a1.043 1.043 0 1 1 0 2.086h-5A1.043 1.043 0 0 1 8.957 8Zm0 4c0-.576.467-1.043 1.043-1.043h5a1.044 1.044 0 0 1 0 2.086h-5A1.043 1.043 0 0 1 8.957 12Z",
    clipRule: "evenodd"
  })));
};

var _path$1Z;
function _extends$1Z() { _extends$1Z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1Z.apply(this, arguments); }
var SvgSearchCheck = function SvgSearchCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1Z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1Z || (_path$1Z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm-9 7a9 9 0 1 1 16.032 5.618l3.675 3.675a1 1 0 0 1-1.414 1.414l-3.675-3.675A9 9 0 0 1 2 11Zm12.707-2.707a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 1 1 1.414-1.414L10 11.586l3.293-3.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$1Y;
function _extends$1Y() { _extends$1Y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1Y.apply(this, arguments); }
var SvgSearchCode = function SvgSearchCode(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1Y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1Y || (_path$1Y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm-9 7a9 9 0 1 1 16.032 5.618l3.675 3.675a1 1 0 0 1-1.414 1.414l-3.675-3.675A9 9 0 0 1 2 11Zm7.707-2.707a1 1 0 0 1 0 1.414L8.414 11l1.293 1.293a1 1 0 0 1-1.414 1.414l-2-2a1 1 0 0 1 0-1.414l2-2a1 1 0 0 1 1.414 0Zm2.586 0a1 1 0 0 1 1.414 0l2 2a1 1 0 0 1 0 1.414l-2 2a1 1 0 0 1-1.414-1.414L13.586 11l-1.293-1.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$1X;
function _extends$1X() { _extends$1X = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1X.apply(this, arguments); }
var SvgSearchSlash = function SvgSearchSlash(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1X({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1X || (_path$1X = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm-9 7a9 9 0 1 1 16.032 5.618l3.675 3.675a1 1 0 0 1-1.414 1.414l-3.675-3.675A9 9 0 0 1 2 11Zm12.207-3.207a1 1 0 0 1 0 1.414l-5 5a1 1 0 0 1-1.414-1.414l5-5a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$1W;
function _extends$1W() { _extends$1W = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1W.apply(this, arguments); }
var SvgSearchX = function SvgSearchX(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1W({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1W || (_path$1W = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm-9 7a9 9 0 1 1 16.032 5.618l3.675 3.675a1 1 0 0 1-1.414 1.414l-3.675-3.675A9 9 0 0 1 2 11Zm5.793-3.207a1 1 0 0 1 1.414 0L11 9.586l1.793-1.793a1 1 0 1 1 1.414 1.414L12.414 11l1.793 1.793a1 1 0 0 1-1.414 1.414L11 12.414l-1.793 1.793a1 1 0 0 1-1.414-1.414L9.586 11 7.793 9.207a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$1V;
function _extends$1V() { _extends$1V = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1V.apply(this, arguments); }
var SvgSend = function SvgSend(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1V({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1V || (_path$1V = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M22.707 1.293a1 1 0 0 1 .237 1.037l-7 20a1 1 0 0 1-1.858.076l-3.844-8.648-8.648-3.844a1 1 0 0 1 .076-1.858l20-7a1 1 0 0 1 1.037.237ZM12.193 13.222l2.696 6.067 4.72-13.483-7.416 7.416Zm6.001-8.83L4.711 9.11l6.067 2.696 7.416-7.416Z",
    clipRule: "evenodd"
  })));
};

var _path$1U;
function _extends$1U() { _extends$1U = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1U.apply(this, arguments); }
var SvgSendHorizonal = function SvgSendHorizonal(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1U({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1U || (_path$1U = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.337 2.252a1 1 0 0 1 1.091-.156l19 9a1 1 0 0 1 0 1.808l-19 9a1 1 0 0 1-1.377-1.22L4.946 12 2.051 3.316a1 1 0 0 1 .286-1.064ZM6.72 13l-2.03 6.093L17.553 13H6.721Zm10.832-2H6.721L4.69 4.907 17.553 11Z",
    clipRule: "evenodd"
  })));
};

var _path$1T;
function _extends$1T() { _extends$1T = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1T.apply(this, arguments); }
var SvgShare = function SvgShare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1T({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1T || (_path$1T = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 1.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L13 4.414V15a1 1 0 1 1-2 0V4.414L8.707 6.707a1 1 0 0 1-1.414-1.414l4-4ZM4 11a1 1 0 0 1 1 1v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8a1 1 0 1 1 2 0v8a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3v-8a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$1S;
function _extends$1S() { _extends$1S = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1S.apply(this, arguments); }
var SvgShareFilled = function SvgShareFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1S({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1S || (_path$1S = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20 6a3 3 0 0 1-4.77 2.422L9.953 11.47a3.02 3.02 0 0 1-.032 1.218l5.18 2.99a3 3 0 1 1-1.039 1.71l-5.224-3.016a3 3 0 1 1 .124-4.64L14.1 6.766A3.005 3.005 0 0 1 17 3a3 3 0 0 1 3 3Z",
    clipRule: "evenodd"
  })));
};

var _path$1R;
function _extends$1R() { _extends$1R = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1R.apply(this, arguments); }
var SvgShareOutline = function SvgShareOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1R({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1R || (_path$1R = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M17 4a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-4 2a4 4 0 1 1 1.305 2.956l-3.445 1.99a4.007 4.007 0 0 1-.044 2.257l3.375 1.949a4 4 0 1 1-1.029 1.716L9.756 14.9a4 4 0 1 1 .111-5.69l3.343-1.93A3.995 3.995 0 0 1 13 6Zm-6 4a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm8 8a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z",
    clipRule: "evenodd"
  })));
};

var _path$1Q;
function _extends$1Q() { _extends$1Q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1Q.apply(this, arguments); }
var SvgShieldAlert = function SvgShieldAlert(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1Q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1Q || (_path$1Q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.649 1.064a1 1 0 0 1 .702 0l8 3A1 1 0 0 1 21 5v7c0 3.446-2.282 6.2-4.341 8.003a22.684 22.684 0 0 1-4.104 2.836 8.28 8.28 0 0 1-.076.04l-.022.01-.006.004h-.002s-.002.001-.449-.893l-.448.894-.003-.001-.006-.004-.022-.01a8.28 8.28 0 0 1-.35-.188 22.686 22.686 0 0 1-3.83-2.689C5.281 18.2 3 15.447 3 12V5a1 1 0 0 1 .649-.936l8-3ZM12 22l-.448.894c.282.14.614.141.895 0L12 22Zm0-1.137a20.69 20.69 0 0 0 3.341-2.365C17.282 16.8 19 14.553 19 12V5.693l-7-2.625-7 2.625V12c0 2.554 1.718 4.8 3.659 6.497A20.692 20.692 0 0 0 12 20.863ZM12 7a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0V8a1 1 0 0 1 1-1Zm-1 9a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$1P;
function _extends$1P() { _extends$1P = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1P.apply(this, arguments); }
var SvgShieldCheck = function SvgShieldCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1P({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1P || (_path$1P = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.649 1.064a1 1 0 0 1 .702 0l8 3A1 1 0 0 1 21 5v7c0 3.446-2.282 6.2-4.341 8.003a22.684 22.684 0 0 1-4.104 2.836 8.28 8.28 0 0 1-.076.04l-.022.01-.006.004h-.002s-.002.001-.449-.893l-.448.894-.003-.001-.006-.004-.022-.01a8.28 8.28 0 0 1-.35-.188 22.686 22.686 0 0 1-3.83-2.689C5.281 18.2 3 15.447 3 12V5a1 1 0 0 1 .649-.936l8-3ZM12 22l-.448.894c.282.14.614.141.895 0L12 22Zm0-1.137a20.69 20.69 0 0 0 3.341-2.365C17.282 16.8 19 14.553 19 12V5.693l-7-2.625-7 2.625V12c0 2.554 1.718 4.8 3.659 6.497A20.692 20.692 0 0 0 12 20.863Zm3.707-11.57a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 1 1 1.414-1.414L11 12.586l3.293-3.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$1O;
function _extends$1O() { _extends$1O = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1O.apply(this, arguments); }
var SvgShieldFilled = function SvgShieldFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1O({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1O || (_path$1O = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 22c.587 0 1.1-.32 1.1-.32 1.792-1.173 3.09-2.542 3.897-3.337C18.467 16.893 20 14.229 20 12V7.008s.026-.524-.287-1.035a1.934 1.934 0 0 0-.817-.755l-6-3.007A2.092 2.092 0 0 0 12 2c-.299 0-.598.07-.896.21l-6 3.008a2.297 2.297 0 0 0-.781.755C4 6.465 4 7.006 4 7.006V12c0 2.253 1.502 4.889 3 6.343.808.783 2.114 2.154 3.918 3.343 0 0 .495.314 1.082.314Z",
    clipRule: "evenodd"
  })));
};

var _path$1N;
function _extends$1N() { _extends$1N = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1N.apply(this, arguments); }
var SvgShieldOutline = function SvgShieldOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1N({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1N || (_path$1N = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 22c-.587 0-1.082-.314-1.082-.314-1.804-1.189-3.11-2.56-3.917-3.343C5.502 16.889 4 14.253 4 12V7.006s0-.541.323-1.033c.215-.329.475-.58.78-.755l6-3.007c.3-.14.598-.211.897-.211.299 0 .598.07.896.21l6 3.008c.335.162.608.413.817.755.313.511.287 1.035.287 1.035V12c0 2.23-1.532 4.892-3.003 6.343-.807.795-2.105 2.164-3.897 3.337 0 0-.513.32-1.1.32Zm0-2c.44 0 3.143-2.29 3.748-2.926C16.85 15.914 18 14 18 12V7c-3.776-2-5.776-3-6-3-.224 0-2.224 1-6 3v5c0 2 1.127 3.911 2.25 5.074C8.857 17.701 11.56 20 12 20Z",
    clipRule: "evenodd"
  })));
};

var _path$1M, _path2$7;
function _extends$1M() { _extends$1M = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1M.apply(this, arguments); }
var SvgShieldTickOutline = function SvgShieldTickOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1M({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1M || (_path$1M = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 22c-.587 0-1.082-.314-1.082-.314-1.804-1.189-3.11-2.56-3.917-3.343C5.502 16.889 4 14.253 4 12V7.006s0-.541.323-1.033c.215-.329.475-.58.78-.755l6-3.007c.3-.14.598-.211.897-.211.299 0 .598.07.896.21l6 3.008c.335.162.608.413.817.755.313.511.287 1.035.287 1.035V12c0 2.23-1.532 4.892-3.003 6.343-.807.795-2.105 2.164-3.897 3.337 0 0-.513.32-1.1.32Zm0-2c.44 0 3.143-2.29 3.748-2.926C16.85 15.914 18 14 18 12V7c-3.776-2-5.776-3-6-3-.224 0-2.224 1-6 3v5c0 2 1.127 3.911 2.25 5.074C8.857 17.701 11.56 20 12 20Z",
    clipRule: "evenodd"
  })), _path2$7 || (_path2$7 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.614 8.21a1 1 0 0 1 .175 1.404l-4.666 6a1 1 0 0 1-1.473.116l-2.333-2.18a1 1 0 0 1 1.366-1.461l1.533 1.434 3.995-5.136a1 1 0 0 1 1.403-.175Z",
    clipRule: "evenodd"
  })));
};

var _path$1L;
function _extends$1L() { _extends$1L = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1L.apply(this, arguments); }
var SvgShirt = function SvgShirt(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1L({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1L || (_path$1L = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9 2a1 1 0 0 0-1.316-.949l-4.38 1.46a3 3 0 0 0-2.01 3.345c.011.067.029.132.052.193.002.037.006.074.012.111l.003.019c.04.242.316 1.954.46 2.582.076.327.159.666.237.94.04.134.082.271.127.39.023.059.053.132.091.205l.001.002c.024.046.11.211.276.352a2.001 2.001 0 0 0 1.298.475l-.336-.027.338.027h-.002 1.15v9a3 3 0 0 0 .878 2.122c.443.443 1.01.65 1.621.753.14.023 3.16.007 4.175.002h.003a129.9 129.9 0 0 1 .647 0c1.016.005 4.036.021 4.175-.002.61-.103 1.178-.31 1.621-.753A3 3 0 0 0 19 20.125v-9h1.15l.335-.027c-.11.018-.223.028-.336.027h-.002l.338-.027a2 2 0 0 0 .962-.448c.166-.14.252-.306.276-.352l.001-.002c.038-.073.068-.146.09-.205a4.99 4.99 0 0 0 .128-.39c.078-.274.161-.613.236-.94.145-.628.422-2.34.46-2.582l.004-.019c.006-.037.01-.074.012-.111.023-.061.04-.126.052-.193a3 3 0 0 0-2.01-3.345l-4.38-1.46A1 1 0 0 0 15 2a3 3 0 1 1-6 0Zm11.721 3.641a1.01 1.01 0 0 1 .013-.117 1 1 0 0 0-.67-1.115l-3.243-1.081a5 5 0 0 1-9.641 0l-3.244 1.08a1 1 0 0 0-.67 1.116c.007.04.011.078.013.117.024.063.042.13.053.2l.001.002.002.01.006.032.02.103.083.554c.055.377.12.818.135.89.052.253.12.569.192.88.07.305.141.594.203.813H6a1 1 0 0 1 1 1v10a1 1 0 0 0 .293.707c.178.178 4 .17 4.632.168h.15c.632.002 4.455.01 4.632-.168a1 1 0 0 0 .293-.707v-10a1 1 0 0 1 1-1h2.026c.062-.22.133-.508.203-.812.072-.312.14-.628.192-.88.015-.073.08-.514.135-.89.041-.277.077-.52.084-.555l.02-.103.006-.032.001-.01V5.84c.012-.07.03-.136.054-.199Z",
    clipRule: "evenodd"
  })));
};

var _path$1K;
function _extends$1K() { _extends$1K = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1K.apply(this, arguments); }
var SvgShoppingBag = function SvgShoppingBag(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1K({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1K || (_path$1K = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.2 1.4A1 1 0 0 1 6 1h12a1 1 0 0 1 .8.4l3 4a1 1 0 0 1 .2.6v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V6a1 1 0 0 1 .2-.6l3-4ZM6.5 3 5 5h14l-1.5-2h-11ZM20 7H4v13a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V7ZM8 9a1 1 0 0 1 1 1 3 3 0 0 0 6 0 1 1 0 1 1 2 0 5 5 0 1 1-10 0 1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$1J;
function _extends$1J() { _extends$1J = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1J.apply(this, arguments); }
var SvgShoppingCart = function SvgShoppingCart(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1J({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1J || (_path$1J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.05 2.05a1 1 0 0 1 1-1h2a1 1 0 0 1 .978.79l.901 4.21h16.16a1 1 0 0 1 .977 1.217l-1.65 7.428a3.002 3.002 0 0 1-2.925 2.355H8.72a3 3 0 0 1-2.988-2.37l-1.58-7.375a.995.995 0 0 1-.019-.09L3.241 3.05H2.05a1 1 0 0 1-1-1Zm5.308 6 1.33 6.21a1.001 1.001 0 0 0 1 .79h9.8a1.002 1.002 0 0 0 .975-.785l1.38-6.215H6.358ZM6 21a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm11 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z",
    clipRule: "evenodd"
  })));
};

var _path$1I;
function _extends$1I() { _extends$1I = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1I.apply(this, arguments); }
var SvgShrink = function SvgShrink(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1I({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1I || (_path$1I = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.293 2.293a1 1 0 0 1 1.414 0L8 6.586V4.2a1 1 0 0 1 2 0V9a1 1 0 0 1-1 1H4.2a1 1 0 0 1 0-2h2.386L2.293 3.707a1 1 0 0 1 0-1.414Zm19.414 0a1 1 0 0 1 0 1.414L17.414 8H19.8a1 1 0 1 1 0 2H15a1 1 0 0 1-1-1V4.2a1 1 0 1 1 2 0v2.386l4.293-4.293a1 1 0 0 1 1.414 0ZM3.2 15a1 1 0 0 1 1-1H9a1 1 0 0 1 1 1v4.8a1 1 0 1 1-2 0v-2.386l-4.293 4.293a1 1 0 0 1-1.414-1.414L6.586 16H4.2a1 1 0 0 1-1-1ZM14 15a1 1 0 0 1 1-1h4.8a1 1 0 1 1 0 2h-2.386l4.293 4.293a1 1 0 0 1-1.414 1.414L16 17.414V19.8a1 1 0 1 1-2 0V15Z",
    clipRule: "evenodd"
  })));
};

var _path$1H;
function _extends$1H() { _extends$1H = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1H.apply(this, arguments); }
var SvgShuffle = function SvgShuffle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1H({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1H || (_path$1H = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M17.293 1.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414-1.414L19.586 7H16.1c-1.019 0-1.97.473-2.456 1.237a1.105 1.105 0 0 1-.028.042l-6.107 8.61C6.522 18.244 5.022 19 3.4 19H2a1 1 0 1 1 0-2h1.4c.976 0 1.875-.444 2.488-1.284l6.083-8.575C12.887 5.72 14.527 5 16.1 5h3.486l-2.293-2.293a1 1 0 0 1 0-1.414ZM1 6a1 1 0 0 1 1-1h1.9c1.893 0 3.618 1.125 4.48 2.726a1 1 0 1 1-1.76.948C6.082 7.675 5.007 7 3.9 7H2a1 1 0 0 1-1-1Zm16.293 7.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414-1.414L19.586 19H16.1c-1.636 0-3.257-.87-4.144-2.263l-.004-.007-.5-.8a1 1 0 0 1 1.696-1.06l.498.796c.513.805 1.49 1.334 2.454 1.334h3.486l-2.293-2.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$1G;
function _extends$1G() { _extends$1G = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1G.apply(this, arguments); }
var SvgSignLanguage = function SvgSignLanguage(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1G({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1G || (_path$1G = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18.669 8.737c.77.247 2.206 1.191 2.633 1.694.428.503.702 1.417.702 2.711 0 1.294-1.218 2.857-1.218 3.681 0 .55.098 1.55.293 3 0 .547-.242.87-.724.968-.483.1-.834-.144-1.054-.73l-.704-3.605-.495 4.402c-.075.77-.444 1.14-1.107 1.107-.662-.032-.96-.436-.896-1.212l.345-4.297-2.306 4.945c-.357.576-.793.736-1.31.483-.516-.254-.637-.68-.363-1.279l2.01-4.93-2.927 3.468c-.423.38-.843.41-1.262.092-.42-.317-.515-.709-.286-1.173l4.204-5.788h-1.731c-1.373.03-2.162-.27-2.368-.9-.308-.943.532-1.18 1.06-1.122.354.039.856.052 1.51.039.883-.055 1.856-.358 2.92-.91 1.596-.827 2.304-.89 3.074-.644ZM3.002 15.542c.998 0 .998 1 .998 1 0 1.111 1.002 1.5 1.5 1.5 0 0 1 .035 1 1.017 0 .983-1 .983-1 .983a3.5 3.5 0 0 1-3.5-3.5s.004-1 1.002-1Zm6.245-12.61.725 5.276.8-4.468c.177-.54.526-.777 1.048-.71.521.065.8.356.834.873l-.457 4.347c-.55-.042-1.65-.127-2.535.312-.919.456-1.587 1.44-1.587 2.355 0 1.615.795 2.632 2.386 3.052L8.714 16.4c-.771-.001-1.631-.093-2.033-.236-.622-.222-1.317-.877-1.964-1.998-.646-1.12-.373-3.083-.785-3.797-.275-.475-.859-1.292-1.753-2.451-.274-.474-.226-.874.142-1.2.37-.327.795-.292 1.278.104l2.411 2.77-1.77-4.06c-.371-.782-.262-1.326.328-1.63.59-.303 1.076-.063 1.46.72l1.85 3.894L7.4 3.08c.011-.68.304-1.038.878-1.077.573-.038.896.272.968.93Zm10.439 2.163s.704.71-.002 1.416-1.413-.001-1.413-.001c-.785-.786-1.769-.353-2.121 0 0 0-.732.682-1.426-.013-.695-.694.012-1.402.012-1.402a3.5 3.5 0 0 1 4.95 0Z",
    clipRule: "evenodd"
  })));
};

var _path$1F;
function _extends$1F() { _extends$1F = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1F.apply(this, arguments); }
var SvgSignOut = function SvgSignOut(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1F({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1F || (_path$1F = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h4a1 1 0 1 1 0 2H5a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3h4a1 1 0 0 1 0 2H5Zm10.293 2.293a1 1 0 0 1 1.414 0l5 5a1 1 0 0 1 0 1.414l-5 5a1 1 0 0 1-1.414-1.414L18.586 13H9a1 1 0 1 1 0-2h9.586l-3.293-3.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$1E;
function _extends$1E() { _extends$1E = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1E.apply(this, arguments); }
var SvgSkipBack = function SvgSkipBack(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1E({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1E || (_path$1E = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M19.433 3.099A1 1 0 0 1 20 4v16a1 1 0 0 1-1.625.78l-10-8a1 1 0 0 1 0-1.56l10-8a1 1 0 0 1 1.058-.121ZM10.601 12 18 17.92V6.08L10.6 12ZM5 4a1 1 0 0 1 1 1v14a1 1 0 1 1-2 0V5a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$1D;
function _extends$1D() { _extends$1D = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1D.apply(this, arguments); }
var SvgSkipForward = function SvgSkipForward(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1D({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1D || (_path$1D = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.567 3.099a1 1 0 0 1 1.058.12l10 8a1 1 0 0 1 0 1.562l-10 8A1 1 0 0 1 4 20V4a1 1 0 0 1 .567-.901ZM6 6.08v11.838L13.4 12 6 6.08ZM19 4a1 1 0 0 1 1 1v14a1 1 0 1 1-2 0V5a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$1C;
function _extends$1C() { _extends$1C = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1C.apply(this, arguments); }
var SvgSlack = function SvgSlack(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1C({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1C || (_path$1C = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.012 1.048A2.5 2.5 0 0 1 11 3.5V5a1 1 0 0 1-1 1H8.5a2.5 2.5 0 0 1-.488-4.952Zm.68 1.99A.5.5 0 1 0 8.5 4H9v-.5a.5.5 0 0 0-.309-.462ZM14.5 3a.5.5 0 0 0-.5.5v5a.5.5 0 0 0 1 0v-5a.5.5 0 0 0-.5-.5Zm-2.5.5a2.5 2.5 0 0 1 5 0v5a2.5 2.5 0 0 1-5 0v-5Zm7.543 2.69A2.5 2.5 0 1 1 20.5 11H19a1 1 0 0 1-1-1V8.5a2.5 2.5 0 0 1 1.543-2.31Zm1.055 1.82A.5.5 0 0 0 20 8.5V9h.5a.5.5 0 0 0 .098-.99ZM3.5 9a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5ZM1 9.5A2.5 2.5 0 0 1 3.5 7h5a2.5 2.5 0 0 1 0 5h-5A2.5 2.5 0 0 1 1 9.5ZM15.5 14a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5Zm-2.5.5a2.5 2.5 0 0 1 2.5-2.5h5a2.5 2.5 0 0 1 0 5h-5a2.5 2.5 0 0 1-2.5-2.5ZM2.111 13.421A2.5 2.5 0 0 1 3.5 13H5a1 1 0 0 1 1 1v1.5a2.5 2.5 0 1 1-3.889-2.079ZM3.5 15a.5.5 0 1 0 .5.5V15h-.5Zm6 0a.5.5 0 0 0-.5.5v5a.5.5 0 0 0 1 0v-5a.5.5 0 0 0-.5-.5Zm-2.5.5a2.5 2.5 0 0 1 5 0v5a2.5 2.5 0 0 1-5 0v-5Zm6 3.5a1 1 0 0 1 1-1h1.5a2.5 2.5 0 1 1-2.5 2.5V19Zm2 1v.5a.5.5 0 1 0 .5-.5H15Z",
    clipRule: "evenodd"
  })));
};

var _path$1B;
function _extends$1B() { _extends$1B = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1B.apply(this, arguments); }
var SvgSmartphone = function SvgSmartphone(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1B({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1B || (_path$1B = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 3a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H7ZM4 4a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v16a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V4Zm7 14a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$1A;
function _extends$1A() { _extends$1A = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1A.apply(this, arguments); }
var SvgSmartphoneCharging = function SvgSmartphoneCharging(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1A({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1A || (_path$1A = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 3a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H7ZM4 4a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v16a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V4Zm9.222 3.168a1 1 0 0 1 .277 1.387L11.869 11H14a1 1 0 0 1 .832 1.555l-2.667 4a1 1 0 1 1-1.664-1.11L12.131 13H10a1 1 0 0 1-.832-1.555l2.667-4a1 1 0 0 1 1.387-.277Z",
    clipRule: "evenodd"
  })));
};

var _path$1z;
function _extends$1z() { _extends$1z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1z.apply(this, arguments); }
var SvgSmartphoneNfc = function SvgSmartphoneNfc(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1z || (_path$1z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M19.413 3.232a1 1 0 0 1 1.365.37 16.911 16.911 0 0 1 .01 16.794 1 1 0 1 1-1.736-.992 14.91 14.91 0 0 0-.01-14.807 1 1 0 0 1 .371-1.365ZM1 7a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7Zm7 0H3v10h5V7Zm7.968-1.66a1 1 0 0 1 1.362.378 12.76 12.76 0 0 1 0 12.564 1 1 0 0 1-1.74-.984 10.76 10.76 0 0 0 0-10.596 1 1 0 0 1 .378-1.362Zm-3.463 2.11a1 1 0 0 1 1.364.374 8.43 8.43 0 0 1 0 8.35 1 1 0 1 1-1.738-.99 6.43 6.43 0 0 0 0-6.37 1 1 0 0 1 .374-1.364Z",
    clipRule: "evenodd"
  })));
};

var _path$1y;
function _extends$1y() { _extends$1y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1y.apply(this, arguments); }
var SvgSmile = function SvgSmile(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1y || (_path$1y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 12a9 9 0 1 1 18 0 9 9 0 0 1-18 0Zm9-11C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1ZM9 8a1 1 0 0 0 0 2h.01a1 1 0 0 0 0-2H9Zm6 0a1 1 0 1 0 0 2h.01a1 1 0 1 0 0-2H15Zm-6.203 5.396A1 1 0 0 0 7.2 14.6l.002.003.003.004.007.009.021.027.07.086a6.949 6.949 0 0 0 1.171 1.08c.798.58 2 1.191 3.526 1.191 1.526 0 2.728-.61 3.526-1.191a6.951 6.951 0 0 0 1.17-1.08c.03-.035.053-.064.07-.086l.022-.027.007-.01.003-.003-.031.04c.331-.442.275-1.112-.167-1.443a1 1 0 0 0-1.397.196l-.004.006a3.64 3.64 0 0 1-.194.219 4.945 4.945 0 0 1-.656.57c-.577.42-1.374.81-2.349.81-.974 0-1.772-.39-2.35-.81a4.945 4.945 0 0 1-.815-.748 1.932 1.932 0 0 1-.033-.041l-.005-.006Z",
    clipRule: "evenodd"
  })));
};

var _path$1x;
function _extends$1x() { _extends$1x = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1x.apply(this, arguments); }
var SvgSmilePlus = function SvgSmilePlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1x({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1x || (_path$1x = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7.679 4.055a9 9 0 0 1 5.22-1.06 1 1 0 0 0 .2-1.99 11 11 0 1 0 9.9 11V11a1 1 0 1 0-2 0v.997A9 9 0 1 1 7.68 4.055ZM20 2a1 1 0 1 0-2 0v2h-2a1 1 0 1 0 0 2h2v2a1 1 0 1 0 2 0V6h2a1 1 0 1 0 0-2h-2V2ZM9 8a1 1 0 1 0 0 2h.01a1 1 0 1 0 0-2H9Zm6 0a1 1 0 1 0 0 2h.01a1 1 0 1 0 0-2H15Zm-6.203 5.396A1 1 0 0 0 7.2 14.6l.002.003.003.004.007.009.021.027.07.086a6.949 6.949 0 0 0 1.171 1.08c.798.58 2 1.191 3.526 1.191 1.526 0 2.728-.61 3.526-1.191a6.951 6.951 0 0 0 1.17-1.08c.03-.035.053-.064.07-.086l.022-.027.007-.01.003-.003-.031.04c.331-.442.275-1.112-.167-1.443a1 1 0 0 0-1.397.196l-.004.006a3.64 3.64 0 0 1-.194.219 4.945 4.945 0 0 1-.656.57c-.577.42-1.374.81-2.349.81-.974 0-1.772-.39-2.35-.81a4.945 4.945 0 0 1-.815-.748 1.932 1.932 0 0 1-.033-.041l-.005-.006Z",
    clipRule: "evenodd"
  })));
};

var _path$1w;
function _extends$1w() { _extends$1w = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1w.apply(this, arguments); }
var SvgSort = function SvgSort(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1w({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1w || (_path$1w = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 3.293a1 1 0 0 1 1.414 0l5 5a1 1 0 0 1-1.414 1.414L12 5.414 7.707 9.707a1 1 0 0 1-1.414-1.414l5-5Zm-5 11a1 1 0 0 1 1.414 0L12 18.586l4.293-4.293a1 1 0 0 1 1.414 1.414l-5 5a1 1 0 0 1-1.414 0l-5-5a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$1v;
function _extends$1v() { _extends$1v = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1v.apply(this, arguments); }
var SvgSparkle = function SvgSparkle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1v({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1v || (_path$1v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2a1 1 0 0 1 .95.688l1.913 5.817a.999.999 0 0 0 .644.643l.004.002 5.8 1.9a1 1 0 0 1 .002 1.9l-5.818 1.913a.999.999 0 0 0-.643.644l-.002.004-1.9 5.8a1 1 0 0 1-1.9.002l-1.913-5.818a1 1 0 0 0-.644-.643l-.004-.002-5.8-1.9a1 1 0 0 1-.001-1.9l5.817-1.913a1 1 0 0 0 .643-.644l.002-.004 1.9-5.8A1 1 0 0 1 12 2Zm.002 4.206-.95 2.901-.001.003a3 3 0 0 1-1.928 1.929h-.002L6.206 12l2.901.95h.003a3 3 0 0 1 1.929 1.928v.002l.96 2.915.95-2.901v-.003a3 3 0 0 1 1.928-1.929h.002l2.915-.96-2.901-.95h-.003a3 3 0 0 1-1.929-1.928v-.002L12 6.206Z",
    clipRule: "evenodd"
  })));
};

var _path$1u;
function _extends$1u() { _extends$1u = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1u.apply(this, arguments); }
var SvgSparkles = function SvgSparkles(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1u({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1u || (_path$1u = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 2a1 1 0 0 1 1 1v1h1a1 1 0 0 1 0 2H6v1a1 1 0 0 1-2 0V6H3a1 1 0 0 1 0-2h1V3a1 1 0 0 1 1-1Zm7 0a1 1 0 0 1 .95.688L14.862 8.5a1 1 0 0 0 .637.637l5.813 1.912a1 1 0 0 1 0 1.9L15.5 14.862a1 1 0 0 0-.637.637l-1.912 5.813a1 1 0 0 1-1.9 0L9.138 15.5a1 1 0 0 0-.637-.637L2.688 12.95a1 1 0 0 1 0-1.9L8.5 9.138a1 1 0 0 0 .637-.637l1.912-5.813A1 1 0 0 1 12 2Zm-.962 7.125a2.998 2.998 0 0 1-1.913 1.913L6.201 12l2.924.962a2.998 2.998 0 0 1 1.913 1.912L12 17.8l.962-2.924a2.998 2.998 0 0 1 1.912-1.913L17.8 12l-2.924-.962a2.998 2.998 0 0 1-1.913-1.913L12 6.201l-.962 2.924ZM19 16a1 1 0 0 1 1 1v1h1a1 1 0 1 1 0 2h-1v1a1 1 0 1 1-2 0v-1h-1a1 1 0 1 1 0-2h1v-1a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$1t;
function _extends$1t() { _extends$1t = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1t.apply(this, arguments); }
var SvgSpellCheck = function SvgSpellCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1t({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1t || (_path$1t = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a1 1 0 0 1 .894.553l3.991 7.982.02.037 1.99 3.98a1 1 0 1 1-1.79.895L15.382 13H8.618l-1.724 3.447a1 1 0 1 1-1.788-.894l1.99-3.98a.987.987 0 0 1 .019-.038l3.99-7.982A1 1 0 0 1 12 3Zm-2.382 8h4.764L12 6.236 9.618 11Zm13.09 6.293a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.415 0l-2-2a1 1 0 1 1 1.414-1.414L18 20.586l3.293-3.293a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$1s;
function _extends$1s() { _extends$1s = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1s.apply(this, arguments); }
var SvgSpellCheckHighlight = function SvgSpellCheckHighlight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1s({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1s || (_path$1s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a1 1 0 0 1 .894.553l3.991 7.982.02.037 1.99 3.98a1 1 0 1 1-1.79.895L15.383 13H8.618l-1.724 3.447a1 1 0 1 1-1.788-.894l1.99-3.98.019-.038 3.99-7.982A1 1 0 0 1 12 3Zm-2.382 8h4.764L12 6.236 9.618 11ZM5.735 21.284c-.3.262-.82.716-1.735.716a1 1 0 1 1 0-2c.15 0 .176-.011.456-.255l.007-.006c.142-.123.37-.322.676-.476.33-.166.71-.263 1.161-.263.452 0 .832.097 1.163.271.293.154.512.354.641.472l.019.017c.148.135.197.173.246.199A.466.466 0 0 0 8.6 20c.15 0 .176-.011.456-.255l.007-.006c.142-.123.37-.322.676-.476.33-.166.71-.263 1.161-.263.915 0 1.435.454 1.735.716l.034.03c.146.127.209.174.27.205A.54.54 0 0 0 13.2 20c.15 0 .176-.011.456-.255l.007-.006c.142-.123.37-.322.676-.476.33-.166.71-.263 1.161-.263.916 0 1.435.454 1.735.716l.034.03c.146.127.209.174.27.205A.54.54 0 0 0 17.8 20c.15 0 .176-.011.456-.255l.007-.006c.142-.123.37-.322.676-.476.33-.166.71-.263 1.161-.263a1 1 0 1 1 0 2 .54.54 0 0 0-.26.05 1.405 1.405 0 0 0-.271.205l-.034.029c-.3.262-.82.716-1.735.716-.451 0-.83-.097-1.161-.263a3.245 3.245 0 0 1-.676-.476l-.007-.006c-.28-.244-.306-.255-.456-.255a.54.54 0 0 0-.26.05 1.405 1.405 0 0 0-.271.205l-.034.029c-.3.262-.82.716-1.735.716-.451 0-.83-.097-1.161-.263a3.245 3.245 0 0 1-.676-.476l-.007-.006c-.28-.244-.306-.255-.456-.255a.54.54 0 0 0-.26.05 1.405 1.405 0 0 0-.271.205l-.034.029c-.3.262-.82.716-1.735.716-.452 0-.832-.097-1.163-.271a3.07 3.07 0 0 1-.641-.472l-.019-.017c-.148-.135-.197-.173-.246-.199A.466.466 0 0 0 6.3 21a.54.54 0 0 0-.26.05 1.405 1.405 0 0 0-.271.205l-.034.029Z",
    clipRule: "evenodd"
  })));
};

var _path$1r;
function _extends$1r() { _extends$1r = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1r.apply(this, arguments); }
var SvgSplit = function SvgSplit(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1r({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1r || (_path$1r = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3 2h5a1 1 0 0 1 0 2H5.414l6.12 6.12A5 5 0 0 1 13 13.706V22a1 1 0 1 1-2 0v-8.31a3 3 0 0 0-.879-2.155L4 5.415V8a1 1 0 0 1-2 0V3a1 1 0 0 1 1-1Zm15.586 2H16a1 1 0 1 1 0-2h5a1 1 0 0 1 1 1v5a1 1 0 1 1-2 0V5.414l-4.293 4.293a1 1 0 0 1-1.414-1.414L18.586 4Z",
    clipRule: "evenodd"
  })));
};

var _path$1q;
function _extends$1q() { _extends$1q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1q.apply(this, arguments); }
var SvgStarFilled = function SvgStarFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1q || (_path$1q = /*#__PURE__*/React.createElement("path", {
    d: "M12.897 1.557a1 1 0 0 0-1.794 0l-2.857 5.79-6.39.934a1 1 0 0 0-.554 1.705l4.624 4.503-1.092 6.362a1 1 0 0 0 1.451 1.054L12 18.9l5.715 3.005a1 1 0 0 0 1.45-1.054l-1.09-6.362 4.623-4.503a1 1 0 0 0-.553-1.705l-6.39-.935-2.858-5.789Z"
  })));
};

var _path$1p;
function _extends$1p() { _extends$1p = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1p.apply(this, arguments); }
var SvgStarHalf = function SvgStarHalf(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1p({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1p || (_path$1p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.43 1.097a1 1 0 0 1 .473 1.333l-3 6.3a1 1 0 0 1-.762.56L4.173 10l3.52 3.379a1 1 0 0 1 .292.892l-.855 4.917 4.411-2.277a1 1 0 0 1 .918 1.778l-6.2 3.2a1 1 0 0 1-1.444-1.06l1.109-6.376-4.617-4.432A1 1 0 0 1 1.86 8.31l6.469-.924 2.77-5.816a1 1 0 0 1 1.332-.473Z",
    clipRule: "evenodd"
  })));
};

var _path$1o;
function _extends$1o() { _extends$1o = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1o.apply(this, arguments); }
var SvgStarOff = function SvgStarOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1o({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1o || (_path$1o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 1.414 0L9.04 7.626l.016.015 9.113 9.113c.091.065.172.146.238.238l4.3 4.3a1 1 0 1 1-1.414 1.415l-2.18-2.18.053.303a1 1 0 0 1-1.45 1.056L12 18.898l-5.717 2.988a1 1 0 0 1-1.449-1.055l1.091-6.342-4.623-4.503a1 1 0 0 1 .553-1.705l4.37-.642-4.932-4.932a1 1 0 0 1 0-1.414Zm6.695 8.11-3.84.563 3.55 3.458a1 1 0 0 1 .288.886l-.838 4.867 4.389-2.293a1 1 0 0 1 .926 0l4.389 2.293-.19-1.1-8.674-8.675ZM12.001 1a1 1 0 0 1 .896.558l2.857 5.798 6.39.924a1 1 0 0 1 .554 1.706l-3.58 3.49a1 1 0 0 1-1.396-1.432l2.128-2.075-4.903-.71a1 1 0 0 1-.754-.547l-2.195-4.454-.542 1.096a1 1 0 0 1-1.792-.888l1.44-2.91A1 1 0 0 1 12 1Z",
    clipRule: "evenodd"
  })));
};

var _path$1n;
function _extends$1n() { _extends$1n = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1n.apply(this, arguments); }
var SvgStarOutline = function SvgStarOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1n({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1n || (_path$1n = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 1a1 1 0 0 1 .897.557l2.857 5.79 6.39.934a1 1 0 0 1 .554 1.705l-4.623 4.503 1.09 6.362a1 1 0 0 1-1.45 1.054L12 18.9l-5.715 3.005a1 1 0 0 1-1.45-1.054l1.09-6.362-4.623-4.503a1 1 0 0 1 .553-1.705l6.39-.935 2.858-5.789A1 1 0 0 1 12 1Zm0 3.26L9.807 8.702a1 1 0 0 1-.752.546l-4.907.718 3.55 3.457a1 1 0 0 1 .288.885l-.838 4.883 4.387-2.307a1 1 0 0 1 .93 0l4.387 2.307-.838-4.883a1 1 0 0 1 .288-.885l3.55-3.457-4.907-.718a1 1 0 0 1-.752-.546L12 4.259Z",
    clipRule: "evenodd"
  })));
};

var _path$1m;
function _extends$1m() { _extends$1m = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1m.apply(this, arguments); }
var SvgStethoscopeFilled = function SvgStethoscopeFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1m({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1m || (_path$1m = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 2c-1.333 0-2 .667-2 2v7c0 2.96 2.19 5.5 5.14 5.91.62 3.01 3.28 5.09 6.36 5.09a6.499 6.499 0 0 0 6.5-6.5v-3.69c1.16-.42 2-1.52 2-2.81a3 3 0 0 0-6 0c0 1.29.84 2.4 2 2.81v3.6c0 2.5-2 4.5-4.5 4.5-2 0-3.68-1.21-4.28-3.01C12 16.3 14 13.8 14 11V4c0-1.333-.667-2-2-2h-1c-.667 0-1 .333-1 1s.333 1 1 1h1v7a4 4 0 1 1-8 0V4h1c.667 0 1-.333 1-1s-.333-1-1-1H4Z",
    clipRule: "evenodd"
  })));
};

var _path$1l;
function _extends$1l() { _extends$1l = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1l.apply(this, arguments); }
var SvgStethoscopeOutline = function SvgStethoscopeOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1l({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1l || (_path$1l = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M19 8c.56 0 1 .43 1 1a1 1 0 0 1-1 1c-.57 0-1-.45-1-1 0-.57.43-1 1-1ZM4 2c-1.333 0-2 .667-2 2v7c0 2.96 2.19 5.5 5.14 5.91.62 3.01 3.28 5.09 6.36 5.09a6.499 6.499 0 0 0 6.5-6.5v-3.69c1.16-.42 2-1.52 2-2.81a3 3 0 0 0-6 0c0 1.29.84 2.4 2 2.81v3.6c0 2.5-2 4.5-4.5 4.5-2 0-3.68-1.21-4.28-3.01C12 16.3 14 13.8 14 11V4c0-1.333-.667-2-2-2h-1c-.667 0-1 .333-1 1s.333 1 1 1h1v7a4 4 0 1 1-8 0V4h1c.667 0 1-.333 1-1s-.333-1-1-1H4Z",
    clipRule: "evenodd"
  })));
};

var _path$1k;
function _extends$1k() { _extends$1k = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1k.apply(this, arguments); }
var SvgStopFilled = function SvgStopFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1k({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1k || (_path$1k = /*#__PURE__*/React.createElement("path", {
    d: "M6 20h12c1.333 0 2-.667 2-2V6c0-1.333-.667-2-2-2H6c-1.333-.009-2 .658-2 2v12c0 1.333.667 2 2 2Z"
  })));
};

var _path$1j;
function _extends$1j() { _extends$1j = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1j.apply(this, arguments); }
var SvgStopOutline = function SvgStopOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1j({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1j || (_path$1j = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 20c-1.333 0-2-.667-2-2V6c0-1.342.667-2.008 2-2h12c1.333 0 2 .667 2 2v12c0 1.333-.667 2-2 2H6ZM6 6v12h12V6H6Z",
    clipRule: "evenodd"
  })));
};

var _path$1i;
function _extends$1i() { _extends$1i = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1i.apply(this, arguments); }
var SvgStrikethrough = function SvgStrikethrough(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1i({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1i || (_path$1i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.429 13H5c-.667.003-1-.33-1-1 0-.67.333-1.003 1-1h3.643a3.867 3.867 0 0 1-.247-.267c-.601-.713-.901-1.634-.901-2.762 0-1.214.443-2.179 1.33-2.896C9.713 4.358 10.881 4 12.331 4c1.512 0 2.903.291 4.174.873.434.265.583.645.445 1.139-.137.493-.483.674-1.036.543-1.298-.554-2.506-.83-3.624-.83-.898 0-1.6.198-2.108.596-.507.397-.761.954-.761 1.671 0 .511.098.935.295 1.272.197.337.514.644.953.921.383.242.957.514 1.723.815H19c.668-.003 1.001.33 1 1-.001.67-.334 1.003-1 1h-3.046c.211.236.385.488.52.756.262.522.393 1.145.393 1.869 0 1.355-.476 2.423-1.43 3.204-.952.78-2.278 1.171-3.976 1.171-1.598 0-2.873-.194-3.825-.582-.118-.048-.763-.458-.614-1.198.149-.74.752-.63 1.002-.542.436.153.896.28 1.381.385.763.163 1.476.244 2.139.244 1.132 0 1.985-.223 2.558-.67.573-.447.86-1.061.86-1.842 0-.518-.104-.944-.311-1.277-.207-.334-.554-.646-1.041-.937-.3-.179-.693-.373-1.181-.581Z",
    clipRule: "evenodd"
  })));
};

var _path$1h;
function _extends$1h() { _extends$1h = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1h.apply(this, arguments); }
var SvgSwitchCamera = function SvgSwitchCamera(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1h({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1h || (_path$1h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.293 1.293a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1 0 1.414l-3 3a1 1 0 0 1-1.414-1.414L6.586 6H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h7a1 1 0 1 1 0 2H4a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h2.586L5.293 2.707a1 1 0 0 1 0-1.414ZM20 6h-7a1 1 0 1 1 0-2h7a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3h-2.586l1.293 1.293a1 1 0 0 1-1.414 1.414l-3-3a1 1 0 0 1 0-1.414l3-3a1 1 0 0 1 1.414 1.414L17.414 18H20a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1Zm-8 4a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-4 2a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z",
    clipRule: "evenodd"
  })));
};

var _path$1g;
function _extends$1g() { _extends$1g = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1g.apply(this, arguments); }
var SvgSync = function SvgSync(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1g({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1g || (_path$1g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.998 4A8 8 0 0 0 4 12a1 1 0 1 1-2 0A10 10 0 0 1 12 2h.004a10.75 10.75 0 0 1 7.431 3.021l.012.012.553.553V3a1 1 0 1 1 2 0v5a1 1 0 0 1-1 1h-5a1 1 0 1 1 0-2h2.586l-.547-.547A8.75 8.75 0 0 0 11.999 4Zm5.659 13.657A8 8 0 0 0 20 12a1 1 0 1 1 2 0 10 10 0 0 1-10 10h-.004a10.75 10.75 0 0 1-7.431-3.021l-.012-.012L4 18.414V21a1 1 0 1 1-2 0v-5a1 1 0 0 1 1-1h5a1 1 0 1 1 0 2H5.414l.547.547A8.75 8.75 0 0 0 12 20a8 8 0 0 0 5.656-2.343Z",
    clipRule: "evenodd"
  })));
};

var _path$1f;
function _extends$1f() { _extends$1f = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1f.apply(this, arguments); }
var SvgTag = function SvgTag(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1f({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1f || (_path$1f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1 2a1 1 0 0 1 1-1h10a1 1 0 0 1 .707.293l9.29 9.29a3.426 3.426 0 0 1 0 4.834l-6.58 6.58a3.426 3.426 0 0 1-4.834 0l-9.29-9.29A1 1 0 0 1 1 12V2Zm2 1v8.586l8.997 8.997c.55.55 1.456.55 2.006 0l6.58-6.58c.55-.55.55-1.456 0-2.006L11.586 3H3Zm3 4a1 1 0 0 1 1-1h.01a1 1 0 0 1 0 2H7a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$1e;
function _extends$1e() { _extends$1e = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1e.apply(this, arguments); }
var SvgTags = function SvgTags(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1e({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1e || (_path$1e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 4a1 1 0 0 0-1 1v7a1 1 0 0 0 .293.707l6.29 6.29a3.426 3.426 0 0 0 4.834 0l3.58-3.58a3.426 3.426 0 0 0 0-4.834l-6.29-6.29A1 1 0 0 0 9 4H2Zm1 7.586V6h5.586l5.997 5.997c.55.55.55 1.456 0 2.006l-3.58 3.58c-.55.55-1.456.55-2.006 0L3 11.586ZM7 9a1 1 0 0 0-2 0v.01a1 1 0 0 0 2 0V9Zm8.707-4.707a1 1 0 1 0-1.414 1.414l6.301 6.301a1.401 1.401 0 0 1 0 1.984l-4.301 4.3a1 1 0 0 0 1.414 1.415l4.299-4.299a3.4 3.4 0 0 0 0-4.816l-6.299-6.3Z",
    clipRule: "evenodd"
  })));
};

var _path$1d;
function _extends$1d() { _extends$1d = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1d.apply(this, arguments); }
var SvgTextCursor = function SvgTextCursor(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1d({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1d || (_path$1d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5.957 2c0-.576.467-1.043 1.043-1.043h1a5.043 5.043 0 0 1 4 1.971 5.044 5.044 0 0 1 4-1.972h1a1.043 1.043 0 1 1 0 2.087h-1A2.957 2.957 0 0 0 13.043 6v12A2.957 2.957 0 0 0 16 20.956h1a1.044 1.044 0 0 1 0 2.088h-1a5.044 5.044 0 0 1-4-1.972 5.044 5.044 0 0 1-4 1.972H7a1.043 1.043 0 0 1 0-2.088h1A2.957 2.957 0 0 0 10.957 18V6A2.956 2.956 0 0 0 8 3.043H7A1.043 1.043 0 0 1 5.957 2Z",
    clipRule: "evenodd"
  })));
};

var _path$1c;
function _extends$1c() { _extends$1c = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1c.apply(this, arguments); }
var SvgTextCursorInput = function SvgTextCursorInput(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1c({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1c || (_path$1c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M3.957 4c0-.576.467-1.043 1.043-1.043h1a4.043 4.043 0 0 1 3 1.332 4.05 4.05 0 0 1 3-1.332h1a1.043 1.043 0 1 1 0 2.086h-1A1.956 1.956 0 0 0 10.043 7v10A1.956 1.956 0 0 0 12 18.956h1a1.044 1.044 0 0 1 0 2.088h-1a4.043 4.043 0 0 1-3-1.333 4.043 4.043 0 0 1-3 1.333H5a1.043 1.043 0 0 1 0-2.088h1A1.957 1.957 0 0 0 7.957 17V7A1.957 1.957 0 0 0 6 5.043H5A1.043 1.043 0 0 1 3.957 4ZM4 9.043a.956.956 0 0 0-.957.957v4a.957.957 0 0 0 .957.957h1a1.043 1.043 0 0 1 0 2.087H4A3.044 3.044 0 0 1 .957 14v-4A3.043 3.043 0 0 1 4 6.957h1a1.043 1.043 0 1 1 0 2.086H4ZM11.957 8c0-.576.467-1.043 1.043-1.043h7A3.044 3.044 0 0 1 23.044 10v4A3.044 3.044 0 0 1 20 17.044h-7a1.044 1.044 0 0 1 0-2.088h7a.957.957 0 0 0 .956-.956v-4A.956.956 0 0 0 20 9.043h-7A1.043 1.043 0 0 1 11.957 8Z",
    clipRule: "evenodd"
  })));
};

var _path$1b;
function _extends$1b() { _extends$1b = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1b.apply(this, arguments); }
var SvgTextQuote = function SvgTextQuote(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1b({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1b || (_path$1b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.957 6c0-.576.467-1.043 1.043-1.043h14a1.043 1.043 0 1 1 0 2.086H3A1.043 1.043 0 0 1 1.957 6ZM3 10.957c.576 0 1.043.467 1.043 1.043v6a1.043 1.043 0 1 1-2.086 0v-6c0-.576.467-1.043 1.043-1.043ZM6.957 12c0-.576.467-1.043 1.043-1.043h13a1.044 1.044 0 0 1 0 2.086H8A1.043 1.043 0 0 1 6.957 12Zm0 6c0-.576.467-1.044 1.043-1.044h13a1.044 1.044 0 0 1 0 2.088H8A1.043 1.043 0 0 1 6.957 18Z",
    clipRule: "evenodd"
  })));
};

var _path$1a;
function _extends$1a() { _extends$1a = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1a.apply(this, arguments); }
var SvgTextSelect = function SvgTextSelect(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1a({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$1a || (_path$1a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2.894 2.832A2.978 2.978 0 0 1 5 1.957c.552 0 1 .467 1 1.043 0 .576-.448 1.043-1 1.043-.279 0-.538.112-.722.296A.935.935 0 0 0 4 5c0 .576-.448 1.043-1 1.043S2 5.576 2 5c0-.822.327-1.6.894-2.168ZM8 3c0-.576.448-1.043 1-1.043h1c.552 0 1 .467 1 1.043 0 .576-.448 1.043-1 1.043H9c-.552 0-1-.467-1-1.043Zm5 0c0-.576.448-1.043 1-1.043h1c.552 0 1 .467 1 1.043 0 .576-.448 1.043-1 1.043h-1c-.552 0-1-.467-1-1.043Zm5 0c0-.576.448-1.043 1-1.043.782 0 1.54.31 2.106.875C21.673 3.4 22 4.178 22 5c0 .576-.448 1.043-1 1.043S20 5.576 20 5a.935.935 0 0 0-.278-.66A1.022 1.022 0 0 0 19 4.042c-.552 0-1-.467-1-1.043ZM6 8c0-.576.448-1.043 1-1.043h8c.552 0 1 .467 1 1.043 0 .576-.448 1.043-1 1.043H7c-.552 0-1-.467-1-1.043Zm-3-.043c.552 0 1 .467 1 1.043v1c0 .576-.448 1.043-1 1.043S2 10.576 2 10V9c0-.576.448-1.043 1-1.043Zm18 0c.552 0 1 .467 1 1.043v1c0 .576-.448 1.043-1 1.043s-1-.467-1-1.043V9c0-.576.448-1.043 1-1.043ZM6 12c0-.576.448-1.043 1-1.043h10c.552 0 1 .467 1 1.043 0 .576-.448 1.043-1 1.043H7c-.552 0-1-.467-1-1.043Zm-3 .957c.552 0 1 .467 1 1.043v1c0 .576-.448 1.044-1 1.044S2 15.576 2 15v-1c0-.576.448-1.043 1-1.043Zm18 0c.552 0 1 .467 1 1.043v1c0 .576-.448 1.044-1 1.044s-1-.468-1-1.044v-1c0-.576.448-1.043 1-1.043ZM6 16c0-.576.448-1.043 1-1.043h6c.552 0 1 .467 1 1.043 0 .576-.448 1.044-1 1.044H7c-.552 0-1-.468-1-1.044Zm-3 1.956c.552 0 1 .468 1 1.044 0 .239.095.478.278.66.184.185.443.296.722.296.552 0 1 .468 1 1.044 0 .576-.448 1.044-1 1.044-.782 0-1.54-.31-2.106-.876A3.066 3.066 0 0 1 2 19c0-.576.448-1.044 1-1.044Zm18 0c.552 0 1 .468 1 1.044 0 .822-.327 1.6-.894 2.168a2.978 2.978 0 0 1-2.106.876c-.552 0-1-.468-1-1.044 0-.576.448-1.044 1-1.044.279 0 .538-.11.722-.295A.935.935 0 0 0 20 19c0-.576.448-1.044 1-1.044ZM8 21c0-.576.448-1.044 1-1.044h1c.552 0 1 .468 1 1.044 0 .576-.448 1.044-1 1.044H9c-.552 0-1-.468-1-1.044Zm5 0c0-.576.448-1.044 1-1.044h1c.552 0 1 .468 1 1.044 0 .576-.448 1.044-1 1.044h-1c-.552 0-1-.468-1-1.044Z",
    clipRule: "evenodd"
  })));
};

var _path$19, _path2$6;
function _extends$19() { _extends$19 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$19.apply(this, arguments); }
var SvgThermometerFilled = function SvgThermometerFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$19({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$19 || (_path$19 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 15.354V5a3 3 0 1 0-6 0v10.354a4 4 0 1 0 6 0ZM9 4.5a.5.5 0 0 1 .5.5v12.134a1 1 0 1 1-1 0V5a.5.5 0 0 1 .5-.5Z",
    clipRule: "evenodd"
  })), _path2$6 || (_path2$6 = /*#__PURE__*/React.createElement("path", {
    d: "M19 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
  })));
};

var _path$18, _path2$5, _path3$1;
function _extends$18() { _extends$18 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$18.apply(this, arguments); }
var SvgThermometerOutline = function SvgThermometerOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$18({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$18 || (_path$18 = /*#__PURE__*/React.createElement("path", {
    d: "M9 4.5a.5.5 0 0 1 .5.5v12.134a1 1 0 1 1-1 0V5a.5.5 0 0 1 .5-.5Z"
  })), _path2$5 || (_path2$5 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 15.354V5a3 3 0 1 0-6 0v10.354a4 4 0 1 0 6 0Zm-2 .914V5a1 1 0 0 0-2 0v11.268A2 2 0 0 0 9 20a2 2 0 0 0 1-3.732Z",
    clipRule: "evenodd"
  })), _path3$1 || (_path3$1 = /*#__PURE__*/React.createElement("path", {
    d: "M19 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
  })));
};

var _path$17;
function _extends$17() { _extends$17 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$17.apply(this, arguments); }
var SvgThumbsDown = function SvgThumbsDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$17({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$17 || (_path$17 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6.5 3a1 1 0 0 0-.96.72l-2.33 8A1.002 1.002 0 0 0 4.17 13H10a1 1 0 0 1 .972 1.236l-1 4.12-.001.004a2.13 2.13 0 0 0 1.456 2.55l3.127-6.245.001-.002A3 3 0 0 1 16 13.27V3H6.5Zm0-2a3 3 0 0 0-2.88 2.16l-2.33 8A3.002 3.002 0 0 0 4.17 15h4.558l-.699 2.88v.002A4.13 4.13 0 0 0 11.988 23a1 1 0 0 0 .906-.552l3.45-6.89.001-.003A1 1 0 0 1 17.24 15H20a3 3 0 0 0 3-3V4a3 3 0 0 0-3-3H6.5ZM18 3v10h2a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1h-2Z",
    clipRule: "evenodd"
  })));
};

var _path$16;
function _extends$16() { _extends$16 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$16.apply(this, arguments); }
var SvgThumbsUp = function SvgThumbsUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$16({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$16 || (_path$16 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.106 1.552A1 1 0 0 1 12.012 1a4.13 4.13 0 0 1 3.959 5.12L15.27 9h4.559a3 3 0 0 1 2.88 3.84l-2.33 8A3 3 0 0 1 17.5 23H4a3 3 0 0 1-3-3v-8a3 3 0 0 1 3-3h2.76a1 1 0 0 0 .894-.555l.002-.003 3.45-6.89ZM6 11H4a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h2V11Zm2 10h9.5a1 1 0 0 0 .96-.72l2.33-8a1.002 1.002 0 0 0-.513-1.174.999.999 0 0 0-.447-.106H14a1 1 0 0 1-.972-1.236l1-4.12.001-.004a2.13 2.13 0 0 0-1.456-2.55L9.446 9.334l-.001.001A3 3 0 0 1 8 10.731V21Z",
    clipRule: "evenodd"
  })));
};

var _path$15;
function _extends$15() { _extends$15 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$15.apply(this, arguments); }
var SvgTimerFilled = function SvgTimerFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$15({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$15 || (_path$15 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9 2a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1ZM3 14a9 9 0 1 1 18 0 9 9 0 0 1-18 0Zm12.707-2.293a1 1 0 0 0-1.414-1.414l-3 3a1 1 0 0 0 1.414 1.414l3-3Z",
    clipRule: "evenodd"
  })));
};

var _path$14;
function _extends$14() { _extends$14 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$14.apply(this, arguments); }
var SvgTimerOff = function SvgTimerOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$14({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$14 || (_path$14 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 1.414 0L7.545 6.13a9 9 0 0 1 10.868 1.568 9 9 0 0 1 1.456 10.756l2.838 2.838a1 1 0 0 1-1.414 1.414l-3.385-3.385-.032-.031L6.71 8.123l-.03-.03-5.386-5.386a1 1 0 0 1 0-1.414ZM9.03 7.616l2.033 2.033A1 1 0 0 1 13 10v1.586l5.384 5.384a7 7 0 0 0-1.393-7.865l-.009-.008A7 7 0 0 0 9.03 7.616ZM9 2a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1Zm-4.016 8.077a1 1 0 0 1 .54 1.307 7 7 0 0 0 1.483 7.609 7 7 0 0 0 7.609 1.484 1 1 0 0 1 .768 1.846 9 9 0 0 1-9.787-1.912l-.008-.008a9 9 0 0 1-1.912-9.787 1 1 0 0 1 1.307-.54Z",
    clipRule: "evenodd"
  })));
};

var _path$13;
function _extends$13() { _extends$13 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$13.apply(this, arguments); }
var SvgTimerOutline = function SvgTimerOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$13({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$13 || (_path$13 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9 2a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1Zm3 5a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm-9 7a9 9 0 1 1 18 0 9 9 0 0 1-18 0Zm12.707-3.707a1 1 0 0 1 0 1.414l-3 3a1 1 0 0 1-1.414-1.414l3-3a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$12;
function _extends$12() { _extends$12 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$12.apply(this, arguments); }
var SvgTimerReset = function SvgTimerReset(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$12({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$12 || (_path$12 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9 2a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1Zm2.992 5a7 7 0 0 0-7 6.125 1 1 0 0 1-1.984-.25A9 9 0 0 1 12.004 5 9 9 0 1 1 6.04 20.75l-.003-.003L5 19.827V22a1 1 0 1 1-2 0v-5a1 1 0 0 1 1-1h5a1 1 0 1 1 0 2H5.955l1.406 1.25.002.001A7 7 0 1 0 11.999 7h-.007ZM12 9a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0v-4a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$11;
function _extends$11() { _extends$11 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$11.apply(this, arguments); }
var SvgTimesDefault = function SvgTimesDefault(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$11({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$11 || (_path$11 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "m8.07 6.657 4.244 4.242 4.242-4.242c.464-.48.935-.48 1.415 0s.48.95 0 1.414l-4.243 4.243 4.243 4.242c.47.472.47.943 0 1.415-.472.471-.943.471-1.415 0l-4.242-4.243L8.07 17.97c-.46.482-.932.482-1.414 0-.482-.483-.482-.954 0-1.415l4.242-4.242L6.657 8.07c-.472-.47-.472-.942 0-1.414s.943-.472 1.414 0v.001Z",
    clipRule: "evenodd"
  })));
};

var _mask, _path$10, _path2$4;
function _extends$10() { _extends$10 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$10.apply(this, arguments); }
var SvgTimesFilled = function SvgTimesFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$10({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _mask || (_mask = /*#__PURE__*/React.createElement("mask", {
    id: "timesFilled_svg__a"
  }, /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10ZM9.707 8.293a1 1 0 0 0-1.414 1.414L10.586 12l-2.293 2.293a1 1 0 1 0 1.414 1.414L12 13.414l2.293 2.293a1 1 0 0 0 1.414-1.414L13.414 12l2.293-2.293a1 1 0 0 0-1.414-1.414L12 10.586 9.707 8.293Z",
    clipRule: "evenodd"
  }))), _path$10 || (_path$10 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10ZM9.707 8.293a1 1 0 0 0-1.414 1.414L10.586 12l-2.293 2.293a1 1 0 1 0 1.414 1.414L12 13.414l2.293 2.293a1 1 0 0 0 1.414-1.414L13.414 12l2.293-2.293a1 1 0 0 0-1.414-1.414L12 10.586 9.707 8.293Z",
    clipRule: "evenodd"
  })), _path2$4 || (_path2$4 = /*#__PURE__*/React.createElement("path", {
    d: "m8.293 8.293 1.414 1.414-1.414-1.414Zm1.414 0L8.293 9.707l1.414-1.414ZM8.293 9.707l1.414-1.414-1.414 1.414ZM10.586 12 12 13.414a2 2 0 0 0 0-2.828L10.586 12Zm-2.293 2.293 1.414 1.414-1.414-1.414Zm0 1.414 1.414-1.414-1.414 1.414Zm1.414 0-1.414-1.414 1.414 1.414ZM12 13.414 13.414 12a2 2 0 0 0-2.828 0L12 13.414Zm2.293 2.293 1.414-1.414-1.414 1.414Zm1.414 0-1.414-1.414 1.414 1.414Zm0-1.414-1.414 1.414 1.414-1.414ZM13.414 12 12 10.586a2 2 0 0 0 0 2.828L13.414 12Zm2.293-2.293-1.414-1.414 1.414 1.414Zm0-1.414-1.414 1.414 1.414-1.414Zm-1.414 0 1.414 1.414-1.414-1.414ZM12 10.586 10.586 12a2 2 0 0 0 2.828 0L12 10.586ZM12 24c6.627 0 12-5.373 12-12h-4a8 8 0 0 1-8 8v4ZM0 12c0 6.627 5.373 12 12 12v-4a8 8 0 0 1-8-8H0ZM12 0C5.373 0 0 5.373 0 12h4a8 8 0 0 1 8-8V0Zm12 12c0-6.627-5.373-12-12-12v4a8 8 0 0 1 8 8h4ZM9.707 9.707a1 1 0 0 1-1.414 0L11.12 6.88a3 3 0 0 0-4.242 0l2.828 2.828Zm0-1.414a1 1 0 0 1 0 1.414L6.88 6.88a3 3 0 0 0 0 4.242l2.828-2.828ZM12 10.586 9.707 8.293 6.88 11.12l2.293 2.293L12 10.586Zm-2.293 5.121L12 13.414l-2.828-2.828-2.293 2.293 2.828 2.828Zm0-1.414a1 1 0 0 1 0 1.414L6.88 12.88a3 3 0 0 0 0 4.242l2.828-2.828Zm-1.414 0a1 1 0 0 1 1.414 0L6.88 17.12a3 3 0 0 0 4.242 0l-2.828-2.828ZM10.586 12l-2.293 2.293 2.828 2.828 2.293-2.293L10.586 12Zm5.121 2.293L13.414 12l-2.828 2.828 2.293 2.293 2.828-2.828Zm-1.414 0a1 1 0 0 1 1.414 0L12.88 17.12a3 3 0 0 0 4.242 0l-2.828-2.828Zm0 1.414a1 1 0 0 1 0-1.414l2.828 2.828a3 3 0 0 0 0-4.242l-2.828 2.828ZM12 13.414l2.293 2.293 2.828-2.828-2.293-2.293L12 13.414Zm2.293-5.121L12 10.586l2.828 2.828 2.293-2.293-2.828-2.828Zm0 1.414a1 1 0 0 1 0-1.414l2.828 2.828a3 3 0 0 0 0-4.242l-2.828 2.828Zm1.414 0a1 1 0 0 1-1.414 0L17.12 6.88a3 3 0 0 0-4.242 0l2.828 2.828ZM13.414 12l2.293-2.293L12.88 6.88l-2.293 2.293L13.414 12ZM8.293 9.707 10.586 12l2.828-2.828-2.293-2.293-2.828 2.828Z",
    mask: "url(#timesFilled_svg__a)"
  })));
};

var _path$$, _path2$3;
function _extends$$() { _extends$$ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$$.apply(this, arguments); }
var SvgTimesOutline = function SvgTimesOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$$({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$$ || (_path$$ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm10-8c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    clipRule: "evenodd"
  })), _path2$3 || (_path2$3 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8.293 8.293a1 1 0 0 1 1.414 0L12 10.586l2.293-2.293a1 1 0 1 1 1.414 1.414L13.414 12l2.293 2.293a1 1 0 0 1-1.414 1.414L12 13.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L10.586 12 8.293 9.707a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$_;
function _extends$_() { _extends$_ = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$_.apply(this, arguments); }
var SvgTrash = function SvgTrash(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$_({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$_ || (_path$_ = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.332 3.332C9.097 3.567 9 3.825 9 4v1h6V4c0-.175-.098-.433-.332-.668C14.433 3.097 14.175 3 14 3h-4c-.175 0-.433.097-.668.332ZM17 5V4c0-.825-.402-1.567-.918-2.082C15.567 1.403 14.825 1 14 1h-4c-.825 0-1.567.403-2.082.918C7.403 2.433 7 3.175 7 4v1H3a1 1 0 0 0 0 2h1v13c0 .825.403 1.567.918 2.082C5.433 22.597 6.175 23 7 23h10c.825 0 1.567-.402 2.082-.918.515-.515.918-1.257.918-2.082V7h1a1 1 0 1 0 0-2h-4ZM6 7v13c0 .175.097.433.332.668.235.235.493.332.668.332h10c.175 0 .433-.098.668-.332.235-.235.332-.493.332-.668V7H6Z",
    clipRule: "evenodd"
  })));
};

var _path$Z, _path2$2;
function _extends$Z() { _extends$Z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$Z.apply(this, arguments); }
var SvgTrashFilled = function SvgTrashFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$Z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$Z || (_path$Z = /*#__PURE__*/React.createElement("path", {
    d: "M7.918 1.918C8.433 1.403 9.175 1 10 1h4c.825 0 1.567.403 2.082.918C16.597 2.433 17 3.175 17 4v1h4a1 1 0 1 1 0 2H3a1 1 0 0 1 0-2h4V4c0-.825.403-1.567.918-2.082Z"
  })), _path2$2 || (_path2$2 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4 9v11c0 .825.403 1.567.918 2.082C5.433 22.597 6.175 23 7 23h10c.825 0 1.567-.402 2.082-.918.515-.515.918-1.257.918-2.082V9H4Zm7 3.5a1 1 0 1 0-2 0V18a1 1 0 1 0 2 0v-5.5Zm4 0a1 1 0 1 0-2 0V18a1 1 0 1 0 2 0v-5.5Z",
    clipRule: "evenodd"
  })));
};

var _path$Y;
function _extends$Y() { _extends$Y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$Y.apply(this, arguments); }
var SvgTrashOutline = function SvgTrashOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$Y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$Y || (_path$Y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.332 3.332C9.097 3.567 9 3.825 9 4v1h6V4c0-.175-.098-.433-.332-.668C14.433 3.097 14.175 3 14 3h-4c-.175 0-.433.097-.668.332ZM17 5V4c0-.825-.402-1.567-.918-2.082C15.567 1.403 14.825 1 14 1h-4c-.825 0-1.567.403-2.082.918C7.403 2.433 7 3.175 7 4v1H3a1 1 0 0 0 0 2h1v13c0 .825.403 1.567.918 2.082C5.433 22.597 6.175 23 7 23h10c.825 0 1.567-.402 2.082-.918.515-.515.918-1.257.918-2.082V7h1a1 1 0 1 0 0-2h-4ZM6 7v13c0 .175.097.433.332.668.235.235.493.332.668.332h10c.175 0 .433-.098.668-.332.235-.235.332-.493.332-.668V7H6Zm4 3a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1Zm4 0a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$X;
function _extends$X() { _extends$X = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$X.apply(this, arguments); }
var SvgTreeDeciduous = function SvgTreeDeciduous(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$X({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$X || (_path$X = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.171 3.172a4 4 0 0 1 6.687 1.772c1.503.094 2.89 1.025 3.606 2.252.016.028.03.056.044.085.582 1.261.737 2.875.03 4.235a4.998 4.998 0 0 1 1.218 4.978l-.002.006a5 5 0 0 1-4.75 3.5H13v2a1 1 0 1 1-2 0v-2H7.996a5 5 0 0 1-4.75-3.5l-.002-.006a5 5 0 0 1 1.238-4.998c-.658-1.305-.696-2.908.037-4.27a.997.997 0 0 1 .017-.03c.742-1.272 2.14-2.089 3.595-2.213a4 4 0 0 1 1.04-1.811ZM15.998 18a3 3 0 0 0 2.847-2.097 3 3 0 0 0-1.201-3.372 1 1 0 0 1-.184-1.504c.639-.702.72-1.815.252-2.863-.525-.861-1.602-1.38-2.503-1.186A1 1 0 0 1 14 6a2 2 0 1 0-4 0 1 1 0 0 1-1.14.99c-1.009-.144-2.096.374-2.587 1.2-.49.925-.35 2.081.302 2.877a1 1 0 0 1-.218 1.464 3 3 0 0 0-1.201 3.372A3 3 0 0 0 8.002 18h7.996Z",
    clipRule: "evenodd"
  })));
};

var _path$W;
function _extends$W() { _extends$W = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$W.apply(this, arguments); }
var SvgTreePine = function SvgTreePine(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$W({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$W || (_path$W = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2a1 1 0 0 1 .732.319l3.991 4.29a2 2 0 0 1-.005 2.795l2.011 2.212a1.999 1.999 0 0 1-.014 2.784l2.014 2.216A2 2 0 0 1 19.314 20H13v2a1 1 0 1 1-2 0v-2H4.686a2 2 0 0 1-1.416-3.384L5.285 14.4a2 2 0 0 1-.015-2.784l2.011-2.212a2 2 0 0 1-.004-2.795l3.99-4.29A1 1 0 0 1 12 2ZM6.714 13H7a1 1 0 0 1 .74 1.673l-3 3.3-.026.027h14.572a.947.947 0 0 1-.026-.027l-3-3.3A1 1 0 0 1 17 13h.286a.947.947 0 0 1-.026-.027l-3-3.3A1 1 0 0 1 15 8h.2c.03 0 .06.001.09.004a1.09 1.09 0 0 1-.022-.023L12 4.468 8.732 7.981a.994.994 0 0 1-.022.023A.903.903 0 0 1 8.8 8H9a1 1 0 0 1 .74 1.673l-3 3.3-.026.027Z",
    clipRule: "evenodd"
  })));
};

var _path$V;
function _extends$V() { _extends$V = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$V.apply(this, arguments); }
var SvgTrees = function SvgTrees(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$V({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$V || (_path$V = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13 2a1 1 0 0 1 .732.319l3.991 4.29a2 2 0 0 1-.004 2.795l2.01 2.212a1.999 1.999 0 0 1-.014 2.784l2.015 2.216A1.999 1.999 0 0 1 20.314 20H14v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 0-2h8.286a.947.947 0 0 1-.026-.027l-3-3.3A1 1 0 0 1 18 13h.286a.947.947 0 0 1-.026-.027l-3-3.3A1 1 0 0 1 16 8h.2a1 1 0 0 1 .09.004 1.085 1.085 0 0 1-.022-.023l-3.27-3.514-.667.715a1 1 0 0 1-1.462-1.364l1.4-1.5A1 1 0 0 1 13.001 2ZM7 8a2 2 0 0 0-2 2v.2a1 1 0 0 1-.642.934A2 2 0 0 0 5.014 15h3.894a2 2 0 0 0 .733-3.867 1 1 0 0 1-.64-.933V10a2 2 0 0 0-2-2Zm-1 9v5a1 1 0 1 0 2 0v-5h.896a4 4 0 0 0 2.08-7.433 4 4 0 0 0-7.952 0A4 4 0 0 0 5 17h1Z",
    clipRule: "evenodd"
  })));
};

var _path$U;
function _extends$U() { _extends$U = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$U.apply(this, arguments); }
var SvgTrendingDown = function SvgTrendingDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$U({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$U || (_path$U = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 6.293a1 1 0 0 1 1.414 0L8.5 12.086l4.293-4.293a1 1 0 0 1 1.414 0L21 14.586V11a1 1 0 1 1 2 0v6a1 1 0 0 1-1 1h-6a1 1 0 1 1 0-2h3.586L13.5 9.914l-4.293 4.293a1 1 0 0 1-1.414 0l-6.5-6.5a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$T;
function _extends$T() { _extends$T = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$T.apply(this, arguments); }
var SvgTrendingUp = function SvgTrendingUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$T({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$T || (_path$T = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16 8a1 1 0 1 1 0-2h6a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0V9.414l-6.793 6.793a1 1 0 0 1-1.414 0L8.5 11.914l-5.793 5.793a1 1 0 0 1-1.414-1.414l6.5-6.5a1 1 0 0 1 1.414 0l4.293 4.293L19.586 8H16Z",
    clipRule: "evenodd"
  })));
};

var _path$S;
function _extends$S() { _extends$S = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$S.apply(this, arguments); }
var SvgTwitter = function SvgTwitter(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$S({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$S || (_path$S = /*#__PURE__*/React.createElement("path", {
    d: "M13.79 10.316 21.804 1h-1.9l-6.958 8.089L7.39 1H.979l8.404 12.231L.979 23h1.899l7.348-8.542L16.096 23h6.41L13.79 10.316Zm-2.601 3.023-.852-1.218L3.562 2.43h2.917l5.468 7.82.851 1.219 7.108 10.166h-2.917l-5.8-8.296Z"
  })));
};

var _path$R;
function _extends$R() { _extends$R = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$R.apply(this, arguments); }
var SvgUnderline = function SvgUnderline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$R({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$R || (_path$R = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.997 4c.666-.002 1 .33 1 .994v5.002c0 1.02 0 6.005-4.963 6.004C7.07 16 7 11.575 7 9.994v-5C6.998 4.331 7.33 4 7.997 4c.666 0 1 .331 1 .994v5.002c0 1.064.07 4.005 3.037 4.005C15 14.001 15 11.065 15 9.994V5c-.002-.665.33-.998.997-1ZM4 19c0-.667.333-1 1-1h14c.667 0 1 .333 1 1s-.333 1-1 1H5c-.667 0-1-.333-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$Q;
function _extends$Q() { _extends$Q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$Q.apply(this, arguments); }
var SvgUndo = function SvgUndo(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$Q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$Q || (_path$Q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M9.707 3.293a1 1 0 0 1 0 1.414L6.414 8H14.5a6.5 6.5 0 1 1 0 13H11a1 1 0 1 1 0-2h3.5a4.501 4.501 0 0 0 1.722-8.658A4.501 4.501 0 0 0 14.5 10H6.414l3.293 3.293a1 1 0 0 1-1.414 1.414l-5-5a1 1 0 0 1 0-1.414l5-5a1 1 0 0 1 1.414 0Z",
    clipRule: "evenodd"
  })));
};

var _path$P;
function _extends$P() { _extends$P = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$P.apply(this, arguments); }
var SvgUnfoldHorizontal = function SvgUnfoldHorizontal(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$P({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$P || (_path$P = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 1a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0V2a1 1 0 0 1 1-1Zm0 6a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0V8a1 1 0 0 1 1-1ZM5.707 8.293a1 1 0 0 1 0 1.414L4.414 11H8a1 1 0 1 1 0 2H4.414l1.293 1.293a1 1 0 1 1-1.414 1.414l-3-3a1 1 0 0 1 0-1.414l3-3a1 1 0 0 1 1.414 0Zm12.586 0a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1 0 1.414l-3 3a1 1 0 0 1-1.414-1.414L19.586 13H16a1 1 0 1 1 0-2h3.586l-1.293-1.293a1 1 0 0 1 0-1.414ZM12 13a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1Zm0 6a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0v-2a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$O;
function _extends$O() { _extends$O = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$O.apply(this, arguments); }
var SvgUnfoldVertical = function SvgUnfoldVertical(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$O({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$O || (_path$O = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 1.293a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1-1.414 1.414L13 4.414V8a1 1 0 1 1-2 0V4.414L9.707 5.707a1 1 0 0 1-1.414-1.414l3-3ZM1 12a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2H2a1 1 0 0 1-1-1Zm6 0a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Zm6 0a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2h-2a1 1 0 0 1-1-1Zm6 0a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2h-2a1 1 0 0 1-1-1Zm-7 3a1 1 0 0 1 1 1v3.586l1.293-1.293a1 1 0 0 1 1.414 1.414l-3 3a1 1 0 0 1-1.414 0l-3-3a1 1 0 1 1 1.414-1.414L11 19.586V16a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$N;
function _extends$N() { _extends$N = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$N.apply(this, arguments); }
var SvgUniversalAccess = function SvgUniversalAccess(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$N({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$N || (_path$N = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2Zm0 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16Zm4.955 5.421c.192.56.02.97-.519 1.23l-2.449.687v2.757c0 .288.247 1.547.74 3.778.101.573-.146.944-.74 1.113-.594.17-1.002-.091-1.224-.782l-.775-4.197-.78 4.197c-.16.635-.55.896-1.171.782-.62-.113-.885-.483-.792-1.11.496-2.225.744-3.486.744-3.78a53.09 53.09 0 0 0 0-2.725l-2.29-.612c-.65-.201-.886-.61-.709-1.225.177-.616.586-.852 1.225-.708l1.765.472a7.78 7.78 0 0 0 2.008.234 7.73 7.73 0 0 0 2-.26l1.815-.49c.575-.134.959.08 1.152.64ZM12 5a2 2 0 1 1 0 4 2 2 0 0 1 0-4Z",
    clipRule: "evenodd"
  })));
};

var _path$M;
function _extends$M() { _extends$M = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$M.apply(this, arguments); }
var SvgUnlink = function SvgUnlink(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$M({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$M || (_path$M = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7 8a4 4 0 0 0 0 8h2a1 1 0 1 1 0 2H7A6 6 0 1 1 7 6h2a1 1 0 0 1 0 2H7Zm7-1a1 1 0 0 1 1-1h2a6 6 0 1 1 0 12h-2a1 1 0 1 1 0-2h2a4 4 0 1 0 0-8h-2a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$L;
function _extends$L() { _extends$L = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$L.apply(this, arguments); }
var SvgUnlinkAlt = function SvgUnlinkAlt(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$L({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$L || (_path$L = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 1a1 1 0 0 1 1 1v3a1 1 0 0 1-2 0V2a1 1 0 0 1 1-1Zm4.776 1.75a6.006 6.006 0 0 1 8.338 0l.002.002a6.004 6.004 0 0 1 .281 8.335.998.998 0 0 1-.132.162l-1.72 1.71a1 1 0 1 1-1.41-1.418l1.591-1.582a1 1 0 0 1 .094-.113 4.004 4.004 0 0 0-.095-5.657 4.006 4.006 0 0 0-5.556-.004l-1.714 1.704a1 1 0 1 1-1.41-1.418l1.72-1.71.01-.01ZM1 8a1 1 0 0 1 1-1h3a1 1 0 0 1 0 2H2a1 1 0 0 1-1-1Zm4.877 3.043a1 1 0 0 1 0 1.414l-1.703 1.704a4.004 4.004 0 0 0 .1 5.65 4.006 4.006 0 0 0 5.556.005l1.703-1.703a1 1 0 0 1 1.414 1.414l-1.71 1.71-.013.013a6.006 6.006 0 0 1-8.338 0l-.002-.002a6.004 6.004 0 0 1-.144-8.482l.013-.013 1.71-1.71a1 1 0 0 1 1.414 0ZM18 16a1 1 0 0 1 1-1h3a1 1 0 1 1 0 2h-3a1 1 0 0 1-1-1Zm-2 2a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0v-3a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$K;
function _extends$K() { _extends$K = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$K.apply(this, arguments); }
var SvgUpload = function SvgUpload(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$K({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$K || (_path$K = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.293 2.293a1 1 0 0 1 1.414 0l5 5a1 1 0 0 1-1.414 1.414L13 5.414V15a1 1 0 1 1-2 0V5.414L7.707 8.707a1 1 0 0 1-1.414-1.414l5-5ZM3 14a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4a1 1 0 1 1 2 0v4a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3v-4a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$J;
function _extends$J() { _extends$J = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$J.apply(this, arguments); }
var SvgUploadCloud = function SvgUploadCloud(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$J({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$J || (_path$J = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.195 4.418a6 6 0 0 0-6.48 9.782 1 1 0 0 1-1.43 1.399A8 8 0 1 1 16.418 7H17.5a5.5 5.5 0 0 1 3.055 10.074 1 1 0 0 1-1.11-1.664A3.5 3.5 0 0 0 17.5 9h-1.79a1 1 0 0 1-.958-.714 5.999 5.999 0 0 0-3.557-3.868Zm.098 6.875a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L13 14.414V21a1 1 0 1 1-2 0v-6.586l-2.293 2.293a1 1 0 1 1-1.414-1.414l4-4Z",
    clipRule: "evenodd"
  })));
};

var _path$I;
function _extends$I() { _extends$I = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$I.apply(this, arguments); }
var SvgUser = function SvgUser(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$I({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$I || (_path$I = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm3.641 8.77a6 6 0 1 0-7.282 0A9 9 0 0 0 3 21a1 1 0 1 0 2 0 7 7 0 1 1 14 0 1 1 0 1 0 2 0 9 9 0 0 0-5.359-8.23Z",
    clipRule: "evenodd"
  })));
};

var _path$H, _path2$1;
function _extends$H() { _extends$H = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$H.apply(this, arguments); }
var SvgUserBan = function SvgUserBan(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$H({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$H || (_path$H = /*#__PURE__*/React.createElement("path", {
    d: "M20.293 2.707a1 1 0 1 1 1.414 1.414L4.03 21.8a1 1 0 0 1-1.414-1.414L20.293 2.707Z"
  })), _path2$1 || (_path2$1 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.328 3.844a6 6 0 1 0-8.484 8.484l1.415-1.415a4 4 0 1 1 5.654-5.654l1.415-1.415ZM11.1 11.899a4 4 0 0 0 4.797-4.797L17.47 5.53c.319.705.505 1.482.528 2.3l-6.172 6.172a7 7 0 0 0-6.824 6.824l-.973.973a1 1 0 0 1-.241.178A1 1 0 0 1 3 21c0-.357.021-.712.063-1.063l8.038-8.038Zm3.175 2.482A6.997 6.997 0 0 1 19 21a1 1 0 1 0 2 0 9 9 0 0 0-5.19-8.154l-1.534 1.534Z",
    clipRule: "evenodd"
  })));
};

var _path$G;
function _extends$G() { _extends$G = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$G.apply(this, arguments); }
var SvgUserCard = function SvgUserCard(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$G({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$G || (_path$G = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.028 2c-.68-.003-1.188.164-1.528.5-.34.337-.506.84-.5 1.51V20c0 .668.166 1.168.5 1.5.334.333.838.5 1.51.5H20c.667 0 1.167-.166 1.5-.5.334-.333.5-.833.5-1.5V4.023c.002-.676-.165-1.183-.5-1.523-.335-.339-.835-.505-1.5-.5H4.028ZM15.57 9.75a3.82 3.82 0 0 1-1.25 2.85c.444.244.854.559 1.215.938A5.386 5.386 0 0 1 17 17.25c0 .414-.32.75-.714.75H7.714C7.32 18 7 17.664 7 17.25c0-1.392.527-2.728 1.464-3.712A5.02 5.02 0 0 1 9.68 12.6a3.82 3.82 0 0 1-1.25-2.85C8.429 7.679 10.028 6 12 6c1.972 0 3.571 1.679 3.571 3.75Z",
    clipRule: "evenodd"
  })));
};

var _path$F;
function _extends$F() { _extends$F = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$F.apply(this, arguments); }
var SvgUserCheck = function SvgUserCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$F({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$F || (_path$F = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 6a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm3.25 6.8a5 5 0 1 0-6.5 0A6.997 6.997 0 0 0 1 19a1 1 0 1 0 2 0 5 5 0 1 1 10 0 1 1 0 1 0 2 0 7 7 0 0 0-3.75-6.2Zm10.043-4.507a1 1 0 1 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L18 11.586l3.293-3.293Z",
    clipRule: "evenodd"
  })));
};

var _path$E;
function _extends$E() { _extends$E = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$E.apply(this, arguments); }
var SvgUserCircle = function SvgUserCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$E({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$E || (_path$E = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 3a9 9 0 0 0-6.708 15 6.999 6.999 0 0 1 3.458-4.2 5 5 0 1 1 6.5 0 6.996 6.996 0 0 1 3.458 4.2A9 9 0 0 0 12 3Zm4.975 16.501a5 5 0 0 0-9.95 0A8.958 8.958 0 0 0 12 21c1.84 0 3.55-.552 4.975-1.499ZM12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Z",
    clipRule: "evenodd"
  })));
};

var _path$D;
function _extends$D() { _extends$D = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$D.apply(this, arguments); }
var SvgUserCog = function SvgUserCog(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$D({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$D || (_path$D = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 6a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm3.25 6.8a5 5 0 1 0-6.5 0A6.997 6.997 0 0 0 1 19a1 1 0 1 0 2 0 5 5 0 1 1 10 0 1 1 0 1 0 2 0 7 7 0 0 0-3.75-6.2ZM19 7a1 1 0 0 1 1 1v.17c.351.125.673.313.95.55l.152-.087a1 1 0 0 1 .996 1.734l-.148.085a3.014 3.014 0 0 1 0 1.096l.148.085a1 1 0 0 1-.996 1.734l-.152-.087a2.996 2.996 0 0 1-.95.55V14a1 1 0 1 1-2 0v-.17a3 3 0 0 1-.95-.55l-.152.087a1 1 0 0 1-.996-1.734l.148-.085a3.014 3.014 0 0 1 0-1.096l-.148-.085a1 1 0 0 1 .996-1.734l.152.087c.277-.237.599-.425.95-.55V8a1 1 0 0 1 1-1Zm-.887 3.538a1.035 1.035 0 0 0 .043-.075 1 1 0 1 1-.043.075Z",
    clipRule: "evenodd"
  })));
};

var _path$C;
function _extends$C() { _extends$C = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$C.apply(this, arguments); }
var SvgUserFilled = function SvgUserFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$C({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$C || (_path$C = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.641 12.77a6 6 0 1 0-7.282 0A9 9 0 0 0 3 21a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1 9 9 0 0 0-5.359-8.23Z",
    clipRule: "evenodd"
  })));
};

var _path$B;
function _extends$B() { _extends$B = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$B.apply(this, arguments); }
var SvgUserMinus = function SvgUserMinus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$B({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$B || (_path$B = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 6a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm3.25 6.8a5 5 0 1 0-6.5 0A6.997 6.997 0 0 0 1 19a1 1 0 1 0 2 0 5 5 0 1 1 10 0 1 1 0 1 0 2 0 7 7 0 0 0-3.75-6.2ZM15 11a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2h-6a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$A;
function _extends$A() { _extends$A = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$A.apply(this, arguments); }
var SvgUserPlus = function SvgUserPlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$A({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$A || (_path$A = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 6a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm3.25 6.8a5 5 0 1 0-6.5 0A6.997 6.997 0 0 0 1 19a1 1 0 1 0 2 0 5 5 0 1 1 10 0 1 1 0 1 0 2 0 7 7 0 0 0-3.75-6.2ZM19 7a1 1 0 0 1 1 1v2h2a1 1 0 1 1 0 2h-2v2a1 1 0 1 1-2 0v-2h-2a1 1 0 1 1 0-2h2V8a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$z;
function _extends$z() { _extends$z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$z.apply(this, arguments); }
var SvgUserReturn = function SvgUserReturn(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$z({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$z || (_path$z = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 9a3 3 0 1 1 6 0 3 3 0 0 1-6 0Zm8 0a4.99 4.99 0 0 1-1.75 3.8A6.996 6.996 0 0 1 15 19a1 1 0 1 1-2 0 5 5 0 1 0-10 0 1 1 0 1 1-2 0 7 7 0 0 1 3.75-6.2A5 5 0 1 1 13 9Zm4.586 3 1.121 1.121a1 1 0 0 1-1.414 1.415l-2.829-2.829a1 1 0 0 1 0-1.414l2.829-2.829a1 1 0 1 1 1.414 1.415L17.586 10H22a1 1 0 1 1 0 2h-4.414Z",
    clipRule: "evenodd"
  })));
};

var _path$y;
function _extends$y() { _extends$y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$y.apply(this, arguments); }
var SvgUserSquare = function SvgUserSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$y || (_path$y = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h.072a7 7 0 0 1 3.678-5.2 5 5 0 1 1 6.5 0 6.996 6.996 0 0 1 3.678 5.2H19a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5Zm14 18a3 3 0 0 0 3-3V5a3 3 0 0 0-3-3H5a3 3 0 0 0-3 3v14a3 3 0 0 0 3 3h14Zm-2.101-2a5 5 0 0 0-9.798 0h9.798ZM12 14a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z",
    clipRule: "evenodd"
  })));
};

var _path$x;
function _extends$x() { _extends$x = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$x.apply(this, arguments); }
var SvgUserTimes = function SvgUserTimes(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$x({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$x || (_path$x = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M8 6a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm3.25 6.8a5 5 0 1 0-6.5 0A6.997 6.997 0 0 0 1 19a1 1 0 1 0 2 0 5 5 0 1 1 10 0 1 1 0 1 0 2 0 7 7 0 0 0-3.75-6.2Zm5.043-5.507a1 1 0 0 1 1.414 0L19.5 9.086l1.793-1.793a1 1 0 1 1 1.414 1.414L20.914 10.5l1.793 1.793a1 1 0 0 1-1.414 1.414L19.5 11.914l-1.793 1.793a1 1 0 0 1-1.414-1.414l1.793-1.793-1.793-1.793a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$w;
function _extends$w() { _extends$w = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$w.apply(this, arguments); }
var SvgVideoFilled = function SvgVideoFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$w({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$w || (_path$w = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.015 4.005c2.015-.007 8.977-.007 10.981 0 1.336.005 2.004.671 2.004 2v3.008l3.403-1.203c.506-.176.898-.176 1.175 0 .278.176.419.476.422.901v6.676c.017.354-.124.62-.422.797-.298.177-.645.207-1.04.09L17 15.004v3.001c0 1.333-.667 2-2 2H4.014c-1.343 0-2.014-.666-2.014-2V6.018c0-1.337.672-2.008 2.015-2.013Zm1.985 11c-.667-.007-1 .325-1 .995 0 .67.333 1.005 1 1.005h4c.667 0 1-.335 1-1.005 0-.67-.333-1.002-1-.995H6Z",
    clipRule: "evenodd"
  })));
};

var _path$v;
function _extends$v() { _extends$v = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$v.apply(this, arguments); }
var SvgVideoOutline = function SvgVideoOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$v({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$v || (_path$v = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.015 4.005c2.015-.007 8.977-.007 10.981 0 1.336.005 2.004.671 2.004 2v3.008l3.403-1.203c.506-.176.898-.176 1.175 0 .278.176.419.476.422.901v6.676c.017.354-.124.62-.422.797-.298.177-.645.207-1.04.09L17 15.004v3.001c0 1.333-.667 2-2 2H4.014c-1.343 0-2.014-.666-2.014-2V6.018c0-1.337.672-2.008 2.015-2.013Zm-.015 2v12h11v-12H4ZM20 10l-3.002 1.005v2.003L20 14v-4ZM6 15.01h4c.667-.007 1 .325 1 .995 0 .67-.333 1.005-1 1.005H6c-.667 0-1-.335-1-1.005 0-.67.333-1.002 1-.995Z",
    clipRule: "evenodd"
  })));
};

var _path$u;
function _extends$u() { _extends$u = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$u.apply(this, arguments); }
var SvgVoicemail = function SvgVoicemail(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$u({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$u || (_path$u = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M6 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm4 6a5 5 0 1 0-4 2h12a5 5 0 1 0-4-2h-4Zm8 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z",
    clipRule: "evenodd"
  })));
};

var _path$t;
function _extends$t() { _extends$t = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$t.apply(this, arguments); }
var SvgVolumeMax = function SvgVolumeMax(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$t({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$t || (_path$t = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18.363 4.363a1 1 0 0 1 1.414 0 11 11 0 0 1 0 15.554 1 1 0 1 1-1.414-1.414 9 9 0 0 0 0-12.726 1 1 0 0 1 0-1.414Zm-6.93-.124a1 1 0 0 1 .567.9v14a1 1 0 0 1-1.625.782L5.65 16.14H2a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1h3.65l4.725-3.781a1 1 0 0 1 1.058-.12ZM10 7.22l-3.375 2.7a1 1 0 0 1-.625.22H3v4h3a1 1 0 0 1 .625.219l3.375 2.7V7.22Zm4.833.673a1 1 0 0 1 1.414 0 6 6 0 0 1 0 8.484 1 1 0 0 1-1.414-1.414 4 4 0 0 0 0-5.656 1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$s;
function _extends$s() { _extends$s = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$s.apply(this, arguments); }
var SvgVolumeMaxFilled = function SvgVolumeMaxFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$s({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$s || (_path$s = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M18.363 4.293a1 1 0 0 1 1.414 0 11 11 0 0 1 0 15.554 1 1 0 1 1-1.414-1.414 9 9 0 0 0 0-12.726 1 1 0 0 1 0-1.414Zm-3.53 3.53a1 1 0 0 1 1.414 0 6 6 0 0 1 0 8.484 1 1 0 0 1-1.414-1.414 4 4 0 0 0 0-5.656 1 1 0 0 1 0-1.414ZM12 5.07a1 1 0 0 0-1.625-.781L5.65 8.069H2a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h3.65l4.725 3.782A1 1 0 0 0 12 19.07v-14Z",
    clipRule: "evenodd"
  })));
};

var _path$r;
function _extends$r() { _extends$r = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$r.apply(this, arguments); }
var SvgVolumeMute = function SvgVolumeMute(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$r({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$r || (_path$r = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.433 4.238A1 1 0 0 1 12 5.14v14a1 1 0 0 1-1.625.78L5.65 16.14H2a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1h3.65l4.725-3.781a1 1 0 0 1 1.058-.12ZM10 7.22l-3.375 2.7a1 1 0 0 1-.625.22H3v4h3a1 1 0 0 1 .625.219l3.375 2.7V7.22Zm5.293 1.213a1 1 0 0 1 1.414 0L19 10.725l2.293-2.292a1 1 0 1 1 1.414 1.414l-2.293 2.293 2.293 2.293a1 1 0 0 1-1.414 1.414L19 13.554l-2.293 2.293a1 1 0 0 1-1.414-1.414l2.293-2.293-2.293-2.293a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$q;
function _extends$q() { _extends$q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$q.apply(this, arguments); }
var SvgVolumeMuteFilled = function SvgVolumeMuteFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$q({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$q || (_path$q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 5.07a1 1 0 0 0-1.625-.781L5.65 8.069H2a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h3.65l4.725 3.782A1 1 0 0 0 12 19.07v-14Zm4.707 3.293a1 1 0 1 0-1.414 1.414l2.293 2.293-2.293 2.293a1 1 0 0 0 1.414 1.414L19 13.484l2.293 2.293a1 1 0 1 0 1.414-1.414l-2.293-2.293 2.293-2.293a1 1 0 0 0-1.414-1.414L19 10.656l-2.293-2.293Z",
    clipRule: "evenodd"
  })));
};

var _path$p;
function _extends$p() { _extends$p = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$p.apply(this, arguments); }
var SvgVolumeNone = function SvgVolumeNone(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$p({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$p || (_path$p = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.433 4.099A1 1 0 0 1 12 5v14a1 1 0 0 1-1.625.78L5.65 16H2a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h3.65l4.725-3.78a1 1 0 0 1 1.058-.121ZM10 7.08l-3.375 2.7A1 1 0 0 1 6 10H3v4h3a1 1 0 0 1 .625.22L10 16.92V7.08Z",
    clipRule: "evenodd"
  })));
};

var _path$o;
function _extends$o() { _extends$o = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$o.apply(this, arguments); }
var SvgVolumeOn = function SvgVolumeOn(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$o({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$o || (_path$o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11.433 4.099A1 1 0 0 1 12 5v14a1 1 0 0 1-1.625.78L5.65 16H2a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h3.65l4.725-3.78a1 1 0 0 1 1.058-.121ZM10 7.08l-3.375 2.7A1 1 0 0 1 6 10H3v4h3a1 1 0 0 1 .625.22L10 16.92V7.08Zm4.833.672a1 1 0 0 1 1.414 0 6 6 0 0 1 0 8.484 1 1 0 0 1-1.414-1.414 4 4 0 0 0 0-5.656 1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$n;
function _extends$n() { _extends$n = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$n.apply(this, arguments); }
var SvgWalker = function SvgWalker(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$n({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$n || (_path$n = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M15.732 2C18.378 2 19 4 19 4.998v11.15c1.34.644 2.006 1.595 2 2.852-.01 1.886-1.587 3.012-2.993 3.012S15 20.9 15 19c0-1.266.666-2.217 2-2.852V11H7.154l-2.118 9.999c-.024.675-.366 1.013-1.027 1.013-.66 0-.996-.338-1.009-1.013L6.537 4.2C7.035 2.733 8.09 2 9.701 2h6.031ZM18 18a1 1 0 1 0 0 2 1 1 0 0 0 0-2ZM17 8H7.77l-.203.998H17V8Zm-.998-4H9.83c-.923 0-1.258.2-1.409 1l-.225 1H17V5c0-.591-.333-.925-.998-1Z",
    clipRule: "evenodd"
  })));
};

var _path$m;
function _extends$m() { _extends$m = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$m.apply(this, arguments); }
var SvgWallet = function SvgWallet(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$m({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$m || (_path$m = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0 0 2h13V4H5Zm15 2V3a1 1 0 0 0-1-1H5a3 3 0 0 0-3 3v14a3 3 0 0 0 3 3h16a1 1 0 0 0 1-1v-4a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1V7a1 1 0 0 0-1-1h-1ZM5 8a3 3 0 0 1-1-.172V19a1 1 0 0 0 1 1h15v-3h-2a3 3 0 0 1 0-6h2V8H5Zm16 5h-3a1 1 0 0 0 0 2h3v-2Z",
    clipRule: "evenodd"
  })));
};

var _path$l;
function _extends$l() { _extends$l = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$l.apply(this, arguments); }
var SvgWalletCards = function SvgWalletCards(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$l({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$l || (_path$l = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v1.172A3 3 0 0 1 5 6h14a3 3 0 0 1 1 .172V5a1 1 0 0 0-1-1H5Zm17 1a3 3 0 0 0-3-3H5a3 3 0 0 0-3 3v14a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3V5Zm-2 4a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v1h2c.995 0 2.07.36 2.8 1.18l1.033.846c.026.021.05.043.074.067 1.21 1.21 3.076 1.21 4.286 0a.997.997 0 0 1 .074-.067l1.064-.87c.66-.64 1.685-1.156 2.769-1.156H20V9Zm0 3h-1.9c-.495 0-1.05.264-1.393.607a.997.997 0 0 1-.074.067l-1.063.87a4.983 4.983 0 0 1-7.04 0l-1.063-.87a.998.998 0 0 1-.135-.134C7.062 12.217 6.574 12 6 12H4v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7Z",
    clipRule: "evenodd"
  })));
};

var _path$k;
function _extends$k() { _extends$k = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$k.apply(this, arguments); }
var SvgWheelchair = function SvgWheelchair(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$k({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$k || (_path$k = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M14.979 4a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm-6.708.293a1 1 0 0 1 1.186-.17l5.5 3a1 1 0 0 1 .35 1.436l-1.787 2.652a5.996 5.996 0 0 1 .808.717l5.486-.914a1 1 0 0 1 1.154 1.127l-1 7a1 1 0 1 1-1.98-.282l.807-5.648-3.288.548a5.994 5.994 0 0 1 .188 4.033 1 1 0 1 1-1.913-.584 4 4 0 0 0-5.504-4.8 1 1 0 1 1-.84-1.816 6 6 0 0 1 4.282-.289l1.315-1.951-3.878-2.116-2.471 2.471A1 1 0 0 1 5.27 7.293l3-3Zm-2.76 9.25a1 1 0 0 1 .664 1.25 4 4 0 0 0 5.504 4.8 1 1 0 0 1 .84 1.815 6 6 0 0 1-8.257-7.2 1 1 0 0 1 1.249-.664Z",
    clipRule: "evenodd"
  })));
};

var _path$j;
function _extends$j() { _extends$j = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$j.apply(this, arguments); }
var SvgWifi = function SvgWifi(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$j({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$j || (_path$j = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12 6a14 14 0 0 0-9.333 3.565 1 1 0 0 1-1.334-1.49 16 16 0 0 1 21.334 0 1 1 0 0 1-1.334 1.49A14 14 0 0 0 12 6Zm0 5.141a9 9 0 0 0-6.3 2.573 1 1 0 1 1-1.4-1.428 11 11 0 0 1 15.4 0 1 1 0 0 1-1.4 1.428A9 9 0 0 0 12 11.14Zm0 4.93a4 4 0 0 0-2.8 1.143 1 1 0 1 1-1.4-1.428 6 6 0 0 1 8.4 0 1 1 0 0 1-1.4 1.428A4 4 0 0 0 12 16.07ZM11 20a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$i;
function _extends$i() { _extends$i = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$i.apply(this, arguments); }
var SvgWifiOff = function SvgWifiOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$i({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$i || (_path$i = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M1.293 1.293a1 1 0 0 1 1.414 0l4.154 4.154.033.032 4.044 4.045a.914.914 0 0 1 .02.019l11.75 11.75a1 1 0 0 1-1.415 1.414l-5.324-5.324a1 1 0 0 1-1.169-.169 4 4 0 0 0-5.6 0 1 1 0 0 1-1.4-1.428 6 6 0 0 1 4.897-1.675l-2.779-2.779a9 9 0 0 0-4.21 2.375 1 1 0 0 1-1.415-1.414A11 11 0 0 1 8.317 9.73L5.952 7.366a14 14 0 0 0-3.284 2.198 1 1 0 1 1-1.336-1.488 16 16 0 0 1 3.132-2.197L1.293 2.707a1 1 0 0 1 0-1.414Zm20.04 8.213a13.986 13.986 0 0 0-10.583-3.51 1 1 0 0 1-.18-1.992 15.99 15.99 0 0 1 12.097 4.01 1 1 0 0 1-1.333 1.492Zm-5.356 1.257a1 1 0 0 1 1.36-.387 11.06 11.06 0 0 1 2.442 1.848 1 1 0 1 1-1.418 1.412 8.998 8.998 0 0 0-1.998-1.512 1 1 0 0 1-.386-1.36ZM11 20a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H12a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$h;
function _extends$h() { _extends$h = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$h.apply(this, arguments); }
var SvgWrapText = function SvgWrapText(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$h({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$h || (_path$h = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M2 6c0-.576.448-1.043 1-1.043h18c.552 0 1 .467 1 1.043 0 .576-.448 1.043-1 1.043H3c-.552 0-1-.467-1-1.043Zm0 6c0-.576.448-1.043 1-1.043h15c1.047 0 2.06.415 2.813 1.168A4.065 4.065 0 0 1 22 15c0 1.087-.432 2.12-1.187 2.875A3.978 3.978 0 0 1 18 19.044h-1.511l.203.203c.399.398.412 1.059.03 1.475a.97.97 0 0 1-1.414.031l-2-2A1.066 1.066 0 0 1 13 18c0-.284.111-.557.308-.753l2-2a.97.97 0 0 1 1.414.031 1.076 1.076 0 0 1-.03 1.475l-.203.203H18c.544 0 1.057-.216 1.43-.588.37-.37.57-.864.57-1.368 0-.504-.2-.997-.57-1.368a2.022 2.022 0 0 0-1.43-.588H3c-.552 0-1-.468-1-1.044Zm0 6c0-.576.448-1.044 1-1.044h7c.552 0 1 .468 1 1.044 0 .576-.448 1.044-1 1.044H3c-.552 0-1-.468-1-1.044Z",
    clipRule: "evenodd"
  })));
};

var _path$g;
function _extends$g() { _extends$g = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$g.apply(this, arguments); }
var SvgWrench = function SvgWrench(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$g({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$g || (_path$g = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.558 3.028a5 5 0 0 0-5.117 7.03 1 1 0 0 1-.204 1.12l-6.91 6.91a1.121 1.121 0 0 0 1.586 1.585l6.91-6.91a1 1 0 0 1 1.12-.204 5 5 0 0 0 7.029-5.117L18.4 10.014a2 2 0 0 1-2.8 0l-.007-.007L13.986 8.4a2 2 0 0 1 0-2.8l.007-.007 2.565-2.565ZM14.742 1.11a7 7 0 0 1 4.14.509 1 1 0 0 1 .295 1.618L15.414 7 17 8.586l3.763-3.763a1 1 0 0 1 1.618.295 7 7 0 0 1-8.606 9.521l-6.448 6.448a3.122 3.122 0 0 1-4.414-4.414l6.448-6.448a7 7 0 0 1 5.38-9.115Z",
    clipRule: "evenodd"
  })));
};

var _path$f;
function _extends$f() { _extends$f = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$f.apply(this, arguments); }
var SvgXOctagon = function SvgXOctagon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$f({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$f || (_path$f = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M7.153 1.293A1 1 0 0 1 7.86 1h8.28a1 1 0 0 1 .707.293l5.86 5.86A1 1 0 0 1 23 7.86v8.28a1 1 0 0 1-.293.707l-5.86 5.86a1 1 0 0 1-.707.293H7.86a1 1 0 0 1-.707-.293l-5.86-5.86A1 1 0 0 1 1 16.14V7.86a1 1 0 0 1 .293-.707l5.86-5.86ZM8.274 3 3 8.274v7.452L8.274 21h7.452L21 15.726V8.274L15.726 3H8.274Zm.019 5.293a1 1 0 0 1 1.414 0L12 10.586l2.293-2.293a1 1 0 1 1 1.414 1.414L13.414 12l2.293 2.293a1 1 0 0 1-1.414 1.414L12 13.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L10.586 12 8.293 9.707a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$e;
function _extends$e() { _extends$e = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$e.apply(this, arguments); }
var SvgXSquare = function SvgXSquare(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$e({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$e || (_path$e = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M5 4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5ZM2 5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5Zm6.293 3.293a1 1 0 0 1 1.414 0L12 10.586l2.293-2.293a1 1 0 1 1 1.414 1.414L13.414 12l2.293 2.293a1 1 0 0 1-1.414 1.414L12 13.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L10.586 12 8.293 9.707a1 1 0 0 1 0-1.414Z",
    clipRule: "evenodd"
  })));
};

var _path$d;
function _extends$d() { _extends$d = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$d.apply(this, arguments); }
var SvgXp = function SvgXp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$d({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$d || (_path$d = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M22 6v8h-4v4h-4V6h8ZM6 6v3h2V6h4v4h-2v4h2v4H8v-3H6v3H2v-4h2v-4H2V6h4Zm13 2h-2v4h2V8Z",
    clipRule: "evenodd"
  })));
};

var _path$c;
function _extends$c() { _extends$c = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$c.apply(this, arguments); }
var SvgYoutube = function SvgYoutube(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$c({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$c || (_path$c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M4.123 6.577a1 1 0 0 0-.652.664 23.12 23.12 0 0 0 0 9.518 1 1 0 0 0 .652.664 48.549 48.549 0 0 0 15.754 0 1.002 1.002 0 0 0 .652-.664c.66-3.138.66-6.38 0-9.518a1 1 0 0 0-.652-.664 48.56 48.56 0 0 0-15.754 0Zm-.386-1.964a50.56 50.56 0 0 1 16.526 0 .99.99 0 0 1 .101.023 3 3 0 0 1 2.114 2.157 25.121 25.121 0 0 1-.014 10.471 3 3 0 0 1-2.1 2.1.991.991 0 0 1-.1.023 50.548 50.548 0 0 1-16.527 0 .994.994 0 0 1-.101-.023 3 3 0 0 1-2.114-2.157 25.12 25.12 0 0 1 .014-10.471 3 3 0 0 1 2.1-2.1.997.997 0 0 1 .1-.023Zm5.77 3.517a1 1 0 0 1 1.008.013l5 3a1 1 0 0 1 0 1.714l-5 3A1 1 0 0 1 9 15V9a1 1 0 0 1 .507-.87ZM11 10.766v2.468L13.056 12 11 10.766Z",
    clipRule: "evenodd"
  })));
};

var _path$b;
function _extends$b() { _extends$b = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b.apply(this, arguments); }
var SvgZap = function SvgZap(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$b || (_path$b = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13.398 1.083a1 1 0 0 1 .594 1.041L13.132 9H21a1 1 0 0 1 .768 1.64l-10 12a1 1 0 0 1-1.76-.764l.86-6.876H3a1 1 0 0 1-.768-1.64l10-12a1 1 0 0 1 1.166-.277ZM5.135 13H12a1 1 0 0 1 .992 1.124l-.577 4.615L18.865 11H12a1 1 0 0 1-.992-1.124l.577-4.616L5.135 13Z",
    clipRule: "evenodd"
  })));
};

var _path$a;
function _extends$a() { _extends$a = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a.apply(this, arguments); }
var SvgZapOff = function SvgZapOff(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$a || (_path$a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M13.397 1.082a1 1 0 0 1 .595 1.041l-.59 4.75a1 1 0 0 1-1.985-.246l.17-1.366-.248.299A1 1 0 0 1 9.8 4.28l2.43-2.92a1 1 0 0 1 1.166-.278Zm-12.104.21a1 1 0 0 1 1.414 0L8.7 7.287l.016.016 7.984 7.984.016.016 5.991 5.99a1 1 0 0 1-1.414 1.415l-5.226-5.226-4.299 5.16a1 1 0 0 1-1.76-.765l.86-6.876H3a1 1 0 0 1-.768-1.64L6.647 8.06 1.293 2.707a1 1 0 0 1 0-1.414Zm6.774 8.19L5.135 13h6.45L8.068 9.481Zm4.894 4.893-.546 4.364 2.232-2.678-1.686-1.686ZM14.66 10a1 1 0 0 1 1-1H21a1 1 0 0 1 .767 1.641l-2.43 2.91a1 1 0 1 1-1.535-1.282L18.863 11H15.66a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$9;
function _extends$9() { _extends$9 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9.apply(this, arguments); }
var SvgZoomMinusFilled = function SvgZoomMinusFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$9 || (_path$9 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20 11a8.962 8.962 0 0 1-1.968 5.618l3.675 3.675a1 1 0 0 1-1.414 1.414l-3.675-3.675A9 9 0 1 1 20 11ZM7 11a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$8;
function _extends$8() { _extends$8 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8.apply(this, arguments); }
var SvgZoomMinusOutline = function SvgZoomMinusOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$8 || (_path$8 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11 4a7 7 0 1 0 4.856 12.042 1.006 1.006 0 0 1 .186-.186A7 7 0 0 0 11 4Zm7.032 12.618a9 9 0 1 0-1.414 1.414l3.675 3.675a1 1 0 0 0 1.414-1.414l-3.675-3.675ZM7 11a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H8a1 1 0 0 1-1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$7;
function _extends$7() { _extends$7 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7.apply(this, arguments); }
var SvgZoomOutline = function SvgZoomOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$7 || (_path$7 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm-9 7a9 9 0 1 1 16.032 5.618l3.675 3.675a1 1 0 0 1-1.414 1.414l-3.675-3.675A9 9 0 0 1 2 11Z",
    clipRule: "evenodd"
  })));
};

var _path$6;
function _extends$6() { _extends$6 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6.apply(this, arguments); }
var SvgZoomPlusFilled = function SvgZoomPlusFilled(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$6 || (_path$6 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M20 11a8.962 8.962 0 0 1-1.968 5.618l3.675 3.675a1 1 0 0 1-1.414 1.414l-3.675-3.675A9 9 0 1 1 20 11Zm-9-4a1 1 0 0 1 1 1v2h2a1 1 0 1 1 0 2h-2v2a1 1 0 1 1-2 0v-2H8a1 1 0 1 1 0-2h2V8a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$5;
function _extends$5() { _extends$5 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5.apply(this, arguments); }
var SvgZoomPlusOutline = function SvgZoomPlusOutline(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24"
  }, props), _path$5 || (_path$5 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M11 4a7 7 0 1 0 4.856 12.042 1.006 1.006 0 0 1 .186-.186A7 7 0 0 0 11 4Zm7.032 12.618a9 9 0 1 0-1.414 1.414l3.675 3.675a1 1 0 0 0 1.414-1.414l-3.675-3.675ZM11 7a1 1 0 0 1 1 1v2h2a1 1 0 1 1 0 2h-2v2a1 1 0 1 1-2 0v-2H8a1 1 0 1 1 0-2h2V8a1 1 0 0 1 1-1Z",
    clipRule: "evenodd"
  })));
};

var _path$4, _path2, _path3, _path4, _path5, _path6, _path7, _path8, _path9, _path10, _path11, _path12, _path13, _path14, _path15, _path16;
function _extends$4() { _extends$4 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4.apply(this, arguments); }
var SvgTrustpilot = function SvgTrustpilot(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4({
    width: "1em",
    height: "1em",
    viewBox: "0 0 162 116",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    className: "trustpilot_svg__Icon-module_iwIcon__MM5At"
  }, props), _path$4 || (_path$4 = /*#__PURE__*/React.createElement("path", {
    d: "M40.897 13.578H56.65v2.946h-6.194V33.09H47.05V16.524h-6.167v-2.946h.014Zm15.08 5.384h2.912v2.726h.055c.096-.386.274-.757.535-1.115s.577-.703.948-.992a5.06 5.06 0 0 1 1.236-.73c.454-.179.92-.275 1.388-.275.357 0 .617.014.755.027.137.014.275.042.426.056v3.001a9.873 9.873 0 0 0-.673-.096 5.7 5.7 0 0 0-.673-.041c-.522 0-1.017.11-1.484.316a3.417 3.417 0 0 0-1.208.923 4.688 4.688 0 0 0-.824 1.515c-.206.605-.303 1.294-.303 2.079v6.72h-3.104V18.961h.014Zm22.525 14.127h-3.05V31.12h-.054c-.385.716-.948 1.28-1.703 1.707-.756.427-1.525.648-2.308.648-1.854 0-3.2-.455-4.024-1.377-.824-.923-1.236-2.313-1.236-4.172v-8.964h3.104v8.66c0 1.24.234 2.121.714 2.63.467.51 1.14.772 1.992.772.659 0 1.195-.097 1.634-.303.44-.207.797-.468 1.058-.813.274-.33.467-.743.59-1.211.124-.468.179-.978.179-1.529v-8.193h3.104V33.09Zm5.288-4.53c.096.909.44 1.542 1.03 1.914.604.358 1.318.55 2.156.55a9.2 9.2 0 0 0 .989-.068 4.14 4.14 0 0 0 1.044-.262c.33-.124.59-.316.81-.564.206-.248.302-.565.289-.964-.014-.4-.165-.73-.44-.978-.275-.261-.618-.454-1.044-.62a10.59 10.59 0 0 0-1.456-.399c-.549-.11-1.098-.234-1.662-.358a15.73 15.73 0 0 1-1.675-.468 5.111 5.111 0 0 1-1.442-.743 3.267 3.267 0 0 1-1.017-1.185c-.26-.482-.384-1.074-.384-1.79 0-.77.192-1.404.563-1.927a4.25 4.25 0 0 1 1.415-1.253 6.545 6.545 0 0 1 1.909-.675 11.58 11.58 0 0 1 2.005-.193c.728 0 1.428.083 2.088.234a5.41 5.41 0 0 1 1.799.757c.535.345.975.8 1.332 1.35.357.55.577 1.225.673 2.01h-3.241c-.151-.743-.481-1.253-1.017-1.5-.535-.262-1.153-.386-1.84-.386-.22 0-.48.014-.783.055-.302.041-.577.11-.852.206-.26.097-.48.248-.672.441-.179.193-.275.44-.275.757 0 .386.137.689.398.923.261.234.605.427 1.03.592.426.151.907.29 1.456.4.55.11 1.113.233 1.69.357.563.124 1.112.29 1.661.469.55.178 1.03.426 1.456.743.426.317.77.702 1.03 1.17.261.469.399 1.06.399 1.75 0 .84-.193 1.541-.577 2.134a4.57 4.57 0 0 1-1.484 1.418 6.963 6.963 0 0 1-2.032.798c-.742.166-1.484.248-2.212.248-.892 0-1.716-.096-2.472-.303-.755-.206-1.415-.51-1.964-.908a4.503 4.503 0 0 1-1.305-1.529c-.316-.606-.48-1.335-.508-2.175h3.132v-.028Zm10.246-9.597h2.348V14.72h3.104v4.242h2.802v2.327h-2.802v7.545c0 .33.014.606.041.854.028.234.097.44.193.606a.88.88 0 0 0 .453.372c.206.082.467.124.824.124.22 0 .44 0 .659-.014.22-.014.44-.041.66-.096v2.41c-.344.04-.687.068-1.003.11-.33.04-.659.054-1.003.054-.824 0-1.483-.082-1.977-.234-.495-.151-.893-.385-1.168-.688-.288-.303-.467-.675-.577-1.13a8.519 8.519 0 0 1-.178-1.555v-8.33h-2.349v-2.355h-.027Zm10.452 0h2.939v1.913h.055c.439-.826 1.044-1.404 1.827-1.762a6 6 0 0 1 2.54-.537c1.113 0 2.074.193 2.898.592a5.675 5.675 0 0 1 2.061 1.611c.549.689.947 1.487 1.222 2.396.275.909.412 1.886.412 2.92 0 .95-.124 1.872-.371 2.753a7.383 7.383 0 0 1-1.112 2.369 5.474 5.474 0 0 1-1.896 1.638c-.769.413-1.662.62-2.705.62a7.62 7.62 0 0 1-1.36-.124 5.979 5.979 0 0 1-1.305-.4 4.722 4.722 0 0 1-1.154-.702 4.35 4.35 0 0 1-.892-.991h-.055v7.05h-3.104V18.962Zm10.85 7.077c0-.633-.082-1.253-.247-1.859a5.015 5.015 0 0 0-.742-1.597 3.792 3.792 0 0 0-1.222-1.115 3.423 3.423 0 0 0-1.69-.427c-1.304 0-2.293.454-2.953 1.363-.659.909-.988 2.12-.988 3.635 0 .716.082 1.377.26 1.983.179.606.426 1.13.783 1.57.344.44.756.785 1.236 1.032.481.262 1.044.386 1.676.386.714 0 1.305-.152 1.799-.44a3.8 3.8 0 0 0 1.209-1.13c.316-.468.549-.991.687-1.583a8.916 8.916 0 0 0 .192-1.818Zm5.48-12.461h3.104v2.946h-3.104v-2.946Zm0 5.384h3.104v14.127h-3.104V18.962Zm5.878-5.384h3.104v19.511h-3.104V13.578Zm12.623 19.897c-1.127 0-2.129-.193-3.008-.565a6.643 6.643 0 0 1-2.239-1.542 6.725 6.725 0 0 1-1.387-2.355c-.316-.908-.481-1.914-.481-3.001a9 9 0 0 1 .481-2.975 6.724 6.724 0 0 1 1.387-2.354c.604-.661 1.36-1.17 2.239-1.542.879-.372 1.881-.565 3.008-.565 1.126 0 2.128.193 3.007.565a6.64 6.64 0 0 1 2.239 1.542 6.724 6.724 0 0 1 1.387 2.354 9 9 0 0 1 .481 2.975c0 1.087-.165 2.093-.481 3.001a6.725 6.725 0 0 1-1.387 2.355c-.604.66-1.36 1.17-2.239 1.542-.879.372-1.881.565-3.007.565Zm0-2.465c.686 0 1.291-.152 1.799-.44a3.85 3.85 0 0 0 1.25-1.143c.329-.469.563-1.006.728-1.598a7.332 7.332 0 0 0 .233-1.817 7.39 7.39 0 0 0-.233-1.804 4.747 4.747 0 0 0-.728-1.598 3.798 3.798 0 0 0-1.25-1.129c-.508-.289-1.113-.44-1.799-.44-.687 0-1.292.151-1.8.44a3.96 3.96 0 0 0-1.25 1.13 4.99 4.99 0 0 0-.727 1.597 7.343 7.343 0 0 0-.234 1.804c0 .62.082 1.225.234 1.817.151.592.398 1.13.727 1.598.33.468.742.853 1.25 1.142.508.303 1.113.441 1.8.441Zm8.021-12.048h2.348V14.72h3.104v4.242h2.802v2.327h-2.802v7.545c0 .33.014.606.041.854.028.234.097.44.193.606a.88.88 0 0 0 .453.372c.206.082.467.124.824.124.22 0 .439 0 .659-.014.22-.014.44-.041.659-.096v2.41c-.343.04-.686.068-1.002.11-.33.04-.659.054-1.003.054-.824 0-1.483-.082-1.978-.234-.494-.151-.892-.385-1.167-.688-.288-.303-.467-.675-.577-1.13a8.46 8.46 0 0 1-.178-1.555v-8.33h-2.349v-2.355h-.027Z",
    fill: "#000"
  })), _path2 || (_path2 = /*#__PURE__*/React.createElement("path", {
    d: "M37.262 13.577h-14.23L18.639 0 14.23 13.577 0 13.563l11.523 8.4-4.408 13.563 11.523-8.386 11.51 8.386-4.396-13.564 11.51-8.385Z",
    fill: "#00B67A"
  })), _path3 || (_path3 = /*#__PURE__*/React.createElement("path", {
    d: "m26.738 25.032-.989-3.07-7.114 5.177 8.103-2.107Z",
    fill: "#005128"
  })), _path4 || (_path4 = /*#__PURE__*/React.createElement("path", {
    d: "M8.131 46.136H35.73V73.43H8.13V46.136Z",
    fill: "#00B67A"
  })), _path5 || (_path5 = /*#__PURE__*/React.createElement("path", {
    d: "m21.952 64.594 4.19-1.095 1.766 5.376-5.956-4.28Zm9.662-6.951h-7.41l-2.286-6.952-2.286 6.951h-7.41l5.99 4.315-2.285 6.952 5.99-4.28 3.706-2.672 5.99-4.315Z",
    fill: "#fff"
  })), _path6 || (_path6 = /*#__PURE__*/React.createElement("path", {
    d: "M37.96 46.136h27.6V73.43h-27.6V46.136Z",
    fill: "#00B67A"
  })), _path7 || (_path7 = /*#__PURE__*/React.createElement("path", {
    d: "m51.785 64.594 4.19-1.095 1.767 5.376-5.956-4.28Zm9.662-6.951h-7.41L51.75 50.69l-2.286 6.951h-7.41l5.99 4.315-2.285 6.952 5.99-4.28 3.706-2.672 5.99-4.315Z",
    fill: "#fff"
  })), _path8 || (_path8 = /*#__PURE__*/React.createElement("path", {
    d: "M67.78 46.136h27.6V73.43h-27.6V46.136Z",
    fill: "#00B67A"
  })), _path9 || (_path9 = /*#__PURE__*/React.createElement("path", {
    d: "m81.605 64.594 4.19-1.095 1.766 5.376-5.956-4.28Zm9.661-6.951h-7.41l-2.285-6.952-2.286 6.951h-7.41l5.99 4.315-2.285 6.952 5.99-4.28 3.706-2.672 5.99-4.315Z",
    fill: "#fff"
  })), _path10 || (_path10 = /*#__PURE__*/React.createElement("path", {
    d: "M97.613 46.136h27.599V73.43H97.613V46.136Z",
    fill: "#00B67A"
  })), _path11 || (_path11 = /*#__PURE__*/React.createElement("path", {
    d: "m111.424 64.594 4.19-1.095 1.766 5.376-5.956-4.28Zm9.661-6.951h-7.41l-2.286-6.952-2.285 6.951h-7.411l5.991 4.315-2.286 6.952 5.991-4.28 3.705-2.672 5.991-4.315Z",
    fill: "#fff"
  })), _path12 || (_path12 = /*#__PURE__*/React.createElement("path", {
    d: "M127.448 46.136h27.598V73.43h-27.598V46.136Z",
    fill: "#DCDCE4"
  })), _path13 || (_path13 = /*#__PURE__*/React.createElement("path", {
    d: "M127.448 46.136h14V73.43h-14V46.136Z",
    fill: "#00B67A"
  })), _path14 || (_path14 = /*#__PURE__*/React.createElement("path", {
    d: "m141.258 64.594 4.19-1.095 1.766 5.376-5.956-4.28Zm9.661-6.951h-7.41l-2.285-6.952-2.286 6.951h-7.41l5.99 4.315-2.285 6.952 5.991-4.28 3.705-2.672 5.99-4.315Z",
    fill: "#fff"
  })), _path15 || (_path15 = /*#__PURE__*/React.createElement("path", {
    d: "M15.226 90.451c.1-.226-.028-.466-.269-.466h-1.174c-.226 0-.396.127-.537.438l-2.518 5.517a1.4 1.4 0 0 0-.127.594v.82c0 .255.141.397.396.397h3.932v1.853c0 .255.142.396.396.396h1.061c.255 0 .396-.141.396-.396V97.75h.977c.254 0 .395-.142.395-.396v-.962c0-.269-.127-.396-.395-.396h-.977v-2.363c0-.269-.127-.396-.396-.396h-1.06c-.255 0-.397.127-.397.396v2.363h-2.277l2.574-5.546Zm4.069 8.573c0 .679.495 1.188 1.188 1.188s1.203-.495 1.203-1.188c0-.68-.524-1.174-1.203-1.174s-1.188.51-1.188 1.174Zm6.87.679 3.904-7.837c.085-.17.113-.34.113-.495v-.99c0-.255-.141-.396-.396-.396h-6.465c-.254 0-.396.127-.396.396v.962c0 .254.142.396.396.396h4.81l-4.003 7.808c-.142.283-.014.453.311.453h1.245a.51.51 0 0 0 .48-.297Zm9.961.297h1.26c.254 0 .395-.141.395-.396V96.42h1.146l2.306 3.324c.141.213.24.255.48.255h1.543c.297 0 .424-.24.255-.467l-2.377-3.367c1.344-.48 2.235-1.584 2.235-2.97 0-1.896-1.542-3.211-3.777-3.211h-3.466c-.254 0-.396.127-.396.396v9.223c0 .255.142.396.396.396Zm3.466-8.162c1.075 0 1.74.523 1.74 1.386 0 .948-.679 1.556-1.74 1.556h-1.81v-2.942h1.81Zm9.714 1.513v.255h-.042c-.439-.495-1.174-.82-1.91-.82-1.796 0-3.24 1.64-3.24 3.692 0 2.037 1.444 3.678 3.24 3.678.75 0 1.471-.312 1.91-.82h.042v.268c0 .255.142.396.41.396h1.104c.255 0 .41-.141.41-.396V93.35c0-.254-.155-.41-.41-.41h-1.103c-.255 0-.41.142-.41.41Zm0 1.91v2.42a2.018 2.018 0 0 1-1.527.692c-1.005 0-1.684-.778-1.684-1.895 0-1.118.68-1.91 1.684-1.91.565 0 1.16.255 1.527.693Zm6.43 2.023V94.61h1.797c.269 0 .396-.141.396-.396v-.877c0-.269-.127-.396-.396-.396h-1.796v-1.796c0-.34-.227-.439-.623-.283l-.905.368c-.297.113-.453.268-.453.452v1.26H52.64c-.255 0-.396.127-.396.395v.877c0 .255.141.396.396.396h1.117v2.815c0 1.726 1.047 2.731 2.56 2.731.552 0 .962-.099 1.472-.326.283-.127.311-.311.155-.608l-.325-.665c-.127-.283-.226-.34-.523-.255-.227.071-.41.1-.566.1-.495 0-.792-.41-.792-1.118Zm3.14-6.465c0 .637.523 1.174 1.131 1.174.665 0 1.217-.523 1.217-1.174 0-.636-.538-1.16-1.217-1.16-.608 0-1.131.524-1.131 1.16Zm2.164 8.785v-6.267c0-.254-.156-.396-.41-.396h-1.16c-.255 0-.41.142-.41.396v6.267c0 .255.155.396.41.396h1.16c.254 0 .41-.141.41-.396Zm3.81-5.998v-.255c0-.254-.155-.41-.41-.41h-1.117c-.255 0-.397.156-.397.41v6.253c0 .255.142.396.397.396h1.174c.269 0 .41-.141.41-.396v-4.357c.311-.368.92-.68 1.457-.68.721 0 1.16.58 1.16 1.529v3.508c0 .255.141.396.396.396h1.188c.255 0 .396-.141.396-.396v-4.102c0-1.57-1.06-2.716-2.503-2.716-.92 0-1.698.367-2.122.82h-.029Zm9.32 9.379c2.193 0 3.763-1.5 3.763-3.622v-6.012c0-.254-.141-.41-.396-.41H76.42c-.255 0-.397.156-.397.41v.27h-.042a2.653 2.653 0 0 0-1.91-.835c-1.796 0-3.239 1.64-3.239 3.692 0 2.023 1.443 3.678 3.24 3.678.707 0 1.428-.312 1.91-.82h.041v.027c0 1.16-.693 1.839-1.853 1.839-.509 0-.905-.113-1.414-.353-.227-.113-.396-.014-.51.226l-.424.835c-.113.226-.056.382.212.523.792.382 1.443.552 2.136.552Zm1.84-7.71v2.39c-.354.454-.948.708-1.528.708-.99 0-1.67-.778-1.67-1.895 0-1.118.68-1.91 1.67-1.91.566 0 1.16.269 1.527.707Zm13.067 2.546c1.018 0 1.881-.82 1.881-1.81 0-.976-.877-1.84-1.881-1.84-1.033 0-1.868.835-1.868 1.84 0 1.018.82 1.81 1.868 1.81Z",
    fill: "#000"
  })), _path16 || (_path16 = /*#__PURE__*/React.createElement("path", {
    d: "M100.819 100h6.168c.269 0 .41-.141.41-.396v-1.09c0-.268-.141-.41-.41-.41h-4.513V95.87h3.891c.254 0 .41-.155.41-.41v-1.047c0-.254-.142-.396-.41-.396h-3.891V91.88h4.513c.269 0 .41-.155.41-.41v-1.09c0-.254-.141-.395-.41-.395h-6.168c-.254 0-.396.141-.396.396v9.223c0 .255.142.396.396.396Zm9.486-6.649c-.255-.353-.326-.41-.552-.41h-1.344c-.325 0-.495.255-.34.467l2.207 3.041-2.32 3.084c-.169.212.014.467.34.467h1.188c.226 0 .311-.057.58-.41l1.301-1.783 1.274 1.783c.226.311.353.41.58.41h1.329c.326 0 .495-.255.34-.467l-2.264-3.084 2.32-3.041c.17-.226-.014-.467-.339-.467h-1.188c-.227 0-.326.057-.58.41l-1.259 1.755-1.273-1.755Zm11.716 1.132c-.58-1.033-1.797-1.698-3.127-1.698-2.065 0-3.621 1.585-3.621 3.693 0 2.08 1.556 3.678 3.593 3.678 1.443 0 2.292-.425 3.056-1.528.141-.227.085-.424-.17-.538l-1.004-.438c-.227-.1-.382-.028-.538.155a1.653 1.653 0 0 1-1.259.566c-1.004 0-1.697-.82-1.697-1.895 0-1.09.693-1.91 1.64-1.91.496 0 .962.226 1.387.707.198.24.325.269.551.17l1.005-.438c.269-.114.325-.298.184-.524Zm4.136-1.698c-1.966 0-3.452 1.57-3.452 3.693 0 2.136 1.585 3.678 3.763 3.678 1.146 0 2.023-.397 2.829-1.26.17-.197.099-.452-.169-.565l-.878-.396c-.198-.085-.311-.029-.537.127-.297.212-.778.354-1.245.354-.905 0-1.556-.495-1.768-1.316h4.569c.255 0 .396-.155.396-.41v-.283c0-2.122-1.457-3.621-3.508-3.621Zm.085 1.684c.707 0 1.245.34 1.471 1.217h-2.985c.184-.764.707-1.217 1.514-1.217Zm6.798 5.135V90.21c0-.255-.142-.396-.396-.396h-1.188c-.255 0-.397.141-.397.396v9.393c0 .255.142.396.397.396h1.188c.254 0 .396-.141.396-.396Zm3.813 0V90.21c0-.255-.142-.396-.396-.396h-1.189c-.254 0-.396.141-.396.396v9.393c0 .255.142.396.396.396h1.189c.254 0 .396-.141.396-.396Zm4.845-6.819c-1.966 0-3.451 1.57-3.451 3.693 0 2.136 1.584 3.678 3.762 3.678 1.146 0 2.023-.397 2.83-1.26.169-.197.099-.452-.17-.565l-.877-.396c-.198-.085-.311-.029-.538.127-.297.212-.778.354-1.245.354-.905 0-1.556-.495-1.768-1.316h4.569c.255 0 .396-.155.396-.41v-.283c0-2.122-1.457-3.621-3.508-3.621Zm.085 1.684c.707 0 1.245.34 1.471 1.217h-2.985c.184-.764.708-1.217 1.514-1.217Zm6.742-.863v-.255c0-.254-.156-.41-.411-.41h-1.117c-.255 0-.396.156-.396.41v6.253c0 .255.141.396.396.396h1.174c.269 0 .41-.141.41-.396v-4.357c.311-.368.92-.68 1.457-.68.722 0 1.16.58 1.16 1.529v3.508c0 .255.142.396.396.396h1.189c.254 0 .396-.141.396-.396v-4.102c0-1.57-1.061-2.716-2.504-2.716-.92 0-1.698.367-2.122.82h-.028Zm8.9 3.678V94.61h1.796c.269 0 .396-.141.396-.396v-.877c0-.269-.127-.396-.396-.396h-1.796v-1.796c0-.34-.226-.439-.623-.283l-.905.368c-.297.113-.453.268-.453.452v1.26h-1.117c-.255 0-.396.127-.396.395v.877c0 .255.141.396.396.396h1.117v2.815c0 1.726 1.047 2.731 2.561 2.731.552 0 .962-.099 1.471-.326.283-.127.311-.311.156-.608l-.326-.665c-.127-.283-.226-.34-.523-.255a1.9 1.9 0 0 1-.566.1c-.495 0-.792-.41-.792-1.118Z",
    fill: "#00B67A"
  })));
};

function getIconSvg(name) {
    switch (name) {
        case 'adjust':
            return SvgAdjust;
        case 'airplay':
            return SvgAirplay;
        case 'alarmCheck':
            return SvgAlarmCheck;
        case 'alarmClock':
            return SvgAlarmClock;
        case 'alarmClockOff':
            return SvgAlarmClockOff;
        case 'alertCircle':
            return SvgAlertCircle;
        case 'alertOctagon':
            return SvgAlertOctagon;
        case 'alertTriangle':
            return SvgAlertTriangle;
        case 'alignCenter':
            return SvgAlignCenter;
        case 'alignJustify':
            return SvgAlignJustify;
        case 'alignLeft':
            return SvgAlignLeft;
        case 'alignRight':
            return SvgAlignRight;
        case 'angleDoubleDown':
            return SvgAngleDoubleDown;
        case 'angleDoubleLeft':
            return SvgAngleDoubleLeft;
        case 'angleDoubleRight':
            return SvgAngleDoubleRight;
        case 'angleDoubleUp':
            return SvgAngleDoubleUp;
        case 'angleDown':
            return SvgAngleDown;
        case 'angleLeft':
            return SvgAngleLeft;
        case 'angleRight':
            return SvgAngleRight;
        case 'angleUp':
            return SvgAngleUp;
        case 'angry':
            return SvgAngry;
        case 'annoyed':
            return SvgAnnoyed;
        case 'archiveFilled':
            return SvgArchiveFilled;
        case 'archiveOutline':
            return SvgArchiveOutline;
        case 'arrowBigDown':
            return SvgArrowBigDown;
        case 'arrowBigDownDash':
            return SvgArrowBigDownDash;
        case 'arrowBigLeft':
            return SvgArrowBigLeft;
        case 'arrowBigLeftDash':
            return SvgArrowBigLeftDash;
        case 'arrowBigRight':
            return SvgArrowBigRight;
        case 'arrowBigRightDash':
            return SvgArrowBigRightDash;
        case 'arrowBigUp':
            return SvgArrowBigUp;
        case 'arrowBigUpDash':
            return SvgArrowBigUpDash;
        case 'arrowDown':
            return SvgArrowDown;
        case 'arrowDownAxAsc':
            return SvgArrowDownAxAsc;
        case 'arrowDownCircle':
            return SvgArrowDownCircle;
        case 'arrowDownFromLine':
            return SvgArrowDownFromLine;
        case 'arrowDownLeft':
            return SvgArrowDownLeft;
        case 'arrowDownLeftFromCircle':
            return SvgArrowDownLeftFromCircle;
        case 'arrowDownLeftSquare':
            return SvgArrowDownLeftSquare;
        case 'arrowDownNarrowWide':
            return SvgArrowDownNarrowWide;
        case 'arrowDownNumAsc':
            return SvgArrowDownNumAsc;
        case 'arrowDownRight':
            return SvgArrowDownRight;
        case 'arrowDownRightFromCircle':
            return SvgArrowDownRightFromCircle;
        case 'arrowDownRightSquare':
            return SvgArrowDownRightSquare;
        case 'arrowDownSquare':
            return SvgArrowDownSquare;
        case 'arrowDownToDot':
            return SvgArrowDownToDot;
        case 'arrowDownUp':
            return SvgArrowDownUp;
        case 'arrowDownWideNarrow':
            return SvgArrowDownWideNarrow;
        case 'arrowLeft':
            return SvgArrowLeft;
        case 'arrowLeftCircle':
            return SvgArrowLeftCircle;
        case 'arrowLeftFromLine':
            return SvgArrowLeftFromLine;
        case 'arrowLeftSquare':
            return SvgArrowLeftSquare;
        case 'arrowLeftToLine':
            return SvgArrowLeftToLine;
        case 'arrowRight':
            return SvgArrowRight;
        case 'arrowRightCircle':
            return SvgArrowRightCircle;
        case 'arrowRightFromLine':
            return SvgArrowRightFromLine;
        case 'arrowRightLeft':
            return SvgArrowRightLeft;
        case 'arrowRightSquare':
            return SvgArrowRightSquare;
        case 'arrowRightToLine':
            return SvgArrowRightToLine;
        case 'arrowUp':
            return SvgArrowUp;
        case 'arrowUpAzDesc':
            return SvgArrowUpAzDesc;
        case 'arrowUpCircle':
            return SvgArrowUpCircle;
        case 'arrowUpDown':
            return SvgArrowUpDown;
        case 'arrowUpFromDot':
            return SvgArrowUpFromDot;
        case 'arrowUpLeft':
            return SvgArrowUpLeft;
        case 'arrowUpLeftFromCircle':
            return SvgArrowUpLeftFromCircle;
        case 'arrowUpLeftSquare':
            return SvgArrowUpLeftSquare;
        case 'arrowUpNarrowWide':
            return SvgArrowUpNarrowWide;
        case 'arrowUpNumDesc':
            return SvgArrowUpNumDesc;
        case 'arrowUpRight':
            return SvgArrowUpRight;
        case 'arrowUpRightFromCircle':
            return SvgArrowUpRightFromCircle;
        case 'arrowUpRightSquare':
            return SvgArrowUpRightSquare;
        case 'arrowUpSquare':
            return SvgArrowUpSquare;
        case 'arrowUpToLine':
            return SvgArrowUpToLine;
        case 'arrowUpWideNarrow':
            return SvgArrowUpWideNarrow;
        case 'arrowsUpFromLine':
            return SvgArrowsUpFromLine;
        case 'badgeAlert':
            return SvgBadgeAlert;
        case 'badgeCheck':
            return SvgBadgeCheck;
        case 'balanceFilled':
            return SvgBalanceFilled;
        case 'balanceOutline':
            return SvgBalanceOutline;
        case 'banFilled':
            return SvgBanFilled;
        case 'banOutline':
            return SvgBanOutline;
        case 'bandaidFilled':
            return SvgBandaidFilled;
        case 'bandaidOutline':
            return SvgBandaidOutline;
        case 'barChart':
            return SvgBarChart;
        case 'barChartAxis':
            return SvgBarChartAxis;
        case 'barChartAxisUptrend':
            return SvgBarChartAxisUptrend;
        case 'barChartBig':
            return SvgBarChartBig;
        case 'barChartHorizontal':
            return SvgBarChartHorizontal;
        case 'barChartHorizontalBig':
            return SvgBarChartHorizontalBig;
        case 'barChartUptrend':
            return SvgBarChartUptrend;
        case 'basketFilled':
            return SvgBasketFilled;
        case 'basketOutline':
            return SvgBasketOutline;
        case 'batteryFull':
            return SvgBatteryFull;
        case 'batteryLow':
            return SvgBatteryLow;
        case 'batteryMedium':
            return SvgBatteryMedium;
        case 'beach':
            return SvgBeach;
        case 'bellDot':
            return SvgBellDot;
        case 'bellFilled':
            return SvgBellFilled;
        case 'bellMinus':
            return SvgBellMinus;
        case 'bellOff':
            return SvgBellOff;
        case 'bellOutline':
            return SvgBellOutline;
        case 'bellPlus':
            return SvgBellPlus;
        case 'bellRing':
            return SvgBellRing;
        case 'bloodPressureFilled':
            return SvgBloodPressureFilled;
        case 'bloodPressureOutline':
            return SvgBloodPressureOutline;
        case 'bluetooth':
            return SvgBluetooth;
        case 'bluetoothOff':
            return SvgBluetoothOff;
        case 'bluetoothSearching':
            return SvgBluetoothSearching;
        case 'bold':
            return SvgBold;
        case 'book':
            return SvgBook;
        case 'bookCopy':
            return SvgBookCopy;
        case 'bookDown':
            return SvgBookDown;
        case 'bookKey':
            return SvgBookKey;
        case 'bookLock':
            return SvgBookLock;
        case 'bookMarked':
            return SvgBookMarked;
        case 'bookMinus':
            return SvgBookMinus;
        case 'bookOpen':
            return SvgBookOpen;
        case 'bookPlus':
            return SvgBookPlus;
        case 'bookTemplate':
            return SvgBookTemplate;
        case 'bookUp':
            return SvgBookUp;
        case 'bookUpDouble':
            return SvgBookUpDouble;
        case 'bookX':
            return SvgBookX;
        case 'bookmarkFilled':
            return SvgBookmarkFilled;
        case 'bookmarkMinus':
            return SvgBookmarkMinus;
        case 'bookmarkOutline':
            return SvgBookmarkOutline;
        case 'bookmarkPlus':
            return SvgBookmarkPlus;
        case 'boxFilled':
            return SvgBoxFilled;
        case 'boxOutline':
            return SvgBoxOutline;
        case 'braille':
            return SvgBraille;
        case 'bricksFilled':
            return SvgBricksFilled;
        case 'bricksOutline':
            return SvgBricksOutline;
        case 'bridge':
            return SvgBridge;
        case 'briefcase':
            return SvgBriefcase;
        case 'calculator':
            return SvgCalculator;
        case 'calendar':
            return SvgCalendar;
        case 'calendarCheck':
            return SvgCalendarCheck;
        case 'calendarCheckInside':
            return SvgCalendarCheckInside;
        case 'calendarClock':
            return SvgCalendarClock;
        case 'calendarFilled':
            return SvgCalendarFilled;
        case 'calendarHeart':
            return SvgCalendarHeart;
        case 'calendarMinus':
            return SvgCalendarMinus;
        case 'calendarOff':
            return SvgCalendarOff;
        case 'calendarOutline':
            return SvgCalendarOutline;
        case 'calendarPlus':
            return SvgCalendarPlus;
        case 'calendarRange':
            return SvgCalendarRange;
        case 'calendarSearch':
            return SvgCalendarSearch;
        case 'calendarX':
            return SvgCalendarX;
        case 'calendarXInside':
            return SvgCalendarXInside;
        case 'cameraFilled':
            return SvgCameraFilled;
        case 'cameraOff':
            return SvgCameraOff;
        case 'cameraOutline':
            return SvgCameraOutline;
        case 'caretDown':
            return SvgCaretDown;
        case 'caretLeft':
            return SvgCaretLeft;
        case 'caretRight':
            return SvgCaretRight;
        case 'caretUp':
            return SvgCaretUp;
        case 'chatFilled':
            return SvgChatFilled;
        case 'chatOutline':
            return SvgChatOutline;
        case 'checkCheck':
            return SvgCheckCheck;
        case 'checkCircle':
            return SvgCheckCircle;
        case 'checkCircleFilled':
            return SvgCheckCircleFilled;
        case 'checkCircleOutline':
            return SvgCheckCircleOutline;
        case 'checkDefault':
            return SvgCheckDefault;
        case 'checkSquare':
            return SvgCheckSquare;
        case 'chevronDownCircle':
            return SvgChevronDownCircle;
        case 'chevronDownSquare':
            return SvgChevronDownSquare;
        case 'chevronFirst':
            return SvgChevronFirst;
        case 'chevronLast':
            return SvgChevronLast;
        case 'chevronLeftCircle':
            return SvgChevronLeftCircle;
        case 'chevronLeftSquare':
            return SvgChevronLeftSquare;
        case 'chevronRightCircle':
            return SvgChevronRightCircle;
        case 'chevronRightSquare':
            return SvgChevronRightSquare;
        case 'chevronUpCircle':
            return SvgChevronUpCircle;
        case 'chevronUpSquare':
            return SvgChevronUpSquare;
        case 'chevronsDownUp':
            return SvgChevronsDownUp;
        case 'chevronsLeftRight':
            return SvgChevronsLeftRight;
        case 'chevronsRightLeft':
            return SvgChevronsRightLeft;
        case 'circleCrossFilled':
            return SvgCircleCrossFilled;
        case 'circleCrossOutline':
            return SvgCircleCrossOutline;
        case 'circleDollarSign':
            return SvgCircleDollarSign;
        case 'circleEllipsis':
            return SvgCircleEllipsis;
        case 'circleEmpty':
            return SvgCircleEmpty;
        case 'circleError':
            return SvgCircleError;
        case 'circleInProgress':
            return SvgCircleInProgress;
        case 'circleStop':
            return SvgCircleStop;
        case 'clearFormat':
            return SvgClearFormat;
        case 'clip':
            return SvgClip;
        case 'clipboard':
            return SvgClipboard;
        case 'clipboardCheck':
            return SvgClipboardCheck;
        case 'clipboardCopy':
            return SvgClipboardCopy;
        case 'clipboardEdit':
            return SvgClipboardEdit;
        case 'clipboardList':
            return SvgClipboardList;
        case 'clipboardPaste':
            return SvgClipboardPaste;
        case 'clipboardSignature':
            return SvgClipboardSignature;
        case 'clipboardType':
            return SvgClipboardType;
        case 'clipboardX':
            return SvgClipboardX;
        case 'clock':
            return SvgClock;
        case 'clock1':
            return SvgClock1;
        case 'clock10':
            return SvgClock10;
        case 'clock11':
            return SvgClock11;
        case 'clock12':
            return SvgClock12;
        case 'clock2':
            return SvgClock2;
        case 'clock3':
            return SvgClock3;
        case 'clock4':
            return SvgClock4;
        case 'clock6':
            return SvgClock6;
        case 'clock7':
            return SvgClock7;
        case 'clock8':
            return SvgClock8;
        case 'clock9':
            return SvgClock9;
        case 'clockFilled':
            return SvgClockFilled;
        case 'clockOutline':
            return SvgClockOutline;
        case 'cloud':
            return SvgCloud;
        case 'cloudCog':
            return SvgCloudCog;
        case 'cloudDrizzle':
            return SvgCloudDrizzle;
        case 'cloudFilled':
            return SvgCloudFilled;
        case 'cloudFog':
            return SvgCloudFog;
        case 'cloudHail':
            return SvgCloudHail;
        case 'cloudLightning':
            return SvgCloudLightning;
        case 'cloudMoon':
            return SvgCloudMoon;
        case 'cloudMoonRain':
            return SvgCloudMoonRain;
        case 'cloudOff':
            return SvgCloudOff;
        case 'cloudOutline':
            return SvgCloudOutline;
        case 'cloudRain':
            return SvgCloudRain;
        case 'cloudRainWind':
            return SvgCloudRainWind;
        case 'cloudSnow':
            return SvgCloudSnow;
        case 'cloudSun':
            return SvgCloudSun;
        case 'cloudSunRain':
            return SvgCloudSunRain;
        case 'cloudy':
            return SvgCloudy;
        case 'code':
            return SvgCode;
        case 'conciergeBell':
            return SvgConciergeBell;
        case 'contrast':
            return SvgContrast;
        case 'contrastActive':
            return SvgContrastActive;
        case 'copyCheck':
            return SvgCopyCheck;
        case 'copyFilled':
            return SvgCopyFilled;
        case 'copyLeft':
            return SvgCopyLeft;
        case 'copyMinus':
            return SvgCopyMinus;
        case 'copyOutline':
            return SvgCopyOutline;
        case 'copyPlus':
            return SvgCopyPlus;
        case 'copyRight':
            return SvgCopyRight;
        case 'copySlash':
            return SvgCopySlash;
        case 'copyX':
            return SvgCopyX;
        case 'creditCardFilled':
            return SvgCreditCardFilled;
        case 'creditCardOutline':
            return SvgCreditCardOutline;
        case 'crossFilled':
            return SvgCrossFilled;
        case 'crossOutline':
            return SvgCrossOutline;
        case 'dataFilled':
            return SvgDataFilled;
        case 'dataOutline':
            return SvgDataOutline;
        case 'database':
            return SvgDatabase;
        case 'databaseBackup':
            return SvgDatabaseBackup;
        case 'deaf':
            return SvgDeaf;
        case 'decreaseFont':
            return SvgDecreaseFont;
        case 'delete':
            return SvgDelete;
        case 'desktopFilled':
            return SvgDesktopFilled;
        case 'desktopOutline':
            return SvgDesktopOutline;
        case 'dogLeashed':
            return SvgDogLeashed;
        case 'dollarSign':
            return SvgDollarSign;
        case 'dots':
            return SvgDots;
        case 'download':
            return SvgDownload;
        case 'downloadCloud':
            return SvgDownloadCloud;
        case 'dragDrop':
            return SvgDragDrop;
        case 'dribbble':
            return SvgDribbble;
        case 'dropFilled':
            return SvgDropFilled;
        case 'dropOutline':
            return SvgDropOutline;
        case 'droplet':
            return SvgDroplet;
        case 'droplets':
            return SvgDroplets;
        case 'dumbbellFilled':
            return SvgDumbbellFilled;
        case 'dumbbellOutline':
            return SvgDumbbellOutline;
        case 'emailFilled':
            return SvgEmailFilled;
        case 'emailOutline':
            return SvgEmailOutline;
        case 'euro':
            return SvgEuro;
        case 'exchange':
            return SvgExchange;
        case 'exclamationDefault':
            return SvgExclamationDefault;
        case 'exclamationTriangleFilled':
            return SvgExclamationTriangleFilled;
        case 'exclamationTriangleOutline':
            return SvgExclamationTriangleOutline;
        case 'expand':
            return SvgExpand;
        case 'externalLink':
            return SvgExternalLink;
        case 'eyeFilled':
            return SvgEyeFilled;
        case 'eyeHiddenFilled':
            return SvgEyeHiddenFilled;
        case 'eyeHiddenOutline':
            return SvgEyeHiddenOutline;
        case 'eyeOutline':
            return SvgEyeOutline;
        case 'facebook':
            return SvgFacebook;
        case 'faucetFilled':
            return SvgFaucetFilled;
        case 'faucetOutline':
            return SvgFaucetOutline;
        case 'fileArchive':
            return SvgFileArchive;
        case 'fileAudio':
            return SvgFileAudio;
        case 'fileAxis3d':
            return SvgFileAxis3D;
        case 'fileBadge':
            return SvgFileBadge;
        case 'fileBadgeInside':
            return SvgFileBadgeInside;
        case 'fileBarChart':
            return SvgFileBarChart;
        case 'fileBarChartUpTrend':
            return SvgFileBarChartUpTrend;
        case 'fileBox':
            return SvgFileBox;
        case 'fileCheck':
            return SvgFileCheck;
        case 'fileCheckInside':
            return SvgFileCheckInside;
        case 'fileClock':
            return SvgFileClock;
        case 'fileCode':
            return SvgFileCode;
        case 'fileCodeInside':
            return SvgFileCodeInside;
        case 'fileCog':
            return SvgFileCog;
        case 'fileCogInside':
            return SvgFileCogInside;
        case 'fileDiff':
            return SvgFileDiff;
        case 'fileDigit':
            return SvgFileDigit;
        case 'fileDown':
            return SvgFileDown;
        case 'fileEdit':
            return SvgFileEdit;
        case 'fileEmptyFilled':
            return SvgFileEmptyFilled;
        case 'fileEmptyOutline':
            return SvgFileEmptyOutline;
        case 'fileHeart':
            return SvgFileHeart;
        case 'fileImage':
            return SvgFileImage;
        case 'fileInput':
            return SvgFileInput;
        case 'fileJson':
            return SvgFileJson;
        case 'fileJsonInside':
            return SvgFileJsonInside;
        case 'fileKey':
            return SvgFileKey;
        case 'fileKeyInside':
            return SvgFileKeyInside;
        case 'fileLineChart':
            return SvgFileLineChart;
        case 'fileLock':
            return SvgFileLock;
        case 'fileLockInside':
            return SvgFileLockInside;
        case 'fileMinus':
            return SvgFileMinus;
        case 'fileMinusInside':
            return SvgFileMinusInside;
        case 'fileOutput':
            return SvgFileOutput;
        case 'filePieChart':
            return SvgFilePieChart;
        case 'filePlus':
            return SvgFilePlus;
        case 'filePlusInside':
            return SvgFilePlusInside;
        case 'fileQuestion':
            return SvgFileQuestion;
        case 'fileScan':
            return SvgFileScan;
        case 'fileSearch':
            return SvgFileSearch;
        case 'fileSignature':
            return SvgFileSignature;
        case 'fileStack':
            return SvgFileStack;
        case 'fileSymlink':
            return SvgFileSymlink;
        case 'fileTerminal':
            return SvgFileTerminal;
        case 'fileText':
            return SvgFileText;
        case 'fileType':
            return SvgFileType;
        case 'fileTypeInside':
            return SvgFileTypeInside;
        case 'fileUp':
            return SvgFileUp;
        case 'fileVideo':
            return SvgFileVideo;
        case 'fileVideoInside':
            return SvgFileVideoInside;
        case 'fileVisualizationFilled':
            return SvgFileVisualizationFilled;
        case 'fileVisualizationOutline':
            return SvgFileVisualizationOutline;
        case 'fileVolume':
            return SvgFileVolume;
        case 'fileVolumeInside':
            return SvgFileVolumeInside;
        case 'fileWarning':
            return SvgFileWarning;
        case 'fileWithItemsFilled':
            return SvgFileWithItemsFilled;
        case 'fileWithItemsOutline':
            return SvgFileWithItemsOutline;
        case 'fileX':
            return SvgFileX;
        case 'fileXInside':
            return SvgFileXInside;
        case 'files':
            return SvgFiles;
        case 'filterFilled':
            return SvgFilterFilled;
        case 'filterOutline':
            return SvgFilterOutline;
        case 'filterX':
            return SvgFilterX;
        case 'flag':
            return SvgFlag;
        case 'flashlight':
            return SvgFlashlight;
        case 'flashlightOff':
            return SvgFlashlightOff;
        case 'foldHorizontal':
            return SvgFoldHorizontal;
        case 'foldVertical':
            return SvgFoldVertical;
        case 'folder':
            return SvgFolder;
        case 'folderArchive':
            return SvgFolderArchive;
        case 'folderCheck':
            return SvgFolderCheck;
        case 'folderClock':
            return SvgFolderClock;
        case 'folderClosed':
            return SvgFolderClosed;
        case 'folderCog':
            return SvgFolderCog;
        case 'folderCogInside':
            return SvgFolderCogInside;
        case 'folderDot':
            return SvgFolderDot;
        case 'folderDown':
            return SvgFolderDown;
        case 'folderEdit':
            return SvgFolderEdit;
        case 'folderFilled':
            return SvgFolderFilled;
        case 'folderGit':
            return SvgFolderGit;
        case 'folderGitInside':
            return SvgFolderGitInside;
        case 'folderHeart':
            return SvgFolderHeart;
        case 'folderInput':
            return SvgFolderInput;
        case 'folderKanban':
            return SvgFolderKanban;
        case 'folderKey':
            return SvgFolderKey;
        case 'folderLock':
            return SvgFolderLock;
        case 'folderMinus':
            return SvgFolderMinus;
        case 'folderOpen':
            return SvgFolderOpen;
        case 'folderOutput':
            return SvgFolderOutput;
        case 'folderPlus':
            return SvgFolderPlus;
        case 'folderRoot':
            return SvgFolderRoot;
        case 'folderSearch':
            return SvgFolderSearch;
        case 'folderSearchInside':
            return SvgFolderSearchInside;
        case 'folderSymlink':
            return SvgFolderSymlink;
        case 'folderSync':
            return SvgFolderSync;
        case 'folderTree':
            return SvgFolderTree;
        case 'folderUp':
            return SvgFolderUp;
        case 'folderX':
            return SvgFolderX;
        case 'folders':
            return SvgFolders;
        case 'frown':
            return SvgFrown;
        case 'fullScreen':
            return SvgFullScreen;
        case 'fullScreenOff':
            return SvgFullScreenOff;
        case 'gauge':
            return SvgGauge;
        case 'gaugeCircle':
            return SvgGaugeCircle;
        case 'gearFilled':
            return SvgGearFilled;
        case 'gearOutline':
            return SvgGearOutline;
        case 'glucometer':
            return SvgGlucometer;
        case 'gripHorizontal':
            return SvgGripHorizontal;
        case 'hamburgerMenu':
            return SvgHamburgerMenu;
        case 'hand':
            return SvgHand;
        case 'handMetal':
            return SvgHandMetal;
        case 'headphones':
            return SvgHeadphones;
        case 'heartCrack':
            return SvgHeartCrack;
        case 'heartFilled':
            return SvgHeartFilled;
        case 'heartHandshake':
            return SvgHeartHandshake;
        case 'heartOff':
            return SvgHeartOff;
        case 'heartOutline':
            return SvgHeartOutline;
        case 'heartPulse':
            return SvgHeartPulse;
        case 'heartRateFilled':
            return SvgHeartRateFilled;
        case 'heartRateOutline':
            return SvgHeartRateOutline;
        case 'helpingHand':
            return SvgHelpingHand;
        case 'highlighter':
            return SvgHighlighter;
        case 'history':
            return SvgHistory;
        case 'homeFilled':
            return SvgHomeFilled;
        case 'homeOutline':
            return SvgHomeOutline;
        case 'hospital':
            return SvgHospital;
        case 'hourglass':
            return SvgHourglass;
        case 'houseFilled':
            return SvgHouseFilled;
        case 'houseOutline':
            return SvgHouseOutline;
        case 'imageFilled':
            return SvgImageFilled;
        case 'imageMinus':
            return SvgImageMinus;
        case 'imageOff':
            return SvgImageOff;
        case 'imageOutline':
            return SvgImageOutline;
        case 'imagePlus':
            return SvgImagePlus;
        case 'imcFilled':
            return SvgImcFilled;
        case 'import':
            return SvgImport;
        case 'inbox':
            return SvgInbox;
        case 'increaseFont':
            return SvgIncreaseFont;
        case 'infoCircleFilled':
            return SvgInfoCircleFilled;
        case 'infoCircleOutline':
            return SvgInfoCircleOutline;
        case 'infoDefault':
            return SvgInfoDefault;
        case 'instagram':
            return SvgInstagram;
        case 'italic':
            return SvgItalic;
        case 'japaneseYen':
            return SvgJapaneseYen;
        case 'keyFilled':
            return SvgKeyFilled;
        case 'keyOutline':
            return SvgKeyOutline;
        case 'laugh':
            return SvgLaugh;
        case 'layout':
            return SvgLayout;
        case 'layoutDashboard':
            return SvgLayoutDashboard;
        case 'layoutGrid':
            return SvgLayoutGrid;
        case 'layoutList':
            return SvgLayoutList;
        case 'layoutPanelLeft':
            return SvgLayoutPanelLeft;
        case 'layoutPanelTop':
            return SvgLayoutPanelTop;
        case 'layoutTemplate':
            return SvgLayoutTemplate;
        case 'lightbulbFilled':
            return SvgLightbulbFilled;
        case 'lightbulbOff':
            return SvgLightbulbOff;
        case 'lightbulbOutline':
            return SvgLightbulbOutline;
        case 'lightningBolt':
            return SvgLightningBolt;
        case 'link':
            return SvgLink;
        case 'linkOff':
            return SvgLinkOff;
        case 'linkedin':
            return SvgLinkedin;
        case 'list':
            return SvgList;
        case 'listChecks':
            return SvgListChecks;
        case 'listFilter':
            return SvgListFilter;
        case 'listStart':
            return SvgListStart;
        case 'llp':
            return SvgLlp;
        case 'loader':
            return SvgLoader;
        case 'loading':
            return SvgLoading;
        case 'lockCloseFilled':
            return SvgLockCloseFilled;
        case 'lockCloseOutline':
            return SvgLockCloseOutline;
        case 'lockOpenFilled':
            return SvgLockOpenFilled;
        case 'lockOpenOutline':
            return SvgLockOpenOutline;
        case 'logIn':
            return SvgLogIn;
        case 'lungs':
            return SvgLungs;
        case 'mailCheck':
            return SvgMailCheck;
        case 'mailMinus':
            return SvgMailMinus;
        case 'mailOpen':
            return SvgMailOpen;
        case 'mailPlus':
            return SvgMailPlus;
        case 'mailQuestion':
            return SvgMailQuestion;
        case 'mailSearch':
            return SvgMailSearch;
        case 'mailWarning':
            return SvgMailWarning;
        case 'mailX':
            return SvgMailX;
        case 'mailbox':
            return SvgMailbox;
        case 'mails':
            return SvgMails;
        case 'mapFilled':
            return SvgMapFilled;
        case 'mapMarkerFilled':
            return SvgMapMarkerFilled;
        case 'mapMarkerOutline':
            return SvgMapMarkerOutline;
        case 'mapOutline':
            return SvgMapOutline;
        case 'mapPinOff':
            return SvgMapPinOff;
        case 'maximize':
            return SvgMaximize;
        case 'medicineBottleFilled':
            return SvgMedicineBottleFilled;
        case 'medicineBottleOutline':
            return SvgMedicineBottleOutline;
        case 'megaphone':
            return SvgMegaphone;
        case 'megaphoneOff':
            return SvgMegaphoneOff;
        case 'meh':
            return SvgMeh;
        case 'menuSquare':
            return SvgMenuSquare;
        case 'messageCircle':
            return SvgMessageCircle;
        case 'messageSquare':
            return SvgMessageSquare;
        case 'messageSquareDashed':
            return SvgMessageSquareDashed;
        case 'messageSquarePlus':
            return SvgMessageSquarePlus;
        case 'messagesSquare':
            return SvgMessagesSquare;
        case 'microphoneFilled':
            return SvgMicrophoneFilled;
        case 'microphoneOutline':
            return SvgMicrophoneOutline;
        case 'minimize':
            return SvgMinimize;
        case 'minus':
            return SvgMinus;
        case 'minusCircle':
            return SvgMinusCircle;
        case 'minusSquare':
            return SvgMinusSquare;
        case 'module':
            return SvgModule;
        case 'monitorCheck':
            return SvgMonitorCheck;
        case 'monitorPlay':
            return SvgMonitorPlay;
        case 'monitorSmartphone':
            return SvgMonitorSmartphone;
        case 'moreHorizontal':
            return SvgMoreHorizontal;
        case 'mouse':
            return SvgMouse;
        case 'mousePointer':
            return SvgMousePointer;
        case 'mousePointerClick':
            return SvgMousePointerClick;
        case 'move':
            return SvgMove;
        case 'moveDiagonalL2R':
            return SvgMoveDiagonalL2R;
        case 'moveDiagonalR2L':
            return SvgMoveDiagonalR2L;
        case 'moveDown':
            return SvgMoveDown;
        case 'moveDownLeft':
            return SvgMoveDownLeft;
        case 'moveDownRight':
            return SvgMoveDownRight;
        case 'moveHorizontal':
            return SvgMoveHorizontal;
        case 'moveLeft':
            return SvgMoveLeft;
        case 'moveRight':
            return SvgMoveRight;
        case 'moveUp':
            return SvgMoveUp;
        case 'moveUpLeft':
            return SvgMoveUpLeft;
        case 'moveUpRight':
            return SvgMoveUpRight;
        case 'moveVertical':
            return SvgMoveVertical;
        case 'mugFilled':
            return SvgMugFilled;
        case 'mugOutline':
            return SvgMugOutline;
        case 'multipleUsersFilled':
            return SvgMultipleUsersFilled;
        case 'multipleUsersOutline':
            return SvgMultipleUsersOutline;
        case 'needleFilled':
            return SvgNeedleFilled;
        case 'needleOutline':
            return SvgNeedleOutline;
        case 'nurse':
            return SvgNurse;
        case 'nurseView':
            return SvgNurseView;
        case 'o2Filled':
            return SvgO2Filled;
        case 'openDoor':
            return SvgOpenDoor;
        case 'packageCheck':
            return SvgPackageCheck;
        case 'packageOpen':
            return SvgPackageOpen;
        case 'packageSearch':
            return SvgPackageSearch;
        case 'partyPopper':
            return SvgPartyPopper;
        case 'pause':
            return SvgPause;
        case 'pauseCircle':
            return SvgPauseCircle;
        case 'pauseFilled':
            return SvgPauseFilled;
        case 'pen':
            return SvgPen;
        case 'penFilled':
            return SvgPenFilled;
        case 'penOutline':
            return SvgPenOutline;
        case 'penSquare':
            return SvgPenSquare;
        case 'penTool':
            return SvgPenTool;
        case 'penToolFilled':
            return SvgPenToolFilled;
        case 'pencilLine':
            return SvgPencilLine;
        case 'pencilRuler':
            return SvgPencilRuler;
        case 'percent':
            return SvgPercent;
        case 'petFilled':
            return SvgPetFilled;
        case 'petOutline':
            return SvgPetOutline;
        case 'phoneCall':
            return SvgPhoneCall;
        case 'phoneFilled':
            return SvgPhoneFilled;
        case 'phoneForwarded':
            return SvgPhoneForwarded;
        case 'phoneIncoming':
            return SvgPhoneIncoming;
        case 'phoneMissed':
            return SvgPhoneMissed;
        case 'phoneOff':
            return SvgPhoneOff;
        case 'phoneOutgoing':
            return SvgPhoneOutgoing;
        case 'phoneOutline':
            return SvgPhoneOutline;
        case 'pillFilled':
            return SvgPillFilled;
        case 'pillOutline':
            return SvgPillOutline;
        case 'pipeFilled':
            return SvgPipeFilled;
        case 'pipeOutline':
            return SvgPipeOutline;
        case 'playCircle':
            return SvgPlayCircle;
        case 'playFilled':
            return SvgPlayFilled;
        case 'playOutline':
            return SvgPlayOutline;
        case 'playSquare':
            return SvgPlaySquare;
        case 'plus':
            return SvgPlus;
        case 'plusCircle':
            return SvgPlusCircle;
        case 'plusSquare':
            return SvgPlusSquare;
        case 'pointer':
            return SvgPointer;
        case 'pollFilled':
            return SvgPollFilled;
        case 'pollOutline':
            return SvgPollOutline;
        case 'pound':
            return SvgPound;
        case 'presentationFilled':
            return SvgPresentationFilled;
        case 'presentationOutline':
            return SvgPresentationOutline;
        case 'printerFilled':
            return SvgPrinterFilled;
        case 'printerOutline':
            return SvgPrinterOutline;
        case 'qrCode':
            return SvgQrCode;
        case 'questionMarkFilled':
            return SvgQuestionMarkFilled;
        case 'questionMarkOutline':
            return SvgQuestionMarkOutline;
        case 'quote':
            return SvgQuote;
        case 'quoteLeft':
            return SvgQuoteLeft;
        case 'quoteRight':
            return SvgQuoteRight;
        case 'redo':
            return SvgRedo;
        case 'refreshCcw':
            return SvgRefreshCcw;
        case 'refreshCcwDot':
            return SvgRefreshCcwDot;
        case 'refreshCwOff':
            return SvgRefreshCwOff;
        case 'roadFilled':
            return SvgRoadFilled;
        case 'roadOutline':
            return SvgRoadOutline;
        case 'rocket':
            return SvgRocket;
        case 'rotateCcw':
            return SvgRotateCcw;
        case 'rotateCw':
            return SvgRotateCw;
        case 'rulerFilled':
            return SvgRulerFilled;
        case 'rulerOutline':
            return SvgRulerOutline;
        case 'save':
            return SvgSave;
        case 'saveAll':
            return SvgSaveAll;
        case 'saveFilled':
            return SvgSaveFilled;
        case 'scale':
            return SvgScale;
        case 'scale3d':
            return SvgScale3D;
        case 'scan':
            return SvgScan;
        case 'scanFace':
            return SvgScanFace;
        case 'scanLine':
            return SvgScanLine;
        case 'scrollText':
            return SvgScrollText;
        case 'searchCheck':
            return SvgSearchCheck;
        case 'searchCode':
            return SvgSearchCode;
        case 'searchSlash':
            return SvgSearchSlash;
        case 'searchX':
            return SvgSearchX;
        case 'send':
            return SvgSend;
        case 'sendHorizonal':
            return SvgSendHorizonal;
        case 'share':
            return SvgShare;
        case 'shareFilled':
            return SvgShareFilled;
        case 'shareOutline':
            return SvgShareOutline;
        case 'shieldAlert':
            return SvgShieldAlert;
        case 'shieldCheck':
            return SvgShieldCheck;
        case 'shieldFilled':
            return SvgShieldFilled;
        case 'shieldOutline':
            return SvgShieldOutline;
        case 'shieldTickOutline':
            return SvgShieldTickOutline;
        case 'shirt':
            return SvgShirt;
        case 'shoppingBag':
            return SvgShoppingBag;
        case 'shoppingCart':
            return SvgShoppingCart;
        case 'shrink':
            return SvgShrink;
        case 'shuffle':
            return SvgShuffle;
        case 'signLanguage':
            return SvgSignLanguage;
        case 'signOut':
            return SvgSignOut;
        case 'skipBack':
            return SvgSkipBack;
        case 'skipForward':
            return SvgSkipForward;
        case 'slack':
            return SvgSlack;
        case 'smartphone':
            return SvgSmartphone;
        case 'smartphoneCharging':
            return SvgSmartphoneCharging;
        case 'smartphoneNfc':
            return SvgSmartphoneNfc;
        case 'smile':
            return SvgSmile;
        case 'smilePlus':
            return SvgSmilePlus;
        case 'sort':
            return SvgSort;
        case 'sparkle':
            return SvgSparkle;
        case 'sparkles':
            return SvgSparkles;
        case 'spellCheck':
            return SvgSpellCheck;
        case 'spellCheckHighlight':
            return SvgSpellCheckHighlight;
        case 'split':
            return SvgSplit;
        case 'starFilled':
            return SvgStarFilled;
        case 'starHalf':
            return SvgStarHalf;
        case 'starOff':
            return SvgStarOff;
        case 'starOutline':
            return SvgStarOutline;
        case 'stethoscopeFilled':
            return SvgStethoscopeFilled;
        case 'stethoscopeOutline':
            return SvgStethoscopeOutline;
        case 'stopFilled':
            return SvgStopFilled;
        case 'stopOutline':
            return SvgStopOutline;
        case 'strikethrough':
            return SvgStrikethrough;
        case 'switchCamera':
            return SvgSwitchCamera;
        case 'sync':
            return SvgSync;
        case 'tag':
            return SvgTag;
        case 'tags':
            return SvgTags;
        case 'textCursor':
            return SvgTextCursor;
        case 'textCursorInput':
            return SvgTextCursorInput;
        case 'textQuote':
            return SvgTextQuote;
        case 'textSelect':
            return SvgTextSelect;
        case 'thermometerFilled':
            return SvgThermometerFilled;
        case 'thermometerOutline':
            return SvgThermometerOutline;
        case 'thumbsDown':
            return SvgThumbsDown;
        case 'thumbsUp':
            return SvgThumbsUp;
        case 'timerFilled':
            return SvgTimerFilled;
        case 'timerOff':
            return SvgTimerOff;
        case 'timerOutline':
            return SvgTimerOutline;
        case 'timerReset':
            return SvgTimerReset;
        case 'timesDefault':
            return SvgTimesDefault;
        case 'timesFilled':
            return SvgTimesFilled;
        case 'timesOutline':
            return SvgTimesOutline;
        case 'trash':
            return SvgTrash;
        case 'trashFilled':
            return SvgTrashFilled;
        case 'trashOutline':
            return SvgTrashOutline;
        case 'treeDeciduous':
            return SvgTreeDeciduous;
        case 'treePine':
            return SvgTreePine;
        case 'trees':
            return SvgTrees;
        case 'trendingDown':
            return SvgTrendingDown;
        case 'trendingUp':
            return SvgTrendingUp;
        case 'twitter':
            return SvgTwitter;
        case 'underline':
            return SvgUnderline;
        case 'undo':
            return SvgUndo;
        case 'unfoldHorizontal':
            return SvgUnfoldHorizontal;
        case 'unfoldVertical':
            return SvgUnfoldVertical;
        case 'universalAccess':
            return SvgUniversalAccess;
        case 'unlink':
            return SvgUnlink;
        case 'unlinkAlt':
            return SvgUnlinkAlt;
        case 'upload':
            return SvgUpload;
        case 'uploadCloud':
            return SvgUploadCloud;
        case 'user':
            return SvgUser;
        case 'userBan':
            return SvgUserBan;
        case 'userCard':
            return SvgUserCard;
        case 'userCheck':
            return SvgUserCheck;
        case 'userCircle':
            return SvgUserCircle;
        case 'userCog':
            return SvgUserCog;
        case 'userFilled':
            return SvgUserFilled;
        case 'userMinus':
            return SvgUserMinus;
        case 'userPlus':
            return SvgUserPlus;
        case 'userReturn':
            return SvgUserReturn;
        case 'userSquare':
            return SvgUserSquare;
        case 'userTimes':
            return SvgUserTimes;
        case 'videoFilled':
            return SvgVideoFilled;
        case 'videoOutline':
            return SvgVideoOutline;
        case 'voicemail':
            return SvgVoicemail;
        case 'volumeMax':
            return SvgVolumeMax;
        case 'volumeMaxFilled':
            return SvgVolumeMaxFilled;
        case 'volumeMute':
            return SvgVolumeMute;
        case 'volumeMuteFilled':
            return SvgVolumeMuteFilled;
        case 'volumeNone':
            return SvgVolumeNone;
        case 'volumeOn':
            return SvgVolumeOn;
        case 'walker':
            return SvgWalker;
        case 'wallet':
            return SvgWallet;
        case 'walletCards':
            return SvgWalletCards;
        case 'wheelchair':
            return SvgWheelchair;
        case 'wifi':
            return SvgWifi;
        case 'wifiOff':
            return SvgWifiOff;
        case 'wrapText':
            return SvgWrapText;
        case 'wrench':
            return SvgWrench;
        case 'xOctagon':
            return SvgXOctagon;
        case 'xSquare':
            return SvgXSquare;
        case 'xp':
            return SvgXp;
        case 'youtube':
            return SvgYoutube;
        case 'zap':
            return SvgZap;
        case 'zapOff':
            return SvgZapOff;
        case 'zoomMinusFilled':
            return SvgZoomMinusFilled;
        case 'zoomMinusOutline':
            return SvgZoomMinusOutline;
        case 'zoomOutline':
            return SvgZoomOutline;
        case 'zoomPlusFilled':
            return SvgZoomPlusFilled;
        case 'zoomPlusOutline':
            return SvgZoomPlusOutline;
        // Remove from iconComponent.tsx.hbs in 3.0 release
        case 'envelope':
            return SvgEmailFilled;
        case 'phone':
            return SvgPhoneFilled;
        case 'timerOutlined':
            return SvgTimerOutline;
        case 'dragdrop':
            return SvgDragDrop;
        case 'folderOutline':
            return SvgFolder;
        case 'multipleUsers':
            return SvgMultipleUsersOutline;
        case 'userOutline':
            return SvgUser;
        case 'trustPilotBadge':
            return SvgTrustpilot;
    }
}
var Icon = function (_a) {
    var icon = _a.icon, fill = _a.fill, iconProps = __rest(_a, ["icon", "fill"]);
    // Remove from iconComponent.tsx.hbs in 3.0 release
    if ([
        'envelope',
        'phone',
        'timerOutlined',
        'dragdrop',
        'folderOutline',
        'multipleUsers',
        'userOutline',
        'trustPilotBadge',
    ].includes(icon)) {
        console.warn("".concat(icon, " has been deprecated and will be removed in the next major version, please update to new name"));
    }
    var Icon = getIconSvg(icon);
    return (React__default.createElement(Icon, __assign({ className: styles$m.iwIcon, style: { fill: fill } }, iconProps)));
};

var styles$l = {"spinner":"Spinner-module_spinner__nBiZ-","loading":"Spinner-module_loading__6wPXK","small":"Spinner-module_small__bFtnF","medium":"Spinner-module_medium__KIY4-","large":"Spinner-module_large__hhqEE"};

var Spinner = function (_a) {
    var _b = _a.size, size = _b === void 0 ? 'medium' : _b, className = _a.className, dataTestId = _a.dataTestId;
    return (React__default.createElement("span", { className: cn(className, styles$l.spinner, styles$l[size]), "data-testid": dataTestId }));
};
Spinner.displayName = 'Spinner';

/**
 * @experiemental Component to display tabular data
 */
var Table = function (_a) {
    var _b, _c, _d;
    var data = _a.data, columns = _a.columns, small = _a.small, maxHeight = _a.maxHeight, _e = _a.pinFirstCol, pinFirstCol = _e === void 0 ? true : _e, onRowClick = _a.onRowClick, rowClassName = _a.rowClassName, rowStyle = _a.rowStyle, style = _a.style, className = _a.className, loading = _a.loading, tableProps = __rest(_a, ["data", "columns", "small", "maxHeight", "pinFirstCol", "onRowClick", "rowClassName", "rowStyle", "style", "className", "loading"]);
    var tableRef = useRef(null);
    var tableBodyRef = useRef(null);
    var tableHeadRef = useRef(null);
    var tableFootRef = useRef(null);
    var scrollbarWidth = useScrollBarWidth();
    var _f = useState(0), scrollXPos = _f[0], setScrollXPos = _f[1];
    var _g = useState(0), scrollYPos = _g[0], setScrollYPos = _g[1];
    var _h = useState(false), scrollYVisible = _h[0], setScrollYVisible = _h[1];
    var _j = React__default.useState([]), sorting = _j[0], setSorting = _j[1];
    var hasFooter = columns.some(function (col) { return col.footer; });
    columns = useMemo(function () {
        return columns.map(function (col) { return (__assign(__assign({}, col), { enableSorting: col.enableSorting === undefined ? false : col.enableSorting })); });
    }, [columns]);
    var columnVisibility = useMemo(function () {
        return columns.reduce(function (accumulator, col) {
            var _a;
            var _b;
            return col.id
                ? __assign(__assign({}, accumulator), (_a = {}, _a[col.id] = (_b = col.visible) !== null && _b !== void 0 ? _b : true, _a)) : accumulator;
        }, {});
    }, [columns]);
    var table = useReactTable({
        data: data,
        columns: columns,
        state: {
            sorting: sorting,
            columnVisibility: columnVisibility,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });
    var headers = table.getHeaderGroups();
    var rows = table.getRowModel().rows;
    var footers = table.getFooterGroups();
    // Track number of Columns
    useEffect(function () {
        var _a;
        (_a = tableRef === null || tableRef === void 0 ? void 0 : tableRef.current) === null || _a === void 0 ? void 0 : _a.style.setProperty('--noCols', columns.length.toString());
    }, [tableRef, columns]);
    // Track scroll Left/Top for overlays
    useLayoutEffect(function () {
        var scrollChange = function (ev) {
            if (tableHeadRef.current && tableBodyRef.current) {
                setScrollXPos(ev.target.scrollLeft);
                setScrollYPos(ev.target.scrollTop);
                tableHeadRef.current.scrollLeft = ev.target.scrollLeft;
                tableBodyRef.current.scrollLeft = ev.target.scrollLeft;
            }
        };
        var tfoot = tableFootRef.current;
        var tbody = tableBodyRef.current;
        tfoot === null || tfoot === void 0 ? void 0 : tfoot.addEventListener('scroll', scrollChange);
        tbody === null || tbody === void 0 ? void 0 : tbody.addEventListener('scroll', scrollChange);
        return function () {
            tfoot === null || tfoot === void 0 ? void 0 : tfoot.removeEventListener('scroll', scrollChange);
            tbody === null || tbody === void 0 ? void 0 : tbody.removeEventListener('scroll', scrollChange);
        };
    }, [tableBodyRef, tableHeadRef, tableFootRef]);
    // check if vertical scroll is visible
    useLayoutEffect(function () {
        if (tableBodyRef === null || tableBodyRef === void 0 ? void 0 : tableBodyRef.current) {
            setScrollYVisible(tableBodyRef.current.scrollHeight > tableBodyRef.current.clientHeight);
        }
    }, [tableBodyRef]);
    var renderHeader = function () {
        return (React__default.createElement("thead", { ref: tableHeadRef, className: styles$n.thead, style: {
                paddingRight: scrollYVisible ? "".concat(scrollbarWidth, "px") : "0",
            } }, headers.map(function (headerGroup) { return (React__default.createElement("tr", { key: headerGroup.id, className: styles$n.tr }, headerGroup.headers.map(function (header) { return (React__default.createElement("th", { key: header.id, className: styles$n.th, style: {
                width: header.getSize() !== 150 ? header.getSize() : undefined,
                minWidth: header.getSize() !== 150 ? header.getSize() : undefined,
                cursor: header.column.getCanSort() ? 'pointer' : undefined,
            }, onClick: header.column.getToggleSortingHandler() },
            header.isPlaceholder
                ? null
                : flexRender(header.column.columnDef.header, header.getContext()),
            header.column.getIsSorted() === 'asc' && (React__default.createElement("button", { className: styles$n.sortButton, type: "button", "aria-label": "Sort Descending" },
                React__default.createElement(Icon, { icon: "arrowUp" }))),
            header.column.getIsSorted() === 'desc' && (React__default.createElement("button", { className: styles$n.sortButton, type: "button", "aria-label": "Remove Sort" },
                React__default.createElement(Icon, { icon: "arrowDown" }))),
            header.column.getIsSorted() === false &&
                header.column.getCanSort() && (React__default.createElement("button", { className: styles$n.sortButton, type: "button", "aria-label": "Sort Descending" },
                React__default.createElement(Icon, { icon: "sort" }))))); }))); })));
    };
    var renderRow = function (index, style) {
        var _a;
        var row = rows[index];
        delete style.width;
        return (React__default.createElement("tr", { key: row.id, className: cn(styles$n.tr, (_a = {},
                _a[styles$n.lastRow] = !hasFooter && index === rows.length - 1,
                _a)), style: __assign({ cursor: onRowClick ? 'pointer' : undefined }, style), onClick: function () { return onRowClick && onRowClick(row); } }, row.getVisibleCells().map(function (cell) {
            var columnDef = cell.column.columnDef;
            // @ts-ignore
            return (React__default.createElement("td", { key: cell.id, className: cn(styles$n.td, columnDef.className, rowClassName && typeof rowClassName === 'function'
                    ? rowClassName(row)
                    : rowClassName), style: __assign(__assign({ width: cell.column.getSize() !== 150
                        ? cell.column.getSize()
                        : undefined, minWidth: cell.column.getSize() !== 150
                        ? cell.column.getSize()
                        : undefined }, columnDef.style), (rowStyle && typeof rowStyle === 'function'
                    ? rowStyle(row)
                    : rowStyle)) }, loading ? (React__default.createElement(Skeleton, { lastLineLength: 70 })) : (flexRender(cell.column.columnDef.cell, cell.getContext()))));
        })));
    };
    var renderFooter = function () {
        return (React__default.createElement("tfoot", { ref: tableFootRef, className: styles$n.tfoot, style: {
                paddingRight: scrollYVisible ? "".concat(scrollbarWidth, "px") : "0",
            } }, footers.map(function (footerGroup) { return (React__default.createElement("tr", { key: footerGroup.id, className: styles$n.tr }, footerGroup.headers.map(function (header) { return (React__default.createElement("th", { key: header.id, className: styles$n.th }, header.isPlaceholder ? null : loading ? (React__default.createElement(Skeleton, { lastLineLength: 70 })) : (flexRender(header.column.columnDef.footer, header.getContext())))); }))); })));
    };
    return (React__default.createElement("table", __assign({ ref: tableRef, className: cn(styles$n.table, (_b = {},
            _b[styles$n.small] = small,
            _b[styles$n.scrolledX] = scrollXPos > 0,
            _b[styles$n.scrolledY] = scrollYPos > 0,
            _b[styles$n.pinFirstCol] = pinFirstCol,
            _b[styles$n.loading] = loading,
            _b[styles$n.noData] = rows.length === 0,
            _b), className), style: style, "aria-rowcount": rows.length }, tableProps),
        renderHeader(),
        rows.length > 300 && maxHeight ? (
        // Windowed Table rows
        React__default.createElement("tbody", { className: cn(styles$n.tbody, (_c = {},
                _c[styles$n.scrollX] = !hasFooter,
                _c)), style: { maxHeight: "".concat(maxHeight, "px") } },
            React__default.createElement("tr", { className: styles$n.verticalScrollOverlay, hidden: true, "aria-hidden": "true" }),
            React__default.createElement(FixedSizeList, { height: maxHeight, itemCount: rows.length, itemSize: small ? 49 : 57, width: "100%", overscanCount: 5, outerRef: tableBodyRef }, function (_a) {
                var index = _a.index, style = _a.style;
                return renderRow(index, style);
            }))) : (
        // Render all rows
        React__default.createElement("tbody", { ref: tableBodyRef, className: cn(styles$n.tbody, (_d = {},
                _d[styles$n.scrollX] = !hasFooter,
                _d)), style: { maxHeight: "".concat(maxHeight, "px") } },
            React__default.createElement("tr", { className: styles$n.verticalScrollOverlay, hidden: true, "aria-hidden": "true" }),
            loading && rows.length === 0 ? (React__default.createElement("tr", { className: styles$n.tr },
                React__default.createElement("td", { className: styles$n.td },
                    React__default.createElement(Spinner, null)))) : rows.length === 0 ? (React__default.createElement("tr", { className: cn(styles$n.noData, styles$n.tr) },
                React__default.createElement("td", { className: styles$n.td }, '  '))) : (rows.map(function (row, index) { return renderRow(index, {}); })))),
        hasFooter && renderFooter()));
};

var styles$k = {"formElementWrapper":"Input-module_formElementWrapper__OPHoX","inputWrapper":"Input-module_inputWrapper__H85cH","topMargin":"Input-module_topMargin__lruFp","leftIconPadding":"Input-module_leftIconPadding__3I4ix","rightIconPadding":"Input-module_rightIconPadding__NqoG8","disabled":"Input-module_disabled__rzHXQ","iconLeft":"Input-module_iconLeft__RwAQ9","iconRight":"Input-module_iconRight__LrwYQ","error":"Input-module_error__F8HlP","status":"Input-module_status__mHFte"};

var styles$j = {"label":"Label-module_label__3iACP"};

var Label = React__default.forwardRef(function (_a, ref) {
    var className = _a.className, labelProps = __rest(_a, ["className"]);
    return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    React__default.createElement("label", __assign({ ref: ref, className: cn(styles$j.label, className) }, labelProps)));
});

var styles$i = {"description":"Description-module_description__9m7gI"};

var Description = function (_a) {
    var description = _a.description, id = _a.id, className = _a.className, props = __rest(_a, ["description", "id", "className"]);
    return (React__default.createElement("span", __assign({ className: cn(styles$i.description, className), id: id }, props), description));
};

var styles$h = {"message":"Message-module_message__hKZ3E","error":"Message-module_error__jdrHR","success":"Message-module_success__FkTrm","small":"Message-module_small__w8fns","medium":"Message-module_medium__HubdQ","large":"Message-module_large__7p8qs","icon":"Message-module_icon__fIMIp"};

var Message = function (_a) {
    var _b;
    var message = _a.message, _c = _a.type, type = _c === void 0 ? 'benefit' : _c, _d = _a.size, size = _d === void 0 ? 'small' : _d, icon = _a.icon, className = _a.className;
    switch (type) {
        case 'error':
            icon = 'exclamationTriangleOutline';
            break;
        case 'success':
            icon = 'checkDefault';
            break;
        default:
            icon = 'checkCircleOutline';
    }
    return (React__default.createElement("div", { className: cn(styles$h.message, (_b = {},
            _b[styles$h.error] = type === 'error',
            _b[styles$h.success] = type === 'success',
            _b[styles$h.small] = size === 'small',
            _b[styles$h.medium] = size === 'medium',
            _b[styles$h.large] = size === 'large',
            _b), className) },
        React__default.createElement(Icon, { className: styles$h.icon, icon: icon }),
        React__default.createElement("p", { className: styles$h.message }, message)));
};

var orionUID = function () {
    return 'xxxx-xxxx-xxx-xxxx'.replace(/[x]/g, function (c) {
        var r = Math.floor(Math.random() * 16);
        return r.toString(16);
    });
};
var useOrionUID = function () { return useMemo(function () { return orionUID(); }, []); };

var sanitizePartialCurrencyValue = function (value, currency) {
    // remove any character not 0-9 or . or ,
    var sanitizedValue = value.replaceAll(/[^(0-9).,]/g, '');
    // removes thousands separators
    // changes minor units separator to . if needed
    // removes .xx if minor units is false
    switch (currency.locale) {
        case 'de-DE':
            sanitizedValue = sanitizedValue.replaceAll('.', '');
            if (currency.minorUnits) {
                sanitizedValue = sanitizedValue.replaceAll(',', '.');
            }
            else {
                sanitizedValue = sanitizedValue.replaceAll(/,\d*$/g, '');
            }
            break;
        case 'en-GB':
        default:
            sanitizedValue = sanitizedValue.replaceAll(',', '');
            if (!currency.minorUnits) {
                sanitizedValue = sanitizedValue.replaceAll(/\.\d*$/g, '');
            }
    }
    return sanitizedValue;
};
var formatAsPartialCurrency = function (sanitizedValue, currency) {
    var decimalSeparator = getDecimalSeperator(currency.locale);
    var value = sanitizedValue.trim();
    if (value === '') {
        return '';
    }
    // trying to type third decimal place
    if (value.match(/(\.\d{3})$/)) {
        value = value.split('').slice(0, -1).join('');
    }
    // typing second decimal place
    if (value.match(/(\.\d{2})$/)) {
        return formatAsCurrency(value, currency);
    }
    // starting to type minor units
    var startingMinorUnits = !!value.match(/\.$/);
    var starting0 = !!value.match(/\.0$/);
    value = value.replace(/\.$/, '');
    // format to partial currency string
    var currencyString = Number(value).toLocaleString(currency.locale, {
        maximumFractionDigits: 2,
    });
    if (startingMinorUnits) {
        currencyString = "".concat(currencyString).concat(decimalSeparator);
    }
    if (starting0) {
        currencyString = "".concat(currencyString).concat(decimalSeparator, "0");
    }
    if (currency === null || currency === void 0 ? void 0 : currency.currencySymbol) {
        switch (currency.locale) {
            case 'de-DE':
                return "".concat(currencyString, " \u20AC");
            case 'en-GB':
                return "\u00A3 ".concat(currencyString);
        }
    }
    return currencyString;
};
var formatAsCurrency = function (value, currency) {
    value = value.trim();
    if (value === '') {
        return '';
    }
    var formatter = new Intl.NumberFormat(currency.locale, {
        style: currency.currencySymbol ? 'currency' : undefined,
        currency: currency.locale === 'de-DE' ? 'EUR' : 'GBP',
        minimumFractionDigits: currency.minorUnits ? 2 : 0,
        maximumFractionDigits: currency.minorUnits ? 2 : 0,
    });
    var formattedValue = formatter.format(Number(value));
    switch (currency.locale) {
        case 'de-DE':
            formattedValue = formattedValue.replace(/\s(\D+)$/, ' $1'); //enforce correct space character before €
            formattedValue = currency.currencySymbol
                ? formattedValue.replace(/,00\s€$/, ' €')
                : formattedValue.replace(/,00$/, '');
            break;
        case 'en-GB':
            formattedValue = formattedValue
                .replace(/^(\D+)/, '$1 ') // Add space after £ symbol
                .replace(/\.00$/, '');
            break;
    }
    return formattedValue;
};
var getDecimalSeperator = function (locale) {
    switch (locale) {
        case 'de-DE':
            return ',';
        case 'en-GB':
            return '.';
    }
};
var updateCursorLocation = function (ev, currency) {
    if ((currency === null || currency === void 0 ? void 0 : currency.locale) === 'de-DE' && (currency === null || currency === void 0 ? void 0 : currency.currencySymbol)) {
        var valueLength = ev.target.value.length - 2;
        ev.currentTarget.setSelectionRange(valueLength, valueLength);
    }
};

var Input = React__default.forwardRef(function (_a, ref) {
    var _b;
    var className = _a.className, disabled = _a.disabled, iconLeft = _a.iconLeft, iconRight = _a.iconRight, label = _a.label, labelProps = _a.labelProps, description = _a.description, descriptionProps = _a.descriptionProps, error = _a.error, success = _a.success, wrapperClassName = _a.wrapperClassName, type = _a.type, currency = _a.currency, inputProps = __rest(_a, ["className", "disabled", "iconLeft", "iconRight", "label", "labelProps", "description", "descriptionProps", "error", "success", "wrapperClassName", "type", "currency"]);
    useEffect(function () {
        if (type === 'currency') {
            console.warn('<Input /> with type currency has been depreciated, please use <CurrencyInput /> component ');
        }
    }, [type]);
    var orionUID = useOrionUID();
    var isCurrency = type === 'currency' && currency;
    var value = isCurrency && inputProps.value
        ? formatAsCurrency(inputProps.value.toString(), currency)
        : inputProps.value;
    var defaultValue = isCurrency && inputProps.defaultValue
        ? formatAsCurrency(inputProps.defaultValue.toString(), currency)
        : inputProps.defaultValue;
    var onChange = function (ev) {
        if (isCurrency) {
            var sanitisedValue = sanitizePartialCurrencyValue(ev.target.value, currency);
            var formattedCurrency = formatAsPartialCurrency(sanitisedValue, currency);
            ev.target.value = formattedCurrency;
            // update cursor location
            updateCursorLocation(ev, currency);
        }
        inputProps.onChange && inputProps.onChange(ev);
    };
    var onBlur = function (ev) {
        if (isCurrency) {
            var sanitisedValue = sanitizePartialCurrencyValue(ev.target.value, currency);
            ev.target.value = formatAsCurrency(sanitisedValue, currency);
        }
        inputProps.onBlur && inputProps.onBlur(ev);
    };
    return (React__default.createElement("div", { className: cn(styles$k.formElementWrapper, wrapperClassName) },
        label && (React__default.createElement(Label, __assign({ htmlFor: inputProps.id || orionUID }, labelProps), label)),
        description && (React__default.createElement(Description, __assign({ className: styles$k.description, description: description, id: "".concat(inputProps.id || orionUID, "_desc") }, descriptionProps))),
        React__default.createElement("div", { className: cn(styles$k.inputWrapper, className, (_b = {},
                _b[styles$k.disabled] = disabled,
                _b[styles$k.leftIconPadding] = !!iconLeft,
                _b[styles$k.rightIconPadding] = !!iconRight,
                _b[styles$k.topMargin] = !!label || !!description,
                _b[styles$k.error] = error,
                _b)) },
            iconLeft && React__default.createElement(Icon, { className: styles$k.iconLeft, icon: iconLeft }),
            React__default.createElement("input", __assign({ ref: ref, disabled: disabled, "aria-describedby": "".concat(inputProps.id || orionUID, "_desc"), id: orionUID, type: type }, inputProps, { value: value, defaultValue: defaultValue, onChange: onChange, onBlur: onBlur, 
                // ensures correct keyboard is shown on ios / android
                inputMode: isCurrency ? 'decimal' : inputProps.inputMode })),
            iconRight && React__default.createElement(Icon, { className: styles$k.iconRight, icon: iconRight })),
        React__default.createElement("div", { className: styles$k.status },
            error && React__default.createElement(Message, { message: error, type: "error" }),
            success && React__default.createElement(Message, { message: success, type: "success" }))));
});

var CurrencyInput = React__default.forwardRef(function (_a, ref) {
    var currency = _a.currency, inputProps = __rest(_a, ["currency"]);
    var orionUID = useOrionUID();
    var value = inputProps.value
        ? formatAsCurrency(inputProps.value.toString(), currency)
        : inputProps.value;
    var defaultValue = inputProps.defaultValue
        ? formatAsCurrency(inputProps.defaultValue.toString(), currency)
        : inputProps.defaultValue;
    var onChange = function (ev) {
        var sanitisedValue = sanitizePartialCurrencyValue(ev.target.value, currency);
        var formattedCurrency = formatAsPartialCurrency(sanitisedValue, currency);
        ev.target.value = formattedCurrency;
        // update cursor location
        updateCursorLocation(ev, currency);
        inputProps.onChange && inputProps.onChange(ev);
    };
    var onBlur = function (ev) {
        var sanitisedValue = sanitizePartialCurrencyValue(ev.target.value, currency);
        ev.target.value = formatAsCurrency(sanitisedValue, currency);
        inputProps.onBlur && inputProps.onBlur(ev);
    };
    return (React__default.createElement(Input, __assign({ id: orionUID }, inputProps, { onBlur: onBlur, onChange: onChange, defaultValue: defaultValue, value: value, type: "text", inputMode: "decimal" })));
});

var styles$g = {"optionsContainer":"ComboBox-module_optionsContainer__ky44F","options":"ComboBox-module_options__t5q-k","option":"ComboBox-module_option__mHfAx","leftIconPadding":"ComboBox-module_leftIconPadding__ehRN-","rightIconPadding":"ComboBox-module_rightIconPadding__l-7yg","iconLeft":"ComboBox-module_iconLeft__Nl-dt","iconRight":"ComboBox-module_iconRight__AM5HI","activeOption":"ComboBox-module_activeOption__98Nv-"};

var ComboBox = function (props) {
    var onOptionSelect = props.onOptionSelect, value = props.value, name = props.name, onQueryChange = props.onQueryChange, displayValue = props.displayValue, children = props.children, forceOpen = props.open, disabled = props.disabled, inputProps = __rest(props, ["onOptionSelect", "value", "name", "onQueryChange", "displayValue", "children", "open", "disabled"]);
    return (React__default.createElement(Combobox, { onChange: function (value) {
            onOptionSelect === null || onOptionSelect === void 0 ? void 0 : onOptionSelect(value);
        }, value: value, name: name, disabled: disabled }, function (_a) {
        var open = _a.open;
        return (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Combobox.Input, { onChange: function (event) {
                    onQueryChange === null || onQueryChange === void 0 ? void 0 : onQueryChange(event.target.value);
                }, displayValue: displayValue, as: Fragment },
                React__default.createElement(Input, __assign({ name: name, autoComplete: "off" }, inputProps))),
            (open || Boolean(forceOpen)) && (React__default.createElement("div", { className: styles$g.optionsContainer },
                React__default.createElement(Combobox.Options, { static: true, className: styles$g.options }, React__default.Children.map(children, function (child) {
                    if (!isComboBoxOption(child)) {
                        return child;
                    }
                    return (React__default.createElement(Combobox.Option, { as: Fragment, key: child.props.value, value: child.props.value, disabled: child.props.disabled }, function (_a) {
                        var active = _a.active, selected = _a.selected;
                        // @ts-expect-error
                        return React__default.cloneElement(child, {
                            active: active,
                            selected: selected,
                        });
                    }));
                }))))));
    }));
};
function isComboBoxOption(child) {
    var _a;
    if (!React__default.isValidElement(child))
        return false;
    if (((_a = child === null || child === void 0 ? void 0 : child.props) === null || _a === void 0 ? void 0 : _a.value) === undefined)
        return false;
    return true;
}
/**
 * Display an option for the `<ComboBox />` component.
 *
 * It must be placed as a direct child of the `<ComboBox />`
 * otherwise it will break.
 */
var ComboBoxOption = React__default.forwardRef(function (_a, ref) {
    var _b;
    var children = _a.children; _a.value; var active = _a.active; _a.selected; var className = _a.className, activeClassName = _a.activeClassName, iconLeft = _a.iconLeft, iconRight = _a.iconRight, props = __rest(_a, ["children", "value", "active", "selected", "className", "activeClassName", "iconLeft", "iconRight"]);
    return (React__default.createElement("li", __assign({}, props, { ref: ref, className: cn(styles$g.option, className, (_b = {},
            _b[styles$g.activeOption] = active,
            _b[activeClassName] = active,
            _b[styles$g.leftIconPadding] = Boolean(iconLeft),
            _b[styles$g.rightIconPadding] = Boolean(iconRight),
            _b)) }),
        iconLeft && React__default.createElement(Icon, { className: styles$g.iconLeft, icon: iconLeft }),
        children,
        iconRight && React__default.createElement(Icon, { className: styles$g.iconRight, icon: iconRight })));
});
/**
 * Component to display additional elements inside the `<ComboBox />`
 * if they should not be treated like `<ComboBoxOption />`, but have
 * identical formatting.
 */
var ComboBoxElement = React__default.forwardRef(function (_a, ref) {
    var className = _a.className, children = _a.children;
    return (React__default.createElement("div", { className: cn(styles$g.option, className), ref: ref }, children));
});

var useLang = function () {
    var lang = useState(document.documentElement.lang)[0];
    return lang;
};

var styles$f = {"fileDrop":"FileDrop-module_fileDrop__Tq-5R","highlight":"FileDrop-module_highlight__h8D6O","stacked":"FileDrop-module_stacked__3BPHA","uploadLabel":"FileDrop-module_uploadLabel__OwK1s","fileInput":"FileDrop-module_fileInput__lDFhV"};

var FileDrop = function (_a) {
    var _b;
    var _c = _a.showButton, showButton = _c === void 0 ? true : _c, _d = _a.stacked, stacked = _d === void 0 ? true : _d, onChange = _a.onChange, className = _a.className, inputProps = __rest(_a, ["showButton", "stacked", "onChange", "className"]);
    var lang = useLang();
    var orionUID = useOrionUID();
    var _e = useState(false), draggedOver = _e[0], setDraggedOver = _e[1];
    var handleDrag = function (ev, draggedOver) {
        ev.preventDefault();
        setDraggedOver(draggedOver);
    };
    var handleDrop = function (ev) {
        var data = ev.dataTransfer;
        var files = data.files;
        onChange(Array.from(files));
    };
    return (React__default.createElement("div", { className: cn(styles$f.fileDrop, (_b = {},
            _b[styles$f.highlight] = draggedOver,
            _b[styles$f.stacked] = stacked,
            _b), className), onDragEnter: function (ev) { return handleDrag(ev, true); }, onDragOver: function (ev) { return handleDrag(ev, true); }, onDragLeave: function (ev) { return handleDrag(ev, false); }, onDrop: function (ev) {
            handleDrag(ev, false);
            handleDrop(ev);
        } },
        showButton && (React__default.createElement(React__default.Fragment, null,
            React__default.createElement("input", __assign({ className: styles$f.fileInput, id: orionUID, type: "file", multiple: true, onChange: function (ev) {
                    return ev.target.files && onChange(Array.from(ev.target.files));
                } }, inputProps)),
            React__default.createElement("label", { className: styles$f.uploadLabel, htmlFor: inputProps.id || orionUID },
                React__default.createElement(Icon, { icon: "upload" }),
                React__default.createElement("span", null, lang === 'de' ? 'Laden' : 'Upload')))),
        lang === 'de' ? (React__default.createElement("span", null, "Sie hier weitere Unterlagen hoch")) : (React__default.createElement("span", null,
            showButton && 'or',
            " drag & drop files here"))));
};

var styles$e = {"button":"Button-module_button__isZvi","disabled":"Button-module_disabled__L1Q6Q","noShadow":"Button-module_noShadow__QFEsj","primary":"Button-module_primary__rC5s0","secondary":"Button-module_secondary__DYu-u","success":"Button-module_success__yat3t","action":"Button-module_action__A4KVd","denim":"Button-module_denim__uX9Hk","secondary-alt":"Button-module_secondary-alt__uvjNz","tertiary":"Button-module_tertiary__3jnpf","small":"Button-module_small__b6bpC","medium":"Button-module_medium__QkaSz","large":"Button-module_large__ZBVkR","iconOnly":"Button-module_iconOnly__9D3Oz","loading":"Button-module_loading__zsj-2","spinner":"Button-module_spinner__wDFL-"};

var Button = React__default.forwardRef(function (_a, ref) {
    var _b, _c, _d, _e;
    var className = _a.className, href = _a.href, _f = _a.variant, variant = _f === void 0 ? 'primary' : _f, _g = _a.size, size = _g === void 0 ? 'medium' : _g, iconOnly = _a.iconOnly, children = _a.children, disabled = _a.disabled, _h = _a.loading, loading = _h === void 0 ? false : _h, _j = _a.noShadow, noShadow = _j === void 0 ? false : _j, props = __rest(_a, ["className", "href", "variant", "size", "iconOnly", "children", "disabled", "loading", "noShadow"]);
    var classNames = cn(styles$e.button, styles$e[variant], styles$e[size], (_b = {}, _b[styles$e.iconOnly] = iconOnly, _b), (_c = {}, _c[styles$e.disabled] = disabled || loading, _c), (_d = {}, _d[styles$e.loading] = loading, _d), (_e = {}, _e[styles$e.noShadow] = noShadow, _e), className);
    if (href !== undefined) {
        return (React__default.createElement("a", __assign({}, props, { href: href, className: classNames, ref: ref, "aria-busy": loading }),
            React__default.createElement(React__default.Fragment, null,
                loading && React__default.createElement(Spinner, { className: styles$e.spinner, size: size }),
                children)));
    }
    return (React__default.createElement("button", __assign({}, props, { className: classNames, ref: ref, disabled: disabled || loading, "aria-busy": loading }),
        loading && React__default.createElement(Spinner, { className: styles$e.spinner, size: size }),
        children));
});
Button.displayName = 'Button';

var styles$d = {"accordion":"Accordion-module_accordion__HTUfb","toggleButton":"Accordion-module_toggleButton__bQ-CG","primary":"Accordion-module_primary__R-Xad","frame":"Accordion-module_frame__aeaq2","secondary":"Accordion-module_secondary__bSkdL","header":"Accordion-module_header__FRvo4","iconHugHeader":"Accordion-module_iconHugHeader__UCkH2","title":"Accordion-module_title__NI140","noFlexGrow":"Accordion-module_noFlexGrow__jrFAM","icon":"Accordion-module_icon__xGKgk","collapsable":"Accordion-module_collapsable__x8sy7","panel":"Accordion-module_panel__Vy2PD","small":"Accordion-module_small__rWiEj","animate":"Accordion-module_animate__o7VPx"};

var Accordion = function (_a) {
    var _b;
    var icon = _a.icon, title = _a.title, children = _a.children, _c = _a.frame, frame = _c === void 0 ? true : _c, _d = _a.iconPosition, iconPosition = _d === void 0 ? 'default' : _d, _e = _a.variant, variant = _e === void 0 ? 'primary' : _e, small = _a.small, className = _a.className, titleClassName = _a.titleClassName, headerClassName = _a.headerClassName, accordionProps = __rest(_a, ["icon", "title", "children", "frame", "iconPosition", "variant", "small", "className", "titleClassName", "headerClassName"]);
    return (React__default.createElement(Disclosure, __assign({ as: "div", className: cn(styles$d.accordion, (_b = {},
            _b[styles$d.primary] = variant === 'primary',
            _b[styles$d.secondary] = variant === 'secondary',
            _b[styles$d.small] = small,
            _b[styles$d.frame] = frame,
            _b), className) }, accordionProps), function (_a) {
        var _b, _c;
        var open = _a.open;
        return (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Disclosure.Button, { as: "div", className: cn(styles$d.header, headerClassName, (_b = {},
                    _b[styles$d.iconHugHeader] = iconPosition === 'hug',
                    _b)) },
                React__default.createElement("div", { className: cn(styles$d.title, titleClassName, (_c = {},
                        _c[styles$d.noFlexGrow] = iconPosition === 'hug',
                        _c)) },
                    icon && React__default.createElement(Icon, { className: styles$d.icon, icon: icon }),
                    title),
                React__default.createElement(Button, { className: styles$d.toggleButton, iconOnly: true, variant: "tertiary", type: "button", "aria-label": accordionProps['aria-label'] ||
                        (typeof title === 'string' ? title : undefined) },
                    React__default.createElement(Icon, { icon: "angleDown" }))),
            React__default.createElement(Disclosure.Panel, { static: true, className: styles$d.collapsable, "aria-hidden": !open, as: "section" },
                React__default.createElement("div", { className: styles$d.panel }, children))));
    }));
};

var styles$c = {"formElementWrapper":"TextArea-module_formElementWrapper__IExtG","textArea":"TextArea-module_textArea__quPne","topMargin":"TextArea-module_topMargin__lQhjf","status":"TextArea-module_status__AT7Aj"};

var TextArea = React__default.forwardRef(function (_a, ref) {
    var _b;
    var className = _a.className, disabled = _a.disabled, label = _a.label, labelProps = _a.labelProps, description = _a.description, descriptionProps = _a.descriptionProps, error = _a.error, success = _a.success, wrapperClassName = _a.wrapperClassName, textAreaProps = __rest(_a, ["className", "disabled", "label", "labelProps", "description", "descriptionProps", "error", "success", "wrapperClassName"]);
    var orionUID = useOrionUID();
    return (React__default.createElement("div", { className: cn(styles$c.formElementWrapper, wrapperClassName) },
        label && (React__default.createElement(Label, __assign({ htmlFor: textAreaProps.id || orionUID }, labelProps), label)),
        description && (React__default.createElement(Description, __assign({ className: styles$c.description, description: description, id: "".concat(textAreaProps.id || orionUID, "_desc") }, descriptionProps))),
        React__default.createElement("textarea", __assign({ className: cn(styles$c.textArea, className, (_b = {},
                _b[styles$c.topMargin] = !!label || !!description,
                _b)), ref: ref, disabled: disabled, "aria-describedby": "".concat(textAreaProps.id || orionUID, "_desc"), id: orionUID }, textAreaProps)),
        React__default.createElement("div", { className: styles$c.status },
            error && React__default.createElement(Message, { message: error, type: "error" }),
            success && React__default.createElement(Message, { message: success, type: "success" }))));
});

var styles$b = {"link":"Link-module_link__mCa48","disabled":"Link-module_disabled__Uvrep","small":"Link-module_small__vK4QZ","medium":"Link-module_medium__d0dnd","large":"Link-module_large__PkZm0"};

var Link = React__default.forwardRef(function (_a, ref) {
    var className = _a.className, _b = _a.size, size = _b === void 0 ? 'medium' : _b, children = _a.children, props = __rest(_a, ["className", "size", "children"]);
    var classNames = cn(styles$b.link, styles$b[size], className);
    return (React__default.createElement("a", __assign({}, props, { className: classNames, ref: ref }), children));
});

var _path$3;
function _extends$3() { _extends$3 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }
var SvgIwoca = function SvgIwoca(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3({
    viewBox: "0 0 129 128",
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em"
  }, props), _path$3 || (_path$3 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M121.659 0H8.17C4.111 0 .915 3.203.915 7.27L.96 120.73c0 4.068 3.196 7.271 7.255 7.271h113.746c4.059 0 7.054-3.043 6.953-7.111V7.271c0-4.017-3.247-7.271-7.256-7.271Zm-8.998 111.746H17.168V16.117l95.493.203V111.746ZM77.776 82.705l13.091 5.186c.97.363 2.048-.208 2.048-1.297V41.421c0-1.038-1.078-1.712-2.048-1.297l-13.09 5.29c-.485.208-.862.726-.862 1.4V81.46c0 .518.269.985.861 1.245Zm-25.723.011-13.091 5.185c-.97.363-2.047-.311-2.047-1.348V41.396c0-.985 1.077-1.66 2.047-1.296l13.09 5.288c.486.207.863.726.863 1.4V81.42c0 .57-.27 1.089-.862 1.296Z"
  })));
};

var _path$2;
function _extends$2() { _extends$2 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }
var SvgIwocaText = function SvgIwocaText(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2({
    viewBox: "0 0 394 128",
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em"
  }, props), _path$2 || (_path$2 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M273.17 0h113.489c4.009 0 7.256 3.254 7.256 7.27V120.89c.101 4.068-2.894 7.111-6.953 7.111H273.216c-4.059 0-7.255-3.203-7.255-7.271l-.046-113.458c0-4.068 3.196-7.271 7.255-7.271Zm8.999 111.746h95.492V16.321l-95.492-.204v95.629Zm73.698-23.855-13.091-5.187c-.592-.259-.861-.726-.861-1.244V46.815c0-.675.377-1.193.861-1.4l13.091-5.29c.97-.416 2.048.258 2.048 1.296v45.173c0 1.09-1.078 1.66-2.048 1.297Zm-51.905.01 13.091-5.185c.592-.207.862-.726.862-1.296V46.788c0-.674-.378-1.193-.862-1.4L303.962 40.1c-.97-.363-2.047.31-2.047 1.296v45.157c0 1.037 1.077 1.71 2.047 1.348ZM6.939 34.856c-3.286 0-6.024-2.675-6.024-5.973S3.653 23 6.939 23c3.469 0 6.207 2.585 6.207 5.883s-2.738 5.973-6.207 5.973Zm108.986 6.418c13.784 0 24.098 9.804 24.098 22.907 0 13.014-10.405 22.819-24.097 22.819-13.784 0-24.098-9.804-24.098-22.819 0-13.103 10.314-22.907 24.097-22.907Zm131.99 42.784c0 1.693-1.46 2.674-4.108 2.674-4.747 0-8.124-2.05-9.674-5.705-4.2 4.01-9.311 5.972-15.7 5.972-12.232 0-21.36-9.716-21.36-22.819 0-13.102 9.128-22.907 21.36-22.907 5.75 0 11.5 2.14 14.33 5.26h.182v-2.14c0-1.427.821-2.228 2.282-2.228h5.294c1.461 0 2.282.801 2.282 2.228v29.504c0 1.96.73 4.1 2.738 4.1h.183c1.461 0 2.191.713 2.191 2.05v4.01ZM156.544 64.18c0 8.38 5.842 14.262 14.148 14.262 4.93 0 8.861-1.97 11.504-5.704.548-.802 1.187-1.248 1.917-1.248.182 0 .547.09 1.186.357l4.198 1.694c.913.357 1.369 1.07 1.369 1.693 0 .356-.091.802-.456 1.337C186.028 83.345 179 87 170.693 87c-13.874 0-24.189-9.804-24.189-22.819 0-13.102 10.223-22.907 24.098-22.907 8.671 0 16.271 4.144 19.899 10.964.274.446.365.801.365 1.158 0 .713-.547 1.337-1.552 1.783l-4.29 1.782c-.123.04-.228.076-.319.107-.316.107-.473.16-.685.16-.731 0-1.279-.357-2.009-1.426-2.83-4.011-6.389-5.972-11.41-5.972-8.215 0-14.057 5.972-14.057 14.35ZM69.83 74.52l8.671-30.484c.366-1.248 1.187-1.871 2.555-1.871h4.839c1.278 0 2.008.623 2.008 1.693 0 .356 0 .624-.091.802L75.216 84.237c-.366 1.248-1.279 1.871-2.556 1.871h-5.842c-1.278 0-2.19-.624-2.556-1.782l-9.859-29.683h-.182l-9.767 29.683c-.365 1.159-1.278 1.782-2.556 1.782h-5.841c-1.279 0-2.191-.624-2.557-1.871L20.813 44.66c-.091-.178-.091-.445-.091-.802 0-1.07.73-1.693 2.008-1.693h5.568c1.46 0 2.19.534 2.556 1.871l8.398 30.485h.182l9.492-30.574c.366-1.159 1.279-1.782 2.648-1.782h6.207c1.37 0 2.282.712 2.647 1.871l9.22 30.485h.182Zm46.096-24.69c-8.215 0-14.057 5.973-14.057 14.351 0 8.29 5.842 14.261 14.057 14.262 8.216 0 14.057-5.883 14.057-14.262 0-8.378-5.841-14.35-14.057-14.35ZM14.788 77.997c1.461 0 2.191.713 2.191 2.05v4.01c0 1.694-1.46 2.675-4.107 2.675-6.755 0-10.77-4.189-10.77-11.32V44.393c0-1.427.82-2.228 2.281-2.228h5.203c1.46 0 2.282.801 2.282 2.228v29.504c0 1.96.73 4.1 2.738 4.1h.182Zm218.156-22.283c-3.104-3.922-7.302-5.884-12.414-5.884-8.279 0-13.418 6.062-13.418 14.262 0 8.201 5.112 14.351 13.418 14.351 4.93 0 9.219-2.05 12.414-6.15v-16.58Z"
  })));
};

var _path$1;
function _extends$1() { _extends$1 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }
var SvgIwocaPay = function SvgIwocaPay(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    viewBox: "0 0 312 128",
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em"
  }, props), _path$1 || (_path$1 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M7.896 128h113.672c3.976 0 7.19-3.214 7.136-7.19V7.19a7.183 7.183 0 0 0-7.19-7.19H7.896a7.183 7.183 0 0 0-7.19 7.19v113.62a7.183 7.183 0 0 0 7.19 7.19Zm106.483-14.434H15.14v-99.24h99.239v99.24ZM91.225 41.234V86.77c0 1.09-1.035 1.744-2.015 1.362l-11.493-4.793c-.544-.272-.871-.762-.871-1.361v-35.95c0-.599.327-1.143.871-1.361l11.493-4.793a1.468 1.468 0 0 1 2.015 1.361Zm-38.596 4.79v35.948c0 .6-.327 1.09-.872 1.362l-11.492 4.793c-.98.381-2.016-.272-2.016-1.362V41.23a1.468 1.468 0 0 1 2.015-1.362l11.493 4.793c.49.218.872.763.872 1.362Zm105.054 43.098h6.12c1.579 0 2.468-.888 2.468-2.468V64.741h12.535c15.004 0 25.368-9.476 25.368-22.801 0-13.326-10.364-22.703-25.368-22.703h-21.123c-1.579 0-2.468.888-2.468 2.468v64.95c0 1.579.889 2.467 2.468 2.467Zm35.535-47.182c0 7.699-5.528 12.536-14.412 12.536h-12.535V29.503h12.535c8.884 0 14.412 4.836 14.412 12.437Zm62.889 33.659V42.828c0-1.58-.888-2.468-2.468-2.468h-5.527c-1.58 0-2.468.889-2.468 2.468v2.566h-.198c-3.059-3.356-8.094-6.02-14.213-6.02-13.129 0-22.999 10.561-22.999 25.367 0 14.806 9.87 25.368 22.999 25.368 6.909 0 12.535-3.356 16.089-7.206 1.382 4.64 4.343 6.614 9.081 6.614 2.863 0 5.232-1.086 5.232-4.047v-3.06c0-1.48-.79-2.27-2.369-2.27h-.198c-2.171 0-2.961-2.37-2.961-4.541Zm-10.463-20.827V74.71c-2.665 3.356-7.601 5.725-12.536 5.725-8.39 0-14.214-6.416-14.214-15.695 0-9.278 5.824-15.694 14.214-15.694 4.935 0 9.871 2.369 12.536 5.725Zm65.57-11.648-21.223 54.684c-3.158 8.094-8.587 12.043-16.681 12.043-2.468 0-5.528-.494-9.081-2.172-1.58-.691-1.974-1.777-1.086-3.553l1.678-3.455c.79-1.678 1.678-2.073 3.257-1.382 1.185.493 2.962 1.086 5.232 1.086 3.553 0 6.12-1.975 7.699-6.219l1.777-5.034-17.866-45.998c-.593-1.58.197-2.764 2.072-2.764h5.627c1.579 0 2.27.593 2.764 2.073l12.536 34.844h.197l12.733-34.844c.494-1.382 1.283-2.073 2.764-2.073h5.528c1.776 0 2.665 1.382 2.073 2.764Z"
  })));
};

var _path;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
var SvgIwocaPayText = function SvgIwocaPayText(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    viewBox: "0 0 596 128",
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em"
  }, props), _path || (_path = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M405.568 128H291.896a7.183 7.183 0 0 1-7.189-7.19V7.19A7.182 7.182 0 0 1 291.896 0h113.618a7.183 7.183 0 0 1 7.19 7.19v113.62c.054 3.976-3.159 7.19-7.136 7.19ZM299.14 113.566h99.239v-99.24H299.14v99.24Zm76.085-51.797V41.234a1.468 1.468 0 0 0-2.015-1.361l-11.493 4.793c-.544.218-.871.763-.871 1.362v35.949c0 .599.327 1.089.871 1.361l11.493 4.794c.98.38 2.015-.273 2.015-1.362V61.769Zm-38.596 20.203V46.023c0-.599-.382-1.144-.872-1.362l-11.492-4.793a1.468 1.468 0 0 0-2.016 1.362v45.535c0 1.09 1.035 1.743 2.016 1.362l11.492-4.793c.545-.273.872-.763.872-1.362Zm111.174 7.15h-6.12c-1.579 0-2.468-.888-2.468-2.468v-64.95c0-1.579.889-2.467 2.468-2.467h21.123c15.004 0 25.368 9.377 25.368 22.703 0 13.325-10.364 22.801-25.368 22.801H450.27v21.913c0 1.58-.888 2.468-2.467 2.468Zm15.003-34.646c8.884 0 14.412-4.837 14.412-12.536 0-7.6-5.528-12.437-14.412-12.437H450.27v24.973h12.536Zm77.301-11.648V75.6c0 2.172.789 4.54 2.961 4.54h.197c1.58 0 2.369.79 2.369 2.27v3.06c0 2.962-2.369 4.048-5.231 4.048-4.738 0-7.699-1.974-9.081-6.614-3.554 3.85-9.18 7.206-16.09 7.206-13.128 0-22.998-10.562-22.998-25.368s9.87-25.368 22.998-25.368c6.12 0 11.154 2.665 14.214 6.022h.198v-2.567c0-1.58.888-2.468 2.467-2.468h5.528c1.579 0 2.468.889 2.468 2.468Zm-10.463 31.883V54.77c-2.665-3.355-7.601-5.724-12.536-5.724-8.39 0-14.214 6.416-14.214 15.694 0 9.279 5.824 15.695 14.214 15.695 4.935 0 9.871-2.37 12.536-5.725Zm44.347 23.097 21.222-54.684c.593-1.382-.296-2.764-2.072-2.764h-5.528c-1.481 0-2.27.691-2.764 2.073l-12.733 34.844h-.198l-12.535-34.844c-.494-1.48-1.185-2.073-2.764-2.073h-5.627c-1.875 0-2.665 1.185-2.072 2.764l17.866 45.998-1.777 5.034c-1.579 4.244-4.146 6.219-7.699 6.219-2.271 0-4.047-.593-5.232-1.086-1.579-.691-2.468-.296-3.257 1.382l-1.678 3.455c-.889 1.776-.494 2.862 1.086 3.553 3.553 1.678 6.613 2.172 9.081 2.172 8.094 0 13.523-3.949 16.681-12.043ZM.915 24.838c0 3.652 2.96 6.613 6.514 6.613 3.751 0 6.712-2.961 6.712-6.614 0-3.652-2.96-6.514-6.712-6.514-3.553 0-6.514 2.862-6.514 6.514ZM2.198 76.56c0 7.897 4.343 12.536 11.746 12.536 2.862 0 4.442-1.086 4.442-2.961v-4.442c0-1.48-.79-2.27-2.37-2.27h-.197c-2.171 0-2.96-2.37-2.96-4.54V42.11c0-1.579-.89-2.467-2.469-2.467H4.665c-1.579 0-2.467.888-2.467 2.467v34.45ZM84.98 41.717l-9.476 33.856h-.197L65.24 41.717c-.395-1.284-1.382-2.073-2.863-2.073H56.06c-1.48 0-2.468.69-2.863 1.974L42.734 75.573h-.197l-9.081-33.856c-.395-1.481-1.185-2.073-2.764-2.073H24.67c-1.58 0-2.567 1.283-2.073 2.764l13.72 43.924c.395 1.382 1.382 2.073 2.764 2.073h6.317c1.382 0 2.37-.69 2.764-1.974l10.562-32.968h.197L69.484 86.43c.395 1.283 1.382 1.974 2.764 1.974h6.317c1.382 0 2.37-.69 2.764-2.073l13.72-43.924c.494-1.481-.493-2.764-2.073-2.764h-5.231c-1.48 0-2.37.69-2.764 2.073Zm39.295 47.675c-14.313 0-25.072-10.858-25.072-25.367 0-14.51 10.759-25.368 25.072-25.368 14.313 0 25.072 10.759 25.072 25.368 0 14.51-10.759 25.367-25.072 25.367Zm0-9.673c8.193 0 14.411-6.712 14.411-15.694 0-8.983-6.218-15.695-14.411-15.695s-14.411 6.712-14.411 15.695c0 8.982 6.218 15.694 14.411 15.694Zm78.457-29.316c-4.146-7.304-12.042-11.746-21.123-11.746-13.819 0-24.184 10.858-24.184 25.368 0 14.51 10.365 25.367 24.184 25.367 9.278 0 15.99-3.553 20.827-11.252.888-1.48.395-2.665-1.086-3.356l-4.935-2.172c-1.382-.592-2.468-.197-3.356 1.086-2.665 3.75-6.614 6.021-11.45 6.021-7.699 0-13.523-6.712-13.523-15.694 0-8.983 5.824-15.695 13.523-15.695 4.935 0 8.883 2.566 11.647 6.416.987 1.382 1.876 1.777 3.356 1.086l4.936-2.172c1.48-.69 2.073-1.777 1.184-3.257Zm54.523-8.292v32.771c0 2.172.79 4.54 2.961 4.54h.198c1.579 0 2.369.79 2.369 2.271v3.06c0 2.961-2.369 4.047-5.232 4.047-4.738 0-7.699-1.974-9.081-6.613-3.553 3.85-9.18 7.205-16.089 7.205-13.128 0-22.999-10.561-22.999-25.367 0-14.807 9.871-25.368 22.999-25.368 6.12 0 11.154 2.665 14.214 6.02h.197v-2.566c0-1.579.888-2.467 2.468-2.467h5.527c1.58 0 2.468.888 2.468 2.467Zm-10.463 31.883V54.055c-2.665-3.356-7.6-5.725-12.536-5.725-8.39 0-14.214 6.416-14.214 15.695 0 9.278 5.824 15.694 14.214 15.694 4.936 0 9.871-2.369 12.536-5.725Z"
  })));
};

var IwocaLogo = function (_a) {
    var _b = _a.color, color = _b === void 0 ? 'red' : _b, iconOnly = _a.iconOnly, iwocaPay = _a.iwocaPay, className = _a.className;
    var fill = useMemo(function () {
        switch (color) {
            case 'red':
                return '#fb5950';
            case 'white':
                return '#fff';
            case 'black':
                return '#000';
        }
    }, [color]);
    if (iwocaPay) {
        return iconOnly ? (React__default.createElement(SvgIwocaPay, { className: className, fill: fill, height: "auto", width: "auto" })) : (React__default.createElement(SvgIwocaPayText, { className: className, fill: fill, height: "auto", width: "auto" }));
    }
    else {
        return iconOnly ? (React__default.createElement(SvgIwoca, { className: className, fill: fill, height: "auto", width: "auto" })) : (React__default.createElement(SvgIwocaText, { className: className, fill: fill, height: "auto", width: "auto" }));
    }
};

var styles$a = {"formWrapper":"Radio-module_formWrapper__QMHmw","radio":"Radio-module_radio__GyFv7","frame":"Radio-module_frame__bWUl9"};

var Radio = React__default.forwardRef(function (_a, ref) {
    var _b;
    var label = _a.label, labelProps = _a.labelProps, wrapperClassName = _a.wrapperClassName, frame = _a.frame, disabled = _a.disabled, className = _a.className, inputProps = __rest(_a, ["label", "labelProps", "wrapperClassName", "frame", "disabled", "className"]);
    var orionUID = useOrionUID();
    return (React__default.createElement("div", { className: cn(styles$a.formWrapper, (_b = {},
            _b[styles$a.frame] = frame,
            _b), wrapperClassName) },
        React__default.createElement("input", __assign({ type: "radio", className: cn(styles$a.radio, className), disabled: disabled, ref: ref, id: orionUID }, inputProps)),
        label && (React__default.createElement(Label, __assign({ htmlFor: inputProps.id || orionUID }, labelProps), label))));
});

var styles$9 = {"formWrapper":"CheckBox-module_formWrapper__f6sU-","checkbox":"CheckBox-module_checkbox__agCjy","status":"CheckBox-module_status__KF3p3","frame":"CheckBox-module_frame__JyCMU"};

var CheckBox = React__default.forwardRef(function (_a, ref) {
    var _b;
    var label = _a.label, labelProps = _a.labelProps, wrapperClassName = _a.wrapperClassName, frame = _a.frame, disabled = _a.disabled, className = _a.className, inputProps = __rest(_a, ["label", "labelProps", "wrapperClassName", "frame", "disabled", "className"]);
    var orionUID = useOrionUID();
    return (React__default.createElement("div", { className: cn(styles$9.formWrapper, (_b = {},
            _b[styles$9.frame] = frame,
            _b), wrapperClassName) },
        React__default.createElement("input", __assign({ type: "checkbox", className: cn(styles$9.checkbox, className), disabled: disabled, ref: ref, id: orionUID }, inputProps)),
        label && (React__default.createElement(Label, __assign({ htmlFor: inputProps.id || orionUID }, labelProps), label))));
});

var styles$8 = {"contentSwitcherWrapper":"ContentSwitcher-module_contentSwitcherWrapper__c5wXW","description":"ContentSwitcher-module_description__MK93V","contentSwitcher":"ContentSwitcher-module_contentSwitcher__1gD6k","topMargin":"ContentSwitcher-module_topMargin__FF-qc","contentSwitcherButton":"ContentSwitcher-module_contentSwitcherButton__6QdND","selected":"ContentSwitcher-module_selected__ykZvh"};

var CsButton = function (_a) {
    var _b;
    var selected = _a.selected, setSelected = _a.setSelected, children = _a.children, className = _a.className, buttonProps = __rest(_a, ["selected", "setSelected", "children", "className"]);
    return (React__default.createElement("button", __assign({ type: "button" }, buttonProps, { className: cn(styles$8.contentSwitcherButton, (_b = {},
            _b[styles$8.selected] = selected,
            _b), className), onClick: function (ev) {
            setSelected && buttonProps.id && setSelected(buttonProps.id);
            buttonProps.onClick && buttonProps.onClick(ev);
        } }), children));
};

var ContentSwitcher = function (_a) {
    var _b;
    var children = _a.children, className = _a.className, defaultValue = _a.defaultValue, value = _a.value, label = _a.label, labelProps = _a.labelProps, description = _a.description, descriptionProps = _a.descriptionProps, wrapperClassName = _a.wrapperClassName;
    var _c = useState(value || defaultValue), selected = _c[0], setSelected = _c[1];
    var orionUID = useOrionUID();
    useEffect(function () {
        if (value) {
            setSelected(value);
        }
    }, [value]);
    var childrenWithProps = React__default.Children.map(children, function (child, index) {
        if (React__default.isValidElement(child)) {
            var childId = child.props.id || "".concat(orionUID, "_").concat(index);
            var props = {
                id: childId,
                selected: selected === childId,
            };
            if (!value) {
                props.setSelected = setSelected;
            }
            return React__default.cloneElement(child, props);
        }
        return child;
    });
    return (React__default.createElement("div", { className: cn(styles$8.contentSwitcherWrapper, wrapperClassName) },
        label && (React__default.createElement(Label, __assign({ id: "".concat(orionUID, "_label") }, labelProps), label)),
        description && (React__default.createElement(Description, __assign({ description: description, id: "".concat(orionUID, "_desc") }, descriptionProps))),
        React__default.createElement("div", { className: cn(styles$8.contentSwitcher, (_b = {},
                _b[styles$8.topMargin] = !!label || !!description,
                _b), className), "aria-labelledby": "".concat(orionUID, "_label"), "aria-describedby": "".concat(orionUID, "_desc") }, childrenWithProps)));
};
ContentSwitcher.Button = CsButton;

var styles$7 = {"switch":"Switch-module_switch__dEJi0","on":"Switch-module_on__9QkrO","disabled":"Switch-module_disabled__C-5mW"};

var Switch = React__default.forwardRef(function (_a, ref) {
    var _b;
    var checked = _a.checked, disabled = _a.disabled, className = _a.className, props = __rest(_a, ["checked", "disabled", "className"]);
    var _c = useState(checked || false), internalValue = _c[0], setInternalValue = _c[1];
    useEffect(function () {
        setInternalValue(checked || false);
    }, [checked]);
    return (React__default.createElement("button", __assign({}, props, { ref: ref, className: cn(styles$7.switch, (_b = {}, _b[styles$7.on] = internalValue, _b[styles$7.disabled] = disabled, _b), className), disabled: disabled, type: "button", role: "switch", "aria-checked": internalValue, "aria-readonly": disabled, onClick: function (ev) {
            setInternalValue(!internalValue);
            props.onClick && props.onClick(ev);
        } }),
        React__default.createElement("span", null)));
});
Switch.displayName = 'Switch';

var styles$6 = {"container":"slider-module_container__kE6e-","disabled":"slider-module_disabled__xFPKD","sliderContainer":"slider-module_sliderContainer__W0inJ","slider":"slider-module_slider__jDMCa","hide":"slider-module_hide__dEYJK","stops":"slider-module_stops__hCPEi","squareSliderLeft":"slider-module_squareSliderLeft__-sHWT","squareSliderRight":"slider-module_squareSliderRight__9rlxy","min":"slider-module_min__GDqxO","max":"slider-module_max__hi4Ix","fakeBar":"slider-module_fakeBar__NQg5C","fakeLeftBar":"slider-module_fakeLeftBar__GtzE6","fakeRightBar":"slider-module_fakeRightBar__GpWY1"};

var Slider = React__default.forwardRef(function (props, ref) {
    var _a, _b, _c, _d;
    var stops = props.stops, absMin = props.absMin, min = props.min, max = props.max, absMax = props.absMax, leftLabel = props.leftLabel, rightLabel = props.rightLabel, defaultValue = props.defaultValue, disabled = props.disabled, labelClassName = props.labelClassName, rightLabelClassName = props.rightLabelClassName, leftLabelClassName = props.leftLabelClassName, inputProps = __rest(props, ["stops", "absMin", "min", "max", "absMax", "leftLabel", "rightLabel", "defaultValue", "disabled", "labelClassName", "rightLabelClassName", "leftLabelClassName"]);
    var leftStop = useRef(null);
    var rightStop = useRef(null);
    var containerRef = useRef(null);
    var _e = useState(Number(defaultValue) || Number(inputProps.value) || min), internalValue = _e[0], setInternalValue = _e[1];
    // keep internal state in sync with external state
    useEffect(function () {
        if (Number(inputProps.value) >= min && Number(inputProps.value) <= max) {
            setInternalValue(Number(inputProps.value));
        }
    }, [inputProps.value, setInternalValue, min, max]);
    // make fake bar relative sizes acurate
    var fakeLeftSize = useMemo(function () {
        if (absMin !== undefined) {
            return (100 * (min - absMin)) / ((absMax !== null && absMax !== void 0 ? absMax : max) - absMin);
        }
        return 0;
    }, [absMin, absMax, min, max]);
    var fakeRightSize = useMemo(function () {
        if (absMax !== undefined) {
            return (100 * (absMax - max)) / (absMax - (absMin !== null && absMin !== void 0 ? absMin : min));
        }
        return 0;
    }, [absMin, absMax, min, max]);
    var sliderSize = useMemo(function () { return 100 - fakeLeftSize - fakeRightSize; }, [fakeLeftSize, fakeRightSize]);
    // updates css variables for background bar color & thumb position
    // cross browser compatability
    useEffect(function () {
        if (containerRef.current) {
            var percentage = (100 * (internalValue - min)) / (max - min);
            containerRef.current.style.setProperty('--sliderVal', "".concat(percentage, "%"));
        }
    }, [containerRef, internalValue, inputProps.value, min, max]);
    // updates stop data attribute for labels
    useLayoutEffect(function () {
        if (stops && leftStop.current && typeof absMin === 'number') {
            leftStop.current.setAttribute('data-value', "".concat(min.toLocaleString()));
        }
        else if (stops && leftStop.current) {
            leftStop.current.setAttribute('data-value', "");
        }
        if (stops && rightStop.current && typeof absMax === 'number') {
            rightStop.current.setAttribute('data-value', "".concat(max.toLocaleString()));
        }
        else if (stops && rightStop.current) {
            rightStop.current.setAttribute('data-value', "");
        }
    }, [stops, min, max, absMin, absMax]);
    // check if mins and maxes make sense
    if (![absMin, min, max, absMax].filter(function (val) { return val !== undefined; }).every(function (val, i, arr) { return i === 0 || val >= arr[i - 1]; })) {
        return React__default.createElement("div", null, "Input Values in Incorrect Order");
    }
    return (React__default.createElement("div", { className: cn(styles$6.container, (_a = {}, _a[styles$6.disabled] = disabled, _a)), ref: containerRef },
        leftLabel && (React__default.createElement("span", { className: cn(labelClassName, leftLabelClassName) }, leftLabel)),
        absMin !== undefined && absMin !== min && (React__default.createElement("div", { "data-testid": "fakeLeftBar", style: { width: "".concat(fakeLeftSize, "%") }, className: cn(styles$6.fakeBar, styles$6.fakeLeftBar) })),
        React__default.createElement("div", { className: cn(styles$6.sliderContainer, (_b = {},
                _b[styles$6.hide] = sliderSize === 0,
                _b)), style: {
                width: "".concat(sliderSize, "%"),
            } },
            stops && (React__default.createElement("div", { ref: leftStop, className: cn(styles$6.stops, styles$6.min) })),
            React__default.createElement("input", __assign({ ref: ref, type: "range", className: cn(styles$6.slider, (_c = {}, _c[styles$6.squareSliderLeft] = !stops && absMin && absMin !== min, _c), (_d = {},
                    _d[styles$6.squareSliderRight] = !stops && absMax && absMax !== max,
                    _d), inputProps.className) }, inputProps, { value: internalValue, min: min, max: max, disabled: disabled, onChange: function (ev) {
                    setInternalValue(parseFloat(ev.target.value));
                    inputProps.onChange && inputProps.onChange(ev);
                } })),
            stops && (React__default.createElement("div", { ref: rightStop, className: cn(styles$6.stops, styles$6.max) }))),
        absMax !== undefined && absMax !== max && (React__default.createElement("div", { "data-testid": "fakeRightBar", style: { width: "".concat(fakeRightSize, "%") }, className: cn(styles$6.fakeBar, styles$6.fakeRightBar) })),
        rightLabel && (React__default.createElement("span", { className: cn(labelClassName, rightLabelClassName) }, rightLabel))));
});
Slider.displayName = 'Range Input';

var styles$5 = {"wrapper":"Modal-module_wrapper__-N-rK","modal":"Modal-module_modal__8Ux8P","title":"Modal-module_title__UQ2ak","header":"Modal-module_header__4A9Vp","headerBar":"Modal-module_headerBar__WYlUs","subTitle":"Modal-module_subTitle__SlvTs","childWrapper":"Modal-module_childWrapper__kBfXx","closeIcon":"Modal-module_closeIcon__fZdK5","small":"Modal-module_small__G7a3z","modalWithCloseIcon":"Modal-module_modalWithCloseIcon__tYTiG","modalWithHeaderBar":"Modal-module_modalWithHeaderBar__zW-mC","overlay":"Modal-module_overlay__xm74-","medium":"Modal-module_medium__bgfFZ","large":"Modal-module_large__oI-r8"};

var Modal = function (_a) {
    var _b, _c;
    var isOpen = _a.isOpen, children = _a.children, title = _a.title, subTitle = _a.subTitle, className = _a.className, style = _a.style, _d = _a.size, size = _d === void 0 ? 'large' : _d, _e = _a.headerBar, headerBar = _e === void 0 ? false : _e, _f = _a.closeIcon, closeIcon = _f === void 0 ? false : _f, onClose = _a.onClose, props = __rest(_a, ["isOpen", "children", "title", "subTitle", "className", "style", "size", "headerBar", "closeIcon", "onClose"]);
    return (React__default.createElement(Dialog, { className: styles$5.wrapper, open: isOpen, onClose: onClose },
        React__default.createElement("div", { "data-testid": "modalOverlay", className: styles$5.overlay, "aria-hidden": "true" }),
        React__default.createElement(Dialog.Panel, __assign({}, props, { className: cn(styles$5.modal, styles$5[size], className, (_b = {},
                _b[styles$5.modalWithCloseIcon] = closeIcon,
                _b[styles$5.modalWithHeaderBar] = headerBar,
                _b)), style: style }),
            closeIcon && onClose && (React__default.createElement(Button, { className: styles$5.closeIcon, iconOnly: true, variant: "tertiary", size: "small", onClick: onClose, type: "button", "aria-label": "close modal" },
                React__default.createElement(Icon, { "data-testid": "closeIcon", icon: "timesDefault" }))),
            (title || subTitle) && (React__default.createElement("div", { className: cn(styles$5.header, (_c = {}, _c[styles$5.headerBar] = headerBar, _c)) },
                title && (React__default.createElement(Dialog.Title, { "data-testid": "modal-title", className: styles$5.title }, title)),
                subTitle && (React__default.createElement(Dialog.Description, { "data-testid": "modal-subtitle", className: styles$5.subTitle }, subTitle)))),
            React__default.createElement("div", { className: styles$5.childWrapper }, children))));
};

var styles$4 = {"formElementWrapper":"Select-module_formElementWrapper__zl2kf","selectWrapper":"Select-module_selectWrapper__XvEHG","topMargin":"Select-module_topMargin__ey3XP","disabled":"Select-module_disabled__QiPRW","iconPadding":"Select-module_iconPadding__tH-lQ","status":"Select-module_status__xJ0vj"};

var Select = React__default.forwardRef(function (_a, ref) {
    var _b;
    var className = _a.className, wrapperClassName = _a.wrapperClassName, icon = _a.icon, placeholder = _a.placeholder, disabled = _a.disabled, label = _a.label, labelProps = _a.labelProps, description = _a.description, descriptionProps = _a.descriptionProps, error = _a.error, success = _a.success, children = _a.children, selectProps = __rest(_a, ["className", "wrapperClassName", "icon", "placeholder", "disabled", "label", "labelProps", "description", "descriptionProps", "error", "success", "children"]);
    var orionUID = useOrionUID();
    var placeholderProps = selectProps.value === undefined
        ? {
            defaultValue: '',
        }
        : {};
    return (React__default.createElement("div", { className: cn(styles$4.formElementWrapper, wrapperClassName) },
        label && (React__default.createElement(Label, __assign({ htmlFor: selectProps.id || orionUID }, labelProps), label)),
        description && (React__default.createElement(Description, __assign({ className: styles$4.description, description: description, id: "".concat(selectProps.id || orionUID, "_desc") }, descriptionProps))),
        React__default.createElement("div", { className: cn(styles$4.selectWrapper, className, (_b = {},
                _b[styles$4.disabled] = disabled,
                _b[styles$4.iconPadding] = !!icon,
                _b[styles$4.topMargin] = !!label || !!description,
                _b)) },
            icon && React__default.createElement(Icon, { icon: icon }),
            React__default.createElement("select", __assign({ ref: ref, disabled: disabled, "aria-describedby": "".concat(selectProps.id || orionUID, "_desc"), id: orionUID }, placeholderProps, selectProps),
                placeholder && (React__default.createElement("option", { value: "", disabled: true }, placeholder)),
                children)),
        React__default.createElement("div", { className: styles$4.status },
            error && React__default.createElement(Message, { message: error, type: "error" }),
            success && React__default.createElement(Message, { message: success, type: "success" }))));
});

var styles$3 = {"card":"Card-module_card__e-Ujr"};

var Card = function (_a) {
    var className = _a.className, children = _a.children, cardProps = __rest(_a, ["className", "children"]);
    return (React__default.createElement("article", __assign({ className: cn(styles$3.card, className) }, cardProps), children));
};

var styles$2 = {"pill":"Pill-module_pill__42sHu","green":"Pill-module_green__JWQFO","london":"Pill-module_london__pe0Mm","yellow":"Pill-module_yellow__bM3A-","red":"Pill-module_red__VZBEK","small":"Pill-module_small__Iu-WF","medium":"Pill-module_medium__POseD","large":"Pill-module_large__2CHkY"};

var Pill = function (_a) {
    var children = _a.children, _b = _a.variant, variant = _b === void 0 ? 'london' : _b, _c = _a.size, size = _c === void 0 ? 'small' : _c, className = _a.className, pillProps = __rest(_a, ["children", "variant", "size", "className"]);
    return (React__default.createElement("div", __assign({ className: cn(styles$2.pill, styles$2[variant], styles$2[size], className) }, pillProps), children));
};

var styles$1 = {"alertBox":"AlertBox-module_alertBox__iKHU4","success":"AlertBox-module_success__975sx","failure":"AlertBox-module_failure__zrPnA","cancel":"AlertBox-module_cancel__Cibo-","connect":"AlertBox-module_connect__sucDn"};

var AlertBox = function (_a) {
    var _b = _a.variant, variant = _b === void 0 ? 'success' : _b, children = _a.children, className = _a.className, alertProps = __rest(_a, ["variant", "children", "className"]);
    return (React__default.createElement("div", __assign({ role: "alert", className: cn(styles$1.alertBox, styles$1[variant], className) }, alertProps), children));
};
AlertBox.displayName = 'AlertBox';

var styles = {"refElemWrapper":"Tooltip-module_refElemWrapper__cQlG4","icon":"Tooltip-module_icon__PPVZM","secondaryTooltip":"Tooltip-module_secondaryTooltip__uEYiu","tooltip":"Tooltip-module_tooltip__va-Bl","arrow":"Tooltip-module_arrow__h23Tc","tooltipContent":"Tooltip-module_tooltipContent__HYHXI"};

var arrowPadding = function (_a) {
    var placement = _a.placement;
    if (placement.includes('-start') || placement.includes('-end')) {
        return 16;
    }
    else {
        return 0;
    }
};
var useArrowModifier = function (arrowElement) { return ({
    name: 'arrow',
    options: {
        element: arrowElement,
        padding: useCallback(arrowPadding, []),
    },
}); };
// Flip Modifer
var useFlipModifier = function () { return ({ name: 'flip', enabled: true }); };
var offset = function (_a) {
    var placement = _a.placement;
    if (placement.includes('-start')) {
        return [-16, 8];
    }
    else if (placement.includes('-end')) {
        return [16, 8];
    }
    else {
        return [0, 8];
    }
};
var useOffsetModifier = function () { return ({
    name: 'offset',
    options: {
        offset: useCallback(offset, []),
    },
}); };

var Tooltip = function (_a) {
    var _b;
    var content = _a.content, _c = _a.variant, variant = _c === void 0 ? 'primary' : _c, externalOpen = _a.open, openDelay = _a.openDelay, closeDelay = _a.closeDelay, placement = _a.placement, strategy = _a.strategy, dataTestId = _a.dataTestId, children = _a.children, props = __rest(_a, ["content", "variant", "open", "openDelay", "closeDelay", "placement", "strategy", "dataTestId", "children"]);
    var orionUID = useOrionUID();
    var _d = useState(externalOpen || false), open = _d[0], setOpen = _d[1];
    var _e = useState(), referenceElement = _e[0], setReferenceElement = _e[1];
    var _f = useState(), popperElement = _f[0], setPopperElement = _f[1];
    var _g = useState(), arrowElement = _g[0], setArrowElement = _g[1];
    var _h = usePopper(referenceElement, popperElement, {
        placement: placement,
        strategy: strategy,
        modifiers: [
            useArrowModifier(arrowElement),
            useFlipModifier(),
            useOffsetModifier(),
        ],
    }), popperStyles = _h.styles, attributes = _h.attributes, update = _h.update;
    var willOpen = useRef();
    var showTooltip = useCallback(function () {
        willOpen.current = setTimeout(function () {
            setOpen(true);
            update && update();
        }, openDelay);
    }, [setOpen, update, openDelay]);
    var hideTooltip = useCallback(function () {
        clearTimeout(willOpen.current);
        setTimeout(function () {
            setOpen(false);
        }, closeDelay);
    }, [setOpen, closeDelay]);
    useEffect(function () {
        if (externalOpen !== undefined) {
            externalOpen ? showTooltip() : hideTooltip();
        }
    }, [externalOpen, showTooltip, hideTooltip]);
    var keyDown = useCallback(function (ev) {
        if (ev.key === 'Escape') {
            hideTooltip();
        }
    }, [hideTooltip]);
    // hide tooltip on escape for a11y
    useLayoutEffect(function () {
        referenceElement === null || referenceElement === void 0 ? void 0 : referenceElement.addEventListener('keydown', keyDown);
        return function () { return referenceElement === null || referenceElement === void 0 ? void 0 : referenceElement.removeEventListener('keydown', keyDown); };
    }, [referenceElement, hideTooltip, keyDown]);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("span", { "data-testid": dataTestId, className: styles.refElemWrapper, "aria-describedby": "tooltip_".concat(orionUID), ref: setReferenceElement, onMouseEnter: function () {
                if (externalOpen === undefined) {
                    showTooltip();
                }
            }, onFocus: function () {
                if (externalOpen === undefined) {
                    showTooltip();
                }
            }, onMouseLeave: function () {
                if (externalOpen === undefined) {
                    hideTooltip();
                }
            }, onBlur: function () {
                if (externalOpen === undefined) {
                    hideTooltip();
                }
            } }, children ? (children) : (React__default.createElement(Icon, { className: cn(styles.icon, (_b = {},
                _b[styles.secondaryTooltip] = variant === 'secondary',
                _b)), tabIndex: 0, icon: "questionMarkFilled" }))),
        React__default.createElement("div", __assign({ id: "tooltip_".concat(orionUID), role: "tooltip", "data-show": open, style: popperStyles.popper }, props, attributes.popper, { className: cn(styles.tooltip, props.className), ref: setPopperElement }),
            React__default.createElement("div", { className: styles.tooltipContent }, content),
            React__default.createElement("div", { ref: setArrowElement, className: styles.arrow, style: popperStyles.arrow }))));
};
Tooltip.displayName = 'Tooltip';

var currencyToNumber = function (value, currency) {
    return Number(sanitizePartialCurrencyValue(value, currency));
};

export { Accordion, AlertBox, Button, Card, CheckBox, ComboBox, ComboBoxElement, ComboBoxOption, ContentSwitcher, CurrencyInput, Description, FileDrop, Icon, Input, IwocaLogo, Label, Link, Message, Modal, Pill, Radio, Select, Skeleton, Slider, Spinner, Switch, Table, TextArea, Tooltip, currencyToNumber };
//# sourceMappingURL=index.esm.js.map
